// Libraries
import { getDirections } from "@/utils/mapsApi";
import { allowsCaseQuantities, allowsSingleQuantities } from "@/utils/restrictionResolver";
import {
  // Functions
  functions,
  httpsCallable,
  getDoc,
  getDocs,
  query,
  collection,
  where,
  increment,
  serverTimestamp,
  writeBatch,
  doc,
  firestore,
  deleteDoc,
  limit,
  updateDoc,
  setDoc,
  runTransaction,
  // Storage
  storage,
  ref,
  getDownloadURL,
  getMetadata,
  getBytes,
} from "@/firebase/init";
import moment from "moment-timezone";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import { Buffer } from "buffer";
import LeaflinkClient from "@/utils/LeaflinkClient";
import {
  computeOrderBalance,
  getLeaflinkDiscountPayload,
} from "@/utils/leaflinkDiscount";
import { buildOrderEmailHtml } from "@/utils/orderEmailHtml";
import {
  createActivity,
  createInventoryActivity,
} from "@/services/activityService";

import store from "@/store/index.js";
import { addBreadcrumb, logWorkflowWarning } from "@/plugins/sentry";
import { APP_NAME } from "@/constants";
import { computePayableToShippingFee } from "@/utils/salesOrderShipping";
import { resolveLastEligiblePaymentDate } from "@/utils/eligiblePaymentDate";
import { toBusinessDate } from "@/utils/businessDate";
import {
  applyPendingPromotionCredits,
  collectPendingPromotionCreditIds,
  hasActivePromotionCreditPayment,
  removeAppliedIdsFromPendingPromotionCredits,
} from "@/utils/pendingPromotionCredits";
import {
  claimAndWritePromotionCreditPayment,
  roundMoney,
} from "@/utils/promotionCreditPayment";
import {
  NON_CREDITABLE_PAYMENT_TYPES,
  absorbableCapacity,
  computeOverpay,
  creditCapacity,
  creditablePayments,
  dueAfterRemoval,
  isCreditSpent,
  isFullyAbsorbed,
  sortNewestFirst,
  sumAppliedPayments,
  topUpCapacity,
} from "@/utils/salesOrderOverpayment";
import {
  buildLineCostOfGoods,
  buildOrderCostOfGoods,
  resolveBrandCostOfGoods,
} from "@/utils/costOfGoods";

let capitalize = (string) => {
  return string.replace(/\b\w/g, (c) => c.toUpperCase());
};

// Defense-in-depth: clamp cases/singles against the line item's locked
// caseRestriction before persisting, in case an upstream UI bug slips
// through with a restriction-violating split. Sample orders are
// singles-based, so skip clamping when isSample is true.
function clampToCaseRestriction(
  cases,
  singles,
  caseQTY,
  caseRestriction,
  { isSample = false } = {},
) {
  if (!caseRestriction || isSample) return { cases, singles };

  const resolved = {
    soldByCase: caseRestriction.mode,
    caseQTY: caseRestriction.caseQTY,
  };

  if (!allowsCaseQuantities(resolved) && cases > 0) {
    singles += cases * (caseQTY || 0);
    cases = 0;
  }
  if (!allowsSingleQuantities(resolved) && singles > 0) {
    cases += Math.floor(singles / (caseQTY || 1));
    singles = 0;
  }
  return { cases, singles };
}

// Soft-lock heartbeat: other sessions reclaim if no progress update for this long, so every stretch between checkpoints must stay well under it.
const SHIPPING_LOCK_STALE_MS = 2 * 60 * 1000;

// Firestore caps a transaction at 500 writes; the unship spends two on the order doc and brandStats.
const UNSHIP_MAX_LINE_ITEM_WRITES = 498;

function shippingProgressTimestampMs(value) {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  return null;
}

class SalesOrderClient {
  constructor(orderID, { skipInit = false } = {}) {
    this.orderID = orderID;
    this.externalAPI = httpsCallable(functions, "externalAPI");
    this.order = null;
    this.lineItems = [];
    this.payments = [];
    this.categories = [];
    this.priceOverrides = [];
    this.fulfillmentLocations = [];
    this.orderIsExternal = false;
    this.externalOrderType = null;
    this.externalUpdates = {
      leaflink: [],
    };
    // Set when a COGS-tracking brand's costs could not be read, so the caller can
    // warn that lines were frozen without a cost instead of failing silently
    this.costOfGoodsResolutionFailed = false;

    if (!skipInit) {
      console.log("Sales Order Client Initialized");
      this.getOrder();
      this.getLineItems();
      this.getPayments();
    }
  }

  _hasRecordsShipped(order = this.order) {
    return order?.records?.shipped != null;
  }

  /** Shipped/complete without records.shipped — incomplete ship (Budget King / Frosty class). */
  _isIncompleteShip(order = this.order) {
    return (
      (order?.status === "shipped" || order?.status === "complete") &&
      !this._hasRecordsShipped(order)
    );
  }

  /** Fully shipped: status shipped and records.shipped present. */
  _isTrulyShipped(order = this.order) {
    return order?.status === "shipped" && this._hasRecordsShipped(order);
  }

  _assertCompleteShipForPaymentMutation() {
    if (this._isIncompleteShip()) {
      throw new Error(
        `Order #${this.order?.orderNumber?.internal ?? this.orderID} is ${this.order.status} but was never fully shipped (records.shipped missing). Finish or repair the ship before applying or reversing payments.`,
      );
    }
  }

  async _persistShippingProgress(salesOrderDocRef, patch, { awaitWrite = false } = {}) {
    const payload = {
      shippingProgress: {
        ...patch,
        updatedAt: serverTimestamp(),
      },
      updated: serverTimestamp(),
      lastUpdated: {
        name: store.getters.getUser.name,
        uid: store.getters.getUser.uid,
      },
    };
    const write = updateDoc(salesOrderDocRef, payload);
    if (awaitWrite) {
      await write;
      return;
    }
    // Returned so `await checkpoint(...)` waits for the beat to land; the catch keeps a failed beat out of the ship path.
    return write.catch((err) => {
      logWorkflowWarning(
        `shippedAction: shippingProgress write failed — ${err.message}`,
        {
          workflow: "sales-order",
          action: "shipping-progress-write",
          context: { orderId: this.orderID, checkpoint: patch?.checkpoint },
        },
      );
    });
  }

  _sumAppliedPayments(payments = this.payments) {
    return sumAppliedPayments(payments);
  }

  async _checkBalanceAnomaly(action, extra = {}) {
    await this.getPayments();

    const hasDueOverride =
      extra.balanceDue !== undefined || extra.newBalance !== undefined;
    const hasTotalOverride =
      extra.balanceTotal !== undefined || extra.newTotal !== undefined;
    if (!hasDueOverride || !hasTotalOverride) {
      await this.getOrder();
    }

    const due = parseFloat(
      extra.balanceDue ?? extra.newBalance ?? this.order?.balance?.due,
    );
    const total = parseFloat(
      extra.balanceTotal ?? extra.newTotal ?? this.order?.balance?.total,
    );
    const status = this.order?.status;
    if (isNaN(due) || isNaN(total)) return;

    const contextBase = {
      orderId: this.orderID,
      balance: { ...this.order?.balance, due, total },
      status,
      ...extra,
    };

    // Negative due is never valid regardless of status.
    if (due < 0) {
      logWorkflowWarning("Negative balance.due detected", {
        workflow: "sales-order",
        action,
        context: contextBase,
      });
      return;
    }

    const paymentsApplied = this._sumAppliedPayments();
    const impliedPaid = total - due;
    if (Math.abs(paymentsApplied - impliedPaid) > 0.01) {
      logWorkflowWarning("Balance/payment mismatch detected", {
        workflow: "sales-order",
        action,
        context: {
          ...contextBase,
          paymentsApplied,
          impliedPaid,
          delta: parseFloat((impliedPaid - paymentsApplied).toFixed(2)),
        },
      });
    }
  }

  static _formatUSD(value) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(Number(value) || 0);
  }

  /**
   * Throws when a projected overpay cannot be fully absorbed into payment credits.
   * Callers must run this before any commit — checkOverpaymentCredits fails loud, and
   * post-commit there is no safe way to undo inventory / line-item writes.
   */
  _assertOverpaymentAbsorbable(action, overpay, payments = this.payments) {
    if (!(overpay > 0)) return;
    const capacity = absorbableCapacity(payments);
    if (capacity >= overpay) return;

    logWorkflowWarning(
      "Overpayment cannot be absorbed — blocked before commit",
      {
        workflow: "sales-order",
        action,
        context: {
          orderId: this.orderID,
          overpay,
          capacity,
          paymentCount: payments?.length ?? 0,
        },
      },
    );
    throw new Error(
      `This change would overpay the order by ${SalesOrderClient._formatUSD(
        overpay,
      )}, but only ${SalesOrderClient._formatUSD(
        capacity,
      )} of eligible payment credit is available to absorb it. Reverse or adjust the applied payment(s), then retry.`,
    );
  }

  /**
   * Throws when absorbing the overpay would need a brand-new credit but no payableTo record
   * exists — checkOverpaymentCredits hard-throws on that, which post-commit leaves overpaid AR.
   */
  _assertOverpaymentCreditable(
    action,
    overpay,
    payableToEmpty,
    payments = this.payments,
  ) {
    if (!(overpay > 0)) return;
    const capacity = topUpCapacity(payments);
    // Topping up an existing credit never reads payableTo.
    if (overpay <= capacity) return;
    if (!payableToEmpty) return;

    logWorkflowWarning(
      "Overpayment credit needs a payableTo record — blocked before commit",
      {
        workflow: "sales-order",
        action,
        context: {
          orderId: this.orderID,
          overpay,
          topUpCapacity: capacity,
        },
      },
    );
    throw new Error(
      "This change would create an overpayment credit, but no payable-to record exists for this brand/state. Contact support before retrying.",
    );
  }

  /**
   * Throws when a payment's linked overpayment credit is gone. The credit and the payment's
   * `overpaymentCreditID` are only ever cleared together in one batch, so a dangling link is
   * corruption — fail loud rather than synthesizing a credit from zero.
   */
  _assertOverpaymentCreditExists(action, creditSnapshot, payment) {
    if (creditSnapshot.exists()) return;

    logWorkflowWarning("Linked overpayment credit is missing", {
      workflow: "sales-order",
      action,
      context: {
        orderId: this.orderID,
        paymentId: payment?.id ?? null,
        overpaymentCreditID: payment?.overpaymentCreditID ?? null,
      },
    });
    throw new Error(
      `Payment ${
        payment?.id ?? "(unknown)"
      } references an overpayment credit that no longer exists. Contact support to reconcile before retrying.`,
    );
  }

  /** True when this order's brand/state has no payableTo record to mint a credit against. */
  async _isPayableToMissing() {
    const response = await getDocs(
      query(
        collection(firestore, "payableTo"),
        where(
          "location.state.stateID",
          "==",
          this.order.location.shipping.state.stateID,
        ),
        where("brandID", "==", this.order.brand.id),
        limit(1),
      ),
    );
    return response.empty;
  }

  /**
   * A non-finite total/due makes the overpay guards silently no-op and commits NaN to the
   * order doc, so refuse to proceed rather than trusting the checks downstream of it.
   */
  _assertFiniteBalance(action, { total, due }) {
    if (Number.isFinite(Number(total)) && Number.isFinite(Number(due))) return;

    logWorkflowWarning("Non-finite balance computed — blocked before commit", {
      workflow: "sales-order",
      action,
      context: { orderId: this.orderID, total, due },
    });
    throw new Error(
      "Unable to compute a valid order balance from the applied payments. Contact support before retrying.",
    );
  }

  /**
   * The full pre-commit overpay gate against a projected total. Callers must run this before
   * committing or firing an external update — checkOverpaymentCredits hard-throws on an
   * overpay it cannot absorb, and post-commit that leaves the order overpaid.
   */
  async _assertOverpayCommittable(action, { total, due }) {
    this._assertFiniteBalance(action, { total, due });
    // Cross-check the payment docs against the stored ledger so a stale `due` cannot
    // understate the overpay, matching the dual basis updateLineItems commits on.
    const { overpay } = computeOverpay({
      newTotal: Number(total),
      payments: this.payments,
      balanceTotal: this.order.balance.total,
      balanceDue: this.order.balance.due,
    });
    if (overpay <= 0) return;

    this._assertOverpaymentAbsorbable(action, overpay);
    // Gated so the payableTo lookup only runs on the rare overpaying edit.
    this._assertOverpaymentCreditable(
      action,
      overpay,
      await this._isPayableToMissing(),
    );
  }

  /**
   * Puts the stored payment term back after its repricing pass aborted. Never throws — the
   * caller rethrows the original failure, and a failed revert is flagged for manual review.
   */
  async _revertPaymentTerm(previousPaymentTerm, cause) {
    if (previousPaymentTerm == null) return;

    try {
      await updateDoc(doc(firestore, "salesOrders", this.order.id), {
        paymentTerm: previousPaymentTerm,
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
      });
    } catch (revertError) {
      await this._logPostCommitFailure("updateOrderData", revertError, {
        note: `Variable pricing update failed (${
          cause?.message ?? "unknown"
        }) and the payment term could not be reverted — the order holds the new term with the old prices`,
        previousPaymentTerm,
      });
    }
  }

  // Committed data is left intact — inventory / line-item docs cannot be safely rolled back,
  // so a post-commit side-effect failure is flagged for manual review instead of reverted.
  async _logPostCommitFailure(functionName, error, context = {}) {
    try {
      const errorLogRef = doc(collection(firestore, "errorLogs"));
      const timestamp = serverTimestamp();
      await setDoc(errorLogRef, {
        error: {
          message: error.message || String(error),
          stack: error.stack || null,
          name: error.name || null,
          context: {
            function: functionName,
            additionalInfo: { orderId: this?.order?.id || null, ...context },
          },
        },
        needsManualReview: true,
        createdBy: {
          uid: store.getters.getUser?.uid || null,
          email: store.getters.getUser?.email || null,
          name: store.getters.getUser?.name || null,
        },
        created: timestamp,
        updated: timestamp,
        lastUpdated: {
          uid: store.getters.getUser?.uid || null,
          name: store.getters.getUser?.name || null,
        },
        relatedOrder: {
          id: this?.order?.id || null,
          orderNumber: this?.order?.orderNumber || null,
        },
        relatedBrand: {
          id: this?.order?.brand?.id || null,
          name: this?.order?.brand?.name || null,
        },
        relatedAccount: {
          id: this?.order?.account?.id || null,
          name: this?.order?.account?.name || null,
        },
        triggeredFrom: `SalesOrderClient/${functionName}`,
      });
    } catch (logError) {
      console.error("Failed to log error to Firestore errorLogs:", logError);
    }
  }

  // Resolves an order's completion date via the shared eligible-payment rule; never throws.
  async _getLastEligiblePaymentDate(orderID, fallbackTimestamp) {
    let payments = [];
    try {
      const paymentsSnap = await getDocs(
        query(
          collection(firestore, "salesOrders_payments"),
          where("relatedID", "==", orderID),
          where("flags.reversed", "==", false),
        ),
      );
      payments = paymentsSnap.docs.map((paymentDoc) => paymentDoc.data());
    } catch (error) {
      logWorkflowWarning(`_getLastEligiblePaymentDate failed — ${error.message}`, {
        workflow: "sales-order",
        action: "get-last-eligible-payment-date",
        context: { orderID },
      });
    }
    return resolveLastEligiblePaymentDate(payments, fallbackTimestamp);
  }

  // Creates a firstPurchases doc per never-before-bought category on completion. Schema must
  // match the backfill tool (DataMGMT_MyTrace_Recalculate.vue) the bonus report reads.
  async _createFirstPurchases(completionDate) {
    await this.getLineItems();

    // Promos/samples never count toward a first purchase.
    const forSaleItems = this.lineItems.filter(
      (lineItem) => !lineItem.promo && !lineItem.sample,
    );
    const categoryIDs = [
      ...new Set(forSaleItems.map((lineItem) => lineItem.categoryID)),
    ];
    if (categoryIDs.length === 0) return;

    const resolvedDate =
      (await this._getLastEligiblePaymentDate(
        this.order.id,
        this.order.records?.completed,
      )) ?? completionDate;

    const firstPurchasesRef = collection(firestore, "firstPurchases");
    const shipping = this.order.location?.shipping ?? {};

    // Credit the executive, else the manager.
    let creditedTo = null;
    const staff = this.order.account?.staff;
    if (staff?.executive?.id?.length > 0) {
      creditedTo = {
        id: staff.executive.id,
        name: staff.executive.name,
        email: staff.executive.email || "",
      };
    } else if (staff?.manager?.id?.length > 0) {
      creditedTo = {
        id: staff.manager.id,
        name: staff.manager.name,
        email: staff.manager.email || "",
      };
    }

    // Orders store only countryID; fetch the name best-effort (report filters on state, not country).
    const countryID = shipping.countryID ?? null;
    let countryName = "";
    if (countryID) {
      try {
        const countryDoc = await getDoc(doc(firestore, "countries", countryID));
        countryName = countryDoc.exists() ? countryDoc.data().name : "";
      } catch (error) {
        countryName = "";
      }
    }

    const batch = writeBatch(firestore);
    let createdCount = 0;

    for (const categoryID of categoryIDs) {
      const existing = await getDocs(
        query(
          firstPurchasesRef,
          where("brand.id", "==", this.order.brand.id),
          where("account.id", "==", this.order.account.id),
          where("productCategory.id", "==", categoryID),
        ),
      );
      if (!existing.empty) continue;

      const categoryItems = forSaleItems.filter(
        (lineItem) => lineItem.categoryID === categoryID,
      );
      const orderTotal = categoryItems.reduce(
        (amount, lineItem) => amount + lineItem.qty * lineItem.amount,
        0,
      );

      createdCount++;
      batch.set(doc(firstPurchasesRef), {
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        brand: {
          name: this.order.brand.name,
          id: this.order.brand.id,
        },
        productCategory: {
          name: categoryItems[0].categoryName,
          id: categoryID,
        },
        user: this.order.createdBy,
        account: {
          name: this.order.account.name,
          id: this.order.account.id,
        },
        location: {
          country: {
            name: countryName,
            id: countryID,
          },
          state: {
            name: shipping.state?.name ?? "",
            abbreviation: shipping.state?.abbreviation ?? "",
            stateID: shipping.state?.stateID ?? null,
          },
        },
        orderID: this.order.id,
        paymentTerm: this.order.paymentTerm,
        orderCreated: this.order.created,
        orderShipped: this.order.shipDate ?? this.order.created,
        datePaid: resolvedDate,
        buyerGenerated: this.order.flags?.repGenerated ?? false,
        orderNumber: {
          internal:
            this.order.orderNumber?.internal != null
              ? String(this.order.orderNumber.internal).toLowerCase()
              : "",
          external: this.order.orderNumber?.external ?? "",
        },
        orderTotal,
        completionDate: resolvedDate,
        creditedTo,
      });
    }

    if (createdCount > 0) {
      await batch.commit();
    }
  }

  // Deletes every firstPurchases doc tied to this order, then re-scans other complete orders
  // for the same brand+account to fill any category gaps left by the deletion.
  async _removeFirstPurchases() {
    const firstPurchasesRef = collection(firestore, "firstPurchases");
    const existing = await getDocs(
      query(firstPurchasesRef, where("orderID", "==", this.orderID)),
    );
    if (existing.empty) return;

    const deletedCategories = existing.docs
      .map((fp) => ({
        id: fp.data().productCategory?.id,
        name: fp.data().productCategory?.name,
      }))
      .filter((c) => c.id);

    const deleteBatch = writeBatch(firestore);
    existing.docs.forEach((fp) => {
      deleteBatch.delete(doc(firestore, "firstPurchases", fp.id));
    });
    await deleteBatch.commit();

    if (deletedCategories.length === 0) return;

    // Re-scan: find the earliest other complete order covering each deleted category
    // so the gap doesn't persist while those orders remain complete.
    try {
      const otherCompleteSnap = await getDocs(
        query(
          collection(firestore, "salesOrders"),
          where("brand.id", "==", this.order.brand.id),
          where("account.id", "==", this.order.account.id),
          where("status", "==", "complete"),
          where("flags.sample", "==", false),
        ),
      );

      // Earliest-created order wins — matches the backfill tool's canonical rule.
      const otherOrders = otherCompleteSnap.docs
        .filter((o) => o.id !== this.orderID)
        .map((o) => ({ id: o.id, ...o.data() }))
        .sort((a, b) => {
          const aTs = a.created?.seconds ?? 0;
          const bTs = b.created?.seconds ?? 0;
          return aTs - bTs;
        });

      if (otherOrders.length === 0) return;

      const lineItemsRef = collection(firestore, "salesOrders_lineItems");
      const pending = new Set(deletedCategories.map((c) => c.id));
      const replacements = [];

      for (const order of otherOrders) {
        if (pending.size === 0) break;

        const lineItemsSnap = await getDocs(
          query(lineItemsRef, where("relatedID", "==", order.id)),
        );
        const forSaleItems = lineItemsSnap.docs
          .map((li) => ({ id: li.id, ...li.data() }))
          .filter((li) => !li.promo && !li.sample && pending.has(li.categoryID));

        const coveredCategories = new Set(forSaleItems.map((li) => li.categoryID));
        if (coveredCategories.size === 0) continue;

        const shipping = order.location?.shipping ?? {};
        const countryID = shipping.countryID ?? null;
        let countryName = "";
        if (countryID) {
          try {
            const countryDoc = await getDoc(doc(firestore, "countries", countryID));
            countryName = countryDoc.exists() ? countryDoc.data().name : "";
          } catch {
            countryName = "";
          }
        }

        let creditedTo = null;
        const staff = order.account?.staff;
        if (staff?.executive?.id?.length > 0) {
          creditedTo = { id: staff.executive.id, name: staff.executive.name, email: staff.executive.email || "" };
        } else if (staff?.manager?.id?.length > 0) {
          creditedTo = { id: staff.manager.id, name: staff.manager.name, email: staff.manager.email || "" };
        }

        const completionDate = await this._getLastEligiblePaymentDate(
          order.id,
          order.records?.completed,
        );

        for (const categoryID of coveredCategories) {
          const categoryItems = forSaleItems.filter((li) => li.categoryID === categoryID);
          const orderTotal = categoryItems.reduce((sum, li) => sum + li.qty * li.amount, 0);

          replacements.push({
            created: serverTimestamp(),
            updated: serverTimestamp(),
            createdBy: { name: store.getters.getUser.name, uid: store.getters.getUser.uid },
            lastUpdated: { name: store.getters.getUser.name, uid: store.getters.getUser.uid },
            brand: { name: order.brand.name, id: order.brand.id },
            productCategory: { name: categoryItems[0].categoryName, id: categoryID },
            user: order.createdBy,
            account: { name: order.account.name, id: order.account.id },
            location: {
              country: { name: countryName, id: countryID },
              state: {
                name: shipping.state?.name ?? "",
                abbreviation: shipping.state?.abbreviation ?? "",
                stateID: shipping.state?.stateID ?? null,
              },
            },
            orderID: order.id,
            paymentTerm: order.paymentTerm,
            orderCreated: order.created,
            orderShipped: order.shipDate ?? order.created,
            datePaid: completionDate,
            buyerGenerated: order.flags?.repGenerated ?? false,
            orderNumber: {
              internal: order.orderNumber?.internal != null ? String(order.orderNumber.internal).toLowerCase() : "",
              external: order.orderNumber?.external ?? "",
            },
            orderTotal,
            completionDate,
            creditedTo,
          });
          pending.delete(categoryID);
        }
      }

      if (replacements.length > 0) {
        const replaceBatch = writeBatch(firestore);
        replacements.forEach((data) => {
          replaceBatch.set(doc(firstPurchasesRef), data);
        });
        await replaceBatch.commit();
      }
    } catch (error) {
      logWorkflowWarning(
        `_removeFirstPurchases: re-scan failed — ${error.message}`,
        {
          workflow: "sales-order",
          action: "remove-first-purchases-rescan",
          context: {
            orderId: this.orderID,
            accountId: this.order.account?.id,
            brandId: this.order.brand?.id,
          },
        },
      );
    }
  }

  async getOrder() {
    try {
      const orderDoc = await getDoc(
        doc(firestore, "salesOrders", this.orderID),
      );
      this.order = {
        id: orderDoc.id,
        ...orderDoc.data(),
      };

      if (!this.order.account?.id?.length > 0) {
        const orderDoc = await getDoc(
          doc(firestore, "salesOrders-draft", this.orderID),
        );
        this.order = {
          id: orderDoc.id,
          ...orderDoc.data(),
        };
      }

      if (this.order.flags?.external) {
        this.orderIsExternal = true;
        this.externalOrderType = null;

        const payableToRef = collection(firestore, "payableTo");
        const state = where(
            "location.state.stateID",
            "==",
            this.order.location.shipping.state.stateID,
          ),
          brandWhere = where("brandID", "==", this.order.brand.id);
        const q = query(payableToRef, state, brandWhere);
        const response = await getDocs(q, limit(1));

        if (response.empty) {
          logWorkflowWarning("getOrder: payableTo not found for external order", {
            workflow: "sales-order",
            action: "get-order-payable-to",
            context: {
              orderId: this.orderID,
              stateID: this.order.location.shipping.state.stateID,
              brandID: this.order.brand.id,
            },
          });
          return this.order;
        }

        let payableTo = {
          id: response.docs[0].id,
          ...response.docs[0].data(),
        };

        if (
          payableTo?.flags?.external &&
          payableTo?.externalPlatform === "leaflink" &&
          this.order.externalIDs?.leaflink?.length
        ) {
          this.externalOrderType = "leaflink";
          let defaultUpdates = [
            "orderCreated",
            "orderUpdated",
            "inventoryUpdated",
            "statusUpdated",
            "paymentsApplied",
          ];
          this.externalUpdates.leaflink =
            payableTo?.integrations?.leaflink?.hasOwnProperty("pushUpdates")
              ? payableTo?.integrations?.leaflink?.pushUpdates
              : defaultUpdates;
        }

        if (
          this.externalOrderType === "leaflink" &&
          this.order.externalIDs.leaflink
        ) {
          this.leaflinkClient = new LeaflinkClient(
            this.order.externalIDs.leaflink,
            this.order,
          );
        }
      }
    } catch (error) {
      throw error;
    }

    return this.order;
  }

  // GET
  async getLineItems() {
    try {
      const lineItemDocs = await getDocs(
        query(
          collection(firestore, "salesOrders_lineItems"),
          where("relatedID", "==", this.orderID),
        ),
      );

      let lineItems = lineItemDocs.docs.map((doc) => {
        return {
          id: doc.id,
          ...doc.data(),
        };
      });

      let productLots = [];

      await Promise.all(
        lineItems.flatMap((lineItem) =>
          (lineItem.productLots ?? []).map(async (productLot) => {
            const productLotDoc = doc(firestore, "productLots", productLot.id);
            const productLotResponse = await getDoc(productLotDoc);
            productLot.locationData = productLotResponse.data().locationData;

            let availableAmount = Number(
              productLotResponse.data().available ??
                productLotResponse.data().units,
            );

            if (!productLot.locationData?.length) {
              const locationDataDocs = await getDocs(
                collection(
                  firestore,
                  "productLots",
                  productLot.id,
                  "locationData",
                ),
              );

              let locationData = locationDataDocs.docs.map((doc) => {
                return {
                  id: doc.id,
                  ...doc.data(),
                };
              });

              let transfer = 0;

              if (productLot.locationData?.length) {
                transfer = productLot.locationData.reduce(
                  (acc, data) => acc + (data.transfer ?? 0),
                  0,
                );
              }

              productLots.push({
                id: productLot.id,
                available: availableAmount - (transfer ?? 0),
                originalAvailable: availableAmount - (transfer ?? 0),
                total: Number(productLotResponse.data().units),
                product: productLotResponse.data().product.name,
                productID: productLotResponse.data().product.id,
                productCategory: productLotResponse.data().productCategory.name,
                skuID: productLotResponse.data().skuID,
                trackAndTraceID: productLotResponse.data().trackAndTraceID,
                fileType: productLotResponse.data().fileType,
                units: Number(productLotResponse.data().units),
                expiration: moment(
                  productLotResponse.data().expiration.seconds * 1000,
                ).toDate(),
                path:
                  productLotResponse.data().fileType === "upload" &&
                  productLotResponse.data().path &&
                  typeof productLotResponse.data().path === "string"
                    ? await getDownloadURL(
                        ref(storage, productLotResponse.data().path),
                      ).catch(() => null)
                    : productLotResponse.data().path ?? null,
                relatedLineItems:
                  productLotResponse.data().relatedLineItems ?? [],
                locationData,
              });
            }
          })
        )
      );

      if (!lineItems.length > 0) {
        logWorkflowWarning("getLineItems: fresh query returned no results, falling back to order.lineItems", {
          workflow: "sales-order",
          action: "get-line-items-fallback",
          context: { orderId: this.orderID, fallbackCount: this.order.lineItems?.length ?? 0 },
        });
        lineItems = this.order.lineItems;
      }

      this.productLots = productLots;
      this.lineItems = lineItems;

      return lineItems;
    } catch (error) {
      throw error;
    }
  }
  async getPayments() {
    try {
      const payments = await getDocs(
        query(
          collection(firestore, "salesOrders_payments"),
          where("relatedID", "==", this.orderID),
        ),
      );

      this.payments = payments.docs.map((payment) => {
        return {
          id: payment.id,
          ...payment.data(),
        };
      });
      return this.payments;
    } catch (error) {
      throw error;
    }
  }
  async getRouteDetails(origin, destination) {
    try {
      const data = await getDirections(origin, destination);

      if (data?.status?.toLowerCase() === "ok" && data.routes?.length > 0) {
        return data.routes[0].legs[0].steps
          .map((step) => {
            return `${step.html_instructions.replace(/<[^>]+>/g, "")} (${
              step.distance.text
            })`;
          })
          .join(". ");
      } else {
        throw new Error("Status");
      }
    } catch (error) {
      console.error(error);
      logWorkflowWarning(`getRouteDetails: Google Maps API failed — ${error.message}`, {
        workflow: "sales-order",
        action: "google-maps-directions",
        context: { orderId: this.orderID, origin, destination },
      });
      return null;
    }
  }
  async getProductCategories() {
    this.categories = [];

    const productsRef = collection(firestore, "productCategories"),
      forSale = where("flags", "array-contains", "forSale"),
      active = where("active", "==", true),
      brand = where("brandID", "==", this.order.brand.id);

    const q = query(productsRef, active, brand, forSale);
    const response = await getDocs(q);

    if (!response.empty) {
      this.categories = response.docs
        .filter(
          (category) =>
            category
              .data()
              .states?.includes(this.order.location.shipping.state.stateID) &&
            category.data().visibility !== "hidden",
        )
        .map((categories) => {
          let caseQTY = categories.data().caseQTY;

          if (
            categories.data().flags.includes("variableCases") &&
            categories
              .data()
              .variableCases?.find(
                (item) =>
                  item.stateID === this.order.location.shipping.state.stateID,
              )
          ) {
            caseQTY = categories
              .data()
              .variableCases.find(
                (item) =>
                  item.stateID === this.order.location.shipping.state.stateID,
              ).qty;
          }

          return {
            id: categories.id,
            name: categories.data().name,
            flags: categories.data().flags,
            caseQTY,
            price: categories.data().price,
            variablePrice: categories.data().variablePrice,
            listPrice: {
              cod: categories.data().listPrice?.cod ?? null,
              net: categories.data().listPrice?.net ?? null,
            },
          };
        })
        .sort((a, b) => (a.name > b.name ? 1 : -1));
    }
  }
  async getPriceOverrides() {
    this.priceOverrides = [];

    const priceOverridesRef = collection(firestore, "priceOverrides");
    const state = where(
      "state.stateID",
      "==",
      this.order.location.shipping.state.stateID,
    );
    const brand = where("brand.id", "==", this.order.brand.id);
    const active = where("active", "==", true);
    const q = query(priceOverridesRef, state, active, brand);
    const response = await getDocs(q);

    if (!response.empty) {
      this.priceOverrides = response.docs.flatMap((priceOverride) => {
        let category = this.categories.find(
          (category) => category.id === priceOverride.data().category.id,
        );

        if (!category) return [];

        let categoryName = category.name;
        let pricing = category.flags.includes("hasVariablePricing")
          ? "Variable"
          : "Static";
        let accountSpecific = priceOverride.data().hasOwnProperty("account");
        let productSpecific = priceOverride.data().hasOwnProperty("product");

        return {
          id: priceOverride.id,
          ...priceOverride.data(),
          category,
          pricing,
          accountSpecific,
          productSpecific,
          listPrice: {
            cod: priceOverride.data().listPrice?.cod ?? null,
            net: priceOverride.data().listPrice?.net ?? null,
          },
        };
      });
    }
  }
  getPriceOverrideForProduct(product) {
    let overriddenCategoryIDs = this.priceOverrides.map(
      (override) => override.category.id,
    );
    if (overriddenCategoryIDs.includes(product.categoryID)) {
      //  Price Override
      let override;
      if (
        this.priceOverrides
          .filter(
            (item) =>
              item.category.id === product.categoryID &&
              item.product &&
              item.product.id === product.productID,
          )
          .find(
            (option) =>
              option.productSpecific && option.product.id === product.productID,
          )
      ) {
        // SKU Specific Price Override
        override = this.priceOverrides
          .filter((item) => item.category.id === product.categoryID)
          .find(
            (option) =>
              option.productSpecific && option.product.id === product.productID,
          );
      } else {
        // Payable To Price Override
        override = this.priceOverrides.find(
          (item) =>
            item.category.id === product.categoryID &&
            item.accountSpecific === false &&
            item.productSpecific === false,
        );
      }
      if (override) {
        return override;
      } else {
        return null;
      }
    }
  }
  async getFulfillmentLocations() {
    this.fulfillmentLocations = [];
    const stateDoc = doc(
      firestore,
      "states",
      this.order.location.shipping.state.stateID,
    );
    const stateResponse = await getDoc(stateDoc);
    let fulfillmentLocations = [];
    fulfillmentLocations.push({
      locationID: stateResponse.data().fulfillmentLocation,
      mainFulfillmentLocation: true,
    });

    let additionalFulfillmentLocations =
      stateResponse.data().additionalFulfillmentLocations ?? [];
    additionalFulfillmentLocations = additionalFulfillmentLocations.map(
      (locationID) => {
        return {
          locationID,
          mainFulfillmentLocation: false,
        };
      },
    );
    fulfillmentLocations = fulfillmentLocations.concat(
      additionalFulfillmentLocations,
    );

    let capitalize = (word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    };

    fulfillmentLocations = await Promise.all(
      fulfillmentLocations.flatMap(async (location) => {
        if (!location.locationID) return;
        const locationDoc = doc(firestore, "locations", location.locationID);
        const response = await getDoc(locationDoc);
        if (!response.exists()) return [];

        let streetNumber = response.data().location?.street?.number ?? "";
        let streetName = capitalize(
          response.data().location?.street?.name ?? "",
        );
        let city = capitalize(response.data().location?.city ?? "");
        let state = response.data().location?.state?.abbreviation ?? "";
        state = state.toUpperCase();
        let zipCode = response.data().location?.zipCode ?? "";

        let locationAddress = `${streetNumber} ${streetName}, ${city}, ${state}  ${zipCode}`;

        if (
          !this.order.fulfillmentLocation &&
          location.mainFulfillmentLocation
        ) {
          this.order.fulfillmentLocation = {
            id: location.locationID,
            name: capitalize(response.data().name),
          };
        }

        return {
          ...location,
          manager: response.data().manager ?? null,
          phoneNumber: response.data().phoneNumber ?? null,
          name: capitalize(response.data().name),
          address: locationAddress,
          type:
            store.getters.getLocationTypes.find(
              (item) => item.value === response.data().type,
            )?.text ?? "",
        };
      }),
    );

    this.fulfillmentLocations = fulfillmentLocations;
  }

  // POST
  async transitionOrderStatus(newStatus) {
    addBreadcrumb("transitionOrderStatus: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, toStatus: newStatus },
    });
    try {
      // Get current order data to create activity log
      await this.getOrder();

      const oldValue = store.getters.getOrderStatus.find(
        (status) => status.value === this.order.status,
      );
      const newValue = store.getters.getOrderStatus.find(
        (status) => status.value === newStatus,
      );

      await updateDoc(doc(firestore, "salesOrders", this.orderID), {
        status: newStatus,
      });

      // Create activity for order status transition
      await createActivity({
        type: "status",
        action: "Order status transitioned",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} status changed from ${
          oldValue?.text || this.order.status
        } to ${newValue?.text || newStatus}`,
        relatedTo: "salesOrders",
        relatedID: [this.orderID],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "medium",
        priority: "medium",
        changeData: {
          field: "orderStatus",
          oldValue: {
            text: oldValue?.text || this.order.status,
            value: this.order.status,
            color: oldValue?.color || "#000000",
          },
          newValue: {
            text: newValue?.text || newStatus,
            value: newStatus,
            color: newValue?.color || "#000000",
          },
          orderNumber: this.order.orderNumber.internal,
          transitionType: "manual",
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
        },
      });
    } catch (error) {
      throw error;
    }
  }

  async removePayment(payment, reason) {
    addBreadcrumb("removePayment: start", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        paymentId: payment?.id,
        paymentAmount: payment?.amount,
        paymentType: payment?.paymentType,
        currentDue: this.order?.balance?.due,
        orderStatus: this.order?.status,
      },
    });
    try {
      const salesOrderDoc = doc(firestore, "salesOrders", this.orderID);
      const brandStatsDoc = doc(
        firestore,
        "brandStats",
        `${this.order.account.id}_${this.order.brand.id}`,
      );
      const paymentDocRef = doc(firestore, "salesOrders_payments", payment.id);

      await this.getOrder();
      this._assertCompleteShipForPaymentMutation();
      await this.getPayments();
      let staff = [];

      if (payment.overpaymentCreditID) {
        const overpaymentCreditSnap = await getDoc(
          doc(firestore, "promotionCredits", payment.overpaymentCreditID),
        );
        const overpaymentCreditData = overpaymentCreditSnap.exists()
          ? overpaymentCreditSnap.data()
          : null;
        if (
          isCreditSpent(
            overpaymentCreditData,
            payment.creditsApplied ?? 0,
          )
        ) {
          const spentError = new Error(
            "Cannot reverse this payment because its overpayment credit has already been used. Restore or reverse the credit usage first.",
          );
          logWorkflowWarning("removePayment: blocked — overpayment credit spent", {
            workflow: "sales-order",
            action: "remove-payment-overpayment-credit-spent",
            context: {
              orderId: this.orderID,
              paymentId: payment?.id,
              overpaymentCreditID: payment.overpaymentCreditID,
              creditsApplied: payment.creditsApplied,
              available: overpaymentCreditData?.available,
            },
          });
          throw spentError;
        }
      }

      // Cheap fail-fast before any LeafLink calls below. This alone is NOT
      // atomic — two concurrent Undo calls can both pass it. The real guard
      // against a double-reverse is the fresh re-read inside runTransaction()
      // further down, right before the AR/credit increments are applied.
      const paymentDoc = await getDoc(paymentDocRef);
      if (!paymentDoc.exists()) {
        const missingError = new Error("Payment not found.");
        logWorkflowWarning("removePayment: blocked — payment doc missing", {
          workflow: "sales-order",
          action: "remove-payment-missing",
          context: { orderId: this.orderID, paymentId: payment?.id },
        });
        throw missingError;
      }
      if (paymentDoc.data()?.flags?.reversed) {
        const alreadyReversedError = new Error(
          "This payment has already been reversed.",
        );
        logWorkflowWarning("removePayment: blocked — payment already reversed", {
          workflow: "sales-order",
          action: "remove-payment-already-reversed",
          context: {
            orderId: this.orderID,
            paymentId: payment?.id,
            paymentAmount: payment?.amount,
            balanceDue: this.order?.balance?.due,
          },
        });
        throw alreadyReversedError;
      }

      let leaflinkEmail = null;
      let userLeaflinkInfo = null;
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
              let data = await this.leaflinkClient.getUsers();
              staff = data.results;
              const integrationsRef = collection(firestore, "integrations");
              const platform = where("platform", "==", "leaflink");
              const integrationsQ = query(integrationsRef, platform);
              const integrationsResponse = await getDocs(
                integrationsQ,
                limit(1),
              );

              if (!integrationsResponse.empty) {
                leaflinkEmail = integrationsResponse.docs[0].data().email;
                console.log(leaflinkEmail);
              }

              userLeaflinkInfo = staff.find(
                (staffMember) => staffMember.email === leaflinkEmail,
              );
              console.log(userLeaflinkInfo);

              if (!leaflinkEmail) {
                console.error("removePayment: aborting — LeafLink integration email not configured", { orderId: this.orderID, paymentId: payment?.id });
                logWorkflowWarning("removePayment: aborting — LeafLink integration email not configured", {
                  workflow: "sales-order",
                  action: "remove-payment-leaflink-preflight",
                  context: { orderId: this.orderID, paymentId: payment?.id },
                });
                return;
              }
            }
            break;
          default:
            break;
        }
      }

      let paymentType =
        store.getters.getPaymentTypes.find(
          (type) => type.value === payment.paymentType,
        ) ?? "[Error]";

      console.log(paymentType);

      let paymentAmount = Number(parseFloat(payment.amount).toFixed(2));
      let paymentCreditsApplied = Number(
        parseFloat(payment.creditsApplied ?? 0).toFixed(2),
      );

      // Account AR delta for this reversal (unchanged).
      let amountToAdd = paymentAmount - paymentCreditsApplied;

      // Other payments on this order, excluding the one being reversed.
      // Used only to know *which* payment docs to re-read fresh inside the
      // transaction below — the actual amounts driving `due` are re-fetched
      // live at commit time, not taken from this (possibly stale) snapshot.
      const remainingPaymentIds = (this.payments || [])
        .filter((p) => p.id !== payment.id)
        .map((p) => p.id);

      let capitalizedName =
        `${store.getters.getUser.name.first} ${store.getters.getUser.name.last}`.replace(
          /\b\w/g,
          (c) => c.toUpperCase(),
        );

      let reversalNotes = `Payment reversed by ${capitalizedName} on ${moment().format(
        "MM/DD/YYYY",
      )} with reason: ${reason}`;

      let undoNotes =
        !paymentDoc.data().notes || paymentDoc.data().notes.length === 0
          ? reversalNotes
          : `${reversalNotes} | ${paymentDoc.data().notes}`;

      if (payment.overpaymentCreditID) {
        // Set overpayment credit to inactive & remove amount from brandStats doc
        addBreadcrumb("removePayment: deleting overpayment credit", {
          workflow: "sales-order",
          data: {
            orderId: this.orderID,
            overpaymentCreditID: payment.overpaymentCreditID,
            creditsApplied: payment.creditsApplied,
          },
        });
      }

      let promotionCreditDoc = null;
      let selectedPromotionCredit = null;
      let available = null;
      if (payment.paymentType === "promotionCredit") {
        promotionCreditDoc = doc(
          firestore,
          "promotionCredits",
          payment.promotionCredit,
        );
        addBreadcrumb("removePayment: restoring promotion credit balance", {
          workflow: "sales-order",
          data: {
            orderId: this.orderID,
            promotionCreditId: payment.promotionCredit,
            paymentAmount: payment.amount,
          },
        });
        let promotionCreditData = await getDoc(promotionCreditDoc);
        selectedPromotionCredit = promotionCreditData.data();

        available = Number(
          parseFloat(
            selectedPromotionCredit.available + payment.amount,
          ).toFixed(2),
        );
      }

      // All financial side effects land atomically, and "not already
      // reversed" is re-verified at commit time (not just at the preflight
      // above) so two concurrent Undo calls can't both apply their AR /
      // promo-credit increments. Mirrors the atomic-claim pattern used in
      // cancelOrder(). This also runs before any LeafLink/status side
      // effects below, so only the transaction's winner ever reaches them —
      // a losing concurrent call throws here and never touches LeafLink.
      const { due, statusChangedToShipped } = await runTransaction(
        firestore,
        async (transaction) => {
          const freshPaymentSnap = await transaction.get(paymentDocRef);
          if (
            !freshPaymentSnap.exists() ||
            freshPaymentSnap.data()?.flags?.reversed
          ) {
            throw new Error("This payment has already been reversed.");
          }

          // Re-read the order's other payments live, inside the transaction,
          // instead of relying on the `this.payments` snapshot fetched
          // earlier — a concurrent reversal of a *different* payment on this
          // order could otherwise commit in between and make that snapshot
          // stale, causing this write to silently undo the other one's due.
          const freshRemainingSnaps = await Promise.all(
            remainingPaymentIds.map((id) =>
              transaction.get(doc(firestore, "salesOrders_payments", id)),
            ),
          );
          const freshRemainingPayments = freshRemainingSnaps.map((snap) =>
            snap.exists() ? snap.data() : { flags: { reversed: true } },
          );
          // Reconcile from source of truth so a previously corrupted due cannot
          // resurrect removed line-item value (e.g. 0 + 0.10 on an 0.08 total).
          const due = Number(
            parseFloat(
              dueAfterRemoval({
                balanceTotal: this.order.balance.total,
                remainingPayments: freshRemainingPayments,
              }),
            ).toFixed(2),
          );

          // Reopen complete → shipped only when a balance remains after reverse.
          // If remaining payments still cover the total (due === 0), keep complete.
          const statusChangedToShipped =
            due > 0 && this.order.status === "complete";
          const status = statusChangedToShipped ? "shipped" : this.order.status;

          // Reverse payment
          transaction.set(
            paymentDocRef,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              notes: undoNotes,
              flags: {
                reversed: true,
              },
            },
            { merge: true },
          );

          if (payment.overpaymentCreditID) {
            transaction.delete(
              doc(firestore, "promotionCredits", payment.overpaymentCreditID),
            );
            transaction.set(
              brandStatsDoc,
              {
                metrics: {
                  promotionCredits: {
                    total: increment(-payment.creditsApplied),
                    available: increment(-payment.creditsApplied),
                  },
                },
              },
              { merge: true },
            );
          }

          if (payment.paymentType === "promotionCredit") {
            transaction.set(
              promotionCreditDoc,
              {
                updated: serverTimestamp(),
                lastUpdated: {
                  name: store.getters.getUser.name,
                  uid: store.getters.getUser.uid,
                },
                active: true,
                available: increment(payment.amount),
              },
              { merge: true },
            );

            // Brand Stats - Metrics
            transaction.set(
              brandStatsDoc,
              {
                updated: serverTimestamp(),
                lastUpdated: {
                  name: store.getters.getUser.name,
                  uid: store.getters.getUser.uid,
                },
                metrics: {
                  promotionCredits: {
                    available: increment(payment.amount),
                  },
                },
              },
              { merge: true },
            );
          }

          // Order & Account Balance
          transaction.set(
            salesOrderDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              balance: {
                due: parseFloat(due).toFixed(2),
              },
              flags: {
                paid: due <= 0,
              },
              status,
            },
            { merge: true },
          );

          if (payment.paymentType === "chargeOff") {
            //  Charge off balance
            transaction.set(
              brandStatsDoc,
              {
                metrics: {
                  chargeOffBalance: increment(
                    Number(parseFloat(-paymentAmount).toFixed(2)),
                  ),
                },
              },
              { merge: true },
            );
          }

          //  The balance for this order is added to the account's balance on 'shipped'
          //  or 'complete' status. Payments (and their reversals) are allowed on 'complete'
          //  orders so accounting can track overpayments, so both statuses must be handled here.
          if (
            this.order.status === "complete" ||
            this.order.status === "shipped"
          ) {
            transaction.set(
              brandStatsDoc,
              {
                metrics: {
                  balance: increment(Number(parseFloat(amountToAdd).toFixed(2))),
                },
              },
              { merge: true },
            );
          }

          return { due, status, statusChangedToShipped };
        },
      );

      addBreadcrumb("removePayment: committed", {
        workflow: "sales-order",
        data: { orderId: this.orderID, newDue: due },
      });

      // MyTrace bookkeeping must run even if LeafLink sync fails below —
      // the Firestore reversal already committed.
      if (statusChangedToShipped) {
        const oldStatus = store.getters.getOrderStatus.find(
            (option) => option.value === this.order.status,
          ),
          newStatus = store.getters.getOrderStatus.find(
            (option) => option.value === "shipped",
          );

        await createActivity({
          type: "status",
          action: "Order status updated",
          item: `Order #${this.order.orderNumber.internal.toUpperCase()} status reverted from ${
            oldStatus.text
          } to ${newStatus.text} due to payment reversal`,
          relatedTo: "salesOrders",
          relatedID: [salesOrderDoc.id],
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          account: {
            id: this.order.account.id,
            name: this.order.account.name,
          },
          brand: {
            id: this.order.brand.id,
            name: this.order.brand.name,
          },
          businessImpact: "high",
          priority: "high",
          changeData: {
            field: "status",
            oldValue: {
              text: oldStatus.text,
              value: oldStatus.value,
              color: oldStatus.color,
            },
            newValue: {
              text: newStatus.text,
              value: newStatus.value,
              color: newStatus.color,
            },
            reason:
              "Payment reversal caused order completion to revert to shipped status",
            orderNumber: this.order.orderNumber,
          },
        });
      }

      await createActivity({
        type: "financial",
        action: "Payment reversed",
        item: `${paymentType.text}: -${new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(payment.amount)}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          paymentAmount: payment.amount,
          paymentType: paymentType.value,
          checkNumber: payment.checkNumber,
          paymentDate: moment(payment.paymentDate.seconds * 1000).format(
            "YYYY-MM-DD",
          ),
          reversalReason: reason,
          orderNumber: this.order.orderNumber,
          monetaryValue: payment.amount,
        },
      });

      if (payment.paymentType === "promotionCredit") {
        await createActivity({
          type: "financial",
          action: "Promotion credit updated",
          item: `Promotion credit balance restored: +$${new Intl.NumberFormat(
            "en-US",
            {
              style: "currency",
              currency: "USD",
            },
          ).format(payment.amount)} for Order #${
            this.order.orderNumber.internal
          } - Payment reversal`,
          relatedTo: "promotionCredits",
          relatedID: [promotionCreditDoc.id],
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          account: {
            id: this.order.account.id,
            name: this.order.account.name,
          },
          brand: {
            id: this.order.brand.id,
            name: this.order.brand.name,
          },
          businessImpact: "high",
          priority: "medium",
          changeData: {
            field: "promotionCreditBalance",
            creditAmount: amountToAdd,
            availableAmount: available,
            orderNumber: this.order.orderNumber.internal,
            orderNumberExternal: this.order.flags.external
              ? this.order.orderNumber.external
              : null,
            statusChange:
              selectedPromotionCredit.available === 0 && available > 0
                ? {
                    oldValue: "inactive",
                    newValue: "active",
                  }
                : null,
            updateReason: "payment_reversal",
            monetaryValue: amountToAdd,
          },
        });
      }

      await this._checkBalanceAnomaly("remove-payment", { balanceDue: due });

      if (statusChangedToShipped) {
        try {
          await this._removeFirstPurchases();
        } catch (firstPurchaseError) {
          logWorkflowWarning(
            `firstPurchases removal failed — ${firstPurchaseError.message}`,
            {
              workflow: "sales-order",
              action: "remove-first-purchases",
              context: {
                orderId: this.orderID,
                accountId: this.order.account?.id,
                brandId: this.order.brand?.id,
              },
            },
          );
        }
      }

      // External sync after MyTrace ledger/activity is written. Failures here
      // still throw so callers can surface LeafLink reconciliation, but they
      // must not leave a silent reversed payment with no activity.
      if (this.orderIsExternal) {
        addBreadcrumb("leaflink: removePayment reversal start", {
          workflow: "sales-order",
          data: {
            orderId: this.orderID,
            paymentId: payment?.id,
            paymentAmount: payment?.amount,
            externalOrderType: this.externalOrderType,
          },
        });
        try {
          switch (this.externalOrderType) {
            case "leaflink":
              if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
                let paymentData = {
                  order: this.order.externalIDs.leaflink,
                  recorded_by: userLeaflinkInfo.id,
                  total: {
                    amount: Number(
                      parseFloat(
                        (payment.amount - (payment.creditsApplied ?? 0)) * -1,
                      ).toFixed(2),
                    ),
                    currency: "usd",
                  },
                  payment_date: moment().toISOString(),
                  reason: reason || "",
                  payment_type:
                    paymentType.value === "chargeOff" ||
                    paymentType.value === "sample-clearBalance" ||
                    paymentType.value === "promo-clearBalance" ||
                    paymentType.value === "promotionCredit" ||
                    paymentType.value === "vendorCredit"
                      ? "other"
                      : paymentType.value,
                  check_number: payment.checkNumber || "",
                };
                await this.leaflinkClient.createPayment(paymentData);
              }
              if (
                statusChangedToShipped &&
                this.externalUpdates.leaflink.includes("statusUpdated")
              ) {
                addBreadcrumb(
                  "leaflink: transitionOrder ship (removePayment status revert)",
                  {
                    workflow: "sales-order",
                    data: {
                      orderId: this.orderID,
                      fromStatus: this.order?.status,
                    },
                  },
                );
                await this.leaflinkClient.transitionOrder("ship");
              }
              break;
            default:
              break;
          }
        } catch (e) {
          console.error("removePayment: LeafLink sync failed after MyTrace reversal", e);
          logWorkflowWarning(
            `LeafLink: payment reversal failed after ${APP_NAME} reversal already committed — ${e.message}`,
            {
              workflow: "sales-order",
              action: "leaflink-payment-removal",
              context: {
                orderId: this.orderID,
                paymentId: payment?.id,
                paymentAmount: payment?.amount,
                balance: this.order?.balance,
              },
            },
          );
          throw e;
        }
      }
    } catch (error) {
      throw error;
    }
  }

  async applyPayment(data, { skipPendingClear = false } = {}) {
    addBreadcrumb("applyPayment: start", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        paymentCount: Array.isArray(data) ? data.length : 1,
        currentDue: this.order?.balance?.due,
        currentTotal: this.order?.balance?.total,
        orderStatus: this.order?.status,
      },
    });
    try {
      await this.getOrder();
      await this.getLineItems();
      await this.getPayments();

      this._assertCompleteShipForPaymentMutation();

      // Handle both single payment and multiple payments
      const payments = Array.isArray(data) ? data : [data];

      const batch = writeBatch(firestore);
      const salesOrderDoc = doc(firestore, "salesOrders", this.order.id);
      const brandStatsDoc = doc(
        firestore,
        "brandStats",
        `${this.order.account.id}_${this.order.brand.id}`,
      );
      const brandStats = await getDoc(brandStatsDoc);
      let updates = [],
        staff = [];
      let status = this.order.status,
        paid = false,
        userExists = true,
        due = Number(parseFloat(this.order.balance.due).toFixed(2));

      const payableToRef = collection(firestore, "payableTo");
      const state = where(
          "location.state.stateID",
          "==",
          this.order.location.shipping.state.stateID,
        ),
        brand = where("brandID", "==", this.order.brand.id);
      const q = query(payableToRef, state, brand);
      const response = await getDocs(q, limit(1));

      let payableTo = {
        id: response.docs[0].id,
        ...response.docs[0].data(),
      };

      let overpaymentCreditID = null;

      let leaflinkEmail = null;
      // TODO: Add external order logic
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
              let data = await this.leaflinkClient.getUsers();
              staff = data.results;
              const integrationsRef = collection(firestore, "integrations");
              const platform = where("platform", "==", "leaflink");
              const integrationsQ = query(integrationsRef, platform);
              const integrationsResponse = await getDocs(
                integrationsQ,
                limit(1),
              );

              if (!integrationsResponse.empty) {
                leaflinkEmail = integrationsResponse.docs[0].data().email;
              }

              if (!leaflinkEmail) {
                this.loading.submit = false;
                logWorkflowWarning(
                  "applyPayment: aborted — LeafLink integration email not found, payment not applied",
                  {
                    workflow: "sales-order",
                    action: "apply-payment-leaflink-preflight",
                    context: {
                      orderId: this.orderID,
                      balance: this.order?.balance,
                      orderStatus: this.order?.status,
                    },
                  },
                );
                throw new Error(
                  "LeafLink payment could not be applied: integration email not found. No payment was recorded.",
                );
              }
            }
            break;
          default:
            break;
        }
      }

      let userLeaflinkInfo = staff.find(
        (staffMember) => staffMember.email === leaflinkEmail,
      );

      let lastPayment = brandStats.data().metrics.last.payment;
      let totalCreditsApplied = 0;
      let totalPaymentAmount = 0;
      let totalChargeOffAmount = 0;
      let creditsApplied = 0;
      let shouldReturn = false;
      let needsLeafLinkCompleteTransition = false;
      let leaflinkPaymentsToCreate = []; // Store LeafLink payments to create after nudool batch commit

      let nonCreditablePaymentTypes = [...NON_CREDITABLE_PAYMENT_TYPES];

      let currency = (value) => {
        return Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        }).format(value);
      };

      // Track running balance for multiple payments
      let runningBalance = due;

      // Process each payment
      for (
        let paymentIndex = 0;
        paymentIndex < payments.length;
        paymentIndex++
      ) {
        const paymentData = payments[paymentIndex];

        // Reset per-payment credit state — without this, a non-creditable payment
        // (e.g. chargeOff) processed after a creditable overpaying payment in the
        // same batch inherits the prior payment's overpaymentCreditID/creditsApplied.
        creditsApplied = 0;
        overpaymentCreditID = null;

        let paymentAmount = Number(parseFloat(paymentData.amount).toFixed(2));
        let paymentType = paymentData.type;
        let paymentDate = paymentData.date;
        let paymentCheckNumber = paymentData.checkNumber;
        let paymentPromotionCredit = paymentData.promotionCredit;
        let paymentNotes = paymentData.notes;

        // Soft preflight for promo credits (authoritative check is the transaction below).
        // Partial leftovers on the same order are allowed when available > 0.
        let promoCreditClaim = null;
        if (paymentType.value === "promotionCredit" && paymentPromotionCredit) {
          await this.getPayments();

          const promotionCreditDoc = doc(
            firestore,
            "promotionCredits",
            paymentPromotionCredit,
          );
          const promotionCreditResponse = await getDoc(promotionCreditDoc);
          const selectedPromotionCredit = promotionCreditResponse.data();
          const creditAvailable = roundMoney(
            selectedPromotionCredit?.available,
          );

          if (creditAvailable <= 0) {
            const alreadyOnOrder = hasActivePromotionCreditPayment(
              this.payments,
              paymentPromotionCredit,
            );
            throw new Error(
              alreadyOnOrder
                ? "This promotion credit has no remaining balance — it may already be applied on this order. Refresh and try again."
                : "This promotion credit has no remaining balance. Refresh and try again.",
            );
          }

          const maxAllowedAmount = roundMoney(
            Math.min(creditAvailable, runningBalance),
          );

          if (paymentAmount > maxAllowedAmount) {
            throw new Error(
              `Payment amount (${paymentAmount}) exceeds maximum allowed amount (${maxAllowedAmount}) - credit available: ${creditAvailable}, running balance: ${runningBalance}`,
            );
          }
        }

        // Check for overpayment using running balance
        if (
          !nonCreditablePaymentTypes.includes(paymentType.value) &&
          paymentAmount > runningBalance
        ) {
          // Apply credits to account if payment is a creditable type and payment amount is greater than running balance
          addBreadcrumb("applyPayment: overpayment credit will be created", {
            workflow: "sales-order",
            data: {
              orderId: this.orderID,
              paymentAmount,
              runningBalance,
              paymentType: paymentType.value,
            },
          });
          creditsApplied =
            Number(parseFloat(paymentAmount).toFixed(2)) -
            Number(parseFloat(runningBalance).toFixed(2));

          creditsApplied = Number(parseFloat(creditsApplied).toFixed(2));

          totalCreditsApplied += creditsApplied;

          // Create Promotion Credit with "overpayment" credit type
          let creditDoc = doc(collection(firestore, "promotionCredits"));
          overpaymentCreditID = creditDoc.id;
          const brandAbbreviation = this.order.brand.name
            .split(" ")
            .map((word) => word.charAt(0))
            .join("");

          let stateAbbrevation =
            this.order.location.shipping.state.abbreviation;
          let existingCredits = payableTo.records?.creditMemos ?? 0;

          let accountDoc = doc(firestore, "accounts", this.order.account.id);
          let accountResponse = await getDoc(accountDoc);

          let countryID = accountResponse.data().location.shipping.countryID;

          batch.set(
            creditDoc,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              account: {
                name: this.order.account.name,
                id: this.order.account.id,
              },
              brand: {
                id: this.order.brand.id,
                name: this.order.brand.name,
              },
              location: {
                countryID: countryID,
                state: this.order.location.shipping.state.stateID,
              },
              active: true,
              reversed: false,
              date: toBusinessDate(paymentDate),
              expirationDate: moment()
                .add(6, "months")
                .startOf("day")
                .add(9, "hours")
                .tz("America/Los_Angeles")
                .toDate(),
              expirationReminderSent: false,
              orderNumber: {
                internal: `${brandAbbreviation}-cm-${stateAbbrevation}-${
                  existingCredits + 1
                }`,
                external: "",
              },
              total: creditsApplied,
              available: creditsApplied,
              type: "overpayment",
              relatedID: this.order.id,
              notes: "",
              details: "",
              promotion: null,
            },
            {
              merge: true,
            },
          );

          batch.set(
            doc(firestore, "payableTo", payableTo.id),
            {
              records: {
                creditMemos: increment(1),
              },
            },
            { merge: true },
          );

          // Brand Stats - Metrics
          batch.set(
            brandStatsDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              metrics: {
                promotionCredits: {
                  total: increment(creditsApplied),
                  available: increment(creditsApplied),
                },
              },
            },
            { merge: true },
          );

          // Ledger
          let updates = [];
          let active = true;
          updates.push(`Available: <span>${currency(creditsApplied)}</span>`);
          updates.push(
            `Account: <span class="text-capitalize">${this.order.account.name}</span>`,
          );
          let promotionCreditType = store.getters.getCreditTypes.find(
            (item) => item.value === "overpayment",
          )?.text;
          updates.push(
            `Type: <span class="text-capitalize">${promotionCreditType}</span>`,
          );
          updates.push(
            `Brand: <span class="text-capitalize">${this.order.brand.name}</span>`,
          );
          updates.push(
            `<span class="font-weight-bold text-uppercase" style="color: green">active</span>`,
          );
        }

        totalPaymentAmount += paymentAmount;
        if (paymentType.value === "chargeOff") {
          totalChargeOffAmount += paymentAmount;
        }

        // Update running balance for next payment
        runningBalance = Number(
          parseFloat(runningBalance - paymentAmount + creditsApplied).toFixed(
            2,
          ),
        );

        // Store LeafLink payment data to create AFTER nudool batch commit succeeds
        // This ensures nudool is the source of truth and LeafLink syncs after
        if (this.orderIsExternal && paymentAmount > 0) {
          switch (this.externalOrderType) {
            case "leaflink":
              if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
                let actualPaymentDate = toBusinessDate(paymentDate);
                let paymentData = {
                  order: this.order.externalIDs.leaflink,
                  recorded_by: userLeaflinkInfo.id,
                  total: {
                    amount: Number((paymentAmount - creditsApplied).toFixed(2)),
                    currency: "usd",
                  },
                  payment_date: actualPaymentDate.toISOString(),
                  reason: paymentNotes || "",
                  payment_type: nonCreditablePaymentTypes.includes(
                    paymentType.value,
                  )
                    ? "other"
                    : paymentType.value,
                  check_number: paymentCheckNumber || "",
                };
                leaflinkPaymentsToCreate.push(paymentData);
              }
              break;
            default:
              break;
          }
        }

        let assignedTo = "";

        nonCreditablePaymentTypes.push("credit");

        if (!nonCreditablePaymentTypes.includes(paymentType.value)) {
          if (brandStats.data().staff?.executive.id?.length > 0) {
            assignedTo = {
              name: brandStats.data().staff.executive.name,
              id: brandStats.data().staff.executive.id,
            };
          } else if (brandStats.data().staff?.manager.id?.length > 0) {
            assignedTo = {
              name: brandStats.data().staff.manager.name,
              id: brandStats.data().staff.manager.id,
            };
          }
        }

        let shipDate = this.order.shipDate
          ? moment(this.order.shipDate.seconds * 1000)
              .startOf("day")
              .add(9, "hours")
              .tz("America/Los_Angeles")
              .toDate()
          : null;

        // Submit payment. Promo credits use a transaction so concurrent applies
        // cannot both pass an available check and overdraw the same credit.
        const paymentPayload = {
          assignedTo,
          account: {
            ...this.order.account,
            state: this.order.location.shipping.state,
          },
          brand: this.order.brand,
          order: {
            external: this.order.flags?.external,
            number: this.order.orderNumber,
            created: moment(this.order.created?.seconds * 1000).toDate(),
            createdBy: this.order.createdBy,
            shipDate,
            paymentTerm: this.order.paymentTerm,
          },
          notes: paymentNotes,
          amount: paymentAmount,
          checkNumber: paymentCheckNumber,
          promotionCredit: paymentPromotionCredit
            ? paymentPromotionCredit
            : null,
          overpaymentCreditID,
          creditsApplied,
          flags: {
            reversed: false,
            hasSupport: this.order.account?.staff?.support ? true : false,
          },
          support: this.order.account?.staff?.support || null,
          paymentDate: toBusinessDate(paymentDate),
          paymentType: paymentType.value,
          relatedID: this.order.id,
          users: [
            brandStats.data().staff.manager.id,
            brandStats.data().staff.executive.id,
          ],
        };

        if (
          paymentType.value === "promotionCredit" &&
          paymentPromotionCredit
        ) {
          // Claim payment + credit.available only. Order due / paid / A/R stay in
          // this writeBatch so earlier payments in the same submission (not yet
          // committed) are reflected in runningBalance.
          promoCreditClaim = await claimAndWritePromotionCreditPayment({
            creditId: paymentPromotionCredit,
            amount: paymentAmount,
            paymentData: paymentPayload,
            user: store.getters.getUser,
          });
        } else {
          batch.set(
            doc(collection(firestore, "salesOrders_payments")),
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              ...paymentPayload,
            },
            { merge: true },
          );
        }

        // Set Last Payment on brand metrics if payment date > brand metric's last payment date
        // - lastPayment will have a 'seconds' value if it comes from the database, it won't when
        // user is submitting multiple payments
        lastPayment =
          lastPayment && lastPayment?.seconds
            ? moment(lastPayment?.seconds * 1000).toDate()
            : lastPayment
            ? lastPayment
            : null;

        if (!lastPayment || lastPayment < toBusinessDate(paymentDate)) {
          lastPayment = toBusinessDate(paymentDate);

          batch.set(
            brandStatsDoc,
            {
              metrics: {
                last: {
                  payment: lastPayment,
                },
              },
            },
            { merge: true },
          );
        }

        // Ledger
        updates.push(
          `<span class="text-capitalize">amount</span>: <span class="font-weight-bold">+${new Intl.NumberFormat(
            "en-US",
            {
              style: "currency",
              currency: "USD",
            },
          ).format(paymentAmount)}</span>`,
        );
        updates.push(
          `<span class="text-capitalize">payment date</span>: ${moment(
            paymentDate,
          ).format("MM/DD/YYYY")}`,
        );
        updates.push(
          `<span class="text-capitalize">type</span>: ${paymentType.text}`,
        );
        if (paymentType.value === "check") {
          updates.push(
            `<span class="text-capitalize">check #</span>: ${paymentCheckNumber}`,
          );
        }

        // Create activity for payment application
        let paymentDetails = [];
        paymentDetails.push(
          `Amount: ${new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(paymentAmount)}`,
        );
        paymentDetails.push(`Type: ${paymentType.text}`);
        paymentDetails.push(
          `Date: ${moment(paymentDate).format("MM/DD/YYYY")}`,
        );
        if (paymentType.value === "check" && paymentCheckNumber) {
          paymentDetails.push(`Check #: ${paymentCheckNumber}`);
        }
        if (creditsApplied > 0) {
          paymentDetails.push(
            `Credits Applied: ${new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(creditsApplied)}`,
          );
        }
        if (paymentNotes) {
          paymentDetails.push(`Notes: "${paymentNotes}"`);
        }

        await createActivity({
          type: "financial",
          action: "Payment applied",
          item: `Order #${this.order.orderNumber.internal.toUpperCase()} - Payment applied - ${paymentDetails.join(
            ", ",
          )}`,
          relatedTo: "salesOrders",
          relatedID: [this.order.id],
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          account: {
            id: this.order.account.id,
            name: this.order.account.name,
          },
          brand: {
            id: this.order.brand.id,
            name: this.order.brand.name,
          },
          businessImpact: "high",
          priority: "high",
          changeData: {
            field: "paymentApplication",
            paymentAmount: paymentAmount,
            paymentType: paymentType.value,
            paymentTypeText: paymentType.text,
            paymentDate: toBusinessDate(paymentDate),
            checkNumber: paymentCheckNumber,
            creditsApplied: creditsApplied,
            notes: paymentNotes,
            orderNumber: this.order.orderNumber.internal,
            previousBalance: due,
            newBalance: runningBalance,
            paymentMethod: paymentType.value,
            externalOrderUpdated:
              this.externalUpdates.leaflink.includes("paymentsApplied"),
            monetaryValue: paymentAmount,
          },
        });

        if (paymentType.value === "promotionCredit" && promoCreditClaim) {
          // Credit available was claimed in the transaction; brandStats and
          // order due still go through this batch with the rest of the submit.
          const available = promoCreditClaim.newAvailable;
          let updates = [];

          batch.set(
            brandStatsDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              metrics: {
                promotionCredits: {
                  available: increment(-paymentAmount),
                },
              },
            },
            { merge: true },
          );

          // Ledger
          updates.push(
            `Available, <span class="text-uppercase">old</span>: <span>${currency(
              promoCreditClaim.previousAvailable,
            )}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${currency(
              available,
            )}</span>`,
          );
          updates.push(
            `Account, <span class="text-capitalize">${this.order.account.name}</span>`,
          );
          updates.push(
            `Brand, <span class="text-capitalize">${this.order.brand.name}</span>`,
          );
          updates.push(
            `Order #, Internal: ${
              this.order.orderNumber.internal
            } / External: ${
              this.order.flags.external ? this.order.orderNumber.external : "-"
            }`,
          );
          if (available === 0) {
            updates.push(
              `Active, <span class="text-uppercase">old</span>: <span class="text-uppercase font-weight-bold" style="color: green;">active</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold text-uppercase" style="color: red;">inactive</span>`,
            );
            addBreadcrumb("applyPayment: promotion credit fully exhausted", {
              workflow: "sales-order",
              data: {
                orderId: this.orderID,
                promotionCreditId: paymentPromotionCredit,
                paymentAmount,
                paymentId: promoCreditClaim.paymentId,
              },
            });
          }
        }

        if (runningBalance <= 0) {
          // Status
          paid = true;

          if (this.order.status === "shipped") {
            //  Users are able to add payments at any point in the order process
            //  Because of this, we can't set the status to 'complete' unless
            //    the order is already in the 'shipped' status.

            status = "complete";
            const oldStatus = store.getters.getOrderStatus.find(
                (option) => option.value === this.order.status,
              ),
              newStatus = store.getters.getOrderStatus.find(
                (option) => option.value === "complete",
              );

            //  External Order Status - Track that we need to transition LeafLink after batch commit
            if (this.orderIsExternal && !shouldReturn) {
              switch (this.externalOrderType) {
                case "leaflink":
                  if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                    needsLeafLinkCompleteTransition = true;
                  }
                  break;
                default:
                  break;
              }
            }

            // Create activity for status change
            await createActivity({
              type: "status",
              action: "Order completed - fully paid",
              item: `Order #${this.order.orderNumber.internal.toUpperCase()} status changed from ${
                oldStatus.text
              } to ${newStatus.text} due to full payment`,
              relatedTo: "salesOrders",
              relatedID: [this.order.id],
              user: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              account: {
                id: this.order.account.id,
                name: this.order.account.name,
              },
              brand: {
                id: this.order.brand.id,
                name: this.order.brand.name,
              },
              businessImpact: "high",
              priority: "high",
              changeData: {
                field: "status",
                oldValue: {
                  text: oldStatus.text,
                  value: oldStatus.value,
                  color: oldStatus.color,
                },
                newValue: {
                  text: newStatus.text,
                  value: newStatus.value,
                  color: newStatus.color,
                },
                reason: "Order fully paid - automatic completion",
                orderNumber: this.order.orderNumber.internal,
                completionTrigger: "payment_completion",
              },
            });
          }
          batch.set(
            salesOrderDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
            },
            { merge: true },
          );
          batch.update(salesOrderDoc, {
            "records.completed": moment()
              .startOf("day")
              .add(12, "hours")
              .toDate(),
          });
        }

        // Order balance (absolute last-write-wins on runningBalance per payment)
        due = parseFloat(runningBalance).toFixed(2);
        batch.set(
          salesOrderDoc,
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            balance: {
              due,
            },
            flags: {
              paid,
            },
            status,
          },
          { merge: true },
        );
      }

      // Account Balances — once per applyPayment, after all payments are totaled.
      // Must NOT live inside the payment loop: totalPaymentAmount / totalChargeOffAmount
      // are cumulative, and increment() inside the loop double-decrements A/R (#1636).
      if (totalChargeOffAmount > 0) {
        batch.set(
          brandStatsDoc,
          {
            metrics: {
              chargeOffBalance: increment(
                Number(parseFloat(totalChargeOffAmount).toFixed(2)),
              ),
            },
          },
          { merge: true },
        );
      }

      //  The balance for this order is added to the account's balance on 'shipped'
      //  or 'complete' status. Payments are allowed on 'complete' orders so accounting
      //  can track overpayments, so both statuses must be handled here.

      if (this.order.status === "complete" || this.order.status === "shipped") {
        batch.set(
          brandStatsDoc,
          {
            metrics: {
              balance: increment(
                Number(
                  parseFloat(
                    -totalPaymentAmount + totalCreditsApplied,
                  ).toFixed(2),
                ),
              ),
              // credits: increment(this.accountCredits),
            },
          },
          { merge: true },
        );
      }

      if (!shouldReturn) {
        addBreadcrumb("applyPayment: committing batch", {
          workflow: "sales-order",
          data: { orderId: this.orderID, runningBalance, creditsApplied },
        });
        // Don't commit if there were errors submitting leaflink payment(s)
        await batch.commit();
        addBreadcrumb("applyPayment: committed", {
          workflow: "sales-order",
          data: { orderId: this.orderID, newDue: runningBalance },
        });
        await this._checkBalanceAnomaly("apply-payment", {
          balanceDue: runningBalance,
        });

        // Manual/single pays clear pending here. Batch pending apply clears once at the end.
        if (!skipPendingClear) {
          const appliedPromoCreditIds = payments
            .filter(
              (paymentData) =>
                paymentData.type?.value === "promotionCredit" &&
                paymentData.promotionCredit,
            )
            .map((paymentData) => paymentData.promotionCredit);
          if (appliedPromoCreditIds.length > 0) {
            try {
              await this.getOrder();
              await removeAppliedIdsFromPendingPromotionCredits(
                this.orderID,
                this.order,
                appliedPromoCreditIds,
              );
            } catch (pendingClearError) {
              console.error(
                "applyPayment: failed to clear pending promotion credits after payment",
                pendingClearError,
              );
              logWorkflowWarning(
                `applyPayment: pending credit clear failed — ${pendingClearError.message}`,
                {
                  workflow: "sales-order",
                  action: "apply-payment-pending-clear",
                  context: {
                    orderId: this.orderID,
                    appliedPromoCreditIds,
                  },
                },
              );
            }
          }
        }

        // Transitioned into complete via full payment. Never throws (can't roll back the payment).
        if (status === "complete") {
          try {
            await this._createFirstPurchases(
              moment().startOf("day").add(12, "hours").toDate(),
            );
          } catch (firstPurchaseError) {
            logWorkflowWarning(
              `firstPurchases creation failed — ${firstPurchaseError.message}`,
              {
                workflow: "sales-order",
                action: "create-first-purchases",
                context: {
                  orderId: this.orderID,
                  accountId: this.order.account?.id,
                  brandId: this.order.brand?.id,
                },
              },
            );
          }
        }

        // ============================================
        // LEAFLINK OPERATIONS - Only after nudool batch commit succeeds
        // This ensures nudool is the source of truth
        // ============================================

        // Create LeafLink payments - Only after nudool batch commit succeeds
        if (leaflinkPaymentsToCreate.length > 0 && !shouldReturn) {
          for (const paymentData of leaflinkPaymentsToCreate) {
            try {
              if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
                await this.leaflinkClient.createPayment(paymentData);
              }
            } catch (paymentError) {
              // Log error but don't throw - nudool is already updated
              // This prevents a LeafLink API issue from rolling back nudool changes
              console.error("Failed to create LeafLink payment:", paymentError);
              logWorkflowWarning(`LeafLink: createPayment failed — ${paymentError.message}`, {
                workflow: "sales-order",
                action: "leaflink-payment-sync",
                context: { orderId: this.orderID, paymentAmount: paymentData?.total?.amount, balance: this.order?.balance },
              });
              // Create an activity to track this failure
              try {
                await createActivity({
                  type: "error",
                  action: "LeafLink payment sync failed",
                  item: `Order #${this.order.orderNumber.internal.toUpperCase()} - Failed to create payment in LeafLink. ${APP_NAME} payment recorded successfully. Amount: ${
                    paymentData.total.amount
                  }`,
                  relatedTo: "salesOrders",
                  relatedID: [this.order.id],
                  user: {
                    name: store.getters.getUser.name,
                    uid: store.getters.getUser.uid,
                  },
                  account: {
                    id: this.order.account.id,
                    name: this.order.account.name,
                  },
                  brand: {
                    id: this.order.brand.id,
                    name: this.order.brand.name,
                  },
                  businessImpact: "high",
                  priority: "high",
                  changeData: {
                    field: "externalPaymentSync",
                    error: paymentError.message,
                    orderNumber: this.order.orderNumber.internal,
                    paymentAmount: paymentData.total.amount,
                  },
                });
              } catch (activityError) {
                console.error(
                  "Failed to create error activity:",
                  activityError,
                );
                logWorkflowWarning(`applyPayment: activity creation for LeafLink payment failure failed — ${activityError.message}`, {
                  workflow: "sales-order",
                  action: "apply-payment-activity-log",
                  context: { orderId: this.orderID },
                });
              }
            }
          }
        }

        // External Order Status Transition - Only after nudool batch commit succeeds
        // This ensures nudool is updated before we try to sync LeafLink status
        if (needsLeafLinkCompleteTransition && !shouldReturn) {
          try {
            switch (this.externalOrderType) {
              case "leaflink":
                if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                  await this.leaflinkClient.transitionOrder("complete");
                }
                break;
              default:
                break;
            }
          } catch (transitionError) {
            // Log error but don't throw - nudool is already updated
            // This prevents a LeafLink API issue from rolling back nudool changes
            console.error(
              "Failed to transition LeafLink order to complete:",
              transitionError,
            );
            logWorkflowWarning(`LeafLink: transitionOrder(complete) failed — ${transitionError.message}`, {
              workflow: "sales-order",
              action: "leaflink-transition-sync",
              context: { orderId: this.orderID, toStatus: "complete", balance: this.order?.balance },
            });
            // Optionally create an activity to track this failure
            try {
              await createActivity({
                type: "error",
                action: "LeafLink status sync failed",
                item: `Order #${this.order.orderNumber.internal.toUpperCase()} - Failed to transition LeafLink order to 'complete' status. ${APP_NAME} status updated successfully.`,
                relatedTo: "salesOrders",
                relatedID: [this.order.id],
                user: {
                  name: store.getters.getUser.name,
                  uid: store.getters.getUser.uid,
                },
                account: {
                  id: this.order.account.id,
                  name: this.order.account.name,
                },
                brand: {
                  id: this.order.brand.id,
                  name: this.order.brand.name,
                },
                businessImpact: "medium",
                priority: "medium",
                changeData: {
                  field: "externalStatusSync",
                  error: transitionError.message,
                  orderNumber: this.order.orderNumber.internal,
                },
              });
            } catch (activityError) {
              console.error("Failed to create error activity:", activityError);
              logWorkflowWarning(`applyPayment: activity creation for LeafLink status sync failure failed — ${activityError.message}`, {
                workflow: "sales-order",
                action: "apply-payment-status-activity-log",
                context: { orderId: this.orderID },
              });
            }
          }
        }
      }
    } catch (error) {
      throw error;
    }
  }

  // Current cost of goods for the given product IDs. Empty map when the brand
  // hasn't opted into COGS tracking. Sets costOfGoodsResolutionFailed when a
  // tracking brand's read fails so the caller can warn.
  async _resolveCostOfGoods(productIDs = []) {
    this.costOfGoodsResolutionFailed = false;
    const { costByProduct, failed } = await resolveBrandCostOfGoods(
      this.order.brand.id,
      productIDs,
    );
    this.costOfGoodsResolutionFailed = failed;
    return costByProduct;
  }
  async updateLineItems(cart, lineItems) {
    addBreadcrumb("updateLineItems: start", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        cartItemCount: cart?.length,
        existingLineItemCount: lineItems?.length,
        currentTotal: this.order?.balance?.total,
        currentDue: this.order?.balance?.due,
        orderStatus: this.order?.status,
      },
    });
    let transactionCommitted = false;

    try {
      await this.getOrder();
      await this.getLineItems();
      await this.getProductCategories();
      await this.getPriceOverrides();
      // Snapshot for the pre-commit overpay guard below.
      await this.getPayments();

      // Resolved before the transaction (queries aren't allowed inside one) and
      // read from the product docs so newly added items get an authoritative cost.
      // Existing lines keep their frozen cost; only cart product IDs are fetched.
      const productIDs = (cart ?? []).map(
        (item) => item.productID ?? item.id,
      );
      const costOfGoodsByProduct = await this._resolveCostOfGoods(productIDs);

      const payableToResponse = await getDocs(
        query(
          collection(firestore, "payableTo"),
          where(
            "location.state.stateID",
            "==",
            this.order.location.shipping.state.stateID,
          ),
          where("brandID", "==", this.order.brand.id),
          limit(1),
        ),
      );
      const payableToShippingCharge = payableToResponse.empty
        ? null
        : payableToResponse.docs[0].data().shippingCharge;

      // // Use transaction for inventory-sensitive operations
      const result = await runTransaction(firestore, async (transaction) => {
        // Get fresh data within transaction
        const salesOrderDoc = doc(firestore, "salesOrders", this.orderID);

        // Read current order within transaction
        const orderSnapshot = await transaction.get(salesOrderDoc);
        const currentOrder = orderSnapshot.data();

        if (!currentOrder) {
          throw new Error(`Order ${this.orderID} not found`);
        }

        // Add the document ID to the order data
        currentOrder.id = orderSnapshot.id;

        const stateDoc = doc(
          firestore,
          "states",
          currentOrder.location.shipping.state.stateID,
        );

        // Read current state within transaction
        const stateSnapshot = await transaction.get(stateDoc);
        const stateData = stateSnapshot.data();

        if (!stateData) {
          throw new Error(
            `State ${currentOrder.location.shipping.state.stateID} not found`,
          );
        }

        let fulfillmentLocation = stateData.fulfillmentLocation;

        if (currentOrder.fulfillmentLocation) {
          fulfillmentLocation = currentOrder.fulfillmentLocation.id;
        }

        const locationRef = doc(
          firestore,
          "locationManufacturing",
          `${fulfillmentLocation}_${currentOrder.brand.id}`,
        );

        // PRE-VALIDATE INVENTORY AVAILABILITY BEFORE PROCESSING ANY CHANGES
        // This prevents race conditions by checking inventory upfront
        const inventorySnapshot = await transaction.get(locationRef);
        const inventoryData = inventorySnapshot.data()?.manufacturing || {};
        // Create a deep copy of the original inventory data for snapshot purposes
        // This ensures "before" values are always from the original state, not mutated state
        const originalInventoryData = JSON.parse(JSON.stringify(inventoryData));

        // Aggregate total quantities needed per product (combining promo and regular units)
        const productTotals = {};
        for (const cartItem of cart) {
          const categoryID = cartItem.categoryID;
          const productID = cartItem.productID;
          const key = `${categoryID}_${productID}`;

          if (!productTotals[key]) {
            productTotals[key] = {
              categoryID,
              productID,
              name: cartItem.name,
              sku: cartItem.sku,
              existingQty: 0,
              newQty: 0,
              additionalQtyRequested: 0,
            };
          }

          // Calculate quantities for this cart item
          // Handle both case-based and non-case products
          const isCaseProduct =
            cartItem.caseQTY > 0 || cartItem.unitsPerCase > 0;

          let existingQty, newQty;

          if (isCaseProduct) {
            // Case-based product: calculate using cases and singles
            existingQty =
              Number(cartItem.cases.existing || 0) *
                (cartItem.caseQTY || cartItem.unitsPerCase || 0) +
              Number(cartItem.singles.existing || 0);

            newQty =
              Number(cartItem.cases.new || 0) *
                (cartItem.caseQTY || cartItem.unitsPerCase || 0) +
              Number(cartItem.singles.new || 0);
          } else {
            // Non-case product: use singles directly, ignore cases
            existingQty = Number(cartItem.singles.existing || 0);
            newQty = Number(cartItem.singles.new || 0);
          }

          // Aggregate totals for this product
          productTotals[key].existingQty += existingQty;
          productTotals[key].newQty += newQty;
        }

        // Calculate additional quantity requested for each product
        for (const key in productTotals) {
          const product = productTotals[key];
          product.additionalQtyRequested = product.newQty - product.existingQty;
        }

        // Validate that we have enough inventory for all requested changes
        // Now we check inventory per product (not per cart item) to account for promo + regular units
        for (const key in productTotals) {
          const product = productTotals[key];

          // If reducing quantity, no need to check inventory
          if (product.additionalQtyRequested <= 0) {
            continue;
          }

          const totalAvailable =
            inventoryData[product.categoryID]?.available?.[product.productID] ||
            0;
          const totalReserved =
            inventoryData[product.categoryID]?.reserved?.[product.productID] ||
            0;

          // Calculate how much is available for new reservations
          const availableForNewReservations = totalAvailable - totalReserved;

          if (availableForNewReservations < product.additionalQtyRequested) {
            throw new Error(
              `Insufficient inventory for ${product.name} (${product.sku}). Available for new reservations: ${availableForNewReservations}, Additional quantity requested: ${product.additionalQtyRequested}. Please reduce quantity or check inventory levels.`,
            );
          }
        }

        let removedItems = lineItems.filter(
          (item) =>
            !cart.some(
              (cartItem) =>
                cartItem.id === item.id &&
                Boolean(cartItem.promo) === Boolean(item.promo),
            ),
        );

        let addedItems = cart.filter(
          (item) =>
            !lineItems.find(
              (lineItem) =>
                lineItem.id === item.id &&
                Boolean(lineItem.promo) === Boolean(item.promo),
            ),
        );

        let updates = [];
        // Create a set of added items using proper identification (productID + promo + amount)
        let addedItemsSet = new Set(
          addedItems.map((item) => `${item.id}_${item.promo}`),
        );

        let updatedLineItems = cart.filter(
          (item) =>
            (item.cases.new !== item.cases.existing ||
              item.singles.new !== item.singles.existing ||
              item.amount.new !== item.amount.existing) &&
            !addedItemsSet.has(`${item.id}_${item.promo}`),
        );

        let notUpdatedLineItems = cart.filter(
          (item) =>
            item.cases.new === item.cases.existing &&
            item.singles.new === item.singles.existing &&
            item.amount.new === item.amount.existing &&
            !addedItemsSet.has(`${item.id}_${item.promo}`),
        );

        let transitionToAccepted =
          updatedLineItems.length > 0 ||
          removedItems.length > 0 ||
          addedItems.length > 0;

        addBreadcrumb("updateLineItems: diff calculated", {
          workflow: "sales-order",
          data: {
            orderId: this.orderID,
            addedCount: addedItems.length,
            removedCount: removedItems.length,
            updatedCount: updatedLineItems.length,
            transitionToAccepted,
          },
        });

        if (
          transitionToAccepted &&
          currentOrder.status !== "submitted" &&
          currentOrder.status !== "accepted" &&
          currentOrder.status !== "draft"
        ) {
          if (currentOrder.flags.external) {
            switch (this.externalOrderType) {
              case "leaflink":
                if (
                  currentOrder.status !== "deliveryConfirmation" &&
                  this.externalUpdates.leaflink.includes("statusUpdated")
                ) {
                  addBreadcrumb("leaflink: transitionOrder accept", {
                    workflow: "sales-order",
                    data: { orderId: this.orderID, currentStatus: currentOrder.status },
                  });
                  try {
                    await this.leaflinkClient.transitionOrder("accept");
                  } catch (error) {
                    logWorkflowWarning(`LeafLink: transitionOrder("accept") failed — ${error.message}`, {
                      workflow: "sales-order",
                      action: "leaflink-transition-accept",
                      context: { orderId: this.orderID, currentStatus: currentOrder.status, balance: this.order?.balance },
                    });
                    throw error;
                  }
                }
                break;
              default:
                break;
            }
          }
          const orderDoc = doc(firestore, "salesOrders", currentOrder.id);

          const oldValue = store.getters.getOrderStatus.find(
              (status) => status.value === currentOrder.status,
            ),
            newValue = store.getters.getOrderStatus.find(
              (status) => status.value === "accepted",
            );

          // Update order within transaction
          transaction.set(
            orderDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              status: newValue.value,
            },
            { merge: true },
          );

          // Create activity for status transition
          await createActivity({
            type: "status",
            action: "Order status updated",
            item: `Status changed from ${oldValue.text} to ${newValue.text}`,
            relatedTo: "salesOrders",
            relatedID: [orderDoc.id],
            changeData: {
              field: "status",
              oldValue: {
                text: oldValue.text,
                value: oldValue.value,
                color: oldValue.color,
              },
              newValue: {
                text: newValue.text,
                value: newValue.value,
                color: newValue.color,
              },
              reason: "Status transition during order processing",
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });
        }

        // PRE-READ ALL INVENTORY DATA NEEDED FOR UPDATES
        // This ensures all reads happen before any writes
        const inventoryUpdates = [];
        const inventoryFulfilledUpdates = [];

        for (const item of updatedLineItems.concat(notUpdatedLineItems)) {
          if (
            item.cases.new !== item.cases.existing ||
            item.singles.new !== item.singles.existing
          ) {
            item.needsQTYPatch = true;
            //  QTY was modified, need to modify location inventory
            // Handle both case-based and non-case products
            const isCaseProduct = item.caseQTY > 0 || item.unitsPerCase > 0;

            let originalQTY, newQTY, difference;

            if (isCaseProduct) {
              // Case-based product: calculate using cases and singles
              let caseQTY = item.caseQTY ?? item.unitsPerCase;
              originalQTY =
                item.cases.existing * caseQTY + item.singles.existing;
              newQTY = item.cases.new * caseQTY + item.singles.new;
            } else {
              // Non-case product: use singles directly, ignore cases
              originalQTY = item.singles.existing || 0;
              newQTY = item.singles.new || 0;
            }

            difference = newQTY - originalQTY;

            // Store inventory update data for later processing
            inventoryUpdates.push({
              item,
              difference,
              caseQTY: isCaseProduct ? item.caseQTY ?? item.unitsPerCase : 0,
              isCaseProduct,
            });
          }

          if (currentOrder.status === "fulfilled" && transitionToAccepted) {
            // Handle both case-based and non-case products
            const isCaseProduct = item.caseQTY > 0 || item.unitsPerCase > 0;

            // qtyToAdd = EXISTING qty: Firestore must restore the full original amount
            let qtyToAdd;
            if (isCaseProduct) {
              qtyToAdd =
                item.cases.existing * item.unitsPerCase + item.singles.existing;
            } else {
              qtyToAdd = item.singles.existing || 0;
            }

            // activityQty = NEW qty when item had a qty change, so the activity delta
            // matches the line qty change. When no change, use existing qty.
            const hadQtyChange =
              item.cases.new !== item.cases.existing ||
              item.singles.new !== item.singles.existing;
            let activityQty = qtyToAdd;
            if (hadQtyChange) {
              if (isCaseProduct) {
                activityQty =
                  (item.cases.new ?? item.cases.existing) *
                    (item.unitsPerCase ?? item.caseQTY ?? 0) +
                  (item.singles.new ?? item.singles.existing ?? 0);
              } else {
                activityQty = item.singles.new ?? item.singles.existing ?? 0;
              }
            }

            // Store fulfilled inventory update data for later processing
            inventoryFulfilledUpdates.push({
              item,
              qtyToAdd,
              activityQty,
            });
          }
        }

        // Helper function to get listPrice for a product
        const getListPriceForProduct = (product) => {
          let result = null;

          // First check for price override
          const priceOverride = this.getPriceOverrideForProduct(product);
          if (priceOverride?.listPrice) {
            result = priceOverride.listPrice;
          } else {
            // Otherwise check category
            const category = this.categories.find(
              (cat) => cat.id === product.categoryID,
            );
            if (category?.listPrice) {
              result = category.listPrice;
            }
          }

          return result;
        };

        // PRE-READ ALL INVENTORY DATA FOR ADDED ITEMS
        const addedItemInventoryData = [];
        for (let cartItem of addedItems) {
          // Get listPrice for this product
          const listPrice = getListPriceForProduct({
            categoryID: cartItem.categoryID,
            productID: cartItem.productID,
          });

          const caseRestriction = cartItem.caseRestriction ?? null;
          const { cases: clampedCases, singles: clampedSingles } =
            clampToCaseRestriction(
              Number(cartItem.cases.new || 0),
              Number(cartItem.singles.new || 0),
              cartItem.caseQTY || cartItem.unitsPerCase || 0,
              caseRestriction,
              {
                isSample: !!(
                  currentOrder.flags?.sample || cartItem.sample
                ),
              },
            );
          cartItem = {
            account: {
              id: currentOrder.account.id,
              name: currentOrder.account.name,
            },
            brand: {
              id: currentOrder.brand.id,
              name: currentOrder.brand.name,
            },
            state: currentOrder.location.shipping.state,
            amount: cartItem.amount.new,
            notes: cartItem.notes || "",
            flags: {
              case: cartItem.caseQTY > 0,
              fulfilled: false,
              delivered: false,
              onSale:
                cartItem.amount.new <
                (cartItem.originalPrice || cartItem.amount?.existing),
              discountApplied: false,
            },
            externalIDs: {
              ...cartItem.externalIDs,
              leaflink: cartItem.externalIDs?.leaflink ?? null,
            },
            name: cartItem.name,
            price: {
              normal: cartItem.originalPrice ?? cartItem.amount?.existing,
              sale: cartItem.amount.new,
            },
            listPrice: listPrice,
            productID: cartItem.productID,
            categoryID: cartItem.categoryID,
            categoryName: cartItem.category ?? cartItem.categoryName,
            qty: (() => {
              // Handle both case-based and non-case products
              const isCaseProduct =
                cartItem.caseQTY > 0 || cartItem.unitsPerCase > 0;

              if (isCaseProduct) {
                // Case-based product: calculate using cases and singles
                return (
                  clampedCases * (cartItem.caseQTY || cartItem.unitsPerCase || 0) +
                  clampedSingles
                );
              } else {
                // Non-case product: use singles directly, ignore cases
                return clampedSingles;
              }
            })(),
            unitsPerCase: cartItem.caseQTY || 0,
            caseRestriction,
            cases: clampedCases,
            singles: clampedSingles,
            relatedID: currentOrder.id || this.orderID,
            sample: cartItem.sample ?? false,
            promo: cartItem.promo || false,
            sku: cartItem.sku.toLowerCase(),
            tax: 0,
            discount: null,
          };

          // Store added item data for later processing
          addedItemInventoryData.push({
            cartItem,
          });
        }

        // PRE-READ ALL INVENTORY DATA FOR REMOVED ITEMS
        const removedItemInventoryData = [];
        for (const item of removedItems) {
          // Handle both case-based and non-case products
          const isCaseProduct = item.caseQTY > 0 || item.unitsPerCase > 0;

          let qty;
          if (isCaseProduct) {
            // Case-based product: calculate using cases and singles
            qty =
              item.cases * (item.unitsPerCase || item.caseQTY || 0) +
              item.singles;
          } else {
            // Non-case product: use singles directly, ignore cases
            qty = item.singles || 0;
          }

          // Store removed item data for later processing
          removedItemInventoryData.push({
            item,
            qty,
          });
        }

        // NOW ALL READS ARE COMPLETE - START PROCESSING WRITES

        // Collectors for aggregating inventory activities by product so that
        // the same product at different prices (e.g. regular + promo) produces
        // one activity with correct before/after snapshots.
        const reservationActivityCollector = {};
        const restorationActivityCollector = {};

        let existingItems = [];
        for (const item of updatedLineItems.concat(notUpdatedLineItems)) {
          let itemUpdates = [];

          if (item.cases.new !== item.cases.existing) {
            //  Cases updated
            itemUpdates.push(
              `Cases - <span class="text-uppercase">old</span>: <span>${item.cases.existing}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${item.cases.new}</span>`,
            );
          }
          if (item.singles.new !== item.singles.existing) {
            //  Single Units updated
            itemUpdates.push(
              `Single Units - <span class="text-uppercase">old</span>: <span>${item.singles.existing}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${item.singles.new}</span>`,
            );
          }
          if (item.amount.new !== item.amount.existing) {
            //  Price updated
            const currencyFormatter = new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            });
            itemUpdates.push(
              `Price - <span class="text-uppercase">old</span>: <span>${currencyFormatter.format(
                item.amount.existing,
              )}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${currencyFormatter.format(
                item.amount.new,
              )}</span>`,
            );
          }
          if (itemUpdates.length > 0) {
            //  Line item was modified

            itemUpdates = itemUpdates.join(" / ");
            let promoUnit = item.promo
              ? `<span class='font-weight-bold text-no-wrap'>
              <span class='font-weight-bold text-caption pa-1 rounded' style='background-color:
                #0277BD; color: white}
              '>Promo</span>
            </span>`
              : "";
            let itemText = `<span class="text-capitalize font-weight-bold">${item.name}</span> <span class="text-uppercase">(${item.sku})${promoUnit}</span>: ${itemUpdates}`;
            updates.push(itemText);
            item.needsPatch = true;
          }

          if (
            item.cases.new !== item.cases.existing ||
            item.singles.new !== item.singles.existing
          ) {
            item.needsQTYPatch = true;
            //  QTY was modified, need to modify location inventory
            // Handle both case-based and non-case products
            const isCaseProduct = item.caseQTY > 0 || item.unitsPerCase > 0;

            let originalQTY, newQTY, difference;

            if (isCaseProduct) {
              // Case-based product: calculate using cases and singles
              let caseQTY = item.caseQTY ?? item.unitsPerCase;
              originalQTY =
                item.cases.existing * caseQTY + item.singles.existing;
              newQTY = item.cases.new * caseQTY + item.singles.new;
            } else {
              // Non-case product: use singles directly, ignore cases
              originalQTY = item.singles.existing || 0;
              newQTY = item.singles.new || 0;
            }

            difference = newQTY - originalQTY;

            // Use pre-read inventory data instead of reading again
            const inventoryUpdate = inventoryUpdates.find(
              (u) => u.item.id === item.id,
            );
            if (inventoryUpdate) {
              // Use originalInventoryData for "before" values to ensure we capture the state
              // before any mutations, even if the same product is processed multiple times
              const currentAvailable =
                originalInventoryData[inventoryUpdate.item.categoryID]
                  ?.available?.[inventoryUpdate.item.productID] || 0;
              const currentReserved =
                originalInventoryData[inventoryUpdate.item.categoryID]
                  ?.reserved?.[inventoryUpdate.item.productID] || 0;
              // Capture all "before" values from original state BEFORE any updates to inventoryData
              const currentPending =
                originalInventoryData[item.categoryID]?.pending?.[
                  item.productID
                ] || 0;
              const currentTransfer =
                originalInventoryData[item.categoryID]?.transfer?.[
                  item.productID
                ] || 0;
              const currentInTesting =
                originalInventoryData[item.categoryID]?.inTesting?.[
                  item.productID
                ] || 0;

              // Calculate available inventory (available - reserved)
              const availableForReservation =
                currentAvailable - currentReserved;

              // Check if we can reserve the additional quantity
              if (
                inventoryUpdate.difference > 0 &&
                availableForReservation < inventoryUpdate.difference
              ) {
                throw new Error(
                  `Insufficient inventory for update: ${item.name} (${item.sku}). Available: ${availableForReservation}, Additional Requested: ${inventoryUpdate.difference}. Please reduce quantity or check inventory levels.`,
                );
              }

              // Update the inventory data to reflect the new reservation
              if (inventoryData[item.categoryID]) {
                if (!inventoryData[item.categoryID].reserved) {
                  inventoryData[item.categoryID].reserved = {};
                }
                if (!inventoryData[item.categoryID].reserved[item.productID]) {
                  inventoryData[item.categoryID].reserved[item.productID] = 0;
                }
                inventoryData[item.categoryID].reserved[item.productID] +=
                  inventoryUpdate.difference;
              }

              transaction.set(
                locationRef,
                {
                  manufacturing: {
                    [item.categoryID]: {
                      reserved: {
                        [item.productID]: increment(inventoryUpdate.difference),
                      },
                    },
                  },
                },
                { merge: true },
              );

              // Collect reservation/release activity data for aggregation by product
              const productKey = `${item.categoryID}_${item.productID}`;
              if (!reservationActivityCollector[productKey]) {
                reservationActivityCollector[productKey] = {
                  skuID: `${
                    currentOrder.fulfillmentLocation?.id ||
                    currentOrder.location.shipping.id
                  }_${currentOrder.brand.id}_${item.categoryID}_${
                    item.productID
                  }`,
                  itemData: item,
                  netDifference: 0,
                  before: {
                    available: currentAvailable,
                    reserved: currentReserved,
                    pending: currentPending,
                    transfer: currentTransfer,
                    inTesting: currentInTesting,
                  },
                };
              }
              reservationActivityCollector[productKey].netDifference +=
                inventoryUpdate.difference;
            }
          }

          if (currentOrder.status === "fulfilled" && transitionToAccepted) {
            // Use pre-read inventory data instead of reading again
            const fulfilledUpdate = inventoryFulfilledUpdates.find(
              (u) => u.item.id === item.id,
            );
            if (fulfilledUpdate) {
              // Use originalInventoryData for validation (state before any mutations)
              const currentAvailable =
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.available?.[fulfilledUpdate.item.productID] || 0;
              const currentReserved =
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.reserved?.[fulfilledUpdate.item.productID] || 0;

              // Validate that we're not exceeding available inventory
              if (currentAvailable + fulfilledUpdate.qtyToAdd < 0) {
                throw new Error(
                  `Invalid inventory operation: ${item.name} (${item.sku}). Would result in negative available inventory.`,
                );
              }

              // For activity display only: restoration's "before" must be state after reservation
              // updates so the activity log chain is correct (e.g. Line qty increased then Order restored).
              const snapshotAvailable =
                inventoryData[fulfilledUpdate.item.categoryID]?.available?.[
                  fulfilledUpdate.item.productID
                ] ??
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.available?.[fulfilledUpdate.item.productID] ??
                0;
              const snapshotReserved =
                inventoryData[fulfilledUpdate.item.categoryID]?.reserved?.[
                  fulfilledUpdate.item.productID
                ] ??
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.reserved?.[fulfilledUpdate.item.productID] ??
                0;

              // Update the inventory data to reflect the changes
              if (inventoryData[fulfilledUpdate.item.categoryID]) {
                if (!inventoryData[fulfilledUpdate.item.categoryID].available) {
                  inventoryData[fulfilledUpdate.item.categoryID].available = {};
                }
                if (!inventoryData[fulfilledUpdate.item.categoryID].reserved) {
                  inventoryData[fulfilledUpdate.item.categoryID].reserved = {};
                }
                if (
                  !inventoryData[fulfilledUpdate.item.categoryID].available[
                    fulfilledUpdate.item.productID
                  ]
                ) {
                  inventoryData[fulfilledUpdate.item.categoryID].available[
                    fulfilledUpdate.item.productID
                  ] = 0;
                }
                if (
                  !inventoryData[fulfilledUpdate.item.categoryID].reserved[
                    fulfilledUpdate.item.productID
                  ]
                ) {
                  inventoryData[fulfilledUpdate.item.categoryID].reserved[
                    fulfilledUpdate.item.productID
                  ] = 0;
                }
                inventoryData[fulfilledUpdate.item.categoryID].available[
                  fulfilledUpdate.item.productID
                ] += fulfilledUpdate.qtyToAdd;
                inventoryData[fulfilledUpdate.item.categoryID].reserved[
                  fulfilledUpdate.item.productID
                ] += fulfilledUpdate.qtyToAdd;
              }

              transaction.set(
                locationRef,
                {
                  manufacturing: {
                    [fulfilledUpdate.item.categoryID]: {
                      available: {
                        [fulfilledUpdate.item.productID]: increment(
                          fulfilledUpdate.qtyToAdd,
                        ),
                      },
                      reserved: {
                        [fulfilledUpdate.item.productID]: increment(
                          fulfilledUpdate.qtyToAdd,
                        ),
                      },
                    },
                  },
                },
                { merge: true },
              );

              // Collect restoration activity data for aggregation by product
              const restorationKey = `${fulfilledUpdate.item.categoryID}_${fulfilledUpdate.item.productID}`;
              const currentPending =
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.pending?.[fulfilledUpdate.item.productID] || 0;
              const currentTransfer =
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.transfer?.[fulfilledUpdate.item.productID] || 0;
              const currentInTesting =
                originalInventoryData[fulfilledUpdate.item.categoryID]
                  ?.inTesting?.[fulfilledUpdate.item.productID] || 0;

              if (!restorationActivityCollector[restorationKey]) {
                restorationActivityCollector[restorationKey] = {
                  skuID: `${
                    currentOrder.fulfillmentLocation?.id ||
                    currentOrder.location.shipping.id
                  }_${currentOrder.brand.id}_${
                    fulfilledUpdate.item.categoryID
                  }_${fulfilledUpdate.item.productID}`,
                  itemData: fulfilledUpdate.item,
                  totalQtyToAdd: 0,
                  totalActivityQty: 0,
                  before: {
                    available: snapshotAvailable,
                    reserved: snapshotReserved,
                    pending: currentPending,
                    transfer: currentTransfer,
                    inTesting: currentInTesting,
                  },
                };
              }
              restorationActivityCollector[restorationKey].totalQtyToAdd +=
                fulfilledUpdate.qtyToAdd;
              restorationActivityCollector[restorationKey].totalActivityQty +=
                fulfilledUpdate.activityQty;
            }
          }
          let discountValue = 0;

          const { cases: clampedExistingCases, singles: clampedExistingSingles } =
            clampToCaseRestriction(
              Number(item.cases.new || 0),
              Number(item.singles.new || 0),
              item.unitsPerCase || item.caseQTY || 0,
              item.caseRestriction ?? null,
              {
                isSample: !!(currentOrder.flags?.sample || item.sample),
              },
            );

          const processedItem = {
            account: item.account,
            brand: item.brand,
            created:
              item.created ||
              moment(currentOrder.created?.seconds * 1000).toDate(),
            externalIDs: item.externalIDs,
            flags: {
              ...item.flags,
              onSale: item.amount.new < item.price.normal,
              discountApplied: discountValue > 0,
            },
            id: item.id,
            name: item.name,
            price: {
              ...item.price,
              sale: item.amount.new,
            },
            listPrice: item.listPrice ?? null,
            productID: item.productID,
            categoryID: item.categoryID || "",
            categoryName: item.categoryName || "",
            relatedID: item.relatedID,
            sku: item.sku,
            state: item.state || currentOrder.location.shipping.state,
            tax: item.tax,
            amount: item.amount.new,
            qty:
              clampedExistingCases * (item.unitsPerCase || item.caseQTY || 0) +
              clampedExistingSingles,
            caseRestriction: item.caseRestriction ?? null,
            cases: clampedExistingCases,
            singles: clampedExistingSingles,
            notes: item.notes ?? "",
            promo: item.promo || false,
            needsPatch: item.needsPatch || false,
            needsQTYPatch: item.needsQTYPatch || false,
            discount: null,
            // Frozen cost, preferred from the stored line item: the cart is only
            // a working copy, so anything that rebuilds it without carrying the
            // field through must not be able to erase recorded history.
            costOfGoods:
              lineItems?.find((lineItem) => lineItem.id === item.id)
                ?.costOfGoods ??
              item.costOfGoods ??
              null,
          };

          existingItems.push(processedItem);
        }

        // Emit aggregated reservation/release activities (one per product)
        for (const productKey of Object.keys(reservationActivityCollector)) {
          const agg = reservationActivityCollector[productKey];
          if (agg.netDifference === 0) continue;
          const isReservation = agg.netDifference > 0;
          const absDifference = Math.abs(agg.netDifference);
          await createInventoryActivity({
            action: isReservation ? "Inventory reserved" : "Inventory released",
            item: `${absDifference} units of ${agg.itemData.name} (${
              agg.itemData.sku
            }) ${isReservation ? "reserved" : "released"} for Order #${
              currentOrder.orderNumber.internal
            }`,
            skuID: agg.skuID,
            itemData: agg.itemData,
            movement: {
              type: isReservation ? "reservation" : "release",
              from: isReservation ? "available" : "reserved",
              to: isReservation ? "reserved" : "available",
              quantity: absDifference,
              reason: isReservation
                ? "salesOrderQtyIncreased"
                : "salesOrderQtyDecreased",
              reasonID: this.orderID,
            },
            snapshot: { before: agg.before },
            location: {
              id:
                currentOrder.fulfillmentLocation?.id ||
                currentOrder.location.shipping.id,
              name:
                currentOrder.fulfillmentLocation?.name ||
                currentOrder.location.shipping.name,
            },
            order: currentOrder,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            extraSalesOrderData: {
              orderID: this.orderID,
            },
          });
        }

        // Emit aggregated restoration activities (one per product)
        for (const restorationKey of Object.keys(
          restorationActivityCollector,
        )) {
          const agg = restorationActivityCollector[restorationKey];
          if (agg.totalQtyToAdd === 0) continue;
          const afterAvailable =
            (agg.before.available ?? 0) + agg.totalQtyToAdd;
          const afterReserved = (agg.before.reserved ?? 0) + agg.totalQtyToAdd;
          await createInventoryActivity({
            action: "Inventory restored",
            item: `${agg.totalQtyToAdd} units of ${agg.itemData.name} (${agg.itemData.sku}) restored from fulfilled Order #${currentOrder.orderNumber.internal}`,
            skuID: agg.skuID,
            itemData: agg.itemData,
            movement: {
              type: "restoration",
              from: "fulfilled",
              to: "reserved",
              quantity: agg.totalQtyToAdd,
              reason: "salesOrderRestored",
              reasonID: this.orderID,
            },
            snapshot: {
              before: agg.before,
              after: {
                ...agg.before,
                available: afterAvailable,
                reserved: afterReserved,
              },
            },
            location: {
              id:
                currentOrder.fulfillmentLocation?.id ||
                currentOrder.location.shipping.id,
              name:
                currentOrder.fulfillmentLocation?.name ||
                currentOrder.location.shipping.name,
            },
            order: currentOrder,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            extraSalesOrderData: {
              orderID: this.orderID,
            },
          });
        }

        let addedUpdates = [];

        const addedItemActivityCollector = {};

        if (addedItems.length > 0) {
          // Process added items using pre-read data
          for (const addedItemData of addedItemInventoryData) {
            const { cartItem } = addedItemData;

            let promoUnit = cartItem.promo
              ? `<span class='font-weight-bold text-no-wrap'>
                <span class='font-weight-bold text-caption pa-1 rounded' style='background-color:
                  #0277BD; color: #FFFFFF
                '>Promo</span>
              </span>`
              : "";
            let itemText = `<span class="text-capitalize font-weight-bold">${cartItem.name}</span> <span class="text-uppercase">(${cartItem.sku})${promoUnit}</span>`;
            addedUpdates.push(itemText);

            // Use originalInventoryData for validation (state before any mutations in this transaction)
            const currentAvailable =
              originalInventoryData[cartItem.categoryID]?.available?.[
                cartItem.productID
              ] || 0;
            const currentReserved =
              originalInventoryData[cartItem.categoryID]?.reserved?.[
                cartItem.productID
              ] || 0;

            // Calculate available inventory (available - reserved)
            const availableForReservation = currentAvailable - currentReserved;

            // Check if we can reserve the requested quantity
            // if (availableForReservation < cartItem.qty) {
            //   throw new Error(
            //     `Insufficient inventory: ${cartItem.name} (${cartItem.sku}). Available: ${availableForReservation}, Requested: ${cartItem.qty}. Please reduce quantity or check inventory levels.`
            //   );
            // }

            // For activity display only: added-item "before" must be state after restoration/reservation
            // updates so the activity log chain is correct (e.g. Order restored then Order submitted).
            const snapshotAvailable =
              inventoryData[cartItem.categoryID]?.available?.[
                cartItem.productID
              ] ??
              originalInventoryData[cartItem.categoryID]?.available?.[
                cartItem.productID
              ] ??
              0;
            const snapshotReserved =
              inventoryData[cartItem.categoryID]?.reserved?.[
                cartItem.productID
              ] ??
              originalInventoryData[cartItem.categoryID]?.reserved?.[
                cartItem.productID
              ] ??
              0;

            // Update the inventory data to reflect the new reservation
            if (inventoryData[cartItem.categoryID]) {
              if (!inventoryData[cartItem.categoryID].reserved) {
                inventoryData[cartItem.categoryID].reserved = {};
              }
              if (
                !inventoryData[cartItem.categoryID].reserved[cartItem.productID]
              ) {
                inventoryData[cartItem.categoryID].reserved[
                  cartItem.productID
                ] = 0;
              }
              inventoryData[cartItem.categoryID].reserved[cartItem.productID] +=
                cartItem.qty;
            }

            // Only then reserve the inventory
            transaction.set(
              locationRef,
              {
                manufacturing: {
                  [cartItem.categoryID]: {
                    reserved: {
                      [cartItem.productID]: increment(cartItem.qty),
                    },
                  },
                },
              },
              { merge: true },
            );

            // Collect added-item reservation activity for aggregation by product.
            // snapshot.before must reflect post-restoration state (snapshotAvailable/snapshotReserved
            // from inventoryData) so the activity log chain is correct (submit then restore in time).
            const addedProductKey = `${cartItem.categoryID}_${cartItem.productID}`;
            if (!addedItemActivityCollector[addedProductKey]) {
              const currentPending =
                originalInventoryData[cartItem.categoryID]?.pending?.[
                  cartItem.productID
                ] || 0;
              const currentTransfer =
                originalInventoryData[cartItem.categoryID]?.transfer?.[
                  cartItem.productID
                ] || 0;
              const currentInTesting =
                originalInventoryData[cartItem.categoryID]?.inTesting?.[
                  cartItem.productID
                ] || 0;
              addedItemActivityCollector[addedProductKey] = {
                skuID: `${
                  currentOrder.fulfillmentLocation?.id ||
                  currentOrder.location.shipping.id
                }_${currentOrder.brand.id}_${cartItem.categoryID}_${
                  cartItem.productID
                }`,
                itemData: cartItem,
                totalQty: 0,
                before: {
                  available: snapshotAvailable,
                  reserved: snapshotReserved,
                  pending: currentPending,
                  transfer: currentTransfer,
                  inTesting: currentInTesting,
                },
              };
            }
            addedItemActivityCollector[addedProductKey].totalQty +=
              cartItem.qty;

            // Calculate tax
            cartItem.tax = Number(
              parseFloat(
                cartItem.amount -
                  cartItem.amount /
                    (1 + (currentOrder.taxPercentage ?? 0) / 100),
              ).toFixed(2),
            );

            // Ensure relatedID is never undefined
            if (!cartItem.relatedID) {
              console.error("Line item missing relatedID:", cartItem);
              throw new Error("Line item missing relatedID");
            }

            const cartItemDocRef = doc(
              collection(firestore, "salesOrders_lineItems"),
            );

            // Resolve against the product ID before `id` is reassigned to the doc ID.
            // Set on cartItem itself so it reaches both the line item doc and the
            // order's embedded lineItems array (which feeds the order-level total).
            cartItem.costOfGoods = buildLineCostOfGoods(
              costOfGoodsByProduct.get(cartItem.productID ?? cartItem.id),
            );

            // Use the processed cartItem instead of the raw one
            cartItem.id = cartItemDocRef.id;
            // cartItem.id = cartItemDocRef.id;

            transaction.set(
              cartItemDocRef,
              {
                ...cartItem,
                id: cartItemDocRef.id,
                created: serverTimestamp(),
                updated: serverTimestamp(),
              },
              { merge: true },
            );
          }

          if (addedUpdates.length > 0) {
            // Create clean description for activity
            const cleanDescription = addedItems
              .map((item) => {
                const totalQty =
                  (item.cases || 0) * (item.unitsPerCase || 1) +
                  (item.singles || 0);
                return `${item.name} (${item.sku}) - Qty: ${totalQty}${
                  item.promo ? " - Promo" : ""
                }`;
              })
              .join(", ");
            // Create activity for added line items
            await createActivity({
              type: "inventory",
              action: "Line items added",
              item: `Added ${addedItems.length} item${
                addedItems.length !== 1 ? "s" : ""
              }: ${cleanDescription}`,
              relatedTo: "salesOrders",
              relatedID: [this.order.id],
              changeData: {
                field: "lineItems",
                action: "added",
                items: addedItems.map((item) => ({
                  id: item.id || "",
                  name: item.name || "",
                  sku: item.sku || "",
                  cases: item.cases || 0,
                  singles: item.singles || 0,
                  amount: item.amount || 0,
                  promo: item.promo || false,
                  qty:
                    (item.cases || 0) * (item.unitsPerCase || 1) +
                    (item.singles || 0),
                  totalAmount:
                    ((item.cases || 0) * (item.unitsPerCase || 1) +
                      (item.singles || 0)) *
                    (item.amount || 0),
                })),
                count: addedItems.length,
                totalValue: addedItems.reduce((sum, item) => {
                  const totalQty =
                    (item.cases || 0) * (item.unitsPerCase || 1) +
                    (item.singles || 0);
                  return sum + totalQty * (item.amount || 0);
                }, 0),
              },
              user: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              account: {
                id: this.order.account.id,
                name: this.order.account.name,
              },
            });

            addedUpdates = addedUpdates.join(" / ");
          }
        }

        // Emit aggregated added-item activities (one per product)
        for (const addedProductKey of Object.keys(addedItemActivityCollector)) {
          const agg = addedItemActivityCollector[addedProductKey];
          if (agg.totalQty === 0) continue;
          const afterReserved = (agg.before.reserved ?? 0) + agg.totalQty;
          await createInventoryActivity({
            action: "Inventory reserved",
            item: `${agg.totalQty} units of ${agg.itemData.name} (${agg.itemData.sku}) reserved for Order #${currentOrder.orderNumber.internal}`,
            skuID: agg.skuID,
            itemData: agg.itemData,
            movement: {
              type: "reservation",
              from: "available",
              to: "reserved",
              quantity: agg.totalQty,
              reason: "salesOrderSubmitted",
              reasonID: this.orderID,
            },
            snapshot: {
              before: agg.before,
              after: {
                ...agg.before,
                reserved: afterReserved,
              },
            },
            location: {
              id:
                currentOrder.fulfillmentLocation?.id ||
                currentOrder.location.shipping.id,
              name:
                currentOrder.fulfillmentLocation?.name ||
                currentOrder.location.shipping.name,
            },
            order: currentOrder,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            extraSalesOrderData: {
              orderID: this.orderID,
            },
          });
        }

        if (updates.length > 0) {
          // Create clean description for activity
          const updatedCount = updatedLineItems.filter(
            (item) => item.needsPatch,
          ).length;
          const cleanDescription = updatedLineItems
            .filter((item) => item.needsPatch)
            .map((item) => {
              const changes = [];

              // Check for quantity changes
              if (
                item.cases?.new !== item.cases?.existing ||
                item.singles?.new !== item.singles?.existing
              ) {
                const caseQTY = item.caseQTY ?? item.unitsPerCase;
                const oldQty =
                  (item.cases?.existing || 0) * caseQTY +
                  (item.singles?.existing || 0);
                const newQty =
                  (item.cases?.new || 0) * caseQTY + (item.singles?.new || 0);
                changes.push(`Qty: ${oldQty} → ${newQty}`);
              }

              // Check for price changes
              if (item.amount?.new !== item.amount?.existing) {
                const currencyFormatter = new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                });
                changes.push(
                  `Price: ${currencyFormatter.format(
                    item.amount?.existing || 0,
                  )} → ${currencyFormatter.format(item.amount?.new || 0)}`,
                );
              }

              return `${item.name} (${item.sku}) - ${changes.join(", ")}`;
            })
            .join(", ");

          // Create activity for updated line items
          await createActivity({
            type: "inventory",
            action: "Line items updated",
            item: `Updated ${updatedCount} item${
              updatedCount !== 1 ? "s" : ""
            }: ${cleanDescription}`,
            relatedTo: "salesOrders",
            relatedID: [this.order.id],
            changeData: {
              field: "lineItems",
              action: "updated",
              items: updatedLineItems
                .filter((item) => item.needsPatch)
                .map((item) => ({
                  id: item.id || "",
                  name: item.name || "",
                  sku: item.sku || "",
                  changes: {
                    cases:
                      item.cases?.new !== undefined &&
                      item.cases?.existing !== undefined &&
                      item.cases.new !== item.cases.existing
                        ? {
                            old: item.cases.existing,
                            new: item.cases.new,
                          }
                        : null,
                    singles:
                      item.singles?.new !== undefined &&
                      item.singles?.existing !== undefined &&
                      item.singles.new !== item.singles.existing
                        ? {
                            old: item.singles.existing,
                            new: item.singles.new,
                          }
                        : null,
                    amount:
                      item.amount?.new !== undefined &&
                      item.amount?.existing !== undefined &&
                      item.amount.new !== item.amount.existing
                        ? {
                            old: item.amount.existing,
                            new: item.amount.new,
                          }
                        : null,
                    totalQty: (() => {
                      const caseQTY = item.caseQTY ?? item.unitsPerCase;
                      const oldQty =
                        (item.cases?.existing || 0) * caseQTY +
                        (item.singles?.existing || 0);
                      const newQty =
                        (item.cases?.new || 0) * caseQTY +
                        (item.singles?.new || 0);
                      if (oldQty !== newQty) {
                        return { old: oldQty, new: newQty };
                      }
                      return null;
                    })(),
                  },
                })),
              count: updatedLineItems.filter((item) => item.needsPatch).length,
              totalValue: updatedLineItems
                .filter((item) => item.needsPatch)
                .reduce((sum, item) => {
                  const caseQTY = item.caseQTY ?? item.unitsPerCase;
                  const newQty =
                    (item.cases?.new || 0) * caseQTY + (item.singles?.new || 0);
                  return sum + newQty * (item.amount?.new || 0);
                }, 0),
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            account: {
              id: this.order.account.id,
              name: this.order.account.name,
            },
          });

          updates = updates.join(" | ");
        }

        let removedUpdates = [];

        const removedItemActivityCollector = {};

        if (removedItems.length > 0) {
          // delete lineItem documents from salesOrder_lineItems collection
          for (const removedItemData of removedItemInventoryData) {
            const { item, qty } = removedItemData;

            let promoUnit = item.promo
              ? `<span class='font-weight-bold text-no-wrap'>
            <span class='font-weight-bold text-caption pa-1 rounded' style='background-color:
              #0277BD; color: #FFFFFF
            '>Promo</span>
          </span>`
              : "";
            let itemText = `<span class="text-capitalize font-weight-bold">${item.name}</span> <span class="text-uppercase">(${item.sku})${promoUnit}</span>`;
            removedUpdates.push(itemText);

            // Check if order is fulfilled to determine which inventory to update
            if (currentOrder.status === "fulfilled") {
              // For fulfilled orders, update available inventory (not reserved)
              const currentAvailable =
                inventoryData[item.categoryID]?.available?.[item.productID] ||
                0;
              const currentReserved =
                inventoryData[item.categoryID]?.reserved?.[item.productID] || 0;

              // Calculate new available amount, ensuring it never goes below 0
              const newAvailable = Math.max(0, currentAvailable + qty);

              transaction.set(
                locationRef,
                {
                  manufacturing: {
                    [item.categoryID]: {
                      available: {
                        [item.productID]: newAvailable,
                      },
                    },
                  },
                },
                { merge: true },
              );

              // Collect removed-item release activity for aggregation by product
              const removedKey = `${item.categoryID}_${item.productID}`;
              if (!removedItemActivityCollector[removedKey]) {
                const currentPending =
                  inventoryData[item.categoryID]?.pending?.[item.productID] ||
                  0;
                const currentTransfer =
                  inventoryData[item.categoryID]?.transfer?.[item.productID] ||
                  0;
                const currentInTesting =
                  inventoryData[item.categoryID]?.inTesting?.[item.productID] ||
                  0;
                removedItemActivityCollector[removedKey] = {
                  skuID: `${
                    currentOrder.fulfillmentLocation?.id ||
                    currentOrder.location.shipping.id
                  }_${currentOrder.brand.id}_${item.categoryID}_${
                    item.productID
                  }`,
                  itemData: item,
                  totalQty: 0,
                  isFulfilled: true,
                  before: {
                    available: currentAvailable,
                    reserved: currentReserved,
                    pending: currentPending,
                    transfer: currentTransfer,
                    inTesting: currentInTesting,
                  },
                };
              }
              removedItemActivityCollector[removedKey].totalQty += qty;
            } else {
              // For non-fulfilled orders, update reserved inventory
              const currentReserved =
                inventoryData[item.categoryID]?.reserved?.[item.productID] || 0;
              const currentAvailable =
                inventoryData[item.categoryID]?.available?.[item.productID] ||
                0;

              // Calculate new reserved amount, ensuring it never goes below 0
              const newReserved = Math.max(0, currentReserved - qty);

              transaction.set(
                locationRef,
                {
                  manufacturing: {
                    [item.categoryID]: {
                      reserved: {
                        [item.productID]: increment(-qty),
                      },
                    },
                  },
                },
                { merge: true },
              );

              // Collect removed-item release activity for aggregation by product
              const removedKey = `${item.categoryID}_${item.productID}`;
              if (!removedItemActivityCollector[removedKey]) {
                const currentPending =
                  inventoryData[item.categoryID]?.pending?.[item.productID] ||
                  0;
                const currentTransfer =
                  inventoryData[item.categoryID]?.transfer?.[item.productID] ||
                  0;
                const currentInTesting =
                  inventoryData[item.categoryID]?.inTesting?.[item.productID] ||
                  0;
                removedItemActivityCollector[removedKey] = {
                  skuID: `${
                    currentOrder.fulfillmentLocation?.id ||
                    currentOrder.location.shipping.id
                  }_${currentOrder.brand.id}_${item.categoryID}_${
                    item.productID
                  }`,
                  itemData: item,
                  totalQty: 0,
                  isFulfilled: false,
                  before: {
                    available: currentAvailable,
                    reserved: currentReserved,
                    pending: currentPending,
                    transfer: currentTransfer,
                    inTesting: currentInTesting,
                  },
                };
              }
              removedItemActivityCollector[removedKey].totalQty += qty;
            }

            // LeafLink: delete on external platform first so we never remove from Firestore if LeafLink fails (keeps sync)
            if (this.orderIsExternal) {
              switch (this.externalOrderType) {
                case "leaflink": {
                  if (this.externalUpdates.leaflink.includes("orderUpdated")) {
                    let externalItem = null;
                    if (item.externalIDs?.leaflinkLineItemID) {
                      externalItem = {
                        id: item.externalIDs.leaflinkLineItemID,
                      };
                    } else {
                      if (this.leaflinkClient.lineItems?.length === 0) {
                        await this.leaflinkClient.getLineItems();
                      }
                      externalItem = this.leaflinkClient.lineItems.find(
                        (external) =>
                          external.productID === item.externalIDs.leaflink &&
                          item.amount === external.sale_price.amount,
                      );
                    }
                    if (externalItem) {
                      addBreadcrumb("leaflink: deleteLineItem (updateLineItems)", {
                        workflow: "sales-order",
                        data: { orderId: this.orderID, itemId: item.id, externalItemId: externalItem.id },
                      });
                      try {
                        await this.leaflinkClient.deleteLineItem(externalItem);
                      } catch (leaflinkErr) {
                        logWorkflowWarning(`LeafLink: deleteLineItem failed — ${leaflinkErr.message || leaflinkErr}`, {
                          workflow: "sales-order",
                          action: "leaflink-delete-line-item",
                          context: { orderId: this.orderID, itemId: item.id, itemName: item.name, sku: item.sku },
                        });
                        throw new Error(
                          `LeafLink sync failed while removing ${item.name} (${
                            item.sku
                          }). Line item was not removed. ${
                            leaflinkErr.message || leaflinkErr
                          }`,
                        );
                      }
                    }
                  }
                  break;
                }
                default:
                  break;
              }
            }

            await deleteDoc(doc(firestore, "salesOrders_lineItems", item.id));
          }
        }

        // Emit aggregated removed-item activities (one per product)
        for (const removedKey of Object.keys(removedItemActivityCollector)) {
          const agg = removedItemActivityCollector[removedKey];
          if (agg.totalQty === 0) continue;
          const reason = agg.isFulfilled
            ? "salesOrderLineRemovedFulfilled"
            : "salesOrderLineRemovedAccepted";
          await createInventoryActivity({
            action: "Inventory released",
            item: `${agg.totalQty} units of ${agg.itemData.name} (${
              agg.itemData.sku
            }) released from ${agg.isFulfilled ? "fulfilled" : ""} Order #${
              currentOrder.orderNumber.internal
            }`,
            skuID: agg.skuID,
            itemData: agg.itemData,
            movement: {
              type: "release",
              from: agg.isFulfilled ? "fulfilled" : "reserved",
              to: "available",
              quantity: agg.totalQty,
              reason,
              reasonID: this.orderID,
            },
            snapshot: { before: agg.before },
            location: {
              id:
                currentOrder.fulfillmentLocation?.id ||
                currentOrder.location.shipping.id,
              name:
                currentOrder.fulfillmentLocation?.name ||
                currentOrder.location.shipping.name,
            },
            order: currentOrder,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            extraSalesOrderData: {
              orderID: this.orderID,
            },
          });
        }

        if (removedUpdates.length > 0) {
          // Create clean description for activity
          const cleanDescription = removedItems
            .map((item) => {
              const totalQty =
                (item.cases || 0) * (item.unitsPerCase || 1) +
                (item.singles || 0);
              return `${item.name} (${item.sku}) - Qty: ${totalQty}${
                item.promo ? " - Promo" : ""
              }`;
            })
            .join(", ");
          // Create activity for removed line items
          await createActivity({
            type: "inventory",
            action: "Line items removed",
            item: `Removed ${removedItems.length} item${
              removedItems.length !== 1 ? "s" : ""
            }: ${cleanDescription}`,
            relatedTo: "salesOrders",
            relatedID: [this.order.id],
            changeData: {
              field: "lineItems",
              action: "removed",
              items: removedItems.map((item) => ({
                id: item.id || "",
                name: item.name || "",
                sku: item.sku || "",
                cases: item.cases || 0,
                singles: item.singles || 0,
                amount: item.amount || 0,
                promo: item.promo || false,
                qty:
                  (item.cases || 0) * (item.unitsPerCase || 1) +
                  (item.singles || 0),
                totalAmount:
                  ((item.cases || 0) * (item.unitsPerCase || 1) +
                    (item.singles || 0)) *
                  (item.amount || 0),
              })),
              count: removedItems.length,
              totalValue: removedItems.reduce((sum, item) => {
                const totalQty =
                  (item.cases || 0) * (item.unitsPerCase || 1) +
                  (item.singles || 0);
                return sum + totalQty * (item.amount || 0);
              }, 0),
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            account: {
              id: this.order.account.id,
              name: this.order.account.name,
            },
          });

          removedUpdates = removedUpdates.join(" / ");
        }

        // Update individual line items
        if (existingItems.filter((item) => item.needsPatch).length > 0) {
          for (const item of existingItems.filter((item) => item.needsPatch)) {
            const lineItemDoc = doc(
              firestore,
              "salesOrders_lineItems",
              item.id,
            );

            if (this.orderIsExternal) {
              switch (this.externalOrderType) {
                case "leaflink":
                  if (this.externalUpdates.leaflink.includes("orderUpdated")) {
                    let externalItem = null;
                    if (item.externalIDs?.leaflinkLineItemID) {
                      externalItem = {
                        id: item.externalIDs.leaflinkLineItemID,
                      };
                    } else {
                      if (this.leaflinkClient.lineItems?.length === 0) {
                        await this.leaflinkClient.getLineItems();
                      }
                      externalItem = this.leaflinkClient.lineItems.find(
                        (external) =>
                          external.productID === item.externalIDs.leaflink,
                      );
                    }
                    let payloadData = {
                      ordered_unit_price: {
                        amount: item.amount,
                        currency: "USD",
                      },
                      sale_price: {
                        amount: item.amount,
                        currency: "USD",
                      },
                      notes: item.notes,
                    };

                    if (item.needsQTYPatch) {
                      payloadData = {
                        ...payloadData,
                        quantity: item.qty,
                      };
                    }
                    if (externalItem) {
                      addBreadcrumb("leaflink: updateLineItem", {
                        workflow: "sales-order",
                        data: { orderId: this.orderID, itemId: item.id, externalItemId: externalItem.id },
                      });
                      try {
                        await this.leaflinkClient.updateLineItem(externalItem, payloadData);
                        item.externalIDs.leaflinkLineItemID = externalItem.id;
                      } catch (error) {
                        logWorkflowWarning(`LeafLink: updateLineItem failed — ${error.message}`, {
                          workflow: "sales-order",
                          action: "leaflink-update-line-item",
                          context: { orderId: this.orderID, itemId: item.id, balance: this.order?.balance },
                        });
                        throw error;
                      }
                    }
                  }
                  break;
                default:
                  break;
              }
            }

            delete item.needsPatch;
            if (item.needsQTYPatch) {
              delete item.needsQTYPatch;
            }

            item.tax = Number(
              parseFloat(
                item.amount -
                  item.amount / (1 + (currentOrder.taxPercentage ?? 0) / 100),
              ).toFixed(2),
            );

            transaction.set(
              lineItemDoc,
              {
                ...item,
                updated: serverTimestamp(),
                lastUpdated: {
                  name: store.getters.getUser.name,
                  uid: store.getters.getUser.uid,
                },
              },
              { merge: true },
            );
          }
        }

        let allLineItems = existingItems.concat(
          addedItemInventoryData.map((data) => data.cartItem),
        );

        console.log("allLineItems HERE:", allLineItems);

        //  Build allLineItems from complete cart state (not just modified/added items)
        allLineItems.map((cartItem) => {
          // Check if this item was already processed as an existing updated item
          // Use proper identification (productID + promo + amount)
          let existingItem = existingItems.find(
            (item) =>
              (item.id === cartItem.id ||
                item.productID === (cartItem.productID || cartItem.id)) &&
              Boolean(item.promo) === Boolean(cartItem.promo) &&
              item.amount.new === cartItem.amount.new,
          );
          if (existingItem) {
            return existingItem;
          }

          // Check if this item was already processed as a new added item
          // Use proper identification (productID + promo + amount)
          let addedItemData = addedItemInventoryData.find(
            (data) =>
              data.cartItem.productID === (cartItem.productID || cartItem.id) &&
              Boolean(data.cartItem.promo) === Boolean(cartItem.promo) &&
              data.cartItem.amount.new === cartItem.amount.new,
          );
          if (addedItemData) {
            return addedItemData.cartItem;
          }

          return cartItem;
        });

        // Helper function to safely convert to number with fallback
        const safeNumber = (value, fallback = 0) => {
          const num = Number(value);
          return isNaN(num) ? fallback : num;
        };

        let priceTotal = cart.reduce((acc, item) => {
          const amount = safeNumber(item.amount?.new, 0);
          const cases = safeNumber(item.cases?.new, 0);
          const singles = safeNumber(item.singles?.new, 0);
          const caseQTY = safeNumber(item.caseQTY, 0);

          // Handle both case-based and non-case products
          const isCaseProduct = caseQTY > 0;
          const totalUnits = isCaseProduct
            ? cases * caseQTY + singles
            : singles;

          return acc + amount * totalUnits;
        }, 0);

        // Must match SalesOrderCart.vue's cartValue basis (tax excluded)
        let shippingEligibleSubtotal = cart.reduce((acc, item) => {
          const amount = safeNumber(item.amount?.new, 0);
          const tax = safeNumber(item.tax, 0);
          const cases = safeNumber(item.cases?.new, 0);
          const singles = safeNumber(item.singles?.new, 0);
          const caseQTY = safeNumber(item.caseQTY, 0);

          const isCaseProduct = caseQTY > 0;
          const totalUnits = isCaseProduct
            ? cases * caseQTY + singles
            : singles;

          return acc + (amount - tax) * totalUnits;
        }, 0);

        let unitTotal = cart.reduce((acc, item) => {
          const cases = safeNumber(item.cases?.new, 0);
          const singles = safeNumber(item.singles?.new, 0);
          const caseQTY = safeNumber(item.caseQTY, 0);

          // Handle both case-based and non-case products
          const isCaseProduct = caseQTY > 0;
          return acc + (isCaseProduct ? cases * caseQTY + singles : singles);
        }, 0);

        // Include shipping fee in total calculation
        let currentShippingFee = safeNumber(
          currentOrder.balance?.shipping,
          0,
        );
        // Missing flag (pre-existing orders) is treated as manually set to avoid overwriting without a backfill
        const shouldAutoApplyShipping =
          currentOrder.flags?.shippingManuallySet === false &&
          !currentOrder.flags?.sample;

        if (shouldAutoApplyShipping && payableToShippingCharge != null) {
          currentShippingFee = computePayableToShippingFee(
            shippingEligibleSubtotal,
            payableToShippingCharge,
          );
        }

        const currentBalanceTotal = safeNumber(currentOrder.balance?.total, 0);
        const currentBalanceDue = safeNumber(currentOrder.balance?.due, 0);

        const paymentsApplied = sumAppliedPayments(this.payments);
        const {
          total: computedTotal,
          due: computedDue,
          discountTotal,
        } = computeOrderBalance({
          lineSubtotal: priceTotal,
          discountType: currentOrder.discount?.type ?? "%",
          discountValue: currentOrder.discount?.value ?? 0,
          shipping: currentShippingFee,
          paymentsApplied,
        });
        // Preserve existing string .toFixed(2) Firestore shape for this path.
        let newTotal = parseFloat(computedTotal).toFixed(2);
        let newBalance = parseFloat(computedDue).toFixed(2);

        addBreadcrumb("updateLineItems: totals calculated", {
          workflow: "sales-order",
          data: { orderId: this.orderID, newTotal, newBalance, newShipping: currentShippingFee },
        });

        // Throwing here aborts the transaction, so an unabsorbable overpay never reaches
        // a state where inventory / line-item docs are committed but the credit is not.
        const { overpay: projectedOverpay } = computeOverpay({
          newTotal: Number(newTotal),
          payments: this.payments,
          balanceTotal: currentBalanceTotal,
          balanceDue: currentBalanceDue,
        });
        this._assertFiniteBalance("update-line-items-overpay-preflight", {
          total: newTotal,
          due: newBalance,
        });
        this._assertOverpaymentAbsorbable(
          "update-line-items-overpay-preflight",
          projectedOverpay,
        );
        this._assertOverpaymentCreditable(
          "update-line-items-overpay-preflight",
          projectedOverpay,
          payableToResponse.empty,
        );

        // Safety check - ensure no undefined values
        if (unitTotal === undefined || unitTotal === null) {
          console.error("unitTotal is undefined/null:", unitTotal);
          logWorkflowWarning("updateLineItems: unitTotal is undefined/null, defaulting to 0", {
            workflow: "sales-order",
            action: "update-line-items-safety-check",
            context: { orderId: this.orderID },
          });
          unitTotal = 0;
        }

        if (!allLineItems || allLineItems.length === 0) {
          console.error("allLineItems is undefined/empty:", allLineItems);
          logWorkflowWarning("updateLineItems: allLineItems is undefined/empty, defaulting to []", {
            workflow: "sales-order",
            action: "update-line-items-safety-check",
            context: { orderId: this.orderID },
          });
          allLineItems = [];
        }

        // Deep clean allLineItems to remove any undefined values
        allLineItems = allLineItems.map((item) => {
          const cleanedItem = {};
          Object.keys(item).forEach((key) => {
            if (item[key] !== undefined && item[key] !== null) {
              cleanedItem[key] = item[key];
            } else {
              logWorkflowWarning(`updateLineItems: undefined/null value for key '${key}' in line item`, {
                workflow: "sales-order",
                action: "update-line-items-data-integrity",
                context: { orderId: this.orderID, key, itemId: item.id },
              });
              // Provide fallback values for critical fields
              if (key === "relatedID") {
                cleanedItem[key] = currentOrder.id || this.orderID;
              } else if (key === "tax") {
                cleanedItem[key] = 0;
              } else if (key === "externalIDs") {
                cleanedItem[key] = {};
              } else {
                cleanedItem[key] = null;
              }
            }
          });
          return cleanedItem;
        });

        if (newBalance === undefined || newBalance === null) {
          console.error("newBalance is undefined/null:", newBalance);
          logWorkflowWarning("updateLineItems: newBalance is undefined/null, defaulting to 0.00", {
            workflow: "sales-order",
            action: "update-line-items-safety-check",
            context: { orderId: this.orderID, newTotal, unitTotal },
          });
          newBalance = "0.00";
        }

        if (newTotal === undefined || newTotal === null) {
          console.error("newTotal is undefined/null:", newTotal);
          logWorkflowWarning("updateLineItems: newTotal is undefined/null, defaulting to 0.00", {
            workflow: "sales-order",
            action: "update-line-items-safety-check",
            context: { orderId: this.orderID, unitTotal },
          });
          newTotal = "0.00";
        }

        if (currentShippingFee === undefined || currentShippingFee === null) {
          console.error(
            "currentShippingFee is undefined/null:",
            currentShippingFee,
          );
          logWorkflowWarning("updateLineItems: currentShippingFee is undefined/null, defaulting to 0", {
            workflow: "sales-order",
            action: "update-line-items-safety-check",
            context: { orderId: this.orderID },
          });
          currentShippingFee = 0;
        }

        let discount = {
          value: currentOrder.discount?.value ?? 0,
          type: currentOrder.discount?.type ?? "%",
          total: discountTotal,
        };

        // Update order within transaction
        transaction.set(
          salesOrderDoc,
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            units: unitTotal,
            lineItems: allLineItems,
            costOfGoods: buildOrderCostOfGoods(allLineItems, {
              existing: currentOrder.costOfGoods ?? null,
            }),
            balance: {
              due: newBalance,
              total: newTotal,
              shipping: currentShippingFee,
            },
            discount,
          },
          { merge: true },
        );

        // Return data for external processing
        return {
          allLineItems,
          updates,
          addedUpdates,
          removedUpdates,
          addedItems: addedItemInventoryData.map((data) => data.cartItem),
          existingItems,
          newBalance: Number(newBalance),
          newTotal: Number(newTotal),
        };
      });
      transactionCommitted = true;

      addBreadcrumb("updateLineItems: transaction committed", {
        workflow: "sales-order",
        data: { orderId: this.orderID, newBalance: result.newBalance },
      });
      await this._checkBalanceAnomaly("update-line-items", {
        newBalance: result.newBalance,
        newTotal: result.newTotal,
      });

      // Handle external API calls AFTER transaction succeeds
      // This follows Firebase best practices of keeping transactions focused
      if (result.addedItems?.length > 0) {
        await this.processExternalLineItems(
          result.addedItems,
          result.allLineItems,
        );
      }

      // Update existing line items with external IDs if needed
      if (
        this.orderIsExternal &&
        this.externalOrderType === "leaflink" &&
        result.existingItems?.length > 0
      ) {
        if (this.externalUpdates.leaflink.includes("orderUpdated")) {
          await this.processExternalLineItemUpdates(result.existingItems);
        }
      }

      try {
        await this.checkOverpaymentCredits(result.newBalance);
      } catch (absorbError) {
        logWorkflowWarning(
          `updateLineItems: overpayment absorption failed after line-item commit — ${absorbError.message}`,
          {
            workflow: "sales-order",
            action: "update-line-items-overpayment-absorb",
            context: {
              orderId: this.orderID,
              newBalance: result.newBalance,
            },
          },
        );
        throw absorbError;
      }
    } catch (error) {
      if (transactionCommitted) {
        await this._logPostCommitFailure("updateLineItems", error, {
          note: "Line items committed; post-commit side effect failed — order, inventory and line items are consistent, but overpayment credit / external sync may be missing",
        });
      }
      throw error;
    }
  }

  async updateOrderData(fields) {
    await this.getOrder();
    await this.getLineItems();
    await this.getFulfillmentLocations();
    await this.getPayments();
    addBreadcrumb("updateOrderData: start", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        changedFields: Object.keys(fields),
        orderStatus: this.order?.status,
        currentBalance: this.order?.balance,
      },
    });
    const batch = writeBatch(firestore);

    const salesOrderDoc = doc(firestore, "salesOrders", this.order.id);

    let needsExternalUpdate = false;
    let needsPriceUpdate = false;
    let previousPaymentTerm = null;
    let updatePayload = {};
    let updates = [];

    if (fields.hasOwnProperty("paymentTerm")) {
      updatePayload.paymentTerm = fields.paymentTerm;
      previousPaymentTerm = this.order.paymentTerm;

      const oldValue = store.getters.getPaymentTerms.find(
          (option) => option.value === this.order.paymentTerm,
        ),
        newValue = store.getters.getPaymentTerms.find(
          (option) => option.value === fields.paymentTerm,
        );

      updates.push(
        `Payment Term, <span class="text-uppercase">old</span>: ${oldValue.text} => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${newValue.text}</span>`,
      );

      needsPriceUpdate = true;

      // Update external order payment term
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("orderUpdated")) {
              addBreadcrumb("leaflink: updateOrderData payment term", {
                workflow: "sales-order",
                data: { orderId: this.orderID, paymentTerm: fields.paymentTerm },
              });
              try {
                let orderData = await this.leaflinkClient.getOrder();
                let paymentOption = orderData?.available_payment_options.find(
                  (option) =>
                    fields.paymentTerm.replace(/\s/g, "") ===
                    option.payment_term.code,
                );
                if (paymentOption) {
                  let patchData = {
                    selected_payment_option: {
                      payment_method_id: 1,
                      payment_term_id: paymentOption.payment_term.id,
                    },
                  };
                  await this.leaflinkClient.updateOrderData(patchData);
                }
              } catch (error) {
                logWorkflowWarning(`LeafLink: updateOrderData (payment term) failed — ${error.message}`, {
                  workflow: "sales-order",
                  action: "leaflink-update-order-payment-term",
                  context: { orderId: this.orderID, paymentTerm: fields.paymentTerm, balance: this.order?.balance },
                });
                throw error;
              }
            }
            break;
          default:
            break;
        }
      }
    }

    let shippingDifference = 0;

    if (fields.hasOwnProperty("shippingFee")) {
      if (typeof fields.shippingFee !== "number") {
        fields.shippingFee = Number(fields.shippingFee ?? 0);
      }
      shippingDifference =
        Number(fields.shippingFee ?? 0) - Number(this.order.balance.shipping);

      updatePayload.balance = {
        shipping: parseFloat(fields.shippingFee ?? 0).toFixed(2),
        total: parseFloat(
          Number(this.order.balance.total) + shippingDifference,
        ).toFixed(2),
        due: parseFloat(
          Number(this.order.balance.due) + shippingDifference,
        ).toFixed(2),
      };

      // Dropping a shipping fee lowers the total; block before the external PATCH below.
      await this._assertOverpayCommittable(
        "update-order-data-shipping-overpay-preflight",
        updatePayload.balance,
      );

      updatePayload.flags = {
        ...this.order.flags,
        shippingManuallySet: true,
      };

      needsExternalUpdate = true;

      // Update external order payment term
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("orderUpdated")) {
              addBreadcrumb("leaflink: updateOrderData shipping fee", {
                workflow: "sales-order",
                data: { orderId: this.orderID, shippingFee: fields.shippingFee },
              });
              try {
                let patchData = {
                  shipping_charge: {
                    amount: Number(fields.shippingFee ?? 0),
                    currency: "USD",
                  },
                };
                await this.leaflinkClient.updateOrderData(patchData);
              } catch (error) {
                logWorkflowWarning(`LeafLink: updateOrderData (shipping fee) failed — ${error.message}`, {
                  workflow: "sales-order",
                  action: "leaflink-update-order-shipping",
                  context: { orderId: this.orderID, shippingFee: fields.shippingFee, balance: this.order?.balance },
                });
                throw error;
              }
            }
            break;
          default:
            break;
        }
      }

      const oldValue = Number(this.order.balance.shipping),
        newValue = Number(fields.shippingFee ?? 0);

      let currencyFormat = (value) => {
        return Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        }).format(value);
      };

      updates.push(
        `Shipping Cost, <span class="text-uppercase">old</span>: <span>${currencyFormat(
          oldValue,
        )}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${currencyFormat(
          newValue,
        )}</span>`,
      );

      if (["shipped", "complete"].includes(this.order.status)) {
        const brandStatsDoc = doc(
          firestore,
          "brandStats",
          `${this.order.account.id}_${this.order.brand.id}`,
        );

        const shippingDelta = Number(
          parseFloat(shippingDifference).toFixed(2),
        );
        if (!Number.isFinite(shippingDelta)) {
          throw new Error(
            "Invalid shipping difference for brandStats update",
          );
        }
        batch.set(
          brandStatsDoc,
          {
            metrics: {
              balance: increment(shippingDelta),
              totalSales: increment(shippingDelta),
            },
          },
          { merge: true },
        );
      }
    }
    if (fields.hasOwnProperty("shipDate")) {
      updatePayload.shipDate = moment(fields.shipDate)
        .startOf("day")
        .add(9, "hours")
        .tz("America/Los_Angeles")
        .toDate();

      needsExternalUpdate = true;

      const oldValue = this.order.shipDate
          ? moment(this.order.shipDate?.seconds * 1000).format("MM/DD/YYYY")
          : "[Not Set]",
        newValue = moment(fields.shipDate).format("MM/DD/YYYY");

      updates.push(
        `Ship Date, <span class="text-uppercase">old</span>: <span>${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${newValue}</span>`,
      );
    }

    if (fields.hasOwnProperty("manifest")) {
      updatePayload.orderNumber = { manifest: fields.manifest };

      const oldValue =
          this.order.orderNumber?.manifest?.length > 0
            ? this.order.orderNumber.manifest.toUpperCase()
            : "[Not Set]",
        newValue =
          fields.manifest.length > 0
            ? fields.manifest.toUpperCase()
            : "[Not Set]";

      updates.push(
        `Manifest #, <span class="text-uppercase">old</span>: <span>${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${newValue}</span>`,
      );
    }

    if (fields.hasOwnProperty("deliveryOption")) {
      updatePayload.deliveryOption = fields.deliveryOption;

      const oldValue =
          store.getters.getDeliveryTypes.find(
            (item) => item.value === this.order.deliveryOption,
          )?.text ?? "[Not Set]",
        newValue =
          store.getters.getDeliveryTypes.find(
            (item) => item.value === fields.deliveryOption,
          )?.text ?? "[Not Set]";

      updates.push(
        `Delivery Type, <span class="text-uppercase">old</span>: <span class="text-capitalize">${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold text-capitalize">${newValue}</span>`,
      );
    }

    if (fields.hasOwnProperty("deliveredBy")) {
      updatePayload.deliveredBy = {
        id: fields.deliveredBy.id,
        name: fields.deliveredBy.name,
      };

      const oldValue = this.order.deliveredBy?.name || "[Not Set]",
        newValue = fields.deliveredBy?.name ?? "[Not Set]";

      updates.push(
        `Delivery By, <span class="text-uppercase">old</span>: <span class="text-capitalize">${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold text-capitalize">${newValue}</span>`,
      );
    }

    if (fields.hasOwnProperty("deliveryLogisticsFee")) {
      // Internal cost-tracking only — the minimum-order-value fee is already reflected
      // in Shipping Cost, so this must never adjust balance.total/balance.due.
      updatePayload.deliveryLogisticsFee = Number(
        fields.deliveryLogisticsFee ?? 0,
      );

      let currencyFormat = (value) => {
        return Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        }).format(value);
      };

      const oldValue =
          this.order.deliveryLogisticsFee !== null
            ? currencyFormat(this.order.deliveryLogisticsFee)
            : "[Not Set]",
        newValue =
          fields.deliveryLogisticsFee !== null
            ? currencyFormat(fields.deliveryLogisticsFee)
            : "[Not Set]";

      updates.push(
        `Delivery/Logistics Fee, <span class="text-uppercase">old</span>: <span class="text-capitalize">${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold text-capitalize">${newValue}</span>`,
      );
    }

    if (fields.hasOwnProperty("notes")) {
      updatePayload.notes = fields.notes;

      needsExternalUpdate = true;

      const oldValue =
          this.order.notes.length > 0 ? `"${this.order.notes}"` : "[Not Set]",
        newValue = fields.notes.length > 0 ? `"${fields.notes}"` : "[Not Set]";

      updates.push(
        `Notes, <span class="text-uppercase">old</span>: <span>${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${newValue}</span>`,
      );
    }
    if (fields.hasOwnProperty("internalNotes")) {
      updatePayload.internalNotes = fields.internalNotes;

      const oldValue =
          this.order.internalNotes.length > 0
            ? `"${this.order.internalNotes}"`
            : "[Not Set]",
        newValue =
          fields.internalNotes.length > 0
            ? `"${fields.internalNotes}"`
            : "[Not Set]";

      updates.push(
        `Internal Notes, <span class="text-uppercase">old</span>: <span>${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${newValue}</span>`,
      );
    }
    if (fields.hasOwnProperty("campaignID")) {
      updatePayload.campaignID = fields.campaignID;

      const oldValue = fields.previousCampaignName ?? (this.order.campaignID ? this.order.campaignID : "None");
      const newValue = fields.campaignName ?? (fields.campaignID ? fields.campaignID : "None");

      updates.push(`Campaign: ${oldValue} → ${newValue}`);
    }
    if (fields.hasOwnProperty("deliveryPreferences")) {
      updatePayload.deliveryPreferences = fields.deliveryPreferences;

      const oldValue =
          this.order.account.deliveryPreferences &&
          this.order.account.deliveryPreferences.length > 0
            ? `"${this.order.account.deliveryPreferences}"`
            : "[Not Set]",
        newValue =
          fields.deliveryPreferences.length > 0
            ? `"${fields.deliveryPreferences}"`
            : "[Not Set]";

      updates.push(
        `Delivery Preferences, <span class="text-uppercase">old</span>: <span>${oldValue}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${newValue}</span>`,
      );
    }

    if (
      fields.hasOwnProperty("discountType") ||
      fields.hasOwnProperty("discountValue")
    ) {
      let priceTotal = this.lineItems.reduce((a, b) => a + b.amount * b.qty, 0);

      const discountType =
        fields.discountType ?? this.order.discount?.type ?? "%";
      const discountValue =
        fields.discountValue ?? this.order.discount?.value ?? 0;
      const paymentsApplied = sumAppliedPayments(this.payments);
      const {
        total: computedTotal,
        due: computedDue,
        discountTotal,
      } = computeOrderBalance({
        lineSubtotal: priceTotal,
        discountType,
        discountValue,
        shipping: Number(this.order.balance.shipping),
        paymentsApplied,
      });
      // Preserve existing string .toFixed(2) Firestore shape for this path.
      let newTotal = parseFloat(computedTotal).toFixed(2);
      let newBalance = parseFloat(computedDue).toFixed(2);

      updatePayload.discount = {
        type: discountType,
        value: discountValue,
        total: discountTotal,
      };

      updatePayload.balance = {
        total: newTotal,
        due: newBalance,
      };

      // A larger discount lowers the total; block before the LeafLink complete transition below.
      await this._assertOverpayCommittable(
        "update-order-data-discount-overpay-preflight",
        updatePayload.balance,
      );

      if (["shipped", "complete"].includes(this.order.status)) {
        if (newBalance <= 0) {
          updatePayload.status = "complete";
          updatePayload.flags = {
            paid: true,
          };
          updatePayload.records = {
            completed: serverTimestamp(),
          };

          if (this.orderIsExternal) {
            switch (this.externalOrderType) {
              case "leaflink":
                if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                  addBreadcrumb("leaflink: transitionOrder complete (discount)", {
                    workflow: "sales-order",
                    data: { orderId: this.orderID, newBalance, orderStatus: this.order?.status },
                  });
                  try {
                    await this.leaflinkClient.transitionOrder("complete");
                  } catch (error) {
                    logWorkflowWarning(`LeafLink: transitionOrder("complete") failed — ${error.message}`, {
                      workflow: "sales-order",
                      action: "leaflink-transition-complete-discount",
                      context: { orderId: this.orderID, newBalance, balance: this.order?.balance },
                    });
                    throw error;
                  }
                }
                break;
              default:
                break;
            }
          }
        } else {
          updatePayload.status = "shipped";
          updatePayload.flags = {
            paid: false,
          };
          updatePayload.records = {
            completed: null,
          };
        }

        let balanceDifference = newBalance - Number(this.order.balance.due);

        const brandStatsDoc = doc(
          firestore,
          "brandStats",
          `${this.order.account.id}_${this.order.brand.id}`,
        );

        const balanceDelta = Number(
          parseFloat(balanceDifference).toFixed(2),
        );
        if (!Number.isFinite(balanceDelta)) {
          throw new Error(
            "Invalid discount balance difference for brandStats update",
          );
        }
        batch.set(
          brandStatsDoc,
          {
            metrics: {
              balance: increment(balanceDelta),
              totalSales: increment(balanceDelta),
            },
          },
          { merge: true },
        );
      }

      updates.push(`Discount Data`);

      needsExternalUpdate = true;
    }

    if (Object.keys(updatePayload).length > 0) {
      console.log(updatePayload);
      batch.set(salesOrderDoc, updatePayload, { merge: true });
    }

    let externalUpdates = false;
    if (this.orderIsExternal) {
      switch (this.externalOrderType) {
        case "leaflink":
          if (this.externalUpdates.leaflink.includes("orderUpdated")) {
            externalUpdates = true;
          }
          break;
        default:
          break;
      }
    }

    if (updates.length > 0) {
      // Create clean description for activity
      const cleanDescription = updates.join(", ");

      // Create structured change data for better display
      const changeData = {
        orderNumber: this.order.orderNumber,
        fieldChanges: [], // Array of structured field changes
        summary: {
          totalFieldsChanged: Object.keys(fields).length,
          hasPriceChanges: needsPriceUpdate,
          hasExternalUpdates: needsExternalUpdate && externalUpdates,
        },
      };

      // Process each field change into structured format
      if (fields.hasOwnProperty("paymentTerm")) {
        const oldValue = store.getters.getPaymentTerms.find(
          (option) => option.value === this.order.paymentTerm,
        );
        const newValue = store.getters.getPaymentTerms.find(
          (option) => option.value === fields.paymentTerm,
        );

        changeData.fieldChanges.push({
          field: "paymentTerm",
          label: "Payment Term",
          oldValue: oldValue?.text || this.order.paymentTerm,
          newValue: newValue?.text || fields.paymentTerm,
          type: "selection",
          impact: "price",
        });
      }

      if (fields.hasOwnProperty("shippingFee")) {
        const oldValue = Number(this.order.balance.shipping);
        const newValue = Number(fields.shippingFee ?? 0);

        changeData.fieldChanges.push({
          field: "shippingFee",
          label: "Shipping Fee",
          oldValue: `$${oldValue.toFixed(2)}`,
          newValue: `$${newValue.toFixed(2)}`,
          type: "currency",
          impact: "balance",
          difference: shippingDifference,
        });
      }

      if (fields.hasOwnProperty("shipDate")) {
        const oldValue = this.order.shipDate
          ? moment(this.order.shipDate?.seconds * 1000).format("MM/DD/YYYY")
          : "[Not Set]";
        const newValue = moment(fields.shipDate).format("MM/DD/YYYY");

        changeData.fieldChanges.push({
          field: "shipDate",
          label: "Ship Date",
          oldValue: oldValue,
          newValue: newValue,
          type: "date",
          impact: "logistics",
        });
      }

      if (fields.hasOwnProperty("manifest")) {
        const oldValue =
          this.order.orderNumber?.manifest?.length > 0
            ? this.order.orderNumber.manifest.toUpperCase()
            : "[Not Set]";
        const newValue =
          fields.manifest.length > 0
            ? fields.manifest.toUpperCase()
            : "[Not Set]";

        changeData.fieldChanges.push({
          field: "manifest",
          label: "Manifest #",
          oldValue: oldValue,
          newValue: newValue,
          type: "text",
          impact: "logistics",
        });
      }

      if (fields.hasOwnProperty("deliveryOption")) {
        const oldValue =
          store.getters.getDeliveryTypes.find(
            (item) => item.value === this.order.deliveryOption,
          )?.text ?? "[Not Set]";
        const newValue =
          store.getters.getDeliveryTypes.find(
            (item) => item.value === fields.deliveryOption,
          )?.text ?? "[Not Set]";

        changeData.fieldChanges.push({
          field: "deliveryOption",
          label: "Delivery Type",
          oldValue: oldValue,
          newValue: newValue,
          type: "selection",
          impact: "logistics",
        });
      }

      if (fields.hasOwnProperty("deliveredBy")) {
        const oldValue = this.order.deliveredBy?.name || "[Not Set]";
        const newValue = fields.deliveredBy?.name ?? "[Not Set]";

        changeData.fieldChanges.push({
          field: "deliveredBy",
          label: "Delivered By",
          oldValue: oldValue,
          newValue: newValue,
          type: "user",
          impact: "logistics",
        });
      }

      if (fields.hasOwnProperty("deliveryLogisticsFee")) {
        const oldValue =
          this.order.deliveryLogisticsFee !== null
            ? this.order.deliveryLogisticsFee
            : "[Not Set]";
        const newValue =
          fields.deliveryLogisticsFee !== null
            ? fields.deliveryLogisticsFee
            : "[Not Set]";

        changeData.fieldChanges.push({
          field: "deliveryLogisticsFee",
          label: "Delivery/Logistics Fee",
          oldValue:
            oldValue !== "[Not Set]" ? `$${oldValue.toFixed(2)}` : oldValue,
          newValue:
            newValue !== "[Not Set]" ? `$${newValue.toFixed(2)}` : newValue,
          type: "currency",
          impact: "internal",
        });
      }

      if (fields.hasOwnProperty("notes")) {
        const oldValue =
          this.order.notes.length > 0 ? `"${this.order.notes}"` : "[Not Set]";
        const newValue =
          fields.notes.length > 0 ? `"${fields.notes}"` : "[Not Set]";

        changeData.fieldChanges.push({
          field: "notes",
          label: "Notes",
          oldValue: oldValue,
          newValue: newValue,
          type: "text",
          impact: "communication",
        });
      }

      if (fields.hasOwnProperty("internalNotes")) {
        const oldValue =
          this.order.internalNotes.length > 0
            ? `"${this.order.internalNotes}"`
            : "[Not Set]";
        const newValue =
          fields.internalNotes.length > 0
            ? `"${fields.internalNotes}"`
            : "[Not Set]";

        changeData.fieldChanges.push({
          field: "internalNotes",
          label: "Internal Notes",
          oldValue: oldValue,
          newValue: newValue,
          type: "text",
          impact: "internal",
        });
      }

      if (fields.hasOwnProperty("campaignID")) {
        changeData.fieldChanges.push({
          field: "campaignID",
          label: "Campaign",
          oldValue: fields.previousCampaignName ?? "None",
          newValue: fields.campaignName ?? "None",
          type: "selection",
          impact: "accounting",
        });
      }

      if (fields.hasOwnProperty("deliveryPreferences")) {
        const oldValue =
          this.order.account.deliveryPreferences &&
          this.order.account.deliveryPreferences.length > 0
            ? `"${this.order.account.deliveryPreferences}"`
            : "[Not Set]";
        const newValue =
          fields.deliveryPreferences.length > 0
            ? `"${fields.deliveryPreferences}"`
            : "[Not Set]";

        changeData.fieldChanges.push({
          field: "deliveryPreferences",
          label: "Delivery Preferences",
          oldValue: oldValue,
          newValue: newValue,
          type: "text",
          impact: "logistics",
        });
      }

      if (fields.hasOwnProperty("discountType")) {
        const oldValue =
          (this.order.discount?.type ?? "%") === "%"
            ? `Percentage`
            : `Fixed Amount`;
        const newValue =
          fields.discountType === "%" ? `Percentage` : `Fixed Amount`;

        changeData.fieldChanges.push({
          field: "discountType",
          label: "Discount Type",
          oldValue: oldValue,
          newValue: newValue,
          type: "text",
          impact: "price",
        });
      }

      if (fields.hasOwnProperty("discountValue")) {
        const oldValue =
          (this.order.discount?.type ?? "%") === "%"
            ? `${this.order.discount?.value ?? 0}%`
            : `$${this.order.discount?.value ?? 0}`;
        const newValue =
          (fields.discountType ?? this.order?.discount?.type ?? "%") === "%"
            ? `${fields.discountValue ?? 0}%`
            : `$${fields.discountValue ?? 0}`;

        changeData.fieldChanges.push({
          field: "discountValue",
          label: "Discount Value",
          oldValue: oldValue,
          newValue: newValue,
          type: "currency",
          impact: "price",
        });
      }

      changeData.summary.totalFieldsChanged = changeData.fieldChanges.length;

      // Create activity for order details update
      await createActivity({
        type: "order",
        action: "Order details updated",
        item: `Order details updated: ${cleanDescription}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "medium",
        priority: "medium",
        changeData: changeData,
      });

      updates = updates.join(" | ");
    }

    if (updatePayload.balance || fields.hasOwnProperty("discountValue") || fields.hasOwnProperty("discountType")) {
      addBreadcrumb("updateOrderData: balance-affecting change", {
        workflow: "sales-order",
        data: {
          orderId: this.orderID,
          changedFields: Object.keys(fields),
          currentBalance: this.order?.balance,
        },
      });
    }

    // The branches above each guard their own projection, but a delivery-fee change and a
    // combined edit can rewrite balance afterwards — gate whatever is actually committed.
    if (updatePayload.balance) {
      await this._assertOverpayCommittable(
        "update-order-data-overpay-preflight",
        updatePayload.balance,
      );
    }

    await batch.commit();
    if (updatePayload.balance || fields.hasOwnProperty("discountValue") || fields.hasOwnProperty("discountType")) {
      await this._checkBalanceAnomaly("update-order-data");
    }

    // Discount zeroed the balance, flipping shipped → complete. Never throws.
    if (updatePayload.status === "complete" && this.order.status === "shipped") {
      try {
        await this._createFirstPurchases(
          moment().startOf("day").add(12, "hours").toDate(),
        );
      } catch (firstPurchaseError) {
        logWorkflowWarning(
          `firstPurchases creation failed — ${firstPurchaseError.message}`,
          {
            workflow: "sales-order",
            action: "create-first-purchases",
            context: {
              orderId: this.orderID,
              accountId: this.order.account?.id,
              brandId: this.order.brand?.id,
            },
          },
        );
      }
    }

    else if (updatePayload.status === "shipped" && this.order.status === "complete") {
      try {
        await this._removeFirstPurchases();
      } catch (firstPurchaseError) {
        logWorkflowWarning(
          `firstPurchases removal failed — ${firstPurchaseError.message}`,
          {
            workflow: "sales-order",
            action: "remove-first-purchases",
            context: {
              orderId: this.orderID,
              accountId: this.order.account?.id,
              brandId: this.order.brand?.id,
            },
          },
        );
      }
    }

    if (
      needsPriceUpdate &&
      !["shipped", "complete"].includes(this.order.status)
    ) {
      try {
        await this.updateVariablePricingOnOrderTermChange();
      } catch (pricingError) {
        // The term is already committed but the prices it drives are not. Put the old term
        // back so the order stays self-consistent and re-selecting the term retries the change.
        await this._revertPaymentTerm(previousPaymentTerm, pricingError);
        throw pricingError;
      }
    }

    // If order is in "Shipped" or "Complete" status, user should be forced to revert status back to "Fulfilled"
    // so that the account balance stays accurate

    // External - Update Order

    if (this.orderIsExternal && needsExternalUpdate) {
      switch (this.externalOrderType) {
        case "leaflink":
          if (this.externalUpdates.leaflink.includes("orderUpdated")) {
            let shipDate = null;

            const discountSubtotal = this.lineItems.reduce(
              (a, b) => a + b.amount * b.qty,
              0,
            );

            if (fields.hasOwnProperty("shipDate") && updatePayload.shipDate) {
              let shipDateMoment = moment(updatePayload.shipDate);
              shipDate = moment(shipDateMoment.clone().format("MM/DD/YYYY"))
                .add(9, "hours")
                .tz("America/Los_Angeles");
            }
            let patchData = {
              ship_date: updatePayload.shipDate
                ? moment(shipDate).toISOString()
                : null,
              ...getLeaflinkDiscountPayload({
                type: fields.discountType ?? this.order.discount?.type ?? "%",
                value: fields.discountValue ?? this.order.discount?.value ?? 0,
                subtotal: discountSubtotal,
              }),
            };
            addBreadcrumb("leaflink: updateOrderData (order fields)", {
              workflow: "sales-order",
              data: { orderId: this.orderID, changedFields: Object.keys(fields) },
            });
            try {
              await this.leaflinkClient.updateOrderData(patchData);
            } catch (error) {
              logWorkflowWarning(`LeafLink: updateOrderData (order fields) failed — ${error.message}`, {
                workflow: "sales-order",
                action: "leaflink-update-order-fields",
                context: { orderId: this.orderID, changedFields: Object.keys(fields), balance: this.order?.balance },
              });
              throw error;
            }
          }
          break;
        default:
          break;
      }
    }

    if (fields.hasOwnProperty("fulfillmentLocation")) {
      try {
        await this.updateFulfillmentLocation(fields);
      } catch (error) {
        // Re-throw error to be handled by the calling component
        throw error;
      }
    }

    await this.checkOverpaymentCredits();
  }

  // Main
  async updateVariablePricingOnOrderTermChange() {
    addBreadcrumb("updateVariablePricingOnOrderTermChange: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, paymentTerm: this.order?.paymentTerm, balance: this.order?.balance },
    });
    const batch = writeBatch(firestore);
    await this.getOrder();
    await this.getLineItems();
    await this.getProductCategories();
    await this.getPriceOverrides();
    // The overpay preflight below reads this.payments, which this path never loaded.
    await this.getPayments();

    // Get the new payment term
    let term = this.order.paymentTerm;

    // If the order has sample line items, skip the update
    if (this.lineItems.some((item) => item.sample)) return;

    let overriddenCategoryIDs = this.priceOverrides.map(
      (override) => override.category.id,
    );

    this.lineItems.forEach((product) => {
      product.amount = {
        original: product.amount,
        new: product.amount,
      };
      // if (product.promo) return
      let category = this.categories.find(
        (category) => category.id === product.categoryID,
      );
      if (category && category.flags.includes("hasVariablePricing")) {
        product.hasVariablePricing = true;
        product.variablePrice = category.variablePrice;
      }
      if (overriddenCategoryIDs.includes(product.categoryID)) {
        //  Price Override
        let override;
        if (
          this.priceOverrides
            .filter(
              (item) =>
                item.category.id === product.categoryID &&
                item.account &&
                item.account.id === this.order.account.id,
            )
            .find(
              (option) =>
                option.accountSpecific &&
                option.account.id === this.order.account.id,
            )
        ) {
          // Account Specific Price Override
          override = this.priceOverrides
            .filter((item) => item.category.id === product.categoryID)
            .find(
              (option) =>
                option.accountSpecific &&
                option.account.id === this.order.account.id,
            );
        } else if (
          this.priceOverrides
            .filter(
              (item) =>
                item.category.id === product.categoryID &&
                item.product &&
                item.product.id === product.productID,
            )
            .find(
              (option) =>
                option.productSpecific &&
                option.product.id === product.productID,
            )
        ) {
          // SKU Specific Price Override
          override = this.priceOverrides
            .filter((item) => item.category.id === product.categoryID)
            .find(
              (option) =>
                option.productSpecific &&
                option.product.id === product.productID,
            );
        } else {
          // Payable To Price Override
          override = this.priceOverrides.find(
            (item) =>
              item.category.id === product.categoryID &&
              item.accountSpecific === false &&
              item.productSpecific === false,
          );
        }
        if (override && override.variablePrice) {
          let newPrice = override.variablePrice.find(
            (variablePrice) => variablePrice.term === term,
          );
          if (newPrice) {
            product.price.normal = newPrice.price;
            product.price.sale = product.promo ? 0.01 : newPrice.price;
            product.amount.new = product.promo ? 0.01 : newPrice.price;
          }
        } else if (override) {
          product.price.normal = override.price;
          product.price.sale = product.promo ? 0.01 : override.price;
          product.amount.new = product.promo ? 0.01 : override.price;
        } else if (product.hasVariablePricing) {
          // Needed for products within categories where 1 or more products in the category
          // has a price override but this specific product doesn't have an override
          let newPrice = product.variablePrice.find(
            (variablePrice) => variablePrice.term === term,
          );
          if (newPrice) {
            product.price.normal = newPrice.price;
            product.price.sale = product.promo ? 0.01 : newPrice.price;
            product.amount.new = product.promo ? 0.01 : newPrice.price;
          }
        }
      } else if (product.hasVariablePricing) {
        let newPrice = product.variablePrice.find(
          (variablePrice) => variablePrice.term === term,
        );
        if (newPrice) {
          product.price.normal = newPrice.price;
          product.price.sale = product.promo ? 0.01 : newPrice.price;
          product.amount.new = product.promo ? 0.01 : newPrice.price;
        }
      }
    });

    let updates = [];

    let transitionToAccepted = false;

    const orderDoc = doc(firestore, "salesOrders", this.order.id);

    let lineItems = this.lineItems.map((item) => {
      let itemUpdates = [];

      // Get updated price with discount (if applicable)

      if (item.flags.discountApplied && item.discount?.value) {
        let newAmount = item.amount.new;
        item.amount.new = Number(
          parseFloat(newAmount - item.discount.value).toFixed(2),
        );
        item.price.sale = Number(
          parseFloat(newAmount - item.discount.value).toFixed(2),
        );
      }

      if (item.amount.new !== item.amount.original) {
        //  Price updated
        const currencyFormatter = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        });
        itemUpdates.push(
          `Price - <span class="text-uppercase">old</span>: <span>${currencyFormatter.format(
            item.amount.original,
          )}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold">${currencyFormatter.format(
            item.amount.new,
          )}</span>`,
        );
      }

      if (itemUpdates.length > 0) {
        //  Line item was modified

        itemUpdates = itemUpdates.join(" / ");
        let promoUnit = item.promo
          ? `<span class='font-weight-bold text-no-wrap'>
            <span class='font-weight-bold text-caption pa-1 rounded' style='background-color:
              #0277BD; color: #FFFFFF}
            '>Promo</span>
          </span>`
          : "";
        let itemText = `<span class="text-capitalize font-weight-bold">${item.name}</span> <span class="text-uppercase">(${item.sku})${promoUnit}</span>: ${itemUpdates}`;
        updates.push(itemText);
      }

      delete item.hasVariablePricing;
      delete item.variablePrice;

      return {
        ...item,
        externalIDs: item.externalIDs,
        id: item.id,
        price: item.price,
        amount: item.amount.new,
        qty: Number(item.qty || 0),
      };
    });

    //  Sum total price of line items
    let priceTotal = lineItems.reduce((a, b) => a + b.qty * b.amount, 0);

    const paymentsApplied = sumAppliedPayments(this.payments);
    const {
      total: newTotal,
      due: newBalance,
      discountTotal,
    } = computeOrderBalance({
      lineSubtotal: priceTotal,
      discountType: this.order.discount?.type ?? "%",
      discountValue: this.order.discount?.value ?? 0,
      shipping: Number(this.order.balance.shipping),
      paymentsApplied,
    });

    // A cheaper term can drop the total below what is already paid. Blocked here rather than
    // before the commit below so the LeafLink line-item PATCHes never run on a barred change.
    await this._assertOverpayCommittable(
      "update-variable-pricing-overpay-preflight",
      { total: newTotal, due: newBalance },
    );

    const promises = lineItems.map(async (item) => {
      const lineItemDoc = doc(firestore, "salesOrders_lineItems", item.id);

      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("orderUpdated")) {
              let externalItem = null;
              if (item.externalIDs?.leaflinkLineItemID) {
                externalItem = {
                  id: item.externalIDs.leaflinkLineItemID,
                };
              } else {
                if (this.leaflinkClient.lineItems.length === 0) {
                  await this.leaflinkClient.getLineItems();
                }
                externalItem = this.leaflinkClient.lineItems.find(
                  (external) =>
                    external.productID === item.externalIDs.leaflink,
                );
              }
              let itemData = {
                ordered_unit_price: item.price.normal,
                sale_price: item.amount,
              };
              if (externalItem) {
                addBreadcrumb("leaflink: updateLineItem (variable pricing)", {
                  workflow: "sales-order",
                  data: { orderId: this.orderID, itemId: item.id, externalItemId: externalItem.id },
                });
                try {
                  await this.leaflinkClient.updateLineItem(externalItem, itemData);
                  item.externalIDs.leaflinkLineItemID = externalItem.id;
                } catch (error) {
                  logWorkflowWarning(`LeafLink: updateLineItem (variable pricing) failed — ${error.message}`, {
                    workflow: "sales-order",
                    action: "leaflink-update-line-item-variable-pricing",
                    context: { orderId: this.orderID, itemId: item.id, balance: this.order?.balance },
                  });
                  throw error;
                }
              }
            }
            break;
          default:
            break;
        }
      }

      // Ensure amount.new is a valid number, fallback to amount.original if needed
      const itemAmount =
        Number(item.amount.new) || Number(item.amount.original) || 0;

      // Ensure taxPercentage is a valid number
      const taxPercentage = Number(this.order.taxPercentage) || 0;

      // Calculate tax with safe division
      const taxRate = 1 + taxPercentage / 100;
      const taxAmount = taxRate > 0 ? itemAmount - itemAmount / taxRate : 0;

      item.tax = Number(parseFloat(taxAmount).toFixed(2));

      batch.set(
        lineItemDoc,
        {
          ...item,
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
        },
        { merge: true },
      );
    });
    //  Need to use Promise to ensure that all items are removed before setup() runs
    await Promise.all(promises);

    // Update order
    batch.set(
      orderDoc,
      {
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        lineItems,
        balance: {
          due: newBalance,
          total: newTotal,
        },
        discount: {
          value: this.order.discount?.value ?? 0,
          type: this.order.discount?.type ?? "%",
          total: discountTotal,
        },
      },
      { merge: true },
    );

    if (updates.length > 0) {
      // Create detailed description for activity with price changes and balance impact
      const updatedItems = this.lineItems.filter(
        (item) => item.amount.new !== item.amount.original,
      );
      const updatedCount = updatedItems.length;

      // Calculate total balance change
      const totalBalanceChange = updatedItems.reduce((sum, item) => {
        const caseQTY = item.caseQTY ?? item.unitsPerCase;
        const totalQty = (item.cases || 0) * caseQTY + (item.singles || 0);
        const priceDifference = item.amount.new - item.amount.original;
        return sum + priceDifference * totalQty;
      }, 0);

      // Format currency
      const currencyFormatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      });

      // Create detailed item descriptions with price changes
      const detailedDescription = updatedItems
        .map((item) => {
          const caseQTY = item.caseQTY ?? item.unitsPerCase;
          const totalQty = (item.cases || 0) * caseQTY + (item.singles || 0);

          return `${item.name} (${
            item.sku
          }) - Qty: ${totalQty} - Price: ${currencyFormatter.format(
            item.amount.original,
          )} → ${currencyFormatter.format(item.amount.new)}`;
        })
        .join(", ");

      // Calculate old and new order totals
      const oldOrderTotal = Number(this.order.balance.total);
      const newOrderTotal =
        Number(this.order.balance.total) + totalBalanceChange;

      // Create activity for line items update during pricing change
      await createActivity({
        type: "inventory",
        action: "Line items pricing updated",
        item: `Updated pricing for ${updatedCount} item${
          updatedCount !== 1 ? "s" : ""
        }: ${detailedDescription} - Order balance: ${currencyFormatter.format(
          oldOrderTotal,
        )} → ${currencyFormatter.format(newOrderTotal)} (${
          totalBalanceChange >= 0 ? "+" : ""
        }${currencyFormatter.format(totalBalanceChange)})`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "medium",
        changeData: {
          field: "lineItemsPricing",
          reason: "Variable pricing update on payment term change",
          action: "updated",
          items: this.lineItems
            .filter((item) => item.amount.new !== item.amount.original)
            .map((item) => ({
              name: item.name,
              sku: item.sku,
              changes: {
                amount: {
                  old: item.amount.original,
                  new: item.amount.new,
                },
              },
            })),
          orderNumber: this.order.orderNumber,
          monetaryValue: this.lineItems
            .filter((item) => item.amount.new !== item.amount.original)
            .reduce(
              (sum, item) => sum + (item.amount.new - item.amount.original),
              0,
            ),
        },
      });

      updates = updates.join(" | ");
    }

    addBreadcrumb("updateVariablePricingOnOrderTermChange: committing batch", {
      workflow: "sales-order",
      data: { orderId: this.orderID, lineItemCount: this.lineItems.length },
    });
    await batch.commit();
    await this.checkOverpaymentCredits();
  }

  // Fulfillment Location Update
  async updateFulfillmentLocation(fields) {
    await this.getOrder();
    await this.getLineItems();

    const batch = writeBatch(firestore);
    const salesOrderDoc = doc(firestore, "salesOrders", this.order.id);
    try {
      // Check if new location has enough inventory
      let newFulfillmentLocation = this.fulfillmentLocations.find(
        (item) => item.locationID === fields.fulfillmentLocation,
      );
      if (!newFulfillmentLocation) {
        throw new Error("New Fulfillment Location Not Found");
      }

      let originalFulfillmentLocation = null;
      let originalLocationManufacturingRef = null;

      // Only find original location if one is already set
      if (this.order.fulfillmentLocation) {
        originalFulfillmentLocation = this.fulfillmentLocations.find(
          (item) => item.locationID === this.order.fulfillmentLocation.id,
        );

        if (originalFulfillmentLocation) {
          originalLocationManufacturingRef = doc(
            firestore,
            "locationManufacturing",
            `${originalFulfillmentLocation.locationID}_${this.order.brand.id}`,
          );
        }
      }
      const newLocationManufacturingRef = doc(
        firestore,
        "locationManufacturing",
        `${newFulfillmentLocation.locationID}_${this.order.brand.id}`,
      );
      const response = await getDoc(newLocationManufacturingRef);

      if (
        response.exists() &&
        response.data().hasOwnProperty("manufacturing")
      ) {
        let manufacturing = response.data().manufacturing;

        // Read original location so we can merge and write once per location
        let originalManufacturing = {};
        if (originalLocationManufacturingRef) {
          const origSnap = await getDoc(originalLocationManufacturingRef);
          originalManufacturing =
            origSnap.exists() && origSnap.data().manufacturing
              ? JSON.parse(JSON.stringify(origSnap.data().manufacturing))
              : {};
        }

        // Build merged manufacturing: one object per location with all line-item deltas applied
        const newMfg = JSON.parse(JSON.stringify(manufacturing));
        const originalMfg = JSON.parse(JSON.stringify(originalManufacturing));

        for (const lineItem of this.lineItems) {
          let categoryID = lineItem.categoryID;
          let productID = lineItem.productID;

          const isCaseProduct =
            lineItem.unitsPerCase > 0 || lineItem.caseQTY > 0;
          let itemQTY;
          if (isCaseProduct) {
            itemQTY =
              lineItem.singles +
              lineItem.cases * (lineItem.unitsPerCase ?? lineItem.caseQTY ?? 0);
          } else {
            itemQTY = lineItem.singles || 0;
          }

          const availableAtNew =
            newMfg[categoryID]?.available?.[productID] ?? 0;
          const reservedAtNew = newMfg[categoryID]?.reserved?.[productID] ?? 0;
          const transferAtNew = newMfg[categoryID]?.transfer?.[productID] ?? 0;
          const availableAmount =
            availableAtNew - reservedAtNew - transferAtNew;
          if (itemQTY > availableAmount) {
            throw new Error("Inventory Unavailable");
          }

          if (!newMfg[categoryID]) newMfg[categoryID] = {};
          if (!newMfg[categoryID].reserved) newMfg[categoryID].reserved = {};
          newMfg[categoryID].reserved[productID] =
            (newMfg[categoryID].reserved[productID] || 0) + itemQTY;

          if (originalLocationManufacturingRef) {
            if (!originalMfg[categoryID]) originalMfg[categoryID] = {};
            if (!originalMfg[categoryID].reserved) {
              originalMfg[categoryID].reserved = {};
            }
            originalMfg[categoryID].reserved[productID] =
              (originalMfg[categoryID].reserved[productID] || 0) - itemQTY;
          }
        }

        batch.set(
          newLocationManufacturingRef,
          { manufacturing: newMfg },
          { merge: true },
        );
        if (originalLocationManufacturingRef) {
          batch.set(
            originalLocationManufacturingRef,
            {
              manufacturing: originalMfg,
            },
            { merge: true },
          );
        }

        batch.set(
          salesOrderDoc,
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            fulfillmentLocation: {
              id: newFulfillmentLocation.locationID,
              name: newFulfillmentLocation.name,
            },
          },
          { merge: true },
        );

        addBreadcrumb("updateFulfillmentLocation: committing inventory transfer to new location", {
          workflow: "sales-order",
          data: {
            orderId: this.orderID,
            newLocationId: newFulfillmentLocation.locationID,
            lineItemCount: this.lineItems.length,
          },
        });
        await batch.commit();

        // Inventory activities: one per line at old location (unreserve) and one per line at new (reserve)
        // Use pre-commit state for "before"; "after" = before + delta (already applied in merged write)
        for (const lineItem of this.lineItems) {
          const isCaseProduct =
            lineItem.unitsPerCase > 0 || lineItem.caseQTY > 0;
          const itemQTY = isCaseProduct
            ? lineItem.singles +
              lineItem.cases * (lineItem.unitsPerCase ?? lineItem.caseQTY ?? 0)
            : lineItem.singles || 0;

          const categoryID = lineItem.categoryID;
          const productID = lineItem.productID;
          const itemData = {
            categoryID,
            productID,
            name: lineItem.name,
            sku: lineItem.sku,
            categoryName: lineItem.categoryName,
          };

          if (originalLocationManufacturingRef && originalFulfillmentLocation) {
            const beforeAvailable =
              originalManufacturing[categoryID]?.available?.[productID] ?? 0;
            const beforeReserved =
              originalManufacturing[categoryID]?.reserved?.[productID] ?? 0;
            const beforeTransfer =
              originalManufacturing[categoryID]?.transfer?.[productID] ?? 0;
            const afterReserved = Math.max(0, beforeReserved - itemQTY);
            const skuIDOld = `${originalFulfillmentLocation.locationID}_${this.order.brand.id}_${categoryID}_${productID}`;
            await createInventoryActivity({
              action: "Location changed – unreserve",
              item: `${itemQTY} units of ${lineItem.name} (${lineItem.sku}) unreserved at ${originalFulfillmentLocation.name} (fulfillment location changed)`,
              skuID: skuIDOld,
              itemData,
              movement: {
                type: "release",
                from: "reserved",
                to: "available",
                quantity: itemQTY,
                reason: "fulfillmentLocationUnreserve",
                reasonID: this.order.id,
              },
              snapshot: {
                before: {
                  available: beforeAvailable,
                  reserved: beforeReserved,
                  transfer: beforeTransfer,
                },
                after: {
                  available: beforeAvailable,
                  reserved: afterReserved,
                  transfer: beforeTransfer,
                },
              },
              location: {
                id: originalFulfillmentLocation.locationID,
                name: originalFulfillmentLocation.name,
              },
              order: this.order,
              user: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              extraSalesOrderData: { orderID: this.order.id },
            });
          }

          const beforeAvailableNew =
            manufacturing[categoryID]?.available?.[productID] ?? 0;
          const beforeReservedNew =
            manufacturing[categoryID]?.reserved?.[productID] ?? 0;
          const beforeTransferNew =
            manufacturing[categoryID]?.transfer?.[productID] ?? 0;
          const afterReservedNew = beforeReservedNew + itemQTY;
          const skuIDNew = `${newFulfillmentLocation.locationID}_${this.order.brand.id}_${categoryID}_${productID}`;
          await createInventoryActivity({
            action: "Location changed – reserve",
            item: `${itemQTY} units of ${lineItem.name} (${lineItem.sku}) reserved at ${newFulfillmentLocation.name} (fulfillment location changed)`,
            skuID: skuIDNew,
            itemData,
            movement: {
              type: "reservation",
              from: "available",
              to: "reserved",
              quantity: itemQTY,
              reason: "fulfillmentLocationReserve",
              reasonID: this.order.id,
            },
            snapshot: {
              before: {
                available: beforeAvailableNew,
                reserved: beforeReservedNew,
                transfer: beforeTransferNew,
              },
              after: {
                available: beforeAvailableNew,
                reserved: afterReservedNew,
                transfer: beforeTransferNew,
              },
            },
            location: {
              id: newFulfillmentLocation.locationID,
              name: newFulfillmentLocation.name,
            },
            order: this.order,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            extraSalesOrderData: { orderID: this.order.id },
          });
        }

        // Create activity for fulfillment location change
        await createActivity({
          type: "logistics",
          action: "Fulfillment location updated",
          item: `Location changed from ${
            originalFulfillmentLocation?.name ?? "—"
          } to ${newFulfillmentLocation.name}`,
          relatedTo: "salesOrders",
          relatedID: [this.order.id],
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          account: {
            id: this.order.account.id,
            name: this.order.account.name,
          },
          brand: {
            id: this.order.brand.id,
            name: this.order.brand.name,
          },
          businessImpact: "medium",
          priority: "medium",
          changeData: {
            field: "fulfillmentLocation",
            oldValue: {
              id: originalFulfillmentLocation.locationID,
              name: originalFulfillmentLocation.name,
            },
            newValue: {
              id: newFulfillmentLocation.locationID,
              name: newFulfillmentLocation.name,
            },
            inventoryTransfer: {
              itemsTransferred: this.lineItems.length,
              totalQty: this.lineItems.reduce(
                (sum, item) =>
                  sum + (item.singles + item.cases * (item.unitsPerCase ?? 0)),
                0,
              ),
            },
            orderNumber: this.order.orderNumber,
          },
        });

        addBreadcrumb("updateFulfillmentLocation: committing activity and order update batch", {
          workflow: "sales-order",
          data: { orderId: this.orderID },
        });
        await batch.commit();
      } else {
        throw new Error("Inventory Unavailable");
      }
    } catch (error) {
      throw new Error(error.message);
    }
  }

  async generateInvoice(prefetchedData = null) {
    let ordersForPreviousAccountBalance = [];
    let payableTo = {};
    let brandMetrics = {};
    let brand = {};
    let account = {};

    if (prefetchedData) {
      // Public / unauthenticated path — use pre-fetched data from cloud function
      this.order = prefetchedData.order;
      this.lineItems = prefetchedData.lineItems;
      this.payments = prefetchedData.payments;
      payableTo = prefetchedData.payableTo ?? {};
      brand = prefetchedData.brand ?? {};
      brandMetrics = prefetchedData.brandMetrics ?? {};
      account = prefetchedData.account ?? {};

      // Build ordersForPreviousAccountBalance from raw cloud function data
      const rawOrders = prefetchedData.ordersForPreviousAccountBalance ?? [];
      ordersForPreviousAccountBalance = rawOrders
        .map((salesOrder) => {
          const statusDef = store.getters.getOrderStatus.find(
            (s) => s.value === salesOrder.status,
          );
          const term = store.getters.getPaymentTerms.find(
            (t) => t.value === salesOrder.paymentTerm,
          );
          const shipSeconds = salesOrder.shipDate?.seconds ?? salesOrder.shipDate?._seconds;
          const createdSeconds = salesOrder.created?.seconds ?? salesOrder.created?._seconds;
          const updatedSeconds = salesOrder.updated?.seconds ?? salesOrder.updated?._seconds;
          var paymentDueDate = moment(shipSeconds * 1000)
            .add(term?.due ?? 0, "days")
            .toDate();

          return {
            id: salesOrder.id,
            created: moment(createdSeconds * 1000).toDate(),
            updated: moment(updatedSeconds * 1000).toDate(),
            internal: salesOrder.orderNumber?.internal,
            external: salesOrder.orderNumber?.external,
            createdBy: `${salesOrder.createdBy?.name?.first ?? ""} ${salesOrder.createdBy?.name?.last ?? ""}`,
            balance: Number(salesOrder.balance?.due),
            total: Number(salesOrder.balance?.total),
            status: statusDef,
            shipDate: moment(shipSeconds * 1000).toDate(),
            units: salesOrder.units,
            account: salesOrder.account?.name,
            paymentTerm: salesOrder.paymentTerm,
            paymentDueDate: paymentDueDate,
          };
        })
        .sort((a, b) => (a.created > b.created ? 1 : -1))
        .filter((order) => {
          const term = store.getters.getPaymentTerms.find(
            (t) => t.value === order.paymentTerm,
          );
          return moment(order.shipDate).add(term?.due ?? 0, "days").toDate() <= moment().toDate();
        });
    } else {
      // Authenticated path — fetch everything from Firestore
      await this.getOrder();
      await this.getLineItems();
      await this.getPayments();

      const ordersDoc = collection(firestore, "salesOrders");
      const relatedAccount = where("account.id", "==", this.order.account.id),
        relatedBrand = where("brand.id", "==", this.order.brand.id),
        status = where("status", "in", ["shipped", "complete"]);
      const orderQuery = query(ordersDoc, relatedAccount, relatedBrand, status);

      const orderResponse = await getDocs(orderQuery);

      if (!orderResponse.empty) {
        ordersForPreviousAccountBalance = orderResponse.docs
          .filter(
            (order) =>
              Number(order.data().balance.due) > 0 && order.id !== this.order.id,
          )
          .map((salesOrder) => {
            const status = store.getters.getOrderStatus.find(
              (status) => status.value === salesOrder.data().status,
            );

            const term = store.getters.getPaymentTerms.find(
              (paymentTerm) =>
                paymentTerm.value === salesOrder.data().paymentTerm,
            );

            var paymentDueDate = moment(
              salesOrder.data().shipDate?.seconds * 1000,
            )
              .add(term.due, "days")
              .toDate();

            return {
              id: salesOrder.id,
              created: moment(salesOrder.data().created?.seconds * 1000).toDate(),
              updated: moment(salesOrder.data().updated?.seconds * 1000).toDate(),
              internal: salesOrder.data().orderNumber.internal,
              external: salesOrder.data().orderNumber.external,
              createdBy: `${salesOrder.data().createdBy.name.first} ${
                salesOrder.data().createdBy.name.last
              }`,
              balance: Number(salesOrder.data().balance.due),
              total: Number(salesOrder.data().balance.total),
              status,
              shipDate: moment(
                salesOrder.data().shipDate?.seconds * 1000,
              ).toDate(),
              units: salesOrder.data().units,
              account: salesOrder.data().account.name,
              paymentTerm: salesOrder.data().paymentTerm,
              paymentDueDate: paymentDueDate,
            };
          })
          .sort((a, b) => (a.created > b.created ? 1 : -1))
          .filter((order) => {
            const term = store.getters.getPaymentTerms.find(
              (paymentTerm) => paymentTerm.value === order.paymentTerm,
            );
            var shouldCollect =
              moment(order.shipDate).add(term.due, "days").toDate() <=
              moment().toDate();
            return shouldCollect;
          });
      }

      // Payable To
      const payableToRef = collection(firestore, "payableTo");
      const state = where(
          "location.state.stateID",
          "==",
          this.order.location.shipping.state.stateID,
        ),
        brandWhere = where("brandID", "==", this.order.brand.id);
      const q = query(payableToRef, state, brandWhere);
      const response = await getDocs(q, limit(1));

      payableTo = {
        id: response.docs[0].id,
        ...response.docs[0].data(),
      };

      // Brand
      const brandDoc = doc(firestore, "companyBrands", this.order.brand.id);
      const brandResponse = await getDoc(brandDoc);

      // Brand Metrics
      const brandMetricsDoc = doc(
        firestore,
        "brandStats",
        `${this.order.account.id}_${this.order.brand.id}`,
      );
      const brandMetricsResponse = await getDoc(brandMetricsDoc);

      brandMetrics = brandMetricsResponse.data();

      // Account
      const accountDoc = doc(firestore, "accounts", this.order.account.id);
      const accountResponse = await getDoc(accountDoc);

      if (brandResponse.exists()) {
        brand = brandResponse.data();
      }

      if (accountResponse.exists()) {
        account = accountResponse.data();
      }
    }

    let previousAccountBalance = ordersForPreviousAccountBalance.reduce(
      (acc, order) => acc + Number(order.balance),
      0,
    );

    let currencyFormat = (value) => {
      return Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(value);
    };

    const externalOrderNumber = this.order.flags?.external
      ? this.order.orderNumber.external
      : "-";
    const internalOrderNumber = String(
        this.order.orderNumber.internal,
      ).toUpperCase(),
      internalManifestNumber = String(
        this.order.orderNumber.manifest?.length > 0
          ? this.order.orderNumber.manifest
          : "-",
      ).toUpperCase();
    const invoice = new jsPDF({ format: "letter" });
    let fileName = `${this.order.account.name.replace(/(\b[a-z](?!\s))/g, (l) =>
      l.toUpperCase(),
    )} - ${internalOrderNumber}`;

    const pageWidth = invoice.internal.pageSize.getWidth();

    // Header

    let logoLoaded = false;
    if (brand.flags?.hasLogo) {
      try {
        let img;
        if (prefetchedData?.brandLogoDataUri) {
          img = prefetchedData.brandLogoDataUri;
        } else {
          let logoRef = ref(storage, `brand/${this.order.brand.id}/logo`);
          const bytes = await getBytes(logoRef);
          const metadata = await getMetadata(logoRef);
          img =
            "data:" +
            metadata.contentType +
            ";base64," +
            new Buffer.from(bytes).toString("base64");
        }

        let imgProps = invoice.getImageProperties(img);
        let imgHeight = imgProps.height;
        let imgWidth = imgProps.width;

        let width = 35;
        let height = (imgHeight * width) / imgWidth;

        let y = 0;

        if (height < 15) {
          y = 12;
        }

        invoice.addImage(img, 94, y, width, height, "logo", "FAST");
        logoLoaded = true;
      } catch (err) {
        console.error("generateInvoice: logo fetch failed, using text fallback", err);
        logWorkflowWarning(`generateInvoice: logo fetch failed, using text fallback — ${err.message}`, {
          workflow: "sales-order",
          action: "generate-invoice-logo-fetch",
          context: { orderId: this.orderID, brandId: this.order?.brand?.id },
        });
      }
    }
    if (!logoLoaded) {
      invoice.setFontSize(24);
      invoice.setFont("helvetica", "bold");
      let brandName = (brand.name ?? "").replace(/\b\w/g, (l) => l.toUpperCase());
      invoice.text(brandName, 110, 15, "center");
    }

    // --- Setup Dynamic Y Tracker for Left Column ---
    let leftColY = 35; // Starting point for the left column headers

    // 1. Customer / Store Information
    invoice.setFontSize(10);
    invoice.setFont("helvetica", "bold");
    invoice.text("Store Information:", 10, leftColY);

    leftColY += 5; // Move down 5mm for the details

    invoice.setFont("helvetica", "normal");
    const accountName = account.name.replace(/(\b[a-z](?!\s))/g, (l) =>
      l.toUpperCase(),
    );
    const licenseName =
      account.dba?.replace(/(\b[a-z](?!\s))/g, (l) => l.toUpperCase()) ?? "-";

    let phone = "";
    var cleaned = ("" + account.phoneNumber).replace(/\D/g, "");
    var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      phone = "(" + match[1] + ") " + match[2] + "-" + match[3];
    }

    const phoneNumber = "\n" + phone;
    const license = account.license?.number
      ? "\n" + account.license.number.toUpperCase()
      : "";

    var wrappedStoreInformation = invoice.splitTextToSize(
      accountName + `\n${licenseName}` + phoneNumber + license,
      53,
    );
    invoice.text(wrappedStoreInformation, 10, leftColY);

    // Calculate the height of the printed text (approx 4.5mm per line) + add 4mm of padding
    leftColY += wrappedStoreInformation.length * 4.5 + 4;

    // 2. Customer - Shipping Address
    const accountShippingAddress = `${
      this.order.location.shipping.street.number
    } ${this.order.location.shipping.street.name.replace(/\b\w/g, (l) =>
      l.toUpperCase(),
    )}`;
    const accountShippingCityZipCode = `${this.order.location.shipping.city.replace(
      /\b\w/g,
      (l) => l.toUpperCase(),
    )}, ${this.order.location.shipping.state.abbreviation.toUpperCase()} ${
      this.order.location.shipping.zipCode
    }`;

    invoice.setFont("helvetica", "bold");
    invoice.text("Delivery Address:", 10, leftColY);

    leftColY += 5;

    invoice.setFont("helvetica", "normal");
    var wrappedShippingAddress = invoice.splitTextToSize(
      accountShippingAddress + `\n${accountShippingCityZipCode}`,
      53,
    );
    invoice.text(wrappedShippingAddress, 10, leftColY);

    leftColY += wrappedShippingAddress.length * 4.5 + 4;

    // 3. Customer - Billing Address
    const accountBillingAddress = `${
      this.order.location.billing.street.number
    } ${this.order.location.billing.street.name.replace(/\b\w/g, (l) =>
      l.toUpperCase(),
    )}`;
    const accountBillingCityZipCode = `${this.order.location.billing.city.replace(
      /\b\w/g,
      (l) => l.toUpperCase(),
    )}, ${this.order.location.billing.state.abbreviation.toUpperCase()} ${
      this.order.location.billing.zipCode
    }`;

    invoice.setFont("helvetica", "bold");
    invoice.text("Billing Address:", 10, leftColY);

    leftColY += 5;

    invoice.setFont("helvetica", "normal");
    if (account.flags.sameBilling) {
      invoice.text("Same as Delivery Address", 10, leftColY);
      leftColY += 4.5 + 4; // 1 line height + padding
    } else {
      var wrappedBillingAddress = invoice.splitTextToSize(
        accountBillingAddress + `\n${accountBillingCityZipCode}`,
        53,
      );
      invoice.text(wrappedBillingAddress, 10, leftColY);
      leftColY += wrappedBillingAddress.length * 4.5 + 4;
    }

    // 4. Buyer Information
    const buyerInfoY = leftColY;
    if (this.order.buyerInformation) {
      invoice.setFont("helvetica", "bold");
      invoice.text("Buyer Information:", 10, leftColY);

      leftColY += 5;

      invoice.setFont("helvetica", "normal");
      let buyerName = this.order.buyerInformation.name.replace("PARENT - ", "");
      buyerName = buyerName.replace(/\b\w/g, (l) => l.toUpperCase());

      invoice.text(buyerName, 10, leftColY);
      invoice.text(this.order.buyerInformation.email, 10, leftColY + 5);
    }

    // Payable To - Banking Information
    invoice.setFontSize(10);
    invoice.setFont("helvetica", "bold");
    invoice.text("Payable To:", 70, 35);
    let midColY = 40;
    if (payableTo.bankingInformation) {
      invoice.setFontSize(10);
      invoice.setFont("helvetica", "normal");

      // Check if new details format exists
      if (payableTo.bankingInformation.bank?.details) {
        const bankDetails = payableTo.bankingInformation.bank.details;
        const availableHeight = buyerInfoY - midColY - 4;

        // Use font size 10, shrink only if text is too long
        let bankFontSize = 10;
        let wrappedDetails;
        for (let fs = 10; fs >= 6; fs--) {
          invoice.setFontSize(fs);
          wrappedDetails = invoice.splitTextToSize(bankDetails, 68);
          if (wrappedDetails.length * (fs * 0.45) <= availableHeight) {
            bankFontSize = fs;
            break;
          }
          bankFontSize = fs;
        }

        // Expand line height to fill available space down to billing address end
        const naturalHeight = wrappedDetails.length * (bankFontSize * 0.45);
        const lineHeightFactor =
          naturalHeight < availableHeight
            ? availableHeight / wrappedDetails.length / (bankFontSize * 0.3528)
            : 1.15;

        invoice.setFontSize(bankFontSize);
        invoice.setLineHeightFactor(lineHeightFactor);
        invoice.text(wrappedDetails, 70, midColY);
        invoice.setLineHeightFactor(1.15);
        midColY += availableHeight + 4;
      } else {
        invoice.text(
          `Bank:  ${payableTo.bankingInformation.bank.name}`,
          70,
          midColY,
        );
        midColY += 4.5;
        invoice.text(
          `Account Number:  ${payableTo.bankingInformation.account.number}`,
          70,
          midColY,
        );
        midColY += 4.5;
        invoice.text(
          `Routing Number:  ${payableTo.bankingInformation.account.routingNumber}`,
          70,
          midColY,
        );
        midColY += 4.5 + 2;

        let street = `${
          payableTo.bankingInformation.bank.location.street.number
        } ${payableTo.bankingInformation.bank.location.street.name}${
          payableTo.bankingInformation.bank.location.street.unitNumber?.length >
          0
            ? ` #${payableTo.bankingInformation.bank.location.street.unitNumber}`
            : ""
        }`.replace(/\b\w/g, (l) => l.toUpperCase());

        let cityStateZip = `${
          payableTo.bankingInformation.bank.location.city
        }, ${payableTo.bankingInformation.bank.location.state.abbreviation.toUpperCase()} ${
          payableTo.bankingInformation.bank.location.zipCode
        }`.replace(/\b\w/g, (l) => l.toUpperCase());

        let wrappedBankAddress = invoice.splitTextToSize(
          `${street},\n${cityStateZip}`,
          68,
        );
        invoice.text(wrappedBankAddress, 70, midColY);
        midColY += wrappedBankAddress.length * 4.5 + 4;
      }
    }

    // Fulfilled By - always aligned with Buyer Information
    midColY = buyerInfoY;
    invoice.setFontSize(10);
    invoice.setFont("helvetica", "bold");
    invoice.text("Fulfilled By:", 70, midColY);
    midColY += 4;

    invoice.setFontSize(10);
    invoice.setFont("helvetica", "normal");

    const fulfilledByName = payableTo.name.replace(/\b\w/g, (l) =>
      l.toUpperCase(),
    );

    let fulfilledByPhone = "";
    const fulfilledByPhoneCleaned = ("" + payableTo.phoneNumber).replace(
      /\D/g,
      "",
    );
    const fulfilledByPhoneMatch = fulfilledByPhoneCleaned.match(
      /^(\d{3})(\d{3})(\d{4})$/,
    );
    if (fulfilledByPhoneMatch) {
      fulfilledByPhone = `(${fulfilledByPhoneMatch[1]}) ${fulfilledByPhoneMatch[2]}-${fulfilledByPhoneMatch[3]}`;
    }

    const fulfilledByLicense = payableTo.license.number.toUpperCase();

    const fulfilledByStreet =
      !payableTo.location.street?.name || !payableTo.location.street?.number
        ? "[No Address Set]"
        : `${
            payableTo.location.street.number
          } ${payableTo.location.street.name.replace(/\b\w/g, (l) =>
            l.toUpperCase(),
          )}${
            payableTo.location.unitNumber?.length
              ? ` #${payableTo.location.unitNumber}`
              : ""
          }`;

    const fulfilledByAddress = `${fulfilledByStreet}\n${payableTo.location.city.replace(
      /\b\w/g,
      (l) => l.toUpperCase(),
    )}, ${payableTo.location.state.abbreviation.toUpperCase()} ${
      payableTo.location.zipCode
    }`;

    const wrappedFulfilledByInfo = invoice.splitTextToSize(
      fulfilledByName +
        (fulfilledByPhone ? "\n" + fulfilledByPhone : "") +
        "\n" +
        fulfilledByLicense,
      68,
    );
    invoice.text(wrappedFulfilledByInfo, 70, midColY);
    midColY += wrappedFulfilledByInfo.length * 4.5 + 2;

    const wrappedFulfilledByAddress = invoice.splitTextToSize(
      fulfilledByAddress,
      68,
    );
    invoice.text(wrappedFulfilledByAddress, 70, midColY);
    midColY += wrappedFulfilledByAddress.length * 4.5;
    // Right column - Brand / Account
    invoice.setFontSize(10);
    invoice.setFont("helvetica", "bold");
    invoice.text("Brand:", 145, 35);
    invoice.setFont("helvetica", "normal");
    let rightColY = 40;

    let orderPaymentTerm =
      store.getters.getPaymentTerms.find(
        (item) => item.value === this.order.paymentTerm,
      )?.text ?? "[Error]";

    invoice.text("Payment Term", 145, rightColY);
    invoice.text(orderPaymentTerm, 205, rightColY, { align: "right" });
    rightColY += 5;

    invoice.text("External Invoice", 145, rightColY);
    invoice.text(externalOrderNumber, 205, rightColY, { align: "right" });
    rightColY += 5;

    invoice.text("Ship Date", 145, rightColY);
    if (this.order.shipDate) {
      invoice.text(
        moment(this.order.shipDate?.seconds * 1000).format("MMM DD, YYYY"),
        205,
        rightColY,
        { align: "right" },
      );
    } else {
      invoice.text("-", 205, rightColY, { align: "right" });
    }
    rightColY += 5;

    invoice.text("Created", 145, rightColY);
    invoice.text(
      moment(this.order.created?.seconds * 1000).format("MMM DD, YYYY"),
      205,
      rightColY,
      { align: "right" },
    );
    rightColY += 5;

    invoice.text("Created By", 145, rightColY);
    const createdByName =
      this.order.createdBy.name.first +
      " " +
      this.order.createdBy.name.last.charAt(0) +
      ".";
    invoice.text(
      createdByName.replace(/\b\w/g, (l) => l.toUpperCase()),
      205,
      rightColY,
      { align: "right" },
    );
    rightColY += 10; // extra gap before Account section

    invoice.setFont("helvetica", "bold");
    invoice.text("Account:", 145, rightColY);
    rightColY += 5;
    invoice.setFont("helvetica", "normal");

    let hasExecutive =
      brandMetrics.staff.executive.id &&
      brandMetrics.staff.executive.id.length > 0;
    let hasManager =
      brandMetrics.staff.manager.id && brandMetrics.staff.manager.id.length > 0;

    let phoneNumberFormat = (value) => {
      var cleaned = ("" + value).replace(/\D/g, "");
      var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return "(" + match[1] + ") " + match[2] + "-" + match[3];
      }
    };

    let firstNameWithLastInitial = (name) => {
      if (typeof name === "string") {
        return name.split(" ")[0] + " " + name.split(" ")[1]?.charAt(0) + ".";
      }
      return name.first + " " + name.last?.charAt(0) + ".";
    };

    if (hasExecutive) {
      let executivePhoneNumber = "-";
      if (prefetchedData) {
        const raw = prefetchedData.executivePhoneRaw ?? "";
        executivePhoneNumber = raw ? phoneNumberFormat(raw) ?? "-" : "-";
      } else {
        const userDoc = doc(firestore, "users", brandMetrics.staff.executive.id);
        const response = await getDoc(userDoc);
        if (response.exists()) {
          executivePhoneNumber = response.data().phoneNumber
            ? phoneNumberFormat(response.data().phoneNumber)
            : "-";
        }
      }

      invoice.setFont("helvetica", "normal");
      invoice.text("Executive", 145, rightColY);
      invoice.text(
        firstNameWithLastInitial(
          brandMetrics.staff.executive.name.replace(/\b\w/g, (l) =>
            l.toUpperCase(),
          ),
        ),
        205,
        rightColY,
        { align: "right" },
      );
      rightColY += 5;
      invoice.text(executivePhoneNumber, 205, rightColY, { align: "right" });
      rightColY += 5;
      invoice.text(brandMetrics.staff.executive.email, 205, rightColY, {
        align: "right",
      });
      rightColY += 7;
    }

    invoice.setFont("helvetica", "normal");
    if (hasManager) {
      let managerPhoneNumber = "-";
      if (prefetchedData) {
        const raw = prefetchedData.managerPhoneRaw ?? "";
        managerPhoneNumber = raw ? phoneNumberFormat(raw) ?? "-" : "-";
      } else {
        const userDoc = doc(firestore, "users", brandMetrics.staff.manager.id);
        const response = await getDoc(userDoc);
        if (response.exists()) {
          managerPhoneNumber = response.data().phoneNumber
            ? phoneNumberFormat(response.data().phoneNumber)
            : "-";
        }
      }

      invoice.setFont("helvetica", "normal");
      invoice.text("Manager", 145, rightColY);
      invoice.text(
        firstNameWithLastInitial(
          brandMetrics.staff.manager.name.replace(/\b\w/g, (l) =>
            l.toUpperCase(),
          ),
        ),
        205,
        rightColY,
        { align: "right" },
      );
      rightColY += 5;
      invoice.text(managerPhoneNumber, 205, rightColY, { align: "right" });
      rightColY += 5;
      invoice.text(brandMetrics.staff.manager.email, 205, rightColY, {
        align: "right",
      });
      rightColY += 5;
    }

    // Products divider — positioned below whichever column is tallest
    let productsTableDividerY = Math.max(leftColY, midColY, rightColY);

    invoice.setDrawColor(0);
    invoice.setFillColor("#F2F5F6");
    invoice.roundedRect(10, productsTableDividerY, 195, 13, 2, 2, "FD");

    invoice.setFont("helvetica", "bold");
    invoice.setFontSize(9);
    invoice.text(
      "AMOUNT DUE FOR THIS INVOICE",
      15,
      productsTableDividerY + 5.5,
    );
    invoice.setFont("helvetica", "normal");
    invoice.setFontSize(7);
    invoice.text(
      `Current Invoice Only • Order ${internalOrderNumber}`,
      15,
      productsTableDividerY + 9.5,
    );

    invoice.setFont("helvetica", "bold");
    invoice.setFontSize(16);
    invoice.text(
      Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(Number(this.order.balance.due)),
      200,
      productsTableDividerY + 8.5,
      {
        align: "right",
      },
    );
    invoice.setFont("helvetica", "normal");

    let getListPriceForProduct = (product) => {
      let termNeeded = this.order.paymentTerm === "cod" ? "cod" : "net";

      if (product.listPrice?.[termNeeded]) {
        return product.listPrice?.[termNeeded];
      }

      return product.price.normal;
    };

    let capitalize = (value) => {
      return value.charAt(0).toUpperCase() + value.slice(1);
    };

    let addComma = (value) => {
      return value.toLocaleString("en-US");
    };

    let products = this.lineItems
      .filter((item) => !item.promo)
      .sort((a, b) => {
        if (a.categoryName > b.categoryName) {
          return 1;
        } else if (a.categoryName === b.categoryName && a.name > b.name) {
          return 1;
        } else if (a.categoryName === b.categoryName && a.name === b.name) {
          return 0;
        } else if (a.categoryName === b.categoryName && a.name < b.name) {
          return -1;
        } else if (a.categoryName < b.categoryName) {
          return -1;
        }
      })
      .map((item) => {
        let productName = capitalize(
          item.name.replace(/\b\w/g, (l) => l.toUpperCase()),
        );
        let categoryName = capitalize(
          item.categoryName.replace(/\b\w/g, (l) => l.toUpperCase()),
        );

        let notes = item.notes?.length > 0 ? item.notes : `                 `;
        item.price.normal = getListPriceForProduct(item);
        return [
          `${productName}\n${categoryName}`,
          item.sample ? `SAMPLE\n${notes}` : notes,
          // `${item.sample ? "Yes" : "No"}`,
          addComma(item.cases),
          addComma(item.singles),
          addComma(item.qty),
          Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(item.price.normal),
          Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(item.amount - (item.tax ?? 0)),
          Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(item.tax ?? 0),
          Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(item.qty * item.amount),
          item.discount?.value ?? 0, // Case Discount Value
        ];
      });
    let promoUnits = this.lineItems.filter((item) => item.promo);
    if (promoUnits.length > 0) {
      promoUnits = promoUnits.sort((a, b) => {
        if (a.categoryName > b.categoryName) {
          return 1;
        } else if (a.categoryName === b.categoryName && a.name > b.name) {
          return 1;
        } else if (a.categoryName === b.categoryName && a.name === b.name) {
          return 0;
        } else if (a.categoryName === b.categoryName && a.name < b.name) {
          return -1;
        } else if (a.categoryName < b.categoryName) {
          return -1;
        }
      });

      products.push([
        {
          content: "",
          colSpan: 8,
          styles: { halign: "center", fillColor: 255 },
        },
      ]);
      products.push([
        {
          content: "PROMO UNITS",
          colSpan: 8,
          styles: {
            halign: "center",
            fillColor: 255,
            textColor: 0,
            fontStyle: "bold",
          },
        },
      ]);
    }

    let currency = (value) => {
      return Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(value);
    };

    promoUnits.forEach((item) => {
      let productName = capitalize(
        item.name.replace(/\b\w/g, (l) => l.toUpperCase()),
      );
      let categoryName = capitalize(
        item.categoryName.replace(/\b\w/g, (l) => l.toUpperCase()),
      );
      let notes = item.notes?.length > 0 ? item.notes : `                 `;
      item.price.normal = getListPriceForProduct(item);
      products.push([
        `${productName}\n${categoryName}`,
        item.sample ? `SAMPLE\n${notes}` : notes,
        // `${item.sample ? "Yes" : "No"}`,
        addComma(item.cases),
        addComma(item.singles),
        addComma(item.qty),
        Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        }).format(item.price.normal),
        `${currencyFormat(item.amount - (item.tax ?? 0))}`,
        `${currencyFormat(item.tax ?? 0)}`,
        `${currencyFormat(item.qty * item.amount)}`,
        0, // Case Discount Value: Promo Items can not receive case discounts
      ]);
    });
    autoTable(invoice, {
      columns: [
        { dataKey: "item", header: "Item" },
        { dataKey: "notes", header: "Notes" },
        { dataKey: "cases", header: "Cases" },
        { dataKey: "singles", header: "Singles" },
        { dataKey: "units", header: "Units" },
        { dataKey: "listPrice", header: "List Price" },
        { dataKey: "salePrice", header: "Sale Price" },
        { dataKey: "tax", header: "Tax" },
        { dataKey: "total", header: "Total" },
      ],
      body: products,
      startY: productsTableDividerY + 19,
      margin: {
        right: 10,
        left: 10,
        bottom:
          promoUnits.length > 0 && products.length - promoUnits.length <= 11
            ? 10
            : 15,
        top: 20,
      },
      theme: "striped",
      headStyles: {
        fillColor: "black",
        textColor: "white",
        fontSize: 9,
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "left" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
      },
      bodyStyles: {
        fontSize: 9,
      },
      didParseCell: (data) => {
        if (data.section === "head") {
          if (data.column.index > 1) {
            data.cell.styles.halign = "right";
          }
        }
      },
      willDrawCell: (data) => {
        if (data.section === "body") {
          if (data.column.index === 0) {
            const cell = data.cell;
            cell.text = [];
          }
        }
      },
      didDrawCell: (data) => {
        if (data.section === "body" && data.row.cells.salePrice) {
          data.row.cells.salePrice.styles.fontStyle = "bold";
        }

        if (
          data.section === "body" &&
          data.column.dataKey === "total" &&
          data.row.cells.listPrice
        ) {
          let currencyFormat = (value) => {
            return Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "usd",
            }).format(value);
          };

          let listPrice = Number(
            parseFloat(data.row.cells.listPrice.raw.replace("$", "")).toFixed(
              2,
            ),
          );
          let salePrice = Number(
            parseFloat(data.row.cells.salePrice.raw.replace("$", "")).toFixed(
              2,
            ),
          );
          let tax = Number(
            parseFloat(data.row.cells.tax.raw.replace("$", "")).toFixed(2),
          );
          salePrice = salePrice + tax;
          // console.log(data.row.cells.item.raw, listPrice, salePrice);
          if (salePrice < listPrice) {
            let discountAmount = Number(
              parseFloat(listPrice - salePrice).toFixed(2),
            );
            let totalSaved = currencyFormat(
              discountAmount *
                Number(
                  parseFloat(data.row.cells.units.raw.replace(",", "")).toFixed(
                    2,
                  ),
                ),
            );
            let caseDiscountAppliedAmount = data.row.raw[9];
            let discountString =
              caseDiscountAppliedAmount > 0
                ? `${currencyFormat(
                    caseDiscountAppliedAmount,
                  )} CASE DISCOUNT / TOTAL DISCOUNT: ${totalSaved}`
                : `TOTAL DISCOUNT: ${totalSaved}`;
            invoice.setFont("helvetica", "bolditalic");
            invoice.setFontSize(6);
            invoice.text(
              data.cell.x + data.cell.width - 2,
              data.cell.y + 9,
              discountString,
              { align: "right" },
            );
          }
        } else if (data.section === "body" && data.column.index === 0) {
          const cell = data.cell;
          const text = cell.raw ?? "";
          // cell.text = [];
          let splitInfo = cell.colSpan > 1 ? [] : text.split("\n");
          let isHeaderRow = cell.colSpan > 1;

          let { x, y, width } = cell;
          if (isHeaderRow) {
            invoice.setFont("helvetica", "bold");
            invoice.text(text.content, pageWidth / 2, y + 5, {
              align: "center",
            });
          } else {
            let cursorX = x;
            splitInfo.forEach((part, i) => {
              if (i === 0) {
                invoice.setFont("helvetica", "normal");
                invoice.text(part, cursorX + 2, y + 4.5);
                y += invoice.getTextDimensions(part).h;
              } else {
                invoice.setFont("helvetica", "italic");

                invoice.setFontSize(8);
                invoice.text(part, cursorX + 2, y + 5);
                invoice.setFont("helvetica", "normal");

                invoice.setFontSize(9);
              }
            });
          }
        }
      },
    });

    //  We need to check the `pageNumber` property of the products table to see if it
    //    overflowed past page 1.
    //
    //    - If it did, jspdf's `lastAutoTable` has a `finalY`
    //    property that we can add to the y values of all of the content after the
    //    product's table to place the content below the products table correctly.
    //
    //    - If the table did not overflow , we set this value to 0 as we
    //    do not have to modify the position of anything on the second page
    if (
      invoice.lastAutoTable.pageNumber === 1 ||
      invoice.lastAutoTable.finalY > 160
    ) {
      invoice.addPage();
      invoice.setPage(invoice.lastAutoTable.pageNumber + 1);
    }

    const productsTableFinalY =
      invoice.lastAutoTable.pageNumber === 1 ||
      invoice.lastAutoTable.finalY > 160
        ? 10
        : invoice.lastAutoTable.finalY;

    // Notes
    invoice.setFont("helvetica", "bold");
    invoice.setFontSize(10);
    invoice.text(10, 15 + productsTableFinalY, `Notes:`);
    invoice.setFont("helvetica", "italic");
    invoice.setFontSize(9);

    var wrappedNotesForDimensions = invoice.splitTextToSize(
      this.order.notes,
      100,
    );
    let notesDimensions = invoice.getTextDimensions(wrappedNotesForDimensions);
    let notesSubstring =
      this.order.notes.length < 600 && notesDimensions.h <= 52.5
        ? this.order.notes
        : this.order.notes.substr(0, 600) + "...";

    var wrappedNotes = invoice.splitTextToSize(notesSubstring, 100);
    invoice.text(wrappedNotes, 10, 25 + productsTableFinalY);

    // Delivery Preferences
    invoice.setFont("helvetica", "bold");
    invoice.setFontSize(10);
    invoice.text(10, 100 + productsTableFinalY, `Delivery Preferences:`);
    invoice.setFont("helvetica", "italic");
    invoice.setFontSize(9);

    var wrappedDeliveryPreferences = invoice.splitTextToSize(
      this.order.account.deliveryPreferences,
      pageWidth - 20,
    );
    let deliveryPreferencesDimensions = invoice.getTextDimensions(
      wrappedDeliveryPreferences,
    );
    invoice.text(wrappedDeliveryPreferences, 10, 110 + productsTableFinalY);

    // Balance
    invoice.setFont("helvetica", "normal");
    invoice.setFontSize(10);

    let totalQTY = this.lineItems.reduce((acc, item) => {
      // Handle both case-based and non-case products
      const isCaseProduct = item.unitsPerCase > 0 || item.caseQTY > 0;

      if (isCaseProduct) {
        // Case-based product: calculate using cases and singles
        return (
          acc +
          item.cases * (item.unitsPerCase || item.caseQTY || 0) +
          item.singles
        );
      } else {
        // Non-case product: use singles directly, ignore cases
        return acc + (item.singles || 0);
      }
    }, 0);

    let subTotal = this.lineItems.reduce(
      (acc, item) => acc + item.amount * item.qty,
      0,
    );

    let totalSaved = this.lineItems
      .filter((item) => item.amount < item.price.normal)
      .reduce((sum, item) => {
        let discountAmount = item.price.normal - item.amount;
        return sum + discountAmount * item.qty;
      }, 0);

    // Calculate order discount amount
    let orderDiscountAmount = 0;
    if (this.order.discount?.value) {
      let isPercentage = this.order.discount.type === "%";
      let discountValue = this.order.discount.value ?? 0;
      if (isPercentage) {
        orderDiscountAmount = (discountValue / 100) * subTotal;
      } else {
        orderDiscountAmount = discountValue;
      }
    }

    invoice.text(
      `Subtotal (Qty: ${addComma(totalQTY)})`,
      125,
      17.5 + productsTableFinalY,
    );

    invoice.text(
      Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(subTotal + totalSaved),
      205,
      17.5 + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setDrawColor("#E5E7EB");
    invoice.line(
      120,
      20 + productsTableFinalY,
      205,
      20 + productsTableFinalY,
      "S",
    );
    invoice.stroke();
    invoice.text(`List Price Discounts`, 125, 25 + productsTableFinalY);

    invoice.text(
      `${totalSaved > 0 ? "-" : ""}${Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(totalSaved)}`,
      205,
      25 + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setDrawColor("#E5E7EB");
    invoice.line(
      120,
      27.5 + productsTableFinalY,
      205,
      27.5 + productsTableFinalY,
      "S",
    );
    invoice.stroke();
    if (orderDiscountAmount > 0) {
      invoice.text(`Order Discount`, 125, 32.5 + productsTableFinalY);

      invoice.text(
        `-${Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        }).format(orderDiscountAmount)}`,
        205,
        32.5 + productsTableFinalY,
        {
          align: "right",
        },
      );
      invoice.setDrawColor("#E5E7EB");
      invoice.line(
        120,
        35 + productsTableFinalY,
        205,
        35 + productsTableFinalY,
        "S",
      );
      invoice.stroke();
    }
    invoice.text(
      "Adjusted Subtotal",
      125,
      (orderDiscountAmount > 0 ? 40 : 32.5) + productsTableFinalY,
    );
    invoice.text(
      `${Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(subTotal - orderDiscountAmount)}`,
      205,
      (orderDiscountAmount > 0 ? 40 : 32.5) + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setDrawColor("#E5E7EB");
    invoice.line(
      120,
      (orderDiscountAmount > 0 ? 42.5 : 35) + productsTableFinalY,
      205,
      (orderDiscountAmount > 0 ? 42.5 : 35) + productsTableFinalY,
      "S",
    );
    invoice.stroke();
    invoice.text(
      "Shipping",
      125,
      (orderDiscountAmount > 0 ? 47.5 : 40) + productsTableFinalY,
    );
    invoice.text(
      Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(this.order.balance.shipping),
      205,
      (orderDiscountAmount > 0 ? 47.5 : 40) + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setDrawColor("#E5E7EB");
    invoice.line(
      120,
      (orderDiscountAmount > 0 ? 50 : 42.5) + productsTableFinalY,
      205,
      (orderDiscountAmount > 0 ? 50 : 42.5) + productsTableFinalY,
      "S",
    );
    invoice.stroke();
    invoice.text(
      "Tax",
      125,
      (orderDiscountAmount > 0 ? 55 : 47.5) + productsTableFinalY,
    );
    invoice.text(
      `${Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(this.order.balance.excise)}`,
      205,
      (orderDiscountAmount > 0 ? 55 : 47.5) + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setDrawColor("#E5E7EB");
    invoice.line(
      120,
      (orderDiscountAmount > 0 ? 57.5 : 50) + productsTableFinalY,
      205,
      (orderDiscountAmount > 0 ? 57.5 : 50) + productsTableFinalY,
      "S",
    );
    invoice.stroke();
    invoice.text(
      "Payments Received",
      125,
      (orderDiscountAmount > 0 ? 62.5 : 55) + productsTableFinalY,
    );
    invoice.text(
      `${
        Number(this.order.balance.total) - Number(this.order.balance.due) > 0
          ? "-"
          : ""
      }${Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(
        Number(this.order.balance.total) - Number(this.order.balance.due),
      )}`,
      205,
      (orderDiscountAmount > 0 ? 62.5 : 55) + productsTableFinalY,
      {
        align: "right",
      },
    );

    let totalCreditsApplied = 0;
    if (totalCreditsApplied > 0) {
      invoice.text(
        "Credit Memo",
        125,
        (orderDiscountAmount > 0 ? 70 : 62.5) + productsTableFinalY,
      );
      invoice.text(
        `${totalCreditsApplied > 0 ? "-" : ""}${Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        }).format(totalCreditsApplied)}`,
        205,
        (orderDiscountAmount > 0 ? 70 : 62.5) + productsTableFinalY,
        {
          align: "right",
        },
      );
      invoice.setDrawColor("#E5E7EB");
      invoice.line(
        120,
        (orderDiscountAmount > 0 ? 72.5 : 65) + productsTableFinalY,
        205,
        (orderDiscountAmount > 0 ? 72.5 : 65) + productsTableFinalY,
        "S",
      );
      invoice.stroke();
    }
    invoice.setDrawColor(0);
    invoice.line(
      120,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 72.5
          : 65
        : totalCreditsApplied > 0
        ? 65
        : 57.5) + productsTableFinalY,
      205,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 72.5
          : 65
        : totalCreditsApplied > 0
        ? 65
        : 57.5) + productsTableFinalY,
      "S",
    );
    invoice.stroke();
    invoice.setFont("helvetica", "bold");
    invoice.text(
      "Total Due",
      125,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 77.5
          : 70
        : totalCreditsApplied > 0
        ? 70
        : 62.5) + productsTableFinalY,
    );
    invoice.text(
      Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(Number(this.order.balance.due)),
      205,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 77.5
          : 70
        : totalCreditsApplied > 0
        ? 70
        : 62.5) + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setFont("helvetica", "normal");
    invoice.setDrawColor("#E5E7EB");
    invoice.stroke();

    // Account Balance Rect
    invoice.setDrawColor(0);
    invoice.setFillColor("#F2F5F6");
    invoice.roundedRect(
      122.5,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 87.5
          : 80
        : totalCreditsApplied > 0
        ? 80
        : 72.5) + productsTableFinalY,
      85,
      15,
      2,
      2,
      "FD",
    );

    invoice.setFont("helvetica", "normal");
    invoice.setFontSize(10);
    invoice.text(
      125,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 92.5
          : 85
        : totalCreditsApplied > 0
        ? 85
        : 77.5) + productsTableFinalY,
      `Open Invoices`,
    );
    invoice.text(
      currencyFormat(previousAccountBalance),
      205,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 92.5
          : 85
        : totalCreditsApplied > 0
        ? 85
        : 77.5) + productsTableFinalY,
      {
        align: "right",
      },
    );
    invoice.setFont("helvetica", "bold");
    invoice.setFontSize(10);
    invoice.text(
      125,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 100
          : 92.5
        : totalCreditsApplied > 0
        ? 92.5
        : 85) + productsTableFinalY,
      `New Account Balance`,
    );
    invoice.text(
      Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "usd",
      }).format(previousAccountBalance + Number(this.order.balance.due)),
      205,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 100
          : 92.5
        : totalCreditsApplied > 0
        ? 92.5
        : 85) + productsTableFinalY,
      {
        align: "right",
      },
    );

    invoice.setFont("helvetica", "italic");
    invoice.setFontSize(9);
    let wrappedDisclaimer = invoice.splitTextToSize(
      "If a payment has been submitted in the last 10 days, the account balance may not reflect until it's been processed.",
      85,
    );

    invoice.text(
      wrappedDisclaimer,
      165,
      (orderDiscountAmount > 0
        ? totalCreditsApplied > 0
          ? 107.5
          : 100
        : totalCreditsApplied > 0
        ? 100
        : 92.5) + productsTableFinalY,
      {
        align: "center",
      },
    );

    let startYPosition =
      125 + productsTableFinalY + deliveryPreferencesDimensions.h;

    if (startYPosition > 225) {
      invoice.addPage();
      invoice.setPage(invoice.internal.getNumberOfPages() + 1);
      startYPosition = 20;
    }

    // Divider
    invoice.setDrawColor("#F2F5F6");
    invoice.line(10, startYPosition, 205, startYPosition, "S");
    invoice.stroke();

    //   Open Invoices
    if (ordersForPreviousAccountBalance.length > 0) {
      // Divider
      invoice.setDrawColor("#F2F5F6");
      invoice.line(10, startYPosition, 205, startYPosition, "S");
      invoice.stroke();

      //   Open Invoices
      invoice.setFontSize(10);
      invoice.setFont("helvetica", "bold");
      invoice.text("Open Invoices:", 10, startYPosition + 10);

      const orders = ordersForPreviousAccountBalance.map((item) => {
        var shipDate = moment(item.shipDate).format("MM/DD/YY");
        var paymentDueDate = moment(item.paymentDueDate).format("MM/DD/YY");
        var paymentTerm = store.getters.getPaymentTerms.find(
          (paymentTerm) => paymentTerm.value === item.paymentTerm,
        );

        let externalOrderNumber =
          item.external && item.external.length > 0
            ? item.external.toUpperCase()
            : "-";

        const dueDate = moment(item.shipDate)
          .add(paymentTerm.due, "days")
          .toDate();

        let daysPast = moment().diff(moment(dueDate), "days");
        daysPast = daysPast < 0 ? daysPast - 1 : daysPast;

        daysPast = daysPast > 0 ? daysPast : daysPast * -1;

        let daysSinceDelivery = moment()
          .startOf("day")
          .diff(moment(item.shipDate).startOf("day"), "days");

        return [
          shipDate,
          paymentDueDate,
          daysPast,
          paymentTerm.text,
          daysSinceDelivery,
          currencyFormat(item.balance),
          item.internal.toUpperCase(),
          externalOrderNumber,
        ];
      });

      autoTable(invoice, {
        head: [
          [
            "Ship Date",
            "Payment Due Date",
            "Days Past Due",
            "Payment Term",
            "Days Since Delivery",
            "Balance",
            "Internal Order #",
            "External Order #",
          ],
        ],
        body: orders,
        startY: startYPosition + 15,
        margin: {
          right: 10,
          left: 10,
          bottom: 15,
          top: 20,
        },
        theme: "striped",
        headStyles: {
          fillColor: "#4261c7",
          textColor: "white",
          fontSize: 9,
        },
        bodyStyles: {
          fontSize: 9,
        },
      });

      startYPosition = invoice.lastAutoTable.finalY + 10;
    }

    // External Links Section (this remains as it was)
    if (payableTo.invoiceURLData?.url?.length) {
      if (startYPosition > 225) {
        invoice.addPage();
        invoice.setPage(invoice.internal.getNumberOfPages());
      }

      startYPosition = startYPosition > 225 ? 20 : startYPosition;

      // Divider
      invoice.setDrawColor("#F2F5F6");
      invoice.line(10, startYPosition, 205, startYPosition, "S");
      invoice.stroke();

      // External Links
      invoice.setFontSize(10);
      invoice.setFont("helvetica", "bold");
      invoice.text("External Links:", 10, startYPosition + 10);

      let QRCode = require("qrcode");
      let opts = {
        errorCorrectionLevel: "H",
        type: "image/jpeg",
        quality: 0.3,
        margin: 1,
        color: {
          dark: "#000000FF",
          light: "#FFFFFFFF",
        },
      };

      QRCode.toDataURL(payableTo.invoiceURLData.url, opts, function (err, url) {
        if (err) throw err;

        invoice.addImage(
          url,
          10,
          startYPosition + 15,
          20,
          20,
          "licenseeURL",
          "FAST",
        );
      });

      invoice.setFont("helvetica", "normal");
      invoice.setFontSize(9);

      invoice.text(payableTo.invoiceURLData.url, 10, startYPosition + 40);

      let wrappedDescription = invoice.splitTextToSize(
        payableTo.invoiceURLData.description,
        pageWidth - 20,
      );
      invoice.text(wrappedDescription, 10, startYPosition + 45);
    }

    if (payableTo.invoiceURLData?.url?.length) {
      if (startYPosition > 225) {
        invoice.addPage();
        invoice.setPage(invoice.internal.getNumberOfPages());
      }

      startYPosition = startYPosition > 225 ? 20 : startYPosition;

      // Divider
      invoice.setDrawColor("#F2F5F6");
      invoice.line(10, startYPosition, 205, startYPosition, "S");
      invoice.stroke();

      //   External Links
      invoice.setFontSize(10);
      invoice.setFont("helvetica", "bold");
      invoice.text("External Links:", 10, startYPosition + 10);

      let QRCode = require("qrcode");
      let opts = {
        errorCorrectionLevel: "H",
        type: "image/jpeg",
        quality: 0.3,
        margin: 1,
        color: {
          dark: "#000000FF",
          light: "#FFFFFFFF",
        },
      };

      QRCode.toDataURL(payableTo.invoiceURLData.url, opts, function (err, url) {
        if (err) throw err;

        invoice.addImage(
          url,
          10,
          startYPosition + 15,
          20,
          20,
          "licenseeURL",
          "FAST",
        );
      });

      invoice.setFont("helvetica", "normal");
      invoice.setFontSize(9);

      invoice.text(payableTo.invoiceURLData.url, 10, startYPosition + 40);

      let wrappedDescription = invoice.splitTextToSize(
        payableTo.invoiceURLData.description,
        pageWidth - 20,
      );
      invoice.text(wrappedDescription, 10, startYPosition + 45);
    }

    let totalPromotionDiscounts = this.lineItems
      .filter((item) => item.promo)
      .reduce((acc, item) => acc + item.price.normal * item.qty, 0);

    // Promo Unit Totals
    if (totalPromotionDiscounts > 0) {
      invoice.setFontSize(10);
      invoice.setTextColor("#0277BD");
      invoice.text("Total promo value: ", 80, 265, { align: "left" });
      invoice.text(currencyFormat(totalPromotionDiscounts), 130, 265, {
        align: "right",
      });
    }
    invoice.setTextColor("black");

    const QRCode = require("qrcode");
    const customerOrderQrOpts = {
      errorCorrectionLevel: "L",
      type: "image/jpeg",
      quality: 0.3,
      margin: 1,
      color: {
        dark: "#000000FF",
        light: "#FFFFFFFF",
      },
    };
    let customerOrderQrDataUrl = null;
    try {
      // Same path as route `salesOrder-customer-view` (avoid importing router from utils).
      const customerPublicOrderUrl = `${window.location.origin}/salesOrderPublic/${this.order.id}`;
      customerOrderQrDataUrl = await QRCode.toDataURL(
        customerPublicOrderUrl,
        customerOrderQrOpts,
      );
    } catch (err) {
      console.error("SalesOrderClient generateInvoice: customer order QR failed", err);
      logWorkflowWarning(`generateInvoice: customer order QR generation failed — ${err.message}`, {
        workflow: "sales-order",
        action: "generate-invoice-qr-code",
        context: { orderId: this.orderID },
      });
    }

    // END
    var pageCount = invoice.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      invoice.setPage(i);
      invoice.setFontSize(14);
      invoice.setFont("helvetica", "bold");
      invoice.text("invoice".toUpperCase(), 10, 10);
      invoice.setFont("helvetica", "normal");
      invoice.setFontSize(10);
      var orderNumber = invoice.splitTextToSize(
        `Order #: ${internalOrderNumber}`,
        70,
      );
      invoice.text(10, 15, orderNumber);
      invoice.setFont("helvetica", "normal");
      var manifestNumber = invoice.splitTextToSize(
        `Manifest #: ${internalManifestNumber}`,
        80,
      );
      invoice.text(10, 20, manifestNumber);
      invoice.setFont("helvetica", "normal");
      invoice.setFontSize(8);
      invoice.text(
        110,
        270,
        `Page ${
          invoice.internal.getCurrentPageInfo().pageNumber
        } / ${pageCount}`,
        {
          align: "center",
        },
      );

      if (i === 1 && customerOrderQrDataUrl) {
        const qrSize = 12;
        const qrX = pageWidth - 23;
        const qrY = 13;
        invoice.addImage(
          customerOrderQrDataUrl,
          "JPEG",
          qrX,
          qrY,
          qrSize,
          qrSize,
          "customerOrderQr",
          "FAST",
        );

        invoice.setFont("helvetica", "normal");
        invoice.setFontSize(8);
        // Right-align to QR's right edge; slight upward nudge (~1px @ 96dpi in mm).
        invoice.text(qrX + qrSize, qrY + qrSize + 5 - 0.26, "View Invoice", {
          align: "right",
        });
      }
    }

    let externalNumber = this.order.flags.external
      ? ` (${this.order.orderNumber.external})`
      : "";

    fileName = `${this.order.account.name.replace(/(\b[a-z](?!\s))/g, (l) =>
      l.toUpperCase(),
    )} - ${internalOrderNumber}${externalNumber}`;

    // invoice.output("dataurlnewwindow");      //  Opens in a new tab, doesn't work on iOS/Safari
    invoice.save(`${fileName}.pdf`);
  }

  async transitionAction(status) {
    addBreadcrumb("transitionAction: dispatching", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        fromStatus: this.order?.status,
        toStatus: status,
        balance: this.order?.balance,
      },
    });
    switch (status) {
      case "cancelled":
      case "rejected":
        await this.cancelledAction();
        this.transitionOrderStatus("cancelled");
        break;
      case "draft":
        this.transitionOrderStatus("draft");
        break;
      case "submitted":
        this.transitionOrderStatus("submitted");
        break;
      case "accepted":
        this.transitionOrderStatus("accepted");
        break;
      case "deliveryConfirmation":
        this.transitionOrderStatus("deliveryConfirmation");
        break;
      case "fulfilled":
        this.transitionOrderStatus("fulfilled");
        break;
      case "shipped":
        this.transitionOrderStatus("shipped");
        break;
      case "complete":
        this.transitionOrderStatus("complete");
        break;
      default:
        break;
    }
  }

  // MARK: STATUS TRANSITIONS

  // [CANCELLED]
  async cancelledAction(cancelReason) {
    addBreadcrumb("cancelledAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    await this.getOrder();
    await this.getLineItems();

    if (this.order?.status === "cancelled") {
      return;
    }

    // Atomic claim: only one concurrent request can transition to cancelled.
    const salesOrderDocRef = doc(firestore, "salesOrders", this.order.id);
    const applied = await runTransaction(firestore, async (transaction) => {
      const orderSnap = await transaction.get(salesOrderDocRef);
      if (!orderSnap.exists() || orderSnap.data().status === "cancelled") {
        return false;
      }
      transaction.update(salesOrderDocRef, {
        status: "cancelled",
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
      });
      return true;
    });

    if (!applied) {
      return;
    }

    const batch = writeBatch(firestore);
    const brandStatsDoc = doc(
      firestore,
      "brandStats",
      `${this.order.account.id}_${this.order.brand.id}`,
    );

    const salesOrderDoc = salesOrderDocRef;

    const stateDoc = doc(
      firestore,
      "states",
      this.order.location.shipping.state.stateID,
    );
    const stateResponse = await getDoc(stateDoc);

    let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

    if (this.order.fulfillmentLocation) {
      fulfillmentLocation = this.order.fulfillmentLocation.id;
    }

    const locationRef = doc(
      firestore,
      "locationManufacturing",
      `${fulfillmentLocation}_${this.order.brand.id}`,
    );

    // Get current inventory data for activity tracking
    const inventoryCheck = await getDoc(locationRef);
    const inventoryData = inventoryCheck.exists()
      ? inventoryCheck.data().manufacturing || {}
      : {};

    const field = this.order.status === "fulfilled" ? "available" : "reserved",
      direction = this.order.status === "fulfilled" ? 1 : -1;
    const cancelActivityMap = {};
    for (const li of this.lineItems) {
      const key = `${li.categoryID}_${li.productID}`;
      if (!cancelActivityMap[key]) {
        cancelActivityMap[key] = { totalQty: 0, lineItem: li };
      }
      cancelActivityMap[key].totalQty += li.qty ?? 0;
    }

    // Reset inventory
    for (let index = 0; index < this.lineItems.length; index++) {
      let lineItem = this.lineItems[index];

      if (lineItem.productLots?.length) {
        for (let i = 0; i < lineItem.productLots.length; i++) {
          const productLot = lineItem.productLots[i];
          const productLotDoc = doc(firestore, "productLots", productLot.id);
          const productLotResponse = await getDoc(productLotDoc);

          if (productLotResponse.exists()) {
            const productLotData = productLotResponse.data();
            let availableUnits = productLotResponse.data().units;

            if (productLotData.hasOwnProperty("available")) {
              availableUnits =
                Number(productLotResponse.data().available) + productLot.units;
            }

            const newRelatedLineItems =
              productLotData.relatedLineItems?.filter(
                (item) => item.id !== lineItem.id,
              ) ?? [];

            let foundProductLot = this.response.productLots.find(
              (item) => item.id === productLot.id,
            );
            let foundLocationData = foundProductLot?.locationData?.find(
              (item) => item.id === this.order.fulfillmentLocation.id,
            );
            if (
              this.order.fulfillmentLocation?.id?.length &&
              foundLocationData
            ) {
              let locationDataDoc = doc(
                firestore,
                "productLots",
                productLot.id,
                "locationData",
                foundLocationData.id,
              );
              batch.set(
                locationDataDoc,
                {
                  available: increment(productLot.units),
                  updated: serverTimestamp(),
                  lastUpdated: {
                    name: store.getters.getUser.name,
                    uid: store.getters.getUser.uid,
                  },
                },
                { merge: true },
              );
            }

            await updateDoc(
              productLotDoc,
              {
                active: availableUnits > 0,
                available: availableUnits,
                relatedLineItems: newRelatedLineItems,
                updated: serverTimestamp(),
                lastUpdated: {
                  name: store.getters.getUser.name,
                  uid: store.getters.getUser.uid,
                },
              },
              { merge: true },
            );
          }

          let lineItemDoc = doc(
            firestore,
            "salesOrders_lineItems",
            lineItem.id,
          );

          batch.set(
            lineItemDoc,
            {
              productLots: [],
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
            },
            { merge: true },
          );
        }
      }
    }

    // Location manufacturing: one write per product (same product, multiple lines = one aggregated update)
    for (const key of Object.keys(cancelActivityMap)) {
      const agg = cancelActivityMap[key];
      const { categoryID, productID } = agg.lineItem;
      batch.set(
        locationRef,
        {
          manufacturing: {
            [categoryID]: {
              [field]: {
                [productID]: increment(direction * agg.totalQty),
              },
            },
          },
        },
        { merge: true },
      );
    }

    // Order
    const oldStatus = store.getters.getOrderStatus.find(
        (option) => option.value === this.order.status,
      ),
      newStatus = store.getters.getOrderStatus.find(
        (option) => option.value === "cancelled",
      );

    batch.set(
      salesOrderDoc,
      {
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        productLots: [],
        status: "cancelled",
        records: {
          cancelled: moment().startOf("day").add(12, "hours").toDate(),
        },
      },
      { merge: true },
    );
    // External - Update Status
    if (this.orderIsExternal) {
      switch (this.externalOrderType) {
        case "leaflink":
          if (this.externalUpdates.leaflink.includes("statusUpdated")) {
            let transition =
              this.order.status === "draft" ? "cancel" : "reject";
            addBreadcrumb(`leaflink: transitionOrder ${transition} (cancelled)`, {
              workflow: "sales-order",
              data: { orderId: this.orderID, transition, fromStatus: this.order?.status },
            });
            try {
              await this.leaflinkClient.transitionOrder(transition);
            } catch (error) {
              logWorkflowWarning(`LeafLink: transitionOrder("${transition}") (cancelled) failed — ${error.message}`, {
                workflow: "sales-order",
                action: "leaflink-cancelled-transition",
                context: { orderId: this.orderID, transition, balance: this.order?.balance },
              });
              throw error;
            }
          }
          break;
        default:
          break;
      }
    }

    // Create activity for order cancellation
    await createActivity({
      type: "status",
      action: "Order cancelled",
      item: `Order #${this.order.orderNumber.internal.toUpperCase()} cancelled - Status changed from ${
        oldStatus.text
      } to ${newStatus.text}${
        cancelReason ? ` - Reason: ${cancelReason}` : ""
      }`,
      relatedTo: "salesOrders",
      relatedID: [this.order.id],
      user: {
        name: store.getters.getUser.name,
        uid: store.getters.getUser.uid,
      },
      account: {
        id: this.order.account.id,
        name: this.order.account.name,
      },
      brand: {
        id: this.order.brand.id,
        name: this.order.brand.name,
      },
      businessImpact: "high",
      priority: "high",
      changeData: {
        field: "status",
        oldValue: {
          text: oldStatus.text,
          value: oldStatus.value,
          color: oldStatus.color,
        },
        newValue: {
          text: newStatus.text,
          value: newStatus.value,
          color: newStatus.color,
        },
        reason: cancelReason,
        orderNumber: this.order.orderNumber,
      },
    });

    const brandStats = await getDoc(brandStatsDoc);
    if (brandStats.data().metrics.closeDate?.order === this.orderID) {
      batch.set(
        brandStatsDoc,
        {
          metrics: {
            closeDate: null,
          },
        },
        { merge: true },
      );
    }

    addBreadcrumb("cancelledAction: committing batch", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        lineItemCount: this.lineItems.length,
        balance: this.order?.balance,
        fromStatus: this.order?.status,
      },
    });
    await batch.commit();

    // Inventory activities: one per product (same product, multiple lines = one aggregated activity)
    for (const key of Object.keys(cancelActivityMap)) {
      const agg = cancelActivityMap[key];
      const { categoryID, productID } = agg.lineItem;
      const skuID = `${fulfillmentLocation}_${this.order.brand.id}_${categoryID}_${productID}`;
      const currentAvailable =
        inventoryData[categoryID]?.available?.[productID] ?? 0;
      const currentReserved =
        inventoryData[categoryID]?.reserved?.[productID] ?? 0;
      const currentPending =
        inventoryData[categoryID]?.pending?.[productID] ?? 0;
      const currentTransfer =
        inventoryData[categoryID]?.transfer?.[productID] ?? 0;
      const currentInTesting =
        inventoryData[categoryID]?.inTesting?.[productID] ?? 0;
      const fromState =
        this.order.status === "fulfilled" ? "fulfilled" : "reserved";
      const toState = "available";
      await createInventoryActivity({
        action: "Inventory released",
        item: `${agg.totalQty} units of ${agg.lineItem.name} (${agg.lineItem.sku}) released from cancelled Order #${this.order.orderNumber.internal}`,
        skuID,
        itemData: agg.lineItem,
        movement: {
          type: "release",
          from: fromState,
          to: toState,
          quantity: agg.totalQty,
          reason: "salesOrderCancelled",
          reasonID: this.orderID,
        },
        snapshot: {
          before: {
            available: currentAvailable,
            reserved: currentReserved,
            pending: currentPending,
            transfer: currentTransfer,
            inTesting: currentInTesting,
          },
        },
        location: {
          id: fulfillmentLocation,
          name:
            this.order.fulfillmentLocation?.name ||
            this.order.location.shipping.name,
        },
        order: this.order,
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        extraSalesOrderData: {
          cancelReason: cancelReason,
        },
      });
    }
  }

  // [PREVIOUS]
  async previousAction(order, reason) {
    addBreadcrumb("previousAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    await this.getOrder();
    await this.getLineItems();

    if (
      this.order?.status === "shipped" &&
      !this._hasRecordsShipped()
    ) {
      throw new Error(
        `Order #${this.order?.orderNumber?.internal ?? this.orderID} is marked shipped but was never fully shipped (records.shipped missing). Repair the incomplete ship before moving status backward — reverting now would corrupt account balance.`,
      );
    }

    const PREVIOUS_STATUS_MAP = {
      complete: "shipped",
      shipped: "fulfilled",
      fulfilled: "deliveryConfirmation",
      deliveryConfirmation: "accepted",
      accepted: "submitted",
      submitted: "draft",
      draft: "cancelled",
    };
    const expectedCurrentStatus = this.order?.status;
    const targetStatus = PREVIOUS_STATUS_MAP[expectedCurrentStatus];

    if (!targetStatus) {
      return;
    }

    // Atomic claim: only one concurrent request can perform this revert.
    const salesOrderDocRef = doc(firestore, "salesOrders", this.order.id);
    const applied = await runTransaction(firestore, async (transaction) => {
      const orderSnap = await transaction.get(salesOrderDocRef);
      const current = orderSnap.exists() ? orderSnap.data().status : null;
      if (current !== expectedCurrentStatus) {
        return false;
      }
      transaction.update(salesOrderDocRef, {
        status: targetStatus,
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
      });
      return true;
    });

    if (!applied) {
      return;
    }

    // Ensure we have the latest line item qtys (e.g. after user decreased line qty before rollback)
    await this.getLineItems();

    try {
      const batch = writeBatch(firestore);
      const brandStatsDoc = doc(
        firestore,
        "brandStats",
        `${this.order.account.id}_${this.order.brand.id}`,
      );

      const salesOrderDoc = salesOrderDocRef;

      let updates = [];
      const stateDoc = doc(
        firestore,
        "states",
        this.order.location.shipping.state.stateID,
      );

      const stateResponse = await getDoc(stateDoc);
      let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

      if (this.order.fulfillmentLocation) {
        fulfillmentLocation = this.order.fulfillmentLocation.id;
      }

      const locationRef = doc(
        firestore,
        "locationManufacturing",
        `${fulfillmentLocation}_${this.order.brand.id}`,
      );

      // Get current inventory data for activity tracking (needed for fulfilled case)
      const inventoryDoc = await getDoc(locationRef);
      const inventoryData = inventoryDoc.exists() ? inventoryDoc.data() : {};

      let status = "shipped";
      let leaflinkStatus = null;
      let transitionLeaflink = true;

      switch (order.fullStatus.value) {
        case "complete":
          status = "shipped";
          leaflinkStatus = "shipped";
          break;
        case "shipped":
          status = "fulfilled";
          leaflinkStatus = "fulfilled";
          order.lineItems.forEach((item) => {
            item.flags.delivered = false;
          });

          // Account Balance — atomic decrement so concurrent ship/unship cannot clobber AR
          const dueDelta = Number(
            parseFloat(Number(this.order.balance.due)).toFixed(2),
          );
          if (!Number.isFinite(dueDelta)) {
            throw new Error(
              "Invalid order balance for brandStats unship update",
            );
          }

          batch.set(
            brandStatsDoc,
            {
              metrics: {
                balance: increment(-dueDelta),
              },
            },
            { merge: true },
          );

          break;
        case "fulfilled":
          status = "deliveryConfirmation";
          leaflinkStatus = "accepted";

          // Use current line item qtys (getLineItems() was called after transaction).
          // If user edited line qty (e.g. 4→2) before reverting, rollback shows that diff (e.g. -2 available, +2 reserved).
          // If no edit, rollback shows the original fulfilled amount (e.g. -4 available, +4 reserved).
          for (const item of this.lineItems) {
            batch.set(
              locationRef,
              {
                manufacturing: {
                  [item.categoryID]: {
                    available: {
                      [item.productID]: increment(item.qty),
                    },
                    reserved: {
                      [item.productID]: increment(item.qty),
                    },
                  },
                },
              },
              { merge: true },
            );

            // Track inventory movement for this SKU
            const skuID = `${fulfillmentLocation}_${this.order.brand.id}_${item.categoryID}_${item.productID}`;
            const currentAvailable =
              inventoryData.manufacturing?.[item.categoryID]?.available?.[
                item.productID
              ] || 0;
            const currentReserved =
              inventoryData.manufacturing?.[item.categoryID]?.reserved?.[
                item.productID
              ] || 0;
            const currentPending =
              inventoryData.manufacturing?.[item.categoryID]?.pending?.[
                item.productID
              ] || 0;
            const currentTransfer =
              inventoryData.manufacturing?.[item.categoryID]?.transfer?.[
                item.productID
              ] || 0;
            const currentInTesting =
              inventoryData.manufacturing?.[item.categoryID]?.inTesting?.[
                item.productID
              ] || 0;

            await createInventoryActivity({
              action: "Inventory restored to reserved",
              item: `${item.qty} units of ${item.name} (${item.sku}) restored to reserved from fulfilled Order #${this.order.orderNumber.internal}`,
              skuID,
              itemData: item,
              movement: {
                type: "restoration",
                from: "fulfilled",
                to: "reserved",
                quantity: item.qty,
                reason: "salesOrderRestored",
                reasonID: this.orderID,
              },
              snapshot: {
                before: {
                  available: currentAvailable,
                  reserved: currentReserved,
                  pending: currentPending,
                  transfer: currentTransfer,
                  inTesting: currentInTesting,
                },
                // "after" is calculated dynamically from "before" + movement.quantity in the display
              },
              location: {
                id: fulfillmentLocation,
                name:
                  this.order.fulfillmentLocation?.name ||
                  this.order.location.shipping.name,
              },
              order: this.order,
              user: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              extraSalesOrderData: {
                orderID: this.orderID,
              },
            });
          }
          break;
        case "deliveryConfirmation":
          status = "accepted";
          order.lineItems.forEach((item) => {
            item.flags.fulfilled = false;
            delete item.fulfilled;
          });
          transitionLeaflink = false;
          break;
        case "accepted":
          status = "submitted";
          transitionLeaflink = false;
          break;
        case "submitted":
          status = "draft";
          break;
        case "draft":
          status = "cancelled";
          break;
        default:
          break;
      }

      batch.set(
        salesOrderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          status,
          lineItems: order.lineItems,
        },
        { merge: true },
      );

      let transition = null;
      switch (leaflinkStatus) {
        case "shipped":
          transition = "ship";
          break;
        case "fulfilled":
          transition = "fulfill";
          break;
        case "accepted":
          transition = "accept";
          break;
        case null:
          break;
      }

      // External - Update Status
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (
              transitionLeaflink &&
              transition?.length &&
              this.externalUpdates.leaflink.includes("statusUpdated")
            ) {
              addBreadcrumb(`leaflink: transitionOrder ${transition} (previousAction)`, {
                workflow: "sales-order",
                data: { orderId: this.orderID, transition, fromStatus: this.order?.status },
              });
              try {
                await this.leaflinkClient.transitionOrder(transition);
              } catch (error) {
                logWorkflowWarning(`LeafLink: transitionOrder("${transition}") (previousAction) failed — ${error.message}`, {
                  workflow: "sales-order",
                  action: "leaflink-previous-transition",
                  context: { orderId: this.orderID, transition, balance: this.order?.balance },
                });
                throw error;
              }
            }
            break;
          default:
            break;
        }
      }

      const oldStatus = store.getters.getOrderStatus.find(
          (option) => option.value === this.order.status,
        ),
        newStatus = store.getters.getOrderStatus.find(
          (option) => option.value === status,
        );

      // Create activity for status rollback
      await createActivity({
        type: "status",
        action: "Order status rolled back",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} status rolled back from ${
          oldStatus.text
        } to ${newStatus.text}${reason ? ` - Reason: ${reason}` : ""}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "status",
          oldValue: {
            text: oldStatus.text,
            value: oldStatus.value,
            color: oldStatus.color,
          },
          newValue: {
            text: newStatus.text,
            value: newStatus.value,
            color: newStatus.color,
          },
          reason: reason,
          orderNumber: this.order.orderNumber,
        },
      });

      addBreadcrumb("previousAction: committing batch", {
        workflow: "sales-order",
        data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
      });
      await batch.commit();

      // Stepped back complete → shipped: bonus data no longer valid. Never throws.
      if (order.fullStatus.value === "complete" && status === "shipped") {
        try {
          await this._removeFirstPurchases();
        } catch (firstPurchaseError) {
          logWorkflowWarning(
            `firstPurchases removal failed — ${firstPurchaseError.message}`,
            {
              workflow: "sales-order",
              action: "remove-first-purchases",
              context: {
                orderId: this.orderID,
                accountId: this.order.account?.id,
                brandId: this.order.brand?.id,
              },
            },
          );
        }
      }
    } catch (error) {
      throw error;
    }
  }

  // [COMPLETE]
  async completeAction() {
    addBreadcrumb("completeAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    console.log("completeAction");
    await this.getOrder();
    await this.getLineItems();

    if (this._isIncompleteShip()) {
      throw new Error(
        `Order #${this.order?.orderNumber?.internal ?? this.orderID} is ${this.order.status} but was never fully shipped (records.shipped missing). Finish or repair the ship before marking complete.`,
      );
    }

    try {
      const batch = writeBatch(firestore);
      const orderDoc = doc(firestore, "salesOrders", this.order.id);
      console.log(this.order);
      const oldValue = store.getters.getOrderStatus.find(
          (status) => status.value === this.order.status,
        ),
        newValue = store.getters.getOrderStatus.find(
          (status) => status.value === "complete",
        );

      console.log(oldValue, newValue);

      // Stamp completion date so the first-purchase sync below has a consistent completionDate.
      const completionDate = moment().startOf("day").add(12, "hours").toDate();

      // Update order — dotted records path so we do not wipe records.shipped (set+merge is shallow).
      batch.set(
        orderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          status: newValue.value,
        },
        { merge: true },
      );
      batch.update(orderDoc, {
        "records.completed": completionDate,
      });

      // Create activity for order acceptance
      await createActivity({
        type: "status",
        action: "Order completed",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} status updated to complete`,
        relatedTo: "salesOrders",
        relatedID: [orderDoc.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "completionStatus",
          oldValue: {
            text: oldValue.text,
            value: oldValue.value,
            color: oldValue.color,
          },
          newValue: {
            text: newValue.text,
            value: newValue.value,
            color: newValue.color,
          },
          orderNumber: this.order.orderNumber.internal,
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
        },
      });

      // Try nudool operations first
      addBreadcrumb("completeAction: committing batch", {
        workflow: "sales-order",
        data: { orderId: this.orderID, balance: this.order?.balance },
      });
      await batch.commit();

      // Order marked complete. Never throws.
      try {
        await this._createFirstPurchases(completionDate);
      } catch (firstPurchaseError) {
        logWorkflowWarning(
          `firstPurchases creation failed — ${firstPurchaseError.message}`,
          {
            workflow: "sales-order",
            action: "create-first-purchases",
            context: {
              orderId: this.orderID,
              accountId: this.order.account?.id,
              brandId: this.order.brand?.id,
            },
          },
        );
      }

      console.log("this.orderIsExternal", this.orderIsExternal);

      // External - Update Status (only after nudool commit succeeds)
      if (this.orderIsExternal) {
        try {
          switch (this.externalOrderType) {
            case "leaflink":
              if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                let orderData = await this.leaflinkClient.getOrder();
                if (orderData?.status.toLowerCase() === "shipped") {
                  await this.leaflinkClient.transitionOrder("complete");
                }
              }
              break;
            default:
              break;
          }
        } catch (e) {
          console.log("Leaflink transition failed:", e);
          // Note: nudool has already been committed successfully at this point
          // Consider adding rollback logic or notification here if needed
          logWorkflowWarning(`LeafLink: transitionOrder (completeAction) failed — ${e.message}`, {
            workflow: "sales-order",
            action: "leaflink-complete-transition",
            context: { orderId: this.orderID, balance: this.order?.balance },
          });
          throw e;
        }
      }
    } catch (error) {
      throw error;
    }
  }

  // [SHIPPED]
  async rescheduleShippedAction(reason, shipDate = null) {
    addBreadcrumb("rescheduleShippedAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    console.log("rescheduleShippedAction");
    await this.getOrder();
    await this.getLineItems();

    if (this.order?.status !== "shipped") {
      return;
    }

    let updates = [];

    const batch = writeBatch(firestore);
    const salesOrderDoc = doc(firestore, "salesOrders", this.orderID);

    // Order
    batch.set(
      salesOrderDoc,
      {
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        rescheduled: increment(1),
        status: "deliveryConfirmation",
        shipDate: null,
      },
      { merge: true },
    );

    const stateDoc = doc(
      firestore,
      "states",
      this.order.location.shipping.state.stateID,
    );
    const stateResponse = await getDoc(stateDoc);
    let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

    if (this.order.fulfillmentLocation) {
      fulfillmentLocation = this.order.fulfillmentLocation.id;
    }

    const locationRef = doc(
      firestore,
      "locationManufacturing",
      `${fulfillmentLocation}_${this.order.brand.id}`,
    );

    this.lineItems.forEach((item) => {
      batch.set(
        locationRef,
        {
          manufacturing: {
            [item.categoryID]: {
              available: {
                [item.productID]: increment(item.qty),
              },
              reserved: {
                [item.productID]: increment(item.qty),
              },
            },
          },
        },
        { merge: true },
      );
    });

    // External - Ship date

    if (this.orderIsExternal) {
      switch (this.externalOrderType) {
        case "leaflink":
          // let shipDateMoment = moment(shipDate);
          // let shipDate = moment(shipDateMoment.clone().format("MM/DD/YYYY"))
          //   .add(9, "hours")
          //   .tz("America/Los_Angeles");
          let patchData = {
            ship_date: null,
          };
          if (this.externalUpdates.leaflink.includes("orderUpdated")) {
            addBreadcrumb("leaflink: updateOrderData ship_date null (reschedule)", {
              workflow: "sales-order",
              data: { orderId: this.orderID, orderStatus: this.order?.status },
            });
            try {
              await this.leaflinkClient.updateOrderData(patchData);
            } catch (error) {
              logWorkflowWarning(`LeafLink: updateOrderData (reschedule) failed — ${error.message}`, {
                workflow: "sales-order",
                action: "leaflink-reschedule-update-order",
                context: { orderId: this.orderID, balance: this.order?.balance },
              });
              throw error;
            }
          }
          if (this.externalUpdates.leaflink.includes("statusUpdated")) {
            addBreadcrumb("leaflink: transitionOrder accept (reschedule)", {
              workflow: "sales-order",
              data: { orderId: this.orderID, orderStatus: this.order?.status },
            });
            try {
              await this.leaflinkClient.transitionOrder("accept");
            } catch (error) {
              logWorkflowWarning(`LeafLink: transitionOrder("accept") (reschedule) failed — ${error.message}`, {
                workflow: "sales-order",
                action: "leaflink-reschedule-transition-accept",
                context: { orderId: this.orderID, balance: this.order?.balance },
              });
              throw error;
            }
          }
          break;
        default:
          break;
      }
    }

    // Ledger
    updates.push(`Reschedule reason: "${reason}"`);

    // Create activity for order rescheduling
    await createActivity({
      type: "logistics",
      action: "Order rescheduled",
      item: `Order rescheduled: ${updates.join(", ")}`,
      relatedTo: "salesOrders",
      relatedID: [this.order.id],
      user: {
        name: store.getters.getUser.name,
        uid: store.getters.getUser.uid,
      },
      account: {
        id: this.order.account.id,
        name: this.order.account.name,
      },
      brand: {
        id: this.order.brand.id,
        name: this.order.brand.name,
      },
      businessImpact: "medium",
      priority: "medium",
      changeData: {
        field: "shipDate",
        oldValue: this.order.shipDate,
        newValue: null,
        reason: reason,
        from: "shipped",
        to: "deliveryConfirmation",
        rescheduleCount: this.order.rescheduled
          ? this.order.rescheduled + 1
          : 1,
        inventoryReturned: this.lineItems.reduce(
          (sum, item) => sum + item.qty,
          0,
        ),
      },
    });

    addBreadcrumb("rescheduleShippedAction: committing batch", {
      workflow: "sales-order",
      data: { orderId: this.orderID, balance: this.order?.balance },
    });
    await batch.commit();
  }

  async failedShippedAction(reason, notifyUser = null, shipDate = null) {
    addBreadcrumb("failedShippedAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    console.log("failedShippedAction");
    await this.getOrder();
    await this.getLineItems();

    let updates = [];
    let locationBeforeDataForActivity = null;

    const batch = writeBatch(firestore);
    const salesOrderDoc = doc(firestore, "salesOrders", this.orderID);

    // Order
    batch.set(
      salesOrderDoc,
      {
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        rescheduled: increment(1),
        // shipDate: null, (dont write new ship date)
      },
      { merge: true },
    );

    const stateDoc = doc(
      firestore,
      "states",
      this.order.location.shipping.state.stateID,
    );
    const stateResponse = await getDoc(stateDoc);
    let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

    if (this.order.fulfillmentLocation) {
      fulfillmentLocation = this.order.fulfillmentLocation.id;
    }

    const locationRef = doc(
      firestore,
      "locationManufacturing",
      `${fulfillmentLocation}_${this.order.brand.id}`,
    );

    // Read current state for inventory activity recording (before batch changes)
    const locSnap = await getDoc(locationRef);
    locationBeforeDataForActivity = locSnap.exists() ? locSnap.data() : null;

    this.lineItems.forEach((item) => {
      batch.set(
        locationRef,
        {
          manufacturing: {
            [item.categoryID]: {
              available: {
                [item.productID]: increment(item.qty),
              },
              reserved: {
                [item.productID]: increment(item.qty),
              },
            },
          },
        },
        { merge: true },
      );
    });

    // Create notification if user is selected
    if (notifyUser) {
      const notificationRef = doc(collection(firestore, "notifications"));
      batch.set(notificationRef, {
        created: serverTimestamp(),
        updated: serverTimestamp(),
        assignedTo: {
          name: notifyUser.name,
          uid: notifyUser.id,
        },
        createdBy: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        notes: `This order needs to be reviewed. Reason: ${reason}`,
        relatedTo: {
          id: this.orderID,
          resource: "salesOrder",
          name: `Order #${this.order.orderNumber.internal.toUpperCase()}`,
          type: "sales order",
        },
        viewed: false,
      });
    }

    // External - Ship date
    if (this.orderIsExternal) {
      switch (this.externalOrderType) {
        case "leaflink":
          let patchData = {
            ship_date: null,
          };
          if (this.externalUpdates.leaflink.includes("orderUpdated")) {
            addBreadcrumb("leaflink: updateOrderData ship_date null (failedShipped)", {
              workflow: "sales-order",
              data: { orderId: this.orderID, orderStatus: this.order?.status },
            });
            try {
              await this.leaflinkClient.updateOrderData(patchData);
            } catch (error) {
              logWorkflowWarning(`LeafLink: updateOrderData (failedShipped) failed — ${error.message}`, {
                workflow: "sales-order",
                action: "leaflink-failed-shipped-update-order",
                context: { orderId: this.orderID, balance: this.order?.balance },
              });
              throw error;
            }
          }
          if (this.externalUpdates.leaflink.includes("statusUpdated")) {
            addBreadcrumb("leaflink: transitionOrder accept (failedShipped)", {
              workflow: "sales-order",
              data: { orderId: this.orderID, orderStatus: this.order?.status },
            });
            try {
              await this.leaflinkClient.transitionOrder("accept");
            } catch (error) {
              logWorkflowWarning(`LeafLink: transitionOrder("accept") (failedShipped) failed — ${error.message}`, {
                workflow: "sales-order",
                action: "leaflink-failed-shipped-transition-accept",
                context: { orderId: this.orderID, balance: this.order?.balance },
              });
              throw error;
            }
          }
          break;
        default:
          break;
      }
    }

    // Ledger
    updates.push(`Reschedule reason: "${reason}"`);

    // Create activity for order rescheduling
    await createActivity({
      type: "logistics",
      action: "Order rescheduled",
      item: `Order rescheduled: ${updates.join(", ")}`,
      relatedTo: "salesOrders",
      relatedID: [this.order.id],
      user: {
        name: store.getters.getUser.name,
        uid: store.getters.getUser.uid,
      },
      account: {
        id: this.order.account.id,
        name: this.order.account.name,
      },
      brand: {
        id: this.order.brand.id,
        name: this.order.brand.name,
      },
      businessImpact: "medium",
      priority: "medium",
      changeData: {
        field: "shipDate",
        oldValue: this.order.shipDate,
        newValue: null,
        reason: reason,
        from: "shipped",
        to: "shipped",
        rescheduleCount: this.order.rescheduled
          ? this.order.rescheduled + 1
          : 1,
        inventoryReturned: this.lineItems.reduce(
          (sum, item) => sum + item.qty,
          0,
        ),
        notifiedUser: notifyUser
          ? {
              name: notifyUser.fullName,
              id: notifyUser.id,
            }
          : null,
      },
    });

    addBreadcrumb("failedShippedAction: committing batch", {
      workflow: "sales-order",
      data: { orderId: this.orderID, balance: this.order?.balance },
    });
    await batch.commit();

    // Record inventory activities for report (order rescheduled from shipped – inventory returned and re-reserved)
    const locationId =
      this.order.fulfillmentLocation?.id ?? fulfillmentLocation;
    const locationName =
      this.order.fulfillmentLocation?.name ??
      this.order.location?.shipping?.name ??
      "";
    if (locationId && locationBeforeDataForActivity != null) {
      for (const item of this.lineItems) {
        const mfg =
          locationBeforeDataForActivity.manufacturing?.[item.categoryID];
        const before = {
          available: mfg?.available?.[item.productID] ?? 0,
          reserved: mfg?.reserved?.[item.productID] ?? 0,
          transfer: mfg?.transfer?.[item.productID] ?? 0,
        };
        const after = {
          available: before.available + item.qty,
          reserved: before.reserved + item.qty,
          transfer: before.transfer,
        };
        const skuID = `${locationId}_${this.order.brand.id}_${item.categoryID}_${item.productID}`;
        try {
          await createInventoryActivity({
            action: "Inventory restored (reschedule)",
            item: `${item.qty} units of ${item.name} (${item.sku}) returned from shipped and re-reserved – Order #${this.order.orderNumber.internal} rescheduled`,
            skuID,
            itemData: {
              name: item.name,
              sku: item.sku,
              categoryID: item.categoryID,
              productID: item.productID,
              categoryName: item.categoryName || item.category,
            },
            movement: {
              type: "restoration",
              from: "fulfilled",
              to: "reserved",
              quantity: item.qty,
              reason: "orderRescheduled",
              reasonID: this.order.id,
            },
            snapshot: { before, after },
            location: { id: locationId, name: locationName },
            order: this.order,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            extraSalesOrderData: { orderID: this.orderID },
          });
        } catch (activityErr) {
          console.error(
            "createInventoryActivity (order rescheduled from shipped):",
            activityErr,
            { skuID, productName: item.name, orderId: this.order.id },
          );
          logWorkflowWarning(`rescheduleShippedAction: inventory activity creation failed — ${activityErr.message}`, {
            workflow: "sales-order",
            action: "reschedule-shipped-inventory-activity",
            context: { orderId: this.orderID, productName: item.name, skuID },
          });
        }
      }
    }
  }

  // Did the LeafLink `ship` transition land despite the thrown error? true/false/null (cannot tell — callers must not unship on null).
  async _leaflinkShipLanded() {
    try {
      const externalOrder = await this.leaflinkClient.getOrder();
      const status = externalOrder?.status;
      if (typeof status !== "string" || status.length === 0) {
        return null;
      }
      const normalized = status.toLowerCase();
      return normalized === "shipped" || normalized === "complete";
    } catch (statusError) {
      logWorkflowWarning(
        `LeafLink: could not read order status to confirm ship — ${statusError.message}`,
        {
          workflow: "sales-order",
          action: "leaflink-ship-status-probe",
          context: { orderId: this.orderID },
        },
      );
      return null;
    }
  }

  // Compensating unship back to Fulfilled when the LeafLink `ship` did not land; idempotent, and `diverged: true` means nothing was reversed and the caller must record a divergence.
  async _unshipAfterFailedLeaflinkShip({
    salesOrderDocRef,
    attemptId,
    claimUser,
    deltas,
    leaflinkError = null,
  }) {
    const brandStatsDocRef = doc(
      firestore,
      "brandStats",
      `${this.order.account.id}_${this.order.brand.id}`,
    );

    return await runTransaction(firestore, async (transaction) => {
      const orderSnap = await transaction.get(salesOrderDocRef);
      if (!orderSnap.exists()) {
        return { unshipped: false, reason: "missing" };
      }
      const data = orderSnap.data();

      // Preconditions — anything else means the ship was already reversed or the order moved on, so do not decrement AR again.
      if (data.records?.shipped == null) {
        return { unshipped: false, reason: "not_shipped" };
      }
      if (data.status !== "shipped" && data.status !== "complete") {
        return { unshipped: false, reason: `unexpected_status:${data.status}` };
      }
      // shippingClaim.attemptId, not shippingProgress.attemptId — only the claim transaction writes it, so it is a true ownership token.
      if (data.shippingClaim?.attemptId !== attemptId) {
        return { unshipped: false, reason: "attempt_superseded" };
      }

      // One write per line item plus the order and brandStats; a partially reversed order is worse than one left shipped, so decline instead.
      const lineItems = data.lineItems ?? [];
      if (lineItems.length > UNSHIP_MAX_LINE_ITEM_WRITES) {
        return {
          unshipped: false,
          reason: "too_many_line_items",
          diverged: true,
        };
      }

      // Reverse the order's live contribution read inside the transaction, not the frozen ship-time deltas — a payment landing mid-LeafLink would otherwise leave AR short.
      const currentDue = Number(data.balance?.due);
      const currentTotal = Number(data.balance?.total);
      const currentUnits = Number(data.units);
      if (
        !Number.isFinite(currentDue) ||
        !Number.isFinite(currentTotal) ||
        !Number.isFinite(currentUnits)
      ) {
        // Never write NaN into AR — leave the order shipped for a human instead.
        return {
          unshipped: false,
          reason: "unreadable_balance",
          diverged: true,
        };
      }

      const orderPatch = {
        // Restore where this attempt started — a resumed incomplete ship enters at "shipped", and forcing "fulfilled" would move it further back than it was found.
        status: deltas.preShipStatus ?? "fulfilled",
        // null, not deleteField() — orders carry unreached stages as explicit null, and a missing field drops out of equality queries.
        "records.shipped": null,
        lineItems: lineItems.map((item) => ({
          ...item,
          flags: { ...(item.flags ?? {}), delivered: false },
        })),
        shippingProgress: {
          attemptId,
          status: "compensated",
          checkpoint: "leaflink_unshipped",
          batchCommitted: true,
          updatedAt: serverTimestamp(),
          startedBy: claimUser,
          error: null,
          meta: {
            reversedDueDelta: deltas.dueDelta,
            reversedSalesDelta: deltas.salesDelta,
            reversedUnits: deltas.units,
            leaflinkError,
          },
        },
        updated: serverTimestamp(),
        lastUpdated: claimUser,
      };

      // If this attempt assigned the ship date, take it back off — phase 2 never ran, so LeafLink has no matching ship_date either.
      if (deltas.shipSetTheShipDate) {
        orderPatch.shipDate = null;
      }

      // An order moving back out of "complete" cannot keep a completion date, whoever set it — that orphan slips past every incomplete-ship guard.
      if (deltas.autoCompleted || data.status === "complete") {
        orderPatch["records.completed"] = null;
      }
      // flags.paid only reverts when THIS ship set it — a real payment in the window is a payment fact, and a fulfilled order can legitimately be paid.
      if (deltas.autoCompleted) {
        orderPatch["flags.paid"] = deltas.preShipFlagsPaid ?? false;
      }

      transaction.update(salesOrderDocRef, orderPatch);

      // The order view renders flags.delivered from the salesOrders_lineItems subcollection, so reversing only the order doc leaves "Delivered" chips behind.
      for (const item of lineItems) {
        if (!item?.id) continue;
        transaction.set(
          doc(firestore, "salesOrders_lineItems", item.id),
          {
            updated: serverTimestamp(),
            lastUpdated: claimUser,
            flags: { delivered: false },
          },
          { merge: true },
        );
      }

      transaction.set(
        brandStatsDocRef,
        {
          metrics: {
            balance: increment(-currentDue),
            totalSales: increment(-currentTotal),
            totalUnitsSold: increment(-currentUnits),
          },
        },
        { merge: true },
      );

      return { unshipped: true, reason: "compensated" };
    });
  }

  async shippedAction(lineItems, shipDate = null) {
    addBreadcrumb("shippedAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });

    await this.getOrder();
    await this.getLineItems();
    await this.getPayments();

    // Truly shipped — already done. Incomplete ships (status shipped without records.shipped) are resumable.
    if (this._isTrulyShipped()) {
      return;
    }

    // Complete orphans (status complete without records.shipped) are not safely resumable — re-running the
    // full ship batch here could double-apply brandStats/inventory. Require repair instead, same as completeAction.
    if (this.order?.status === "complete" && this._isIncompleteShip()) {
      const completeOrphan = new Error(
        `Order #${this.order?.orderNumber?.internal ?? this.orderID} is complete but was never fully shipped (records.shipped missing). This order needs to be repaired before it can be re-shipped.`,
      );
      completeOrphan.code = "SHIP_COMPLETE_ORPHAN";
      throw completeOrphan;
    }

    if (lineItems.length === 0) {
      throw new Error(
        "No line items were provided. Please refresh the page and try again."
      );
    }

    const attemptId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `ship-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const claimUser = {
      name: store.getters.getUser.name,
      uid: store.getters.getUser.uid,
    };

    // Soft lock only — never write status: shipped here. Status flips with records.shipped in the main batch.
    const salesOrderDocRef = doc(firestore, "salesOrders", this.order.id);
    const claimResult = await runTransaction(firestore, async (transaction) => {
      const orderSnap = await transaction.get(salesOrderDocRef);
      if (!orderSnap.exists()) {
        return { ok: false, reason: "missing" };
      }
      const data = orderSnap.data();
      if (data.status === "shipped" && data.records?.shipped != null) {
        return { ok: false, reason: "already_shipped" };
      }
      if (data.status === "complete" && data.records?.shipped == null) {
        return { ok: false, reason: "complete_orphan" };
      }

      const inProgress = data.flags?.shippingInProgress === true;
      if (inProgress) {
        const lastBeat = shippingProgressTimestampMs(
          data.shippingProgress?.updatedAt ?? data.shippingClaim?.at,
        );
        const stale =
          lastBeat == null || Date.now() - lastBeat > SHIPPING_LOCK_STALE_MS;
        if (!stale) {
          return { ok: false, reason: "locked" };
        }
      }

      transaction.update(salesOrderDocRef, {
        "flags.shippingInProgress": true,
        shippingClaim: {
          uid: claimUser.uid,
          name: claimUser.name,
          at: serverTimestamp(),
          attemptId,
        },
        shippingProgress: {
          attemptId,
          status: inProgress ? "abandoned_then_reclaimed" : "in_progress",
          checkpoint: "soft_lock",
          batchCommitted: false,
          startedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          startedBy: claimUser,
          error: null,
          meta: {},
          ...(inProgress
            ? {
                priorAttemptAbandoned: {
                  attemptId: data.shippingProgress?.attemptId ?? null,
                  checkpoint: data.shippingProgress?.checkpoint ?? null,
                },
              }
            : {}),
        },
        updated: serverTimestamp(),
        lastUpdated: claimUser,
      });
      return { ok: true, reason: inProgress ? "reclaimed" : "claimed" };
    });

    const claimOk = claimResult === true || claimResult?.ok === true;
    const claimReason =
      claimResult === true
        ? "claimed"
        : claimResult?.reason ?? "unknown";

    if (!claimOk) {
      if (claimReason === "locked") {
        throw new Error(
          `Order #${this.order?.orderNumber?.internal ?? this.orderID} is already being shipped by another session. Wait a few minutes and try again.`,
        );
      }
      if (claimReason === "missing") {
        throw new Error(
          `Order #${this.order?.orderNumber?.internal ?? this.orderID} was not found.`,
        );
      }
      if (claimReason === "complete_orphan") {
        const completeOrphan = new Error(
          `Order #${this.order?.orderNumber?.internal ?? this.orderID} is complete but was never fully shipped (records.shipped missing). This order needs to be repaired before it can be re-shipped.`,
        );
        completeOrphan.code = "SHIP_COMPLETE_ORPHAN";
        throw completeOrphan;
      }
      // already_shipped — idempotent no-op
      return;
    }

    let balance = null;
    let brandStatsDoc = null;
    let batchCommitted = false;
    let units = 0;
    let lastCheckpoint = "soft_lock";
    const sideEffectFailures = [];
    const noteSideEffectFailure = (step, err) => {
      sideEffectFailures.push({
        step,
        message: err?.message || String(err),
      });
    };
    // Tracks which conditional branches ran and their pre-ship brandStats values (legacy; post-ship_saved no longer full-reverts).
    const revertState = {
      closeDateRefreshed: false,
      brandStatsActivationRan: false,
      brandStatsSamplesRan: false,
      preShipBrandStatsStatus: null,
      preShipBrandStatsCloseDate: undefined,
      preShipBrandStatsStaffClose: undefined,
      preShipBrandStatsSamplesReceivedDate: undefined,
      overpaymentRevertInfo: null,
    };
    const checkpoint = (stage, { awaitWrite = false, meta = null } = {}) => {
      lastCheckpoint = stage;
      addBreadcrumb("shippedAction: checkpoint", {
        workflow: "sales-order",
        data: { orderId: this.orderID, stage, batchCommitted, attemptId },
      });
      const patch = {
        attemptId,
        status: "in_progress",
        checkpoint: stage,
        batchCommitted,
        startedBy: claimUser,
        ...(meta ? { meta } : {}),
      };
      return this._persistShippingProgress(salesOrderDocRef, patch, {
        awaitWrite,
      });
    };

    // Carried to the end because the success tail replaces the whole shippingProgress map, which would bury the only field a reconciler can find these orders by.
    let shipDivergence = null;
    const noteShipDivergence = (stage, meta) => {
      shipDivergence = { checkpoint: stage, meta };
      // Written now as well as in the tail so the record survives an unexpected throw later in the post-commit side effects.
      checkpoint(stage, { meta });
    };

    await checkpoint("soft_lock", { awaitWrite: true });

    // Captured for forensics / defensive coding; post-ship_saved failures no longer full-revert AR/status.
    const preShipStatus = this.order.status;
    const preShipBalance = this.order.balance;
    const preShipUnits = this.order.units;
    const preShipProductLots = this.order.productLots;
    const preShipLineItems = this.order.lineItems;
    const preShipDiscount = this.order.discount;
    const preShipRecordsShipped = this.order.records?.shipped;
    const preShipRecordsCompleted = this.order.records?.completed;
    const preShipFlagsPaid = this.order.flags?.paid;

    const clearSoftLockOnly = async (progressStatus, errorMessage = null) => {
      try {
        await updateDoc(salesOrderDocRef, {
          "flags.shippingInProgress": false,
          shippingProgress: {
            attemptId,
            status: progressStatus,
            checkpoint: lastCheckpoint,
            batchCommitted,
            updatedAt: serverTimestamp(),
            startedBy: claimUser,
            error: errorMessage
              ? { message: errorMessage, checkpoint: lastCheckpoint }
              : null,
          },
          updated: serverTimestamp(),
          lastUpdated: claimUser,
        });
      } catch (clearErr) {
        logWorkflowWarning(
          `shippedAction: clear soft lock failed — ${clearErr.message}`,
          {
            workflow: "sales-order",
            action: "shipping-lock-clear",
            context: { orderId: this.orderID, lastCheckpoint },
          },
        );
      }
    };

    try {
      await checkpoint("preparing_ship_writes");
      let updates = [];
      let locationBeforeDataForActivity = null;

      const orderSubmissionEmail = httpsCallable(functions, "sendgridEmail");

      const batch = writeBatch(firestore);
      const salesOrderDoc = salesOrderDocRef;
      const deliveredItems = lineItems.filter((item) => item.delivered);
      const rejectedItems = lineItems.filter((item) => !item.delivered);

      const stateDoc = doc(
        firestore,
        "states",
        this.order.location.shipping.state.stateID,
      );
      const stateResponse = await getDoc(stateDoc);
      let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

      if (this.order.fulfillmentLocation) {
        fulfillmentLocation = this.order.fulfillmentLocation.id;
      }

      const locationRef = doc(
        firestore,
        "locationManufacturing",
        `${fulfillmentLocation}_${this.order.brand.id}`,
      );
      let payableToResponse = await getDocs(
        query(
          collection(firestore, "payableTo"),
          where("active", "==", true),
          where(
            "location.state.stateID",
            "==",
            this.order.location.shipping.state.stateID,
          ),
          where("brandID", "==", this.order.brand.id),
        ),
      );
      let payableTo = null;
      if (!payableToResponse.empty) {
        payableTo = {
          id: payableToResponse.docs[0].id,
          ...payableToResponse.docs[0].data(),
        };
      }

      // Heartbeat: from here to the money commit is unbroken work, and without beats a slow ship gets reclaimed mid-flight and aborts with SHIP_LOCK_LOST.
      await checkpoint("ship_reads_loaded");

      // Line Items
      if (deliveredItems.length > 0) {
        deliveredItems.forEach((item) => {
          const lineItemDoc = doc(firestore, "salesOrders_lineItems", item.id);

          batch.set(
            lineItemDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              flags: {
                delivered: true,
              },
            },
            { merge: true },
          );
        });
      }
      if (rejectedItems.length > 0) {
        let removed = [];

        // External - Update Status to accepted temporarily
        //
        //  - Leaflink API doesn't allow rejection of line items while the order status is `fulfilled`
        //     so we have to transition the status back to accepted, delete the line items from the order,
        //     and then transition the status back to fulfilled, then finally transition to shipped.
        //
        //  This is necessary to keep the line items/inventory on Leaflink consistent
        // NOTE: Leaflink operations moved to after batch commit for data consistency

        const stateDoc = doc(
          firestore,
          "states",
          this.order.location.shipping.state.stateID,
        );
        const stateResponse = await getDoc(stateDoc);
        // Assign to outer fulfillmentLocation so activity recording after batch.commit() uses same value
        fulfillmentLocation =
          stateResponse.data()?.fulfillmentLocation ?? fulfillmentLocation;
        if (this.order.fulfillmentLocation) {
          fulfillmentLocation = this.order.fulfillmentLocation.id;
        }

        const locationRef = doc(
          firestore,
          "locationManufacturing",
          `${fulfillmentLocation}_${this.order.brand.id}`,
        );

        // Read current state for activity recording only (does not affect core flow)
        const locSnap = await getDoc(locationRef);
        locationBeforeDataForActivity = locSnap.exists()
          ? locSnap.data()
          : null;

        const promises = rejectedItems.map(async (item) => {
          const lineItemDoc = doc(firestore, "salesOrders_lineItems", item.id);
          batch.delete(lineItemDoc);

          // Reset inventory
          batch.set(
            locationRef,
            {
              manufacturing: {
                [item.categoryID]: {
                  available: {
                    [item.productID]: increment(item.qty),
                  },
                },
              },
            },
            { merge: true },
          );

          removed.push(
            `<span class="text-capitalize">${
              item.name
            }</span> (${item.sku.toUpperCase()}) - <span class="text-uppercase">qty</span>: ${
              item.qty
            }`,
          );

          for (let i = 0; i < item.productLots?.length; i++) {
            const productLot = item.productLots[i];
            const productLotDoc = doc(firestore, "productLots", productLot.id);
            const productLotResponse = await getDoc(productLotDoc);

            if (productLotResponse.exists()) {
              const productLotData = productLotResponse.data();

              // Skip if this line item is already detached from the lot (prior attempt / resume).
              const alreadyRestored = !(
                productLotData.relatedLineItems ?? []
              ).some((lineItem) => lineItem.id === item.id);
              if (alreadyRestored) {
                continue;
              }

              let availableUnits = productLotResponse.data().units;

              if (productLotData.hasOwnProperty("available")) {
                availableUnits =
                  Number(productLotResponse.data().available) +
                  productLot.units;
              }

              const newRelatedLineItems =
                productLotData.relatedLineItems?.filter(
                  (lineItem) => lineItem.id !== item.id,
                ) ?? [];

              // Include in the ship batch so resume cannot double-apply lot restores outside the atomic commit.
              batch.set(
                productLotDoc,
                {
                  active: availableUnits > 0,
                  available: availableUnits,
                  relatedLineItems: newRelatedLineItems,
                  updated: serverTimestamp(),
                  lastUpdated: {
                    name: store.getters.getUser.name,
                    uid: store.getters.getUser.uid,
                  },
                },
                { merge: true },
              );
            }
          }

          // NOTE: Leaflink deleteLineItem operations moved to after batch commit for data consistency
        });

        //  Need to use Promise to ensure that all items are removed before setup() runs
        await Promise.all(promises);

        //  External - Update order status back to fulfilled, then update to shipped
        // NOTE: Leaflink fulfill transition moved to after batch commit for data consistency

        updates.push(
          `<span class="text-uppercase font-weight-bold" style="color: red">-rejected</span>: ${removed.join(
            " / ",
          )}`,
        );
      }
      // Heartbeat: the rejected-item block above is the longest stretch in the pre-commit path (a getDoc and inventory write per line).
      await checkpoint("ship_lines_staged");

      //  External Status Update
      // NOTE: Leaflink ship transition moved to after batch commit for data consistency

      // Recalculate order balance & QTY + Payment due date
      units = deliveredItems.reduce((a, b) => a + b.qty, 0);
      const total = deliveredItems.reduce((a, b) => a + b.qty * b.amount, 0);

      let excise = Number(this.order.balance.excise) || 0;
      let shipping = Number(this.order.balance.shipping) || 0;

      const {
        total: balanceTotal,
        due: balanceDue,
        discountTotal,
      } = computeOrderBalance({
        lineSubtotal: total,
        discountType: this.order.discount?.type ?? "%",
        discountValue: this.order.discount?.value ?? 0,
        shipping,
        paymentsApplied: sumAppliedPayments(this.payments),
      });

      // Preserve existing ship shape: due/total numbers; excise/shipping strings.
      balance = {
        due: balanceDue,
        excise: parseFloat(excise).toFixed(2),
        shipping: parseFloat(shipping).toFixed(2),
        total: balanceTotal,
      };

      // Rejected items can drop the total below what is already paid; block the ship
      // now rather than shipping overpaid with no credit (nothing is committed yet).
      this._assertFiniteBalance("shipped-overpay-preflight", balance);
      // Cross-check the payment docs against the stored ledger so a stale `due` cannot
      // understate the overpay, matching the dual basis updateLineItems commits on.
      const { overpay: shippedOverpay } = computeOverpay({
        newTotal: balance.total,
        payments: this.payments,
        balanceTotal: this.order.balance.total,
        balanceDue: this.order.balance.due,
      });
      this._assertOverpaymentAbsorbable(
        "shipped-overpay-preflight",
        shippedOverpay,
      );
      this._assertOverpaymentCreditable(
        "shipped-overpay-preflight",
        shippedOverpay,
        payableToResponse.empty,
      );

      let rejectedItemIDs = rejectedItems.map((item) => item.id);
      let remainingProductLots =
        this.order.productLots?.filter(
          (productLot) => !rejectedItemIDs.includes(productLot.relatedID),
        ) ?? [];

      // Computed before the batch writes so the exact AR deltas can be persisted on shippingProgress.meta for the compensating unship.
      const dueDelta = Number(parseFloat(Number(balance.due)).toFixed(2));
      const salesDelta = Number(parseFloat(Number(balance.total)).toFixed(2));
      if (!Number.isFinite(dueDelta) || !Number.isFinite(salesDelta)) {
        throw new Error("Invalid order balance for brandStats ship update");
      }

      // Order — do not set nested `records: { shipped }` via set+merge (shallow; wipes sibling record fields).
      batch.set(
        salesOrderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          status: "shipped",
          balance,
          units,
          productLots: remainingProductLots,
          lineItems: deliveredItems
            .map((item) => {
              return {
                ...item,
                flags: {
                  ...item.flags,
                  delivered: true,
                },
              };
            })
            .sort((a, b) => (a.sku > b.sku ? 1 : -1)),
          discount: {
            value: this.order.discount?.value ?? 0,
            type: this.order.discount?.type ?? "%",
            total: discountTotal,
          },
        },
        { merge: true },
      );

      // Dotted paths via update so we don't replace the whole flags/records maps (set+merge is shallow).
      batch.update(salesOrderDoc, {
        "flags.shippingInProgress": false,
        "records.shipped": moment().startOf("day").add(12, "hours").toDate(),
        shippingProgress: {
          attemptId,
          status: "succeeded",
          checkpoint: "ship_saved",
          batchCommitted: true,
          updatedAt: serverTimestamp(),
          startedBy: claimUser,
          error: null,
          meta: {
            deliveredCount: deliveredItems.length,
            rejectedCount: rejectedItems.length,
            // Exact brandStats deltas applied by this ship, reversed by the compensating unship.
            dueDelta,
            salesDelta,
            units,
            autoCompleted: Number(balance.due) <= 0,
          },
        },
      });

      // Snapshotted once: the LeafLink ship_date patch below tests the same condition, and read live it would see the shipDate this batch just wrote and skip itself.
      const shipSetTheShipDate = !this.order.shipDate;

      // Update Ship Date
      if (shipSetTheShipDate) {
        const oldValue = "[Not Set]",
          newValue = moment(shipDate).format("MM/DD/YYYY");

        batch.set(
          salesOrderDoc,
          {
            shipDate: moment(shipDate)
              .startOf("day")
              .add(9, "hours")
              .tz("America/Los_Angeles")
              .toDate(),
          },
          { merge: true },
        );

        // Create activity for ship date set during fulfillment
        await createActivity({
          type: "logistics",
          action: "Ship date set during fulfillment",
          item: `Order #${this.order.orderNumber.internal.toUpperCase()} ship date set to ${moment(
            shipDate,
          ).format("MM/DD/YYYY")} during fulfillment process`,
          relatedTo: "salesOrders",
          relatedID: [salesOrderDoc.id],
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          account: {
            id: this.order.account.id,
            name: this.order.account.name,
          },
          brand: {
            id: this.order.brand.id,
            name: this.order.brand.name,
          },
          businessImpact: "medium",
          priority: "medium",
          changeData: {
            field: "shipDate",
            oldValue: oldValue,
            newValue: newValue,
            orderNumber: this.order.orderNumber.internal,
            updateType: "fulfillment_process",
            shipDate: moment(shipDate).toDate(),
            fulfillmentContext: "order_fulfillment",
            externalOrderUpdated:
              this.externalUpdates.leaflink.includes("orderUpdated"),
          },
        });

        // NOTE: Leaflink ship date update moved to after batch commit for data consistency
      }
      // Account Balance
      brandStatsDoc = doc(
        firestore,
        "brandStats",
        `${this.order.account.id}_${this.order.brand.id}`,
      );
      const accountDoc = doc(firestore, "accounts", this.order.account.id);
      const brandStats = await getDoc(brandStatsDoc);
      const accountData = await getDoc(accountDoc);
      revertState.preShipBrandStatsStatus = brandStats.data().status;
      revertState.preShipBrandStatsCloseDate = brandStats.data().metrics?.closeDate;
      revertState.preShipBrandStatsStaffClose = brandStats.data().staff?.close;
      revertState.preShipBrandStatsSamplesReceivedDate = brandStats.data().samplesReceivedDate;
      // Atomic increments so concurrent ships on the same account cannot clobber AR/sales
      // (absolute read-modify-write lost sibling ship amounts — Cake House Riverside).
      let brandStatsPayload = {
        metrics: {
          balance: increment(dueDelta),
          totalSales: increment(salesDelta),
          totalUnitsSold: increment(units),
        },
      };

      if (brandStats.data().metrics.closeDate?.order === salesOrderDoc.id) {
        revertState.closeDateRefreshed = true;
        // Update account's closeDate to order's ship date
        let shipDate = this.order.shipDate
          ? moment(this.order.shipDate?.seconds * 1000)
              .startOf("day")
              .add(9, "hours")
              .tz("America/Los_Angeles")
              .toDate()
          : moment(shipDate)
              .startOf("day")
              .add(9, "hours")
              .tz("America/Los_Angeles")
              .toDate();
        brandStatsPayload.metrics.closeDate = {
          date: shipDate,
        };
      }

      batch.set(brandStatsDoc, brandStatsPayload, { merge: true });

      if (
        ["lead_cold", "lead_hot", "lead_onHold"].includes(
          brandStats.data().status,
        ) ||
        (["samples_sent", "samples_received", "needs_reonboarding"].includes(
          brandStats.data().status,
        ) &&
          !this.order.flags.sample)
      ) {
        revertState.brandStatsActivationRan = true;
        const oldValue = store.getters.getAccountStatus.find(
            (option) => option.value === brandStats.data().status,
          ),
          newValue = store.getters.getAccountStatus.find(
            (option) => option.value === "active_current",
          );

        let managedBy = null;
        if (brandStats.data().staff.manager?.id) {
          managedBy = brandStats.data().staff.manager;
        } else if (brandStats.data().staff.executive?.id) {
          managedBy = brandStats.data().staff.executive;
        } else {
          managedBy = {
            name: `${this.order.createdBy.name.first} ${this.order.createdBy.name.last}`,
            id: this.order.createdBy.uid,
            email: this.order.createdBy.email ?? "",
          };
        }

        let closer =
          this.order.createdBy.id === "storeGenerated"
            ? managedBy
            : {
                name: `${this.order.createdBy.name.first} ${this.order.createdBy.name.last}`,
                id: this.order.createdBy.uid,
                email: this.order.createdBy.email ?? "",
              };

        batch.set(
          brandStatsDoc,
          {
            status: "active_current",
            metrics: {
              closeDate: {
                date: moment(shipDate).startOf("day").add(12, "hours").toDate(),
                order: this.order.id,
              },
            },
            staff: {
              close: closer,
            },
          },
          { merge: true },
        );
        // Onboarding
        // Duplicate check
        const accountOnboardings = collection(firestore, "accountOnboardings");
        const account = where("account.id", "==", this.order.account.id),
          brand = where("brand.id", "==", this.order.brand.id);

        const q = query(accountOnboardings, account, brand);
        const response = await getDocs(q);
        if (!response.empty) {
          // Onboarding already exists
        } else {
          // Onboarding doesn't exist
          const onboardingDoc = doc(
            collection(firestore, "accountOnboardings"),
          );
          const account = {
            text: this.order.account.name,
            value: this.order.account.id,
            staff: brandStats.data().staff,
            location: this.order.location.shipping,
            flags: accountData.data().flags,
          };
          const brand = {
            text: this.order.brand.name,
            value: this.order.brand.id,
          };

          const trainingTask = doc(collection(firestore, "tasks"));
          const sampleOrderTask = doc(collection(firestore, "tasks"));
          const storeEventTask = doc(collection(firestore, "tasks"));
          const introEmailTask = doc(collection(firestore, "tasks"));
          const logoTask = doc(collection(firestore, "tasks"));

          batch.set(onboardingDoc, {
            created: serverTimestamp(),
            updated: serverTimestamp(),
            createdBy: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            flags: {
              complete: false,
              budtenderTraining: false,
              sampleOrder: false,
              storeEvent: false,
              introEmail: false,
              logo: account.flags.logo,
            },
            externalIDs: {},
            account: {
              name: account.text,
              id: account.value,
              staff: account.staff,
            },
            brand: {
              name: brand.text,
              id: brand.value,
            },
            records: {
              budtenderTraining: trainingTask.id,
              storeEvent: storeEventTask.id,
              sampleOrder: sampleOrderTask.id,
              introEmail: introEmailTask.id,
              logo: account.flags.logo === false ? logoTask.id : "",
            },
            location: account.location,
            metrics: {
              complete: null,
              budtenderTraining: null,
              sampleOrder: null,
              storeEvent: null,
              introEmail: null,
              logo: account.flags.logo ? serverTimestamp() : null,
            },
            notes: "",
          });

          let staff = [];
          let managedBy = null;
          if (brandStats.data().staff.manager?.id?.length > 0) {
            staff.push(brandStats.data().staff.manager.id);
            managedBy = brandStats.data().staff.manager;
          }
          if (brandStats.data().staff.executive?.id?.length > 0) {
            staff.push(brandStats.data().staff.executive.id);
            managedBy = brandStats.data().staff.executive;
          }

          let users = [store.getters.getUser.uid, ...staff];
          users = [...new Set(users)]; //  Removes duplicates
          batch.set(
            trainingTask,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              dueDate: moment().add(3, "days").toDate(),
              completedDate: null,
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              completedBy: null,
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              flags: {
                complete: null,
                system: true,
              },
              status: "notStarted",
              priority: "normal",
              notes: `Schedule Budtender Training for ${capitalize(
                account.text,
              )} ${capitalize(brand.text)}'s onboarding`,
              type: "other",
              relatedTo: {
                name: `${capitalize(account.text)} - Onboarding`,
                id: onboardingDoc.id,
                resource: "accountOnboardings",
              },
              relatedContact: null,
              assignedTo: {
                name: {
                  first: managedBy.name.split(" ")[0],
                  last: managedBy.name.split(" ")[1],
                },
                uid: managedBy.id,
              },
              externalIDs: {},
              users,
            },
            { merge: true },
          );

          // Create activity for budtender training task creation
          await createActivity({
            type: "onboarding",
            action: "Budtender training task created",
            item: `Budtender training task created for ${account.text} ${
              brand.text
            } onboarding - Assigned to ${managedBy.name}, Due: ${moment()
              .add(3, "days")
              .format("MM/DD/YYYY")}`,
            relatedTo: "accountOnboardings",
            relatedID: [onboardingDoc.id],
            changeData: {
              field: "onboardingTask",
              taskType: "budtenderTraining",
              taskId: trainingTask.id,
              accountName: account.text,
              brandName: brand.text,
              assignedTo: managedBy.name,
              dueDate: moment().add(3, "days").toDate(),
              priority: "normal",
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });

          batch.set(
            sampleOrderTask,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              dueDate: moment().add(3, "days").toDate(),
              completedDate: null,
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              completedBy: null,
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              flags: {
                complete: null,
                system: true,
              },
              status: "notStarted",
              priority: "normal",
              notes: `Submit Sample Order for ${capitalize(
                account.text,
              )} ${capitalize(brand.text)}'s onboarding`,
              type: "other",
              relatedTo: {
                name: `${capitalize(account.text)} - Onboarding`,
                id: onboardingDoc.id,
                resource: "accountOnboardings",
              },
              relatedContact: null,
              assignedTo: {
                name: {
                  first: managedBy.name.split(" ")[0],
                  last: managedBy.name.split(" ")[1],
                },
                uid: managedBy.id,
              },
              externalIDs: {},
              users,
            },
            { merge: true },
          );

          // Create activity for sample order task creation
          await createActivity({
            type: "onboarding",
            action: "Sample order task created",
            item: `Sample order task created for ${account.text} ${
              brand.text
            } onboarding - Assigned to ${managedBy.name}, Due: ${moment()
              .add(3, "days")
              .format("MM/DD/YYYY")}`,
            relatedTo: "accountOnboardings",
            relatedID: [onboardingDoc.id],
            changeData: {
              field: "onboardingTask",
              taskType: "sampleOrder",
              taskId: sampleOrderTask.id,
              accountName: account.text,
              brandName: brand.text,
              assignedTo: managedBy.name,
              dueDate: moment().add(3, "days").toDate(),
              priority: "normal",
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });
          //  Schedule Store Event Task

          batch.set(
            storeEventTask,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              dueDate: moment().add(3, "days").toDate(),
              completedDate: null,
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              completedBy: null,
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              flags: {
                complete: null,
                system: true,
              },
              status: "notStarted",
              priority: "normal",
              notes: `Schedule Store Event for ${capitalize(
                account.text,
              )} ${capitalize(brand.text)}'s onboarding`,
              type: "other",
              relatedTo: {
                name: `${capitalize(account.text)} - Onboarding`,
                id: onboardingDoc.id,
                resource: "accountOnboardings",
              },
              relatedContact: null,
              assignedTo: {
                name: {
                  first: managedBy.name.split(" ")[0],
                  last: managedBy.name.split(" ")[1],
                },
                uid: managedBy.id,
              },
              externalIDs: {},
              users,
            },
            { merge: true },
          );

          // Create activity for store event task creation
          await createActivity({
            type: "onboarding",
            action: "Store event task created",
            item: `Store event task created for ${account.text} ${
              brand.text
            } onboarding - Assigned to ${managedBy.name}, Due: ${moment()
              .add(3, "days")
              .format("MM/DD/YYYY")}`,
            relatedTo: "accountOnboardings",
            relatedID: [onboardingDoc.id],
            changeData: {
              field: "onboardingTask",
              taskType: "storeEvent",
              taskId: storeEventTask.id,
              accountName: account.text,
              brandName: brand.text,
              assignedTo: managedBy.name,
              dueDate: moment().add(3, "days").toDate(),
              priority: "normal",
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });
          //  Send Intro Email Task
          batch.set(
            introEmailTask,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              dueDate: moment().add(3, "days").toDate(),
              completedDate: null,
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              completedBy: null,
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              flags: {
                complete: false,
                requiresReview: false,
              },
              status: "notStarted",
              priority: "normal",
              notes: `Send Intro Email to ${capitalize(
                account.text,
              )} for ${capitalize(brand.text)}'s onboarding`,
              type: "email",
              relatedTo: {
                name: `${capitalize(account.text)} - Onboarding`,
                id: onboardingDoc.id,
                resource: "accountOnboardings",
              },
              relatedContact: null,
              assignedTo: {
                name: {
                  first: managedBy.name.split(" ")[0],
                  last: managedBy.name.split(" ")[1],
                },
                uid: managedBy.id,
              },
              externalIDs: {},
              users,
            },
            { merge: true },
          );

          // Create activity for intro email task creation
          await createActivity({
            type: "onboarding",
            action: "Intro email task created",
            item: `Intro email task created for ${account.text} ${
              brand.text
            } onboarding - Assigned to ${managedBy.name}, Due: ${moment()
              .add(3, "days")
              .format("MM/DD/YYYY")}`,
            relatedTo: "accountOnboardings",
            relatedID: [onboardingDoc.id],
            changeData: {
              field: "onboardingTask",
              taskType: "introEmail",
              taskId: introEmailTask.id,
              accountName: account.text,
              brandName: brand.text,
              assignedTo: managedBy.name,
              dueDate: moment().add(3, "days").toDate(),
              priority: "normal",
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });
          //  Upload Logo Task
          batch.set(
            logoTask,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              dueDate: moment().add(3, "days").toDate(),
              completedDate:
                account.flags.logo === true ? serverTimestamp() : null,
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              completedBy: {
                name: account.flags.logo ? store.getters.getUser.name : null,
                id: account.flags.logo ? store.getters.getUser.uid : null,
              },
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              flags: {
                complete: account.flags.logo ? true : null,
                requiresReview: false,
              },
              status: account.flags.logo ? "complete" : "notStarted",
              priority: "normal",
              notes: `Upload logo for ${capitalize(account.text)}`,
              type: "other",
              relatedTo: {
                name: `${capitalize(account.text)} - Onboarding`,
                id: onboardingDoc.id,
                resource: "accountOnboardings",
              },
              relatedContact: null,
              assignedTo: {
                name: {
                  first: managedBy.name.split(" ")[0],
                  last: managedBy.name.split(" ")[1],
                },
                uid: managedBy.id,
              },
              externalIDs: {},
              users,
            },
            { merge: true },
          );

          // Create activity for logo upload task creation
          await createActivity({
            type: "onboarding",
            action: "Logo upload task created",
            item: `Logo upload task created for ${account.text} ${
              brand.text
            } onboarding - Assigned to ${managedBy.name}, Due: ${moment()
              .add(3, "days")
              .format("MM/DD/YYYY")}${
              account.flags.logo ? " (Already completed)" : ""
            }`,
            relatedTo: "accountOnboardings",
            relatedID: [onboardingDoc.id],
            changeData: {
              field: "onboardingTask",
              taskType: "logoUpload",
              taskId: logoTask.id,
              accountName: account.text,
              brandName: brand.text,
              assignedTo: managedBy.name,
              dueDate: moment().add(3, "days").toDate(),
              priority: "normal",
              alreadyComplete: account.flags.logo,
              taskStatus: account.flags.logo ? "complete" : "notStarted",
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });

          // Ledger
          let appendActions = [];
          // if (this.payload.onboarding.logo) {
          //   appendActions.push(`Logo Uploaded`);
          // }

          // Create activity for account onboarding creation
          await createActivity({
            type: "onboarding",
            action: "Account onboarding created",
            item: `Account onboarding created for ${account.text} ${
              brand.text
            } - Assigned to ${managedBy.name}, Due: ${moment()
              .add(3, "days")
              .format("MM/DD/YYYY")} - 5 tasks created`,
            relatedTo: "accountOnboardings",
            relatedID: [onboardingDoc.id],
            changeData: {
              field: "accountOnboarding",
              accountName: account.text,
              brandName: brand.text,
              tasksCreated: {
                budtenderTraining: trainingTask.id,
                sampleOrder: sampleOrderTask.id,
                storeEvent: storeEventTask.id,
                introEmail: introEmailTask.id,
                logoUpload: account.flags.logo === false ? logoTask.id : null,
              },
              flags: {
                complete: false,
                budtenderTraining: false,
                sampleOrder: false,
                storeEvent: false,
                introEmail: false,
                logo: account.flags.logo,
              },
              dueDate: moment().add(3, "days").toDate(),
              assignedTo: managedBy.name,
            },
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });
        }
      } else if (brandStats.data().status === "samples_sent") {
        revertState.brandStatsSamplesRan = true;
        const oldValue = store.getters.getAccountStatus.find(
            (option) => option.value === "samples_sent",
          ),
          newValue = store.getters.getAccountStatus.find(
            (option) => option.value === "samples_received",
          );

        batch.set(
          brandStatsDoc,
          {
            status: "samples_received",
            samplesReceivedDate: moment(this.order.shipDate?.seconds * 1000)
              .startOf("day")
              .add(12, "hours")
              .toDate(),
          },
          { merge: true },
        );

        // Create activity for samples received status update
        await createActivity({
          type: "sales",
          action: "Sample status updated",
          item: `${this.order.brand.name} sample status updated from ${oldValue.text} to ${newValue.text} for ${this.order.account.name} - Order #${this.order.orderNumber.internal}`,
          relatedTo: "accounts",
          relatedID: [this.order.account.id],
          changeData: {
            field: "sampleStatus",
            oldValue: {
              text: oldValue.text,
              value: oldValue.value,
              color: oldValue.color,
            },
            newValue: {
              text: newValue.text,
              value: newValue.value,
              color: newValue.color,
            },
            orderNumber: this.order.orderNumber.internal,
            brandName: this.order.brand.name,
            samplesReceivedDate: moment(this.order.shipDate?.seconds * 1000)
              .startOf("day")
              .add(12, "hours")
              .toDate(),
            followUpTaskCreated: true,
          },
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
        });
        const taskDoc = doc(collection(firestore, "tasks"));

        let assignedTo = null;
        if (brandStats.data().staff.manager.id?.length > 0) {
          assignedTo = {
            name: {
              first: brandStats.data().staff.manager.name.split(" ")[0],
              last: brandStats.data().staff.manager.name.split(" ")[1],
            },
            uid: brandStats.data().staff.manager.id,
          };
        }
        if (brandStats.data().staff.executive.id?.length > 0) {
          assignedTo = {
            name: {
              first: brandStats.data().staff.executive.name.split(" ")[0],
              last: brandStats.data().staff.executive.name.split(" ")[1],
            },
            uid: brandStats.data().staff.executive.id,
          };
        }
        batch.set(
          taskDoc,
          {
            created: serverTimestamp(),
            updated: serverTimestamp(),
            dueDate: moment().add(1, "weeks").endOf("day").toDate(),
            completedDate: null,
            createdBy: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            flags: {
              complete: false,
              system: true,
            },
            type: "orders",
            status: "notStarted",
            priority: "normal",
            notes: `Follow up about samples on Order ${this.order.orderNumber.internal}`,
            relatedTo: {
              name: this.order.account.name,
              id: this.order.account.id,
              resource: "accounts",
            },
            contact: null,
            assignedTo,
            externalIDs: {},
            users: [store.getters.getUser.uid],
          },
          { merge: true },
        );

        // Create activity for follow-up task creation
        await createActivity({
          type: "task",
          action: "Follow-up task created",
          item: `Follow-up task created for samples on Order #${
            this.order.orderNumber.internal
          } - ${this.order.account.name} - Assigned to ${
            assignedTo
              ? `${assignedTo.name.first} ${assignedTo.name.last}`
              : "Unassigned"
          }, Due: ${moment()
            .add(1, "weeks")
            .endOf("day")
            .format("MM/DD/YYYY")}`,
          relatedTo: "tasks",
          relatedID: [taskDoc.id],
          changeData: {
            field: "taskCreation",
            taskType: "sampleFollowUp",
            relatedOrder: this.order.orderNumber.internal,
            accountName: this.order.account.name,
            accountId: this.order.account.id,
            assignedTo: assignedTo
              ? `${assignedTo.name.first} ${assignedTo.name.last}`
              : "Unassigned",
            dueDate: moment().add(1, "weeks").endOf("day").toDate(),
            priority: "normal",
            autoGenerated: true,
          },
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
        });
      }

      // Heartbeat: the activation and samples-received branches above can each add several createActivity calls.
      await checkpoint("ship_activation_staged");

      // External - Status

      // Ledger
      const oldStatus = store.getters.getOrderStatus.find(
          (option) => option.value === this.order.status,
        ),
        newStatus = store.getters.getOrderStatus.find(
          (option) => option.value === "shipped",
        );
      updates.push(
        `<span class="text-capitalize">status</span> - <span class="text-uppercase">old</span>: <span class="font-weight-bold" style="color: ${oldStatus.color};">${oldStatus.text}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold" style="color: ${newStatus.color};">${newStatus.text}</span>`,
      );
      updates.push(
        `<span class="text-capitalize">balance due</span>: <span class="font-weight-bold">${new Intl.NumberFormat(
          "en-US",
          {
            style: "currency",
            currency: "USD",
          },
        ).format(balance.due)}</span>`,
      );

      if (Number(balance.due) <= 0) {
        // Status
        let status = "complete";
        let paid = true;
        const oldStatus = store.getters.getOrderStatus.find(
            (option) => option.value === "shipped",
          ),
          newStatus = store.getters.getOrderStatus.find(
            (option) => option.value === "complete",
          );

        // Order — use dotted records/flags paths so we do not wipe records.shipped in the same batch.
        batch.set(
          salesOrderDoc,
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            status,
            balance,
          },
          { merge: true },
        );
        batch.update(salesOrderDoc, {
          "flags.paid": paid,
          "records.completed": moment()
            .startOf("day")
            .add(12, "hours")
            .toDate(),
        });

        //  External Order Status
        // NOTE: Leaflink complete transition moved to after batch commit for data consistency

        // Create activity for order completion
        await createActivity({
          type: "financial",
          action: "Order completed - fully paid",
          item: `Order #${this.order.orderNumber.internal.toUpperCase()} automatically completed - zero balance remaining`,
          relatedTo: "salesOrders",
          relatedID: [salesOrderDoc.id],
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          account: {
            id: this.order.account.id,
            name: this.order.account.name,
          },
          brand: {
            id: this.order.brand.id,
            name: this.order.brand.name,
          },
          businessImpact: "high",
          priority: "high",
          changeData: {
            field: "orderCompletion",
            oldValue: {
              text: oldStatus.text,
              value: oldStatus.value,
              color: oldStatus.color,
            },
            newValue: {
              text: newStatus.text,
              value: newStatus.value,
              color: newStatus.color,
            },
            finalBalance: balance.due,
            paymentStatus: "fully_paid",
            orderNumber: this.order.orderNumber.internal,
            completionTrigger: "shipped_with_zero_balance",
            completionType: "automatic",
            externalOrderUpdated:
              this.externalUpdates.leaflink.includes("statusUpdated"),
            completionDate: new Date(),
          },
        });
      }

      // Try nudool operations first
      addBreadcrumb("shippedAction: committing batch", {
        workflow: "sales-order",
        data: {
          orderId: this.orderID,
          deliveredCount: deliveredItems.length,
          rejectedCount: rejectedItems.length,
          balance,
          attemptId,
        },
      });

      // Heartbeat: the brandStats/account reads and the activation, samples and auto-complete branches above each add awaits and a createActivity call.
      await checkpoint("ship_writes_staged");

      // Re-verify soft-lock ownership before the financial commit; a plain read, so a reclaim racing batch.commit() is narrowed but not closed.
      const lockSnap = await getDoc(salesOrderDocRef);
      const lockData = lockSnap.exists() ? lockSnap.data() : null;
      if (
        lockData?.status === "shipped" &&
        lockData?.records?.shipped != null
      ) {
        const alreadyDone = new Error(
          `Order #${this.order?.orderNumber?.internal ?? this.orderID} was already shipped by another session. Refresh and verify before retrying.`,
        );
        alreadyDone.code = "SHIP_ALREADY_DONE";
        throw alreadyDone;
      }
      if (lockData?.shippingClaim?.attemptId !== attemptId) {
        const lockLost = new Error(
          `Order #${this.order?.orderNumber?.internal ?? this.orderID} ship aborted — another session reclaimed the shipping lock. Refresh and try again if still needed.`,
        );
        lockLost.code = "SHIP_LOCK_LOST";
        throw lockLost;
      }

      await batch.commit();
      batchCommitted = true;

      // Exactly what the money batch applied — the compensating unship reverses these.
      const shipDeltas = {
        dueDelta,
        salesDelta,
        units,
        autoCompleted: Number(balance.due) <= 0,
        preShipFlagsPaid,
        preShipStatus,
        shipSetTheShipDate,
      };

      // meta is repeated here because _persistShippingProgress replaces the whole map, and a bare checkpoint would wipe the deltas a crashed tab needs to reverse.
      await checkpoint("ship_saved", {
        awaitWrite: true,
        meta: {
          deliveredCount: deliveredItems.length,
          rejectedCount: rejectedItems.length,
          dueDelta,
          salesDelta,
          units,
          autoCompleted: Number(balance.due) <= 0,
        },
      });

      // External — LeafLink runs immediately after the money commit (before credits, first purchases and activities) so a failed ship can be compensated without unwinding those too.
      if (this.orderIsExternal) {
        const syncsStatus =
          this.externalUpdates.leaflink.includes("statusUpdated");
        const syncsOrder =
          this.externalUpdates.leaflink.includes("orderUpdated");

        // Phase 1 — the reject dance and the ship transition; the only stretch whose failure can leave Nudool Shipped against a LeafLink order that is not.
        let shipSyncFailure = null;
        try {
          switch (this.externalOrderType) {
            case "leaflink":
              // 1. If there are rejected items, transition to accept first
              if (rejectedItems.length > 0) {
                if (syncsStatus && syncsOrder) {
                  await this.leaflinkClient.transitionOrder("accept");
                }

                // 2. Delete rejected line items from Leaflink
                for (const item of rejectedItems) {
                  if (syncsOrder) {
                    let externalItem = null;
                    if (item.externalIDs?.leaflinkLineItemID) {
                      externalItem = {
                        id: item.externalIDs.leaflinkLineItemID,
                      };
                    } else {
                      if (this.leaflinkClient.lineItems?.length === 0) {
                        await this.leaflinkClient.getLineItems();
                      }
                      externalItem = this.leaflinkClient.lineItems.find(
                        (external) =>
                          external.productID === item.externalIDs.leaflink,
                      );
                    }
                    if (externalItem) {
                      await this.leaflinkClient.deleteLineItem(externalItem);
                    }
                  }
                }

                // 3. Transition back to fulfilled
                if (syncsStatus && syncsOrder) {
                  await this.leaflinkClient.transitionOrder("fulfill");
                }
              }

              // 4. Transition to shipped
              if (syncsStatus) {
                await this.leaflinkClient.transitionOrder("ship");
              }
              break;
            default:
              break;
          }
        } catch (shipSyncError) {
          shipSyncFailure = shipSyncError;
        }

        if (shipSyncFailure) {
          // transitionOrder rethrows a bare Error with no stable code, so ask LeafLink directly; with status sync off there was no transition to land.
          const landed = syncsStatus ? await this._leaflinkShipLanded() : true;

          logWorkflowWarning(
            `LeafLink: shippedAction ship sync failed — ${shipSyncFailure.message}`,
            {
              workflow: "sales-order",
              action: "leaflink-shipped-operations",
              context: {
                orderId: this.orderID,
                balance: this.order?.balance,
                shipLanded: landed,
              },
            },
          );

          // A ship that rejected lines cannot be reversed: the money batch already deleted those line-item docs and released their inventory, so leave it shipped and flag it.
          const rejectedLinesBlockUnship = rejectedItems.length > 0;

          // Only compensate on a definite "not shipped" — a false unship after LeafLink really shipped is worse than today's sync lag.
          if (landed === false && rejectedLinesBlockUnship) {
            await this._logPostCommitFailure("shippedAction", shipSyncFailure, {
              note: "LeafLink ship did not land, but this ship rejected line items — those line-item docs and their inventory were already released, so the ship cannot be safely reversed",
              lastCheckpoint,
              rejectedCount: rejectedItems.length,
            });
            // Its own step, not "leaflink" — an un-reversed divergence must not read as the ordinary sync lag the benign branches below record.
            noteSideEffectFailure("leaflink_unship_blocked", shipSyncFailure);
            noteShipDivergence("leaflink_unship_blocked", {
              leaflinkError: shipSyncFailure.message || String(shipSyncFailure),
              rejectedCount: rejectedItems.length,
            });
          } else if (landed === false) {
            let unshipResult = null;
            let unshipThrew = false;
            try {
              unshipResult = await this._unshipAfterFailedLeaflinkShip({
                salesOrderDocRef,
                attemptId,
                claimUser,
                deltas: shipDeltas,
                leaflinkError:
                  shipSyncFailure.message || String(shipSyncFailure),
              });
            } catch (unshipError) {
              // The transaction is all-or-nothing, so AR was never decremented and the order is still genuinely shipped — report shipped-with-warnings, not a failure.
              unshipThrew = true;
              await this._logPostCommitFailure("shippedAction", unshipError, {
                note: "LeafLink ship did not land and the compensating unship failed — order is shipped in Nudool only",
                lastCheckpoint,
                leaflinkError: shipSyncFailure.message || String(shipSyncFailure),
              });
              noteSideEffectFailure("leaflink_unship_failed", unshipError);
              noteShipDivergence("leaflink_unship_failed", {
                leaflinkError:
                  shipSyncFailure.message || String(shipSyncFailure),
                unshipError: unshipError.message || String(unshipError),
              });
            }

            // Mutually exclusive: falling through after a thrown unship would race a second shippingProgress write stamping "leaflink_synced" over a failed compensation.
            if (unshipThrew) {
              // handled above
            } else if (unshipResult?.unshipped) {
              // No checkpoint() here — it would rewrite shippingProgress as "in_progress", clobbering the "compensated" state and the reversal audit.
              lastCheckpoint = "leaflink_unshipped";

              // Logged as an activity because the only other trace is shippingProgress, which the next ship attempt overwrites.
              const restoredStatus = shipDeltas.preShipStatus ?? "fulfilled";
              // Resolved from the store so the activity feed renders the same chips/colors as a normal transition.
              const unshippedFrom = store.getters.getOrderStatus.find(
                (option) => option.value === "shipped",
              );
              const unshippedTo = store.getters.getOrderStatus.find(
                (option) => option.value === restoredStatus,
              );
              try {
                await createActivity({
                  type: "status",
                  action: "Ship reversed - LeafLink did not accept the ship",
                  item: `Order #${this.order.orderNumber.internal.toUpperCase()} was returned to ${
                    unshippedTo?.text || restoredStatus
                  } because LeafLink did not accept the ship transition. Balance and units were reversed.`,
                  relatedTo: "salesOrders",
                  relatedID: [this.order.id],
                  user: claimUser,
                  account: {
                    id: this.order.account.id,
                    name: this.order.account.name,
                  },
                  brand: {
                    id: this.order.brand.id,
                    name: this.order.brand.name,
                  },
                  businessImpact: "high",
                  priority: "high",
                  changeData: {
                    field: "status",
                    oldValue: {
                      text: unshippedFrom?.text || "Shipped",
                      value: "shipped",
                      color: unshippedFrom?.color || "#000000",
                    },
                    newValue: {
                      text: unshippedTo?.text || restoredStatus,
                      color: unshippedTo?.color || "#000000",
                      value: restoredStatus,
                    },
                    orderNumber: this.order.orderNumber.internal,
                    reversedDueDelta: shipDeltas.dueDelta,
                    reversedSalesDelta: shipDeltas.salesDelta,
                    reversedUnits: shipDeltas.units,
                    leaflinkError:
                      shipSyncFailure.message || String(shipSyncFailure),
                    // Left in place by design — flagged so a reconciler can find them.
                    accountActivationRetained:
                      revertState.brandStatsActivationRan,
                  },
                });
              } catch (activityErr) {
                logWorkflowWarning(
                  `shippedAction: unship activity failed — ${activityErr.message}`,
                  {
                    workflow: "sales-order",
                    action: "shipped-unship-activity",
                    context: { orderId: this.orderID },
                  },
                );
              }
              const unshipped = new Error(
                `Order #${this.order?.orderNumber?.internal ?? this.orderID} could not be shipped — LeafLink did not accept the ship transition, so the order was returned to Fulfilled. Try shipping again.`,
              );
              unshipped.code = "SHIP_LEAFLINK_UNSHIPPED";
              throw unshipped;
            } else if (unshipResult?.diverged) {
              // The transaction declined to reverse (NaN AR or over the write limit), so nothing moved and this needs reconciliation.
              await this._logPostCommitFailure(
                "shippedAction",
                shipSyncFailure,
                {
                  note: `LeafLink ship did not land and the compensating unship declined to run (${unshipResult.reason}) — order is shipped in Nudool only`,
                  lastCheckpoint,
                  unshipSkipped: unshipResult.reason,
                },
              );
              noteSideEffectFailure("leaflink_unship_refused", shipSyncFailure);
              noteShipDivergence("leaflink_unship_refused", {
                leaflinkError:
                  shipSyncFailure.message || String(shipSyncFailure),
                unshipSkipped: unshipResult.reason,
              });
            } else {
              // Preconditions failed — re-read live state before assuming that's benign; the order may have moved on entirely.
              const liveSnap = await getDoc(salesOrderDocRef);
              const liveData = liveSnap.exists() ? liveSnap.data() : null;
              const stillGenuinelyShipped =
                (liveData?.status === "shipped" ||
                  liveData?.status === "complete") &&
                liveData?.records?.shipped != null;

              if (!stillGenuinelyShipped) {
                await this._logPostCommitFailure(
                  "shippedAction",
                  shipSyncFailure,
                  {
                    note: `LeafLink ship did not land and the compensating unship was skipped (${unshipResult?.reason ?? "unknown"}) — the order is no longer shipped, so this attempt cannot report success`,
                    lastCheckpoint,
                    unshipSkipped: unshipResult?.reason ?? "unknown",
                    liveStatus: liveData?.status ?? "missing",
                  },
                );
                noteSideEffectFailure(
                  "leaflink_unship_skipped_diverged",
                  shipSyncFailure,
                );
                noteShipDivergence("leaflink_unship_skipped_diverged", {
                  leaflinkError:
                    shipSyncFailure.message || String(shipSyncFailure),
                  unshipSkipped: unshipResult?.reason ?? "unknown",
                  liveStatus: liveData?.status ?? "missing",
                });
                const skippedDiverged = new Error(
                  `Order #${this.order?.orderNumber?.internal ?? this.orderID} could not be shipped — LeafLink did not accept the ship transition, and the order is no longer shipped in Nudool. Refresh and verify before retrying.`,
                );
                skippedDiverged.code = "SHIP_UNSHIP_SKIPPED_DIVERGED";
                throw skippedDiverged;
              }

              // Benign — another attempt already landed this exact shipped state.
              noteSideEffectFailure("leaflink", shipSyncFailure);
              checkpoint("leaflink_synced", {
                meta: {
                  leaflinkError:
                    shipSyncFailure.message || String(shipSyncFailure),
                  unshipSkipped: unshipResult?.reason ?? "unknown",
                },
              });
            }
          } else {
            noteSideEffectFailure("leaflink", shipSyncFailure);
            checkpoint("leaflink_synced", {
              meta: {
                leaflinkError:
                  shipSyncFailure.message || String(shipSyncFailure),
                shipLanded: landed,
              },
            });
          }
        } else {
          // Phase 2 — ship date and auto-complete; LeafLink is already Shipped here, so a failure is a warning only.
          try {
            if (this.externalOrderType === "leaflink") {
              // 5. Update ship date if this ship set it
              if (shipSetTheShipDate) {
                let shipDateMoment = moment(shipDate);
                let leaflinkShipDate = moment(
                  shipDateMoment.clone().format("MM/DD/YYYY"),
                )
                  .add(9, "hours")
                  .tz("America/Los_Angeles");
                let patchData = {
                  ship_date: moment(leaflinkShipDate).toISOString(),
                };
                if (syncsOrder) {
                  await this.leaflinkClient.updateOrderData(patchData);
                }
              }

              // 6. If order is complete (balance due <= 0), transition to complete
              if (Number(balance.due) <= 0 && syncsStatus) {
                await this.leaflinkClient.transitionOrder("complete");
              }
            }
            checkpoint("leaflink_synced");
          } catch (postShipError) {
            logWorkflowWarning(
              `LeafLink: shippedAction post-ship sync failed — ${postShipError.message}`,
              {
                workflow: "sales-order",
                action: "leaflink-shipped-post-ship",
                context: { orderId: this.orderID, balance: this.order?.balance },
              },
            );
            // LeafLink already shipped — never unship for these.
            noteSideEffectFailure("leaflink", postShipError);
            checkpoint("leaflink_synced", {
              meta: {
                leaflinkError: postShipError.message || String(postShipError),
              },
            });
          }
        }
      } else {
        checkpoint("leaflink_skipped");
      }

      if (Number(balance.due) <= 0) {
        try {
          await this._createFirstPurchases(
            moment().startOf("day").add(12, "hours").toDate(),
          );
        } catch (error) {
          logWorkflowWarning(
            `shippedAction: _createFirstPurchases failed — ${error.message}`,
            {
              workflow: "sales-order",
              action: "create-first-purchases",
              context: {
                orderId: this.orderID,
                accountId: this.order.account?.id,
                brandId: this.order.brand?.id,
              },
            },
          );
          noteSideEffectFailure("first_purchases", error);
        }
      }

      try {
        revertState.overpaymentRevertInfo = await this.checkOverpaymentCredits(
          balance.due,
        );
        checkpoint("overpayment_credits_checked");
      } catch (creditsErr) {
        logWorkflowWarning(
          `shippedAction: checkOverpaymentCredits failed after ship_saved — ${creditsErr.message}`,
          {
            workflow: "sales-order",
            action: "shipped-overpayment-credits",
            context: { orderId: this.orderID, balance },
          },
        );
        // Absorbability is preflighted above, so reaching here means a config/network
        // failure left real money unaccounted for — flag it, never silently note it.
        await this._logPostCommitFailure("shippedAction", creditsErr, {
          note: "Order shipped; overpayment credit was not created — AR is overpaid until reconciled",
          balanceDue: balance?.due ?? null,
        });
        noteSideEffectFailure("overpayment_credits", creditsErr);
      }

      // Record inventory activities for rejected (not delivered) line items so they appear on the Inventory Activity Report.
      // Same place records both current and legacy (e.g. external/Leaflink) removals; report shows "Line removed (shipped)" for both.
      if (rejectedItems.length > 0) {
        const locationName =
          this.order.fulfillmentLocation?.name ??
          this.order.location?.shipping?.name ??
          "";
        const locationId =
          this.order.fulfillmentLocation?.id ??
          fulfillmentLocation ??
          this.order.location?.shipping?.id;
        const brandId = this.order.brand?.id;
        const orderNumberInternal =
          this.order.orderNumber?.internal ?? this.order.orderNumber;
        for (const item of rejectedItems) {
          let snapshot = {};
          if (locationBeforeDataForActivity != null) {
            const mfg =
              locationBeforeDataForActivity.manufacturing?.[item.categoryID];
            snapshot = {
              before: {
                available: mfg?.available?.[item.productID] ?? 0,
                reserved: mfg?.reserved?.[item.productID] ?? 0,
                transfer: mfg?.transfer?.[item.productID] ?? 0,
              },
            };
          }
          if (!locationId || !brandId) {
            console.error(
              "createInventoryActivity (line removed at ship): missing location or brand, skipping activity",
              { locationId, brandId, productName: item.name, orderId: this.order.id },
            );
            logWorkflowWarning("shippedAction: inventory activity skipped — missing locationId or brandId", {
              workflow: "sales-order",
              action: "shipped-inventory-activity",
              context: { orderId: this.orderID, locationId, brandId, productName: item.name },
            });
            continue;
          }
          const skuID = `${locationId}_${brandId}_${item.categoryID}_${item.productID}`;
          const itemText = `${item.qty} units of ${item.name} (${item.sku}) returned to available – not delivered at ship for Order #${orderNumberInternal}`;
          const reason = this.orderIsExternal
            ? "lineItemRemoved"
            : "salesOrderLineRemovedShipped";
          try {
            await createInventoryActivity({
              action: "Inventory released",
              item: itemText,
              skuID,
              itemData: {
                name: item.name,
                sku: item.sku,
                categoryID: item.categoryID,
                productID: item.productID,
                categoryName: item.categoryName || item.category,
              },
              movement: {
                type: "release",
                from: "reserved",
                to: "available",
                quantity: item.qty,
                reason,
                reasonID: this.order.id,
              },
              snapshot,
              location: { id: locationId, name: locationName },
              order: this.order,
              user: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              extraSalesOrderData: { orderID: this.orderID },
            });
          } catch (activityErr) {
            console.error(
              "createInventoryActivity (line removed at ship):",
              activityErr,
              { skuID, productName: item.name, orderId: this.order.id },
            );
            logWorkflowWarning(`shippedAction: createInventoryActivity failed for rejected item — ${activityErr.message}`, {
              workflow: "sales-order",
              action: "shipped-inventory-activity",
              context: { orderId: this.orderID, skuID, productName: item.name },
            });
            noteSideEffectFailure("inventory_activity", activityErr);
            // Don't throw – order is already shipped; report row may be missing for this SKU
          }
        }
      }
      checkpoint("inventory_activities_done");

      // Create activity for order shipping
      const deliveredCount = deliveredItems.length;
      const rejectedCount = rejectedItems.length;
      const totalItems = deliveredCount + rejectedCount;

      let shippingDetails = [];
      if (deliveredCount > 0) {
        shippingDetails.push(
          `${deliveredCount} item${deliveredCount > 1 ? "s" : ""} delivered`,
        );
      }
      if (rejectedCount > 0) {
        shippingDetails.push(
          `${rejectedCount} item${
            rejectedCount > 1 ? "s" : ""
          } rejected and inventory returned`,
        );
      }

      try {
        await createActivity({
        type: "logistics",
        action: "Order shipped",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} shipped - ${shippingDetails.join(
          ", ",
        )} - Balance due: ${new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(balance.due)}`,
        relatedTo: "salesOrders",
        relatedID: [salesOrderDoc.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "orderShipping",
          oldValue: {
            text: oldStatus.text,
            value: oldStatus.value,
            color: oldStatus.color,
          },
          newValue: {
            text: newStatus.text,
            value: newStatus.value,
            color: newStatus.color,
          },
          shippingSummary: {
            totalItems,
            deliveredItems: deliveredCount,
            rejectedItems: rejectedCount,
            deliveredItemsList: deliveredItems.map((item) => ({
              name: item.name,
              qty: item.qty,
              sku: item.sku,
            })),
            rejectedItemsList: rejectedItems.map((item) => ({
              name: item.name,
              qty: item.qty,
              sku: item.sku,
            })),
          },
          balanceDue: balance.due,
          balanceDueFormatted: new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(balance.due),
          orderNumber: this.order.orderNumber.internal,
          shipDate: shipDate || new Date(),
          inventoryUpdated: rejectedCount > 0,
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
        },
      });
        checkpoint("ship_activity_logged");
      } catch (activityErr) {
        logWorkflowWarning(
          `shippedAction: Order shipped activity failed after ship_saved — ${activityErr.message}`,
          {
            workflow: "sales-order",
            action: "shipped-activity",
            context: { orderId: this.orderID },
          },
        );
        noteSideEffectFailure("ship_activity", activityErr);
      }

      // Add status update activity for shipped status
      const statusOld = store.getters.getOrderStatus.find(
        (option) => option.value === preShipStatus,
      );
      const statusNew = store.getters.getOrderStatus.find(
        (option) => option.value === "shipped",
      );
      try {
        await createActivity({
        type: "status",
        action: "Order status updated",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} status changed from ${
          statusOld?.text || preShipStatus
        } to ${statusNew?.text || "shipped"}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "status",
          oldValue: {
            text: statusOld?.text || preShipStatus,
            value: preShipStatus,
            color: statusOld?.color || "#000000",
          },
          newValue: {
            text: statusNew?.text || "shipped",
            value: "shipped",
            color: statusNew?.color || "#000000",
          },
          orderNumber: this.order.orderNumber.internal,
          fulfillmentContext: "order_shipping",
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
        },
      });
        checkpoint("status_activity_logged");
      } catch (statusActivityErr) {
        logWorkflowWarning(
          `shippedAction: status activity failed after ship_saved — ${statusActivityErr.message}`,
          {
            workflow: "sales-order",
            action: "shipped-status-activity",
            context: { orderId: this.orderID },
          },
        );
        noteSideEffectFailure("status_activity", statusActivityErr);
      }

      let totalSaved = this.lineItems
        .filter((item) => item.amount < item.price.normal)
        .reduce((sum, item) => {
          let discountAmount = item.price.normal - item.amount;
          return sum + discountAmount * item.qty;
        }, 0);

      let totalQTY = this.lineItems.reduce((acc, item) => {
        return acc + Number(item.qty);
      }, 0);

      let orderPaymentTerm =
        store.getters.getPaymentTerms.find(
          (item) => item.value === this.order.paymentTerm,
        )?.text ?? "[Error]";

      let savingsStyle = totalSaved > 0 ? "color: #556B2F" : "color: #000000";
      let savings = `<span style="${savingsStyle}">${Intl.NumberFormat(
        "en-US",
        {
          style: "currency",
          currency: "usd",
        },
      ).format(totalSaved)}</span>`;

      if (
        payableTo?.flags?.orderEmails &&
        this.order.buyerInformation?.email?.length > 0
      ) {
        let cc = new Set();
        if (brandStats.data().staff?.manager?.email?.length > 0) {
          cc.add(brandStats.data().staff.manager.email);
        }
        if (brandStats.data().staff?.executive?.email?.length > 0) {
          cc.add(brandStats.data().staff.executive.email);
        }
        if (payableTo.staff?.inventoryManager?.uid) {
          let inventoryManagerUser = doc(
            firestore,
            "users",
            payableTo.staff?.inventoryManager?.uid,
          );
          const inventoryManagerRes = await getDoc(inventoryManagerUser);
          cc.add(inventoryManagerRes.data().email);
        }
        if (payableTo.staff?.accountManager?.uid) {
          let accountManagerUser = doc(
            firestore,
            "users",
            payableTo.staff?.accountManager?.uid,
          );
          const accountManagerRes = await getDoc(accountManagerUser);
          cc.add(accountManagerRes.data().email);
        }
        cc = Array.from(cc);

        const shippedEmailCurrency = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "usd",
        });

        const shippedEmailTableWrap =
          "width:100%;border-collapse:collapse;margin:0 0 20px 0;font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.4;color:#333333;";
        const shippedEmailTh =
          "text-align:left;padding:12px 14px;border-bottom:2px solid #d0d0d0;background-color:#f4f4f5;font-weight:600;font-size:11px;text-transform:uppercase;letter-spacing:0.06em;color:#555555;";
        const shippedEmailTd =
          "padding:12px 14px;border-bottom:1px solid #ececec;vertical-align:middle;";
        const shippedEmailTdNum =
          "padding:12px 14px;border-bottom:1px solid #ececec;vertical-align:middle;text-align:right;white-space:nowrap;";
        const shippedEmailSectionTitle =
          "font-family:Arial,Helvetica,sans-serif;font-size:13px;font-weight:700;color:#222222;margin:0 0 10px 0;letter-spacing:0.02em;";

        const categoryPriceBuckets = new Map();
        for (const lineItem of deliveredItems) {
          const rawCategory =
            lineItem.categoryName || lineItem.category || "Uncategorized";
          const unitCents = Math.round(Number(lineItem.amount) * 100);
          const key = `${String(rawCategory)
            .toLowerCase()
            .trim()}\x00${unitCents}`;
          const qty = Number(lineItem.qty) || 0;
          const existing = categoryPriceBuckets.get(key);
          if (existing) {
            existing.qty += qty;
          } else {
            categoryPriceBuckets.set(key, {
              categoryLabel: capitalize(String(rawCategory)),
              qty,
              unitPrice: unitCents / 100,
            });
          }
        }

        const categorySummaryRows = Array.from(categoryPriceBuckets.values())
          .sort((a, b) => {
            const byCat = a.categoryLabel.localeCompare(b.categoryLabel, "en", {
              sensitivity: "base",
            });
            if (byCat !== 0) return byCat;
            return a.unitPrice - b.unitPrice;
          })
          .map(
            (row) => `<tr>
                  <td style="${shippedEmailTd}">${row.categoryLabel}</td>
                  <td style="${shippedEmailTdNum}">${row.qty}</td>
                  <td style="${shippedEmailTdNum}">${shippedEmailCurrency.format(
              row.unitPrice,
            )}</td>
                  <td style="${shippedEmailTdNum}">${shippedEmailCurrency.format(
              row.qty * row.unitPrice,
            )}</td>
                </tr>`,
          )
          .join("");

        const lineItemsForDetailTable = [...deliveredItems].sort((a, b) => {
          const catA = (a.categoryName || a.category || "").trim();
          const catB = (b.categoryName || b.category || "").trim();
          const byCat = catA.localeCompare(catB, "en", { sensitivity: "base" });
          if (byCat !== 0) return byCat;
          const nameA = (a.name || "").trim();
          const nameB = (b.name || "").trim();
          return nameA.localeCompare(nameB, "en", { sensitivity: "base" });
        });

        const lineItemDetailRows = lineItemsForDetailTable
          .map((lineItem) => {
            const categorySubline = (
              lineItem.categoryName ||
              lineItem.category ||
              ""
            ).trim();
            const categoryHtml = categorySubline
              ? `<span style="display:block;font-size:11px;color:#777777;margin-top:4px;">${capitalize(
                  categorySubline,
                )}</span>`
              : "";
            const strainTypeRaw = (lineItem.strainType ?? "").toString().trim();
            const strainTypeCell = strainTypeRaw
              ? capitalize(strainTypeRaw)
              : "&#8212;";
            return `<tr>
                  <td style="${shippedEmailTd}">${capitalize(
              lineItem.name,
            )}${categoryHtml}</td>
                  <td style="${shippedEmailTd}">${strainTypeCell}</td>
                  <td style="${shippedEmailTdNum}">${lineItem.qty}</td>
                  <td style="${shippedEmailTdNum}">${shippedEmailCurrency.format(
              lineItem.amount,
            )}</td>
                  <td style="${shippedEmailTdNum}">${shippedEmailCurrency.format(
              Number(lineItem.amount) * Number(lineItem.qty),
            )}</td>
                </tr>`;
          })
          .join("");

        let shipDate = this.order.shipDate
          ? moment(this.order.shipDate.seconds * 1000)
              .startOf("day")
              .add(9, "hours")
              .tz("America/Los_Angeles")
              .toDate()
          : null;

        let replyTo =
          payableTo?.email?.length > 0
            ? {
                email: payableTo.email,
              }
            : null;

        const shippedEmailData = {
          subject: `Your ${capitalize(
            this.order.brand.name
          )} order has been shipped | ${this.order.orderNumber.internal.toUpperCase()}`,
          disposition: `Your order has been shipped! Below is a summary of your order.`,
          brand: capitalize(this.order.brand.name),
          name: capitalize(`${this.order.buyerInformation.name.split(" ")[0]}`),
          toEmail: this.order.buyerInformation.email,
          paymentTerm: orderPaymentTerm,
          totalQty: totalQTY,
          savings,
          balance: Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(Number(this.order.balance.due)),
          subtotal: Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(
            Number(this.order.balance.due) - Number(this.order.balance.shipping)
          ),
          shipping: Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(Number(this.order.balance.shipping)),
          totalSum: Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "usd",
          }).format(Number(this.order.balance.total)),
          storeName: accountData.data().name,
          storeAddress: capitalize(
            accountData.data().location.shipping.street.number +
              " " +
              accountData.data().location.shipping.street.name +
              ", " +
              accountData.data().location.shipping.city.name +
              ", " +
              accountData
                .data()
                .location.shipping.state.abbreviation.toUpperCase() +
              " " +
              accountData.data().location.shipping.zipCode
          ),
          licenseNumber: accountData.data().license.number.toUpperCase(),
          licenseExpiration: moment(
            accountData.data().license.expiration?.seconds * 1000
          ).format("MM/DD/YYYY"),
          deliveryPreferences:
            this.order.account.deliveryPreferences.length > 0
              ? this.order.account.deliveryPreferences
              : "-",
          notes: this.order.notes.length > 0 ? this.order.notes : "-",
          shipDate: shipDate ? moment(shipDate).format("MM/DD/YYYY") : "-",
          lineItems: `<div>
              <p style="${shippedEmailSectionTitle}">Summary</p>
              <table role="presentation" cellpadding="0" cellspacing="0" style="${shippedEmailTableWrap}">
                <thead>
                  <tr>
                    <th width="40%" style="${shippedEmailTh}">Category</th>
                    <th width="20%" style="${shippedEmailTh};text-align:right;">QTY</th>
                    <th width="20%" style="${shippedEmailTh};text-align:right;">Price</th>
                    <th width="20%" style="${shippedEmailTh};text-align:right;">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${categorySummaryRows}
                </tbody>
              </table>
              <p style="${shippedEmailSectionTitle}">Products Shipped</p>
              <table role="presentation" cellpadding="0" cellspacing="0" style="${shippedEmailTableWrap}">
                <thead>
                  <tr>
                    <th width="32%" style="${shippedEmailTh}">Product</th>
                    <th width="18%" style="${shippedEmailTh}">Strain</th>
                    <th width="14%" style="${shippedEmailTh};text-align:right;">Qty</th>
                    <th width="18%" style="${shippedEmailTh};text-align:right;">Price</th>
                    <th width="18%" style="${shippedEmailTh};text-align:right;">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${lineItemDetailRows}
                </tbody>
              </table>
            </div>`,
        };

        try {
          await orderSubmissionEmail({
            subject: shippedEmailData.subject,
            html: buildOrderEmailHtml(shippedEmailData),
            cc,
            reply_to: replyTo,
            dynamic_template_data: { toEmail: shippedEmailData.toEmail },
          });
          checkpoint("email_sent");
        } catch (emailError) {
          logWorkflowWarning(`shippedAction: confirmation email failed — ${emailError.message}`, {
            workflow: "sales-order",
            action: "shipped-confirmation-email",
            context: { orderId: this.orderID, toEmail: shippedEmailData.toEmail },
          });
          // Ship already saved — do not revert for email failures.
          noteSideEffectFailure("email", emailError);
          checkpoint("email_sent", {
            meta: { emailError: emailError.message || String(emailError) },
          });
        }
      } else {
        checkpoint("email_skipped");
      }

      if (shipDivergence) {
        // Terminal state for an order Shipped in Nudool only — deliberately not "succeeded", which would hide it from any sweep for unreconciled orders.
        await this._persistShippingProgress(
          salesOrderDocRef,
          {
            attemptId,
            status: "succeeded_with_divergence",
            checkpoint: shipDivergence.checkpoint,
            batchCommitted: true,
            startedBy: claimUser,
            error: null,
            meta: {
              ...shipDivergence.meta,
              ...(sideEffectFailures.length
                ? { sideEffectFailures: sideEffectFailures.map((f) => f.step) }
                : {}),
            },
          },
          { awaitWrite: true },
        );
      } else {
        await checkpoint("completed", { awaitWrite: true });
        await this._persistShippingProgress(
          salesOrderDocRef,
          {
            attemptId,
            status: "succeeded",
            checkpoint: "completed",
            batchCommitted: true,
            startedBy: claimUser,
            error: null,
            ...(sideEffectFailures.length
              ? {
                  meta: {
                    sideEffectFailures: sideEffectFailures.map((f) => f.step),
                  },
                }
              : {}),
          },
          { awaitWrite: true },
        );
      }
      return {
        shipped: true,
        sideEffectFailures,
      };
    } catch (error) {
      // Pre-ship_saved: clear soft lock only (status never flipped to shipped early).
      // Post-ship_saved: side effects are warn-only above; this path should be rare.
      // The unship already reverted the order, so this must surface as a failure — swallowing it below would toast "Order shipped" over an order that is not.
      // SHIP_UNSHIP_SKIPPED_DIVERGED is the same shape from the other direction: the unship was skipped and the order is not shipped either.
      if (
        error?.code === "SHIP_LEAFLINK_UNSHIPPED" ||
        error?.code === "SHIP_UNSHIP_SKIPPED_DIVERGED"
      ) {
        throw error;
      }

      if (batchCommitted) {
        noteSideEffectFailure("unexpected_post_save", error);
        logWorkflowWarning(
          `shippedAction: unexpected error after ship_saved (not reverting) — ${error.message}`,
          {
            workflow: "sales-order",
            action: "shipped-post-save-error",
            context: {
              orderId: this.orderID,
              lastCheckpoint,
              balance: this.order?.balance,
            },
          },
        );
        try {
          const errorLogRef = doc(collection(firestore, "errorLogs"));
          const timestamp = serverTimestamp();
          await setDoc(errorLogRef, {
            error: {
              message: error.message || String(error),
              stack: error.stack || null,
              name: error.name || null,
              context: {
                function: "shippedAction",
                additionalInfo: {
                  orderId: this?.order?.id || null,
                  lastCheckpoint,
                  batchCommitted: true,
                  note: "Ship saved; side-effect/unexpected error — AR/status not reverted",
                },
              },
            },
            needsManualReview: true,
            createdBy: {
              uid: store.getters.getUser?.uid || null,
              email: store.getters.getUser?.email || null,
              name: store.getters.getUser?.name || null,
            },
            created: timestamp,
            updated: timestamp,
            triggeredFrom: "SalesOrderClient/shippedAction",
            relatedOrder: {
              id: this?.order?.id || null,
              orderNumber: this?.order?.orderNumber || null,
            },
          });
        } catch (logError) {
          console.error("Failed to log error to Firestore errorLogs:", logError);
        }
        // Do not throw — order is shipped; caller should treat as success with possible side-effect gaps.
        return {
          shipped: true,
          sideEffectFailures,
        };
      }

      // Another session owns the lock (or already finished) — do not clear their soft lock.
      if (
        error?.code === "SHIP_LOCK_LOST" ||
        error?.code === "SHIP_ALREADY_DONE"
      ) {
        throw error;
      }

      await clearSoftLockOnly("failed", error.message || String(error));

      try {
        const errorLogRef = doc(collection(firestore, "errorLogs"));
        const timestamp = serverTimestamp();
        await setDoc(errorLogRef, {
          error: {
            message: error.message || String(error),
            stack: error.stack || null,
            name: error.name || null,
            context: {
              function: "shippedAction",
              additionalInfo: {
                orderId: this?.order?.id || null,
                brandId: this?.order?.brand?.id || null,
                accountId: this?.order?.account?.id || null,
                lineItemCount: Array.isArray(lineItems) ? lineItems.length : null,
                lastCheckpoint,
                batchCommitted: false,
                preShipStatus,
              },
            },
          },
          needsManualReview: false,
          createdBy: {
            uid: store.getters.getUser?.uid || null,
            email: store.getters.getUser?.email || null,
            name: store.getters.getUser?.name || null,
          },
          created: timestamp,
          updated: timestamp,
          lastUpdated: {
            uid: store.getters.getUser?.uid || null,
            name: store.getters.getUser?.name || null,
          },
          relatedOrder: {
            id: this?.order?.id || null,
            orderNumber: this?.order?.orderNumber || null,
          },
          relatedBrand: {
            id: this?.order?.brand?.id || null,
            name: this?.order?.brand?.name || null,
          },
          relatedAccount: {
            id: this?.order?.account?.id || null,
            name: this?.order?.account?.name || null,
          },
          triggeredFrom: "SalesOrderClient/shippedAction",
          data: { lineItems },
        });
      } catch (logError) {
        console.error("Failed to log error to Firestore errorLogs:", logError);
        logWorkflowWarning(`shippedAction: errorLogs Firestore write failed — ${logError.message}`, {
          workflow: "sales-order",
          action: "shipped-action-errorlog",
          context: { orderId: this.orderID },
        });
      }
      throw error;
    }
  }

  // [FULFILLED]
  async fulfilledAction(
    deliveryLogistics,
    deliveryMethod,
    deliveredBy,
    shipDate,
    confirmationMethod,
    manifest,
    notes,
    deliveryFee,
    accountCloseData = null,
  ) {
    addBreadcrumb("fulfilledAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    await this.getOrder();
    await this.getLineItems();

    if (this.order?.status === "fulfilled") {
      return;
    }

    // Atomic claim: only one concurrent request can transition to fulfilled.
    const salesOrderDocRef = doc(firestore, "salesOrders", this.order.id);
    const applied = await runTransaction(firestore, async (transaction) => {
      const orderSnap = await transaction.get(salesOrderDocRef);
      if (!orderSnap.exists() || orderSnap.data().status === "fulfilled") {
        return false;
      }
      transaction.update(salesOrderDocRef, {
        status: "fulfilled",
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
      });
      return true;
    });

    if (!applied) {
      return;
    }

    let updates = [];

    try {
      const batch = writeBatch(firestore);

      const salesOrderDoc = salesOrderDocRef;

      let newShipDate = moment(shipDate)
        .startOf("day")
        .add(9, "hours")
        .tz("America/Los_Angeles")
        .toDate();
      let formattedShipDate = moment(newShipDate).format("MM/DD/YYYY");
      let newDeliveredBy = null,
        deliveryOption = deliveryMethod;
      if (deliveryMethod !== "account") {
        const selected = deliveryLogistics.find(
          (option) => option.id === deliveredBy,
        );
        console.log(deliveryLogistics, selected);
        if (selected) {
          newDeliveredBy = {
            name: selected.name,
            id: selected.id,
          };
        }
      }

      batch.set(
        salesOrderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          status: "fulfilled",
          records: {
            fulfilled: moment().startOf("day").add(12, "hours").toDate(),
          },
          deliveryOption: deliveryMethod,
          deliveredBy: newDeliveredBy,
          shipDate: newShipDate,
          deliveryLogisticsFee: deliveryFee,
          orderNumber: {
            manifest,
          },
        },
        { merge: true },
      );

      const stateDoc = doc(
        firestore,
        "states",
        this.order.location.shipping.state.stateID,
      );
      const stateResponse = await getDoc(stateDoc);
      let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

      if (this.order.fulfillmentLocation) {
        fulfillmentLocation = this.order.fulfillmentLocation.id;
      }

      const locationRef = doc(
        firestore,
        "locationManufacturing",
        `${fulfillmentLocation}_${this.order.brand.id}`,
      );

      // CRITICAL: Validate inventory before fulfillment to prevent negative inventory
      const inventoryCheck = await getDoc(locationRef);

      if (
        inventoryCheck.exists() &&
        inventoryCheck.data().hasOwnProperty("manufacturing")
      ) {
        let manufacturing = inventoryCheck.data().manufacturing;

        // Validate each line item BEFORE fulfilling
        this.lineItems.forEach((item) => {
          let available =
            manufacturing?.[item.categoryID]?.available?.[item.productID] ?? 0;
          let reserved =
            manufacturing?.[item.categoryID]?.reserved?.[item.productID] ?? 0;
          let transfer =
            manufacturing?.[item.categoryID]?.transfer?.[item.productID] ?? 0;

          let availableAmount = available - reserved - transfer;

          // Check if we have enough AVAILABLE inventory
          if (available - item.qty < 0) {
            throw new Error(
              `Insufficient available inventory for ${item.name} (${item.sku}). Available: ${available}, Requested: ${item.qty}`,
            );
          }

          // Check if we have enough RESERVED inventory
          if (reserved - item.qty < 0) {
            throw new Error(
              `Insufficient reserved inventory for ${item.name} (${item.sku}). Reserved: ${reserved}, Requested: ${item.qty}`,
            );
          }
        });
      } else {
        throw new Error("Inventory data not found for fulfillment location");
      }

      // Get manufacturing data for inventory snapshots (pre-write state for activities)
      let manufacturing = inventoryCheck.data().manufacturing;

      // Merge fix: one manufacturing update per location with all line-item deltas applied
      const merged = JSON.parse(JSON.stringify(manufacturing));
      this.lineItems.forEach((item) => {
        const cid = item.categoryID;
        const pid = item.productID;
        const qty = item.qty ?? 0;
        if (!merged[cid]) merged[cid] = {};
        if (!merged[cid].available) merged[cid].available = {};
        if (!merged[cid].reserved) merged[cid].reserved = {};
        merged[cid].available[pid] = Math.max(
          0,
          (merged[cid].available[pid] ?? 0) - qty,
        );
        merged[cid].reserved[pid] = Math.max(
          0,
          (merged[cid].reserved[pid] ?? 0) - qty,
        );
      });
      batch.set(locationRef, { manufacturing: merged }, { merge: true });

      // Aggregate fulfillment activity data by product so that the same product
      // at different prices (e.g. regular + promo) produces one activity with
      // correct before/after snapshots instead of overlapping per-line-item entries.
      const fulfillmentActivityMap = {};
      for (const item of this.lineItems) {
        const productKey = `${item.categoryID}_${item.productID}`;
        if (!fulfillmentActivityMap[productKey]) {
          const currentAvailable =
            manufacturing?.[item.categoryID]?.available?.[item.productID] ?? 0;
          const currentReserved =
            manufacturing?.[item.categoryID]?.reserved?.[item.productID] ?? 0;
          const currentPending =
            manufacturing?.[item.categoryID]?.pending?.[item.productID] ?? 0;
          const currentTransfer =
            manufacturing?.[item.categoryID]?.transfer?.[item.productID] ?? 0;
          const currentInTesting =
            manufacturing?.[item.categoryID]?.inTesting?.[item.productID] ?? 0;
          fulfillmentActivityMap[productKey] = {
            skuID: `${fulfillmentLocation}_${this.order.brand.id}_${item.categoryID}_${item.productID}`,
            itemData: item,
            totalQty: 0,
            before: {
              available: currentAvailable,
              reserved: currentReserved,
              pending: currentPending,
              transfer: currentTransfer,
              inTesting: currentInTesting,
            },
          };
        }
        fulfillmentActivityMap[productKey].totalQty += item.qty ?? 0;
      }

      for (const productKey of Object.keys(fulfillmentActivityMap)) {
        const agg = fulfillmentActivityMap[productKey];
        await createInventoryActivity({
          action: "Inventory fulfilled",
          item: `${agg.totalQty} units of ${agg.itemData.name} (${agg.itemData.sku}) fulfilled for Order #${this.order.orderNumber.internal}`,
          skuID: agg.skuID,
          itemData: agg.itemData,
          movement: {
            type: "fulfillment",
            from: "reserved",
            to: "fulfilled",
            quantity: agg.totalQty,
            reason: "salesOrderFulfilled",
            reasonID: this.orderID,
          },
          snapshot: {
            before: agg.before,
            after: {
              available: Math.max(0, agg.before.available - agg.totalQty),
              reserved: Math.max(0, agg.before.reserved - agg.totalQty),
              pending: agg.before.pending,
              transfer: agg.before.transfer,
              inTesting: agg.before.inTesting,
            },
          },
          location: {
            id: fulfillmentLocation,
            name:
              this.order.fulfillmentLocation?.name ||
              this.order.location.shipping.name,
          },
          order: this.order,
          user: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          extraSalesOrderData: {
            deliveryMethod: deliveryMethod,
            shipDate: newShipDate,
          },
        });
      }

      // External - Update Status
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("statusUpdated")) {
              addBreadcrumb("leaflink: transitionOrder fulfill (fulfilledAction)", {
                workflow: "sales-order",
                data: { orderId: this.orderID, fromStatus: this.order?.status },
              });
              try {
                await this.leaflinkClient.transitionOrder("fulfill");
              } catch (error) {
                logWorkflowWarning(`LeafLink: transitionOrder("fulfill") (fulfilledAction) failed — ${error.message}`, {
                  workflow: "sales-order",
                  action: "leaflink-fulfilled-transition",
                  context: { orderId: this.orderID, balance: this.order?.balance },
                });
                throw error;
              }
            }
            let shipDateMoment = moment(newShipDate);
            let shipDate = moment(shipDateMoment.clone().format("MM/DD/YYYY"))
              .add(9, "hours")
              .tz("America/Los_Angeles");
            let patchData = {
              ship_date: moment(shipDate).toISOString(),
            };
            if (this.externalUpdates.leaflink.includes("orderUpdated")) {
              addBreadcrumb("leaflink: updateOrderData ship_date (fulfilledAction)", {
                workflow: "sales-order",
                data: { orderId: this.orderID, newShipDate },
              });
              try {
                await this.leaflinkClient.updateOrderData(patchData);
              } catch (error) {
                logWorkflowWarning(`LeafLink: updateOrderData (fulfilledAction) failed — ${error.message}`, {
                  workflow: "sales-order",
                  action: "leaflink-fulfilled-update-order",
                  context: { orderId: this.orderID, balance: this.order?.balance },
                });
                throw error;
              }
            }
            break;
          default:
            break;
        }
      }

      const oldStatus = store.getters.getOrderStatus.find(
          (option) => option.value === this.order.status,
        ),
        newStatus = store.getters.getOrderStatus.find(
          (option) => option.value === "fulfilled",
        );

      console.log(confirmationMethod);

      // let shipDate = ;
      updates.push(
        `<span class="text-capitalize">status</span>: <span class="text-uppercase">old</span>: <span class="font-weight-bold" style="color: ${oldStatus.color};">${oldStatus.text}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold" style="color: ${newStatus.color};">${newStatus.text}</span>`,
      );
      updates.push(
        `<span class="text-capitalize">ship date</span>: <span class="font-weight-bold">${moment(
          newShipDate,
        ).format("MM/DD/YYYY")}</span>`,
      );
      updates.push(
        `<span class="text-capitalize">confirmed by</span>: <span class="font-weight-bold text-capitalize">${confirmationMethod.text}</span>`,
      );
      updates.push(
        `<span class="text-capitalize">delivery type</span>: <span>${
          store.getters.getDeliveryTypes.find(
            (option) => option.value === deliveryMethod,
          ).text
        }</span>, <span class="text-capitalize">delivery by</span>: <span class="text-capitalize">${
          deliveryMethod !== "account" ? newDeliveredBy?.name ?? "—" : "account"
        }</span>`,
      );
      if (notes?.length > 0) {
        updates.push(
          `<span class="text-capitalize">confirmation notes</span>: <span class="font-weight-bold">"${notes}"</span>`,
        );
      }
      if (manifest) {
        updates.push(
          `<span class="text-capitalize">manifest #</span>: <span class="font-weight-bold text-uppercase">${manifest}</span>`,
        );
      }

      // Create activity for order fulfillment completion
      const fulfillmentDetails = [];

      // Get proper delivery method text
      const deliveryType = store.getters.getDeliveryTypes.find(
        (option) => option.value === deliveryMethod,
      );
      if (deliveryType) {
        fulfillmentDetails.push(`Delivery: ${deliveryType.text}`);
      }

      // Get proper delivered by name
      let deliveredByName = "Account";
      if (deliveryMethod !== "account" && newDeliveredBy) {
        deliveredByName = newDeliveredBy.name;
      }
      fulfillmentDetails.push(`Delivered by: ${deliveredByName}`);

      if (manifest) fulfillmentDetails.push(`Manifest: ${manifest}`);
      if (deliveryFee > 0) fulfillmentDetails.push(`Fee: $${deliveryFee}`);

      await createActivity({
        type: "logistics",
        action: "Order fulfilled",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} fulfilled - ${fulfillmentDetails.join(
          ", ",
        )}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "fulfillmentCompletion",
          deliveryLogistics: deliveryLogistics,
          deliveryMethod: deliveryMethod,
          deliveredBy: deliveredBy,
          shipDate: shipDate,
          confirmationMethod: confirmationMethod,
          manifest: manifest,
          notes: notes,
          deliveryFee: deliveryFee,
          orderNumber: this.order.orderNumber.internal,
          updateType: "fulfillment_completion",
          monetaryValue: deliveryFee || 0,
          fulfillmentDate: new Date(),
          status: "fulfilled",
        },
      });

      // Create activity for status change
      const fulfillmentOldStatus = store.getters.getOrderStatus.find(
        (option) => option.value === this.order.status,
      );
      const fulfillmentNewStatus = store.getters.getOrderStatus.find(
        (option) => option.value === "fulfilled",
      );

      await createActivity({
        type: "status",
        action: "Order status updated",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} status changed from ${
          fulfillmentOldStatus?.text || this.order.status
        } to ${fulfillmentNewStatus?.text || "fulfilled"}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "status",
          oldValue: {
            text: fulfillmentOldStatus?.text || this.order.status,
            value: this.order.status,
            color: fulfillmentOldStatus?.color || "#000000",
          },
          newValue: {
            text: fulfillmentNewStatus?.text || "fulfilled",
            value: "fulfilled",
            color: fulfillmentNewStatus?.color || "#000000",
          },
          orderNumber: this.order.orderNumber.internal,
          fulfillmentContext: "order_fulfillment",
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
        },
      });

      // Ledger
      updates = updates.join(" | ");
      addBreadcrumb("fulfilledAction: committing batch", {
        workflow: "sales-order",
        data: { orderId: this.orderID, balance: this.order?.balance },
      });
      await batch.commit();

      // Search for contact that matches buyerInformation name and email with active === true
      const contactsQuery = query(
        collection(firestore, "contacts"),
        // where("name", "==", accountCloseData.buyerInformation.name),
        where("email", "==", accountCloseData.buyerInformation.email),
        where("active", "==", true),
      );
      const contactsSnapshot = await getDocs(contactsQuery);
      const contactData = contactsSnapshot.empty
        ? null
        : contactsSnapshot.docs[0].data();

      // Add new account close logic after the batch commit
      if (
        accountCloseData &&
        accountCloseData.isNewAccount &&
        accountCloseData.payableToTriggersOnboarding &&
        !accountCloseData.flags.sample
      ) {
        const sendgridEmailCompany = httpsCallable(
          functions,
          "sendgridEmailCompany",
        );

        // Create the JSON data for the email
        const jsonData = {
          accountId: accountCloseData.account.id,
          licenseNumber: accountCloseData.account.license.number.toUpperCase(),
          licenseExpiration:
            accountCloseData.account.license.expiration || null,
          salesRep: {
            id: accountCloseData.user.uid,
            name: `${accountCloseData.user.name}`,
            email: accountCloseData.user.email || null,
          },
          order: {
            orderId: accountCloseData.order.id,
            closeDate: moment().format("MMMM D, YYYY [at] h:mm:ss A [UTC]Z"),
            deliveryDate: accountCloseData.order.deliveryDate
              ? moment(
                  accountCloseData.order.deliveryDate,
                  "MM/DD/YYYY",
                ).format("MMMM D, YYYY [at] h:mm:ss A [UTC]Z")
              : null,
          },
          website: accountCloseData.account.website || null,
          storefrontName:
            accountCloseData.account.name.charAt(0).toUpperCase() +
            accountCloseData.account.name.slice(1),
          licenseName:
            accountCloseData.account.name.charAt(0).toUpperCase() +
            accountCloseData.account.name.slice(1),
          storeType: accountCloseData.account.storeType || "Retail",
          shippingAddress: {
            line1: `${accountCloseData.account.location.shipping.street.number} ${accountCloseData.account.location.shipping.street.name}`,
            city: accountCloseData.account.location.shipping.city.name,
            state:
              accountCloseData.account.location.shipping.state.abbreviation,
            postalCode: accountCloseData.account.location.shipping.zipCode,
          },
          storeContact: {
            name: accountCloseData.account.name,
            phone: accountCloseData.account.phoneNumber,
            email: accountCloseData.account.email,
          },
          buyerInformation: {
            ...accountCloseData.buyerInformation,
            phoneNumber: contactData?.phoneNumber,
          },
          accountContacts: accountCloseData.account.contacts,
          previousOrder: {
            date:
              accountCloseData.timeSinceLastOrder === "No previous orders"
                ? null
                : moment(accountCloseData.timeSinceLastOrder).format(
                    "YYYY-MM-DD",
                  ),
            totalOrders:
              accountCloseData.brandMetrics.metrics?.orders?.total || 0,
          },
        };

        await sendgridEmailCompany({
          sendgridOrderTemplate: "d-0285e0366fe14dff999f35bf5aa356c4",
          cc: [],
          dynamic_template_data: {
            subject: `New Account Close - ${
              accountCloseData.account.name.charAt(0).toUpperCase() +
              accountCloseData.account.name.slice(1)
            }`,
            toEmail: "automation@dime.industries",
            name: accountCloseData.user.name || "N/A",
            storeName:
              accountCloseData.account.name.charAt(0).toUpperCase() +
              accountCloseData.account.name.slice(1),
            disposition:
              accountCloseData.timeSinceLastOrder === "No previous orders"
                ? "This is the first order for this account."
                : `Last order was on ${accountCloseData.timeSinceLastOrder}`,
            brand:
              accountCloseData.brand.name.charAt(0).toUpperCase() +
              accountCloseData.brand.name.slice(1),
            orderTotal: `$${accountCloseData.order.total.toFixed(2)}`,
            accountData: JSON.stringify(jsonData, null, 2),
          },
        });

        console.log("email sent");
      }
    } catch (error) {
      // Revert statuses: order was set to fulfilled in transaction; Leaflink may have been transitioned
      try {
        const salesOrderDocRef = doc(firestore, "salesOrders", this.order.id);
        await updateDoc(salesOrderDocRef, {
          status: "deliveryConfirmation",
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
        });
      } catch (revertError) {
        console.error(
          "Failed to revert order status to deliveryConfirmation:",
          revertError,
        );
        logWorkflowWarning(`fulfilledAction: status revert to deliveryConfirmation failed — ${revertError.message}`, {
          workflow: "sales-order",
          action: "fulfilled-action-revert",
          context: { orderId: this.orderID, balance: this.order?.balance },
        });
      }
      if (this.orderIsExternal && this.externalOrderType === "leaflink") {
        try {
          if (this.externalUpdates.leaflink.includes("statusUpdated")) {
            await this.leaflinkClient.transitionOrder("accept");
          }
        } catch (revertError) {
          const msg =
            revertError?.message ?? revertError?.details ?? String(revertError);
          const alreadyAccepted =
            typeof msg === "string" &&
            msg.toLowerCase().includes("accepted state");
          if (!alreadyAccepted) {
            console.error(
              "Failed to revert Leaflink order to accepted:",
              revertError,
            );
            logWorkflowWarning(`fulfilledAction: LeafLink revert to accepted failed — ${revertError.message}`, {
              workflow: "sales-order",
              action: "leaflink-fulfilled-action-revert",
              context: { orderId: this.orderID, balance: this.order?.balance },
            });
          }
        }
      }

      // Log error to Firestore "errorLogs" collection for fulfilledAction
      try {
        const errorLogRef = doc(collection(firestore, "errorLogs"));
        const timestamp = serverTimestamp();
        await setDoc(errorLogRef, {
          error: {
            message: error.message || String(error),
            stack: error.stack || null,
            name: error.name || null,
            context: {
              function: "fulfilledAction",
              additionalInfo: {
                orderId: this?.order?.id || null,
                brandId: this?.order?.brand?.id || null,
                accountId: this?.order?.account?.id || null,
                lineItemCount: Array.isArray(arguments[0])
                  ? arguments[0].length
                  : null, // argument for lineItems if present
              },
            },
          },
          createdBy: {
            uid: store.getters.getUser?.uid || null,
            email: store.getters.getUser?.email || null,
            name: store.getters.getUser?.name || null,
          },
          created: timestamp,
          updated: timestamp,
          lastUpdated: {
            uid: store.getters.getUser?.uid || null,
            name: store.getters.getUser?.name || null,
          },
          relatedOrder: {
            id: this?.order?.id || null,
            orderNumber: this?.order?.orderNumber || null,
          },
          relatedBrand: {
            id: this?.order?.brand?.id || null,
            name: this?.order?.brand?.name || null,
          },
          relatedAccount: {
            id: this?.order?.account?.id || null,
            name: this?.order?.account?.name || null,
          },
          triggeredFrom: "SalesOrderClient/fulfilledAction",
          data: {
            deliveryLogistics: deliveryLogistics || null,
            deliveryMethod: deliveryMethod || null,
            deliveredBy: deliveredBy || null,
            shipDate: shipDate || null,
            confirmationMethod: confirmationMethod || null,
            manifest: manifest || null,
            notes: notes || null,
            deliveryFee: deliveryFee || null,
            accountCloseData: accountCloseData || null,
          },
        });
      } catch (logError) {
        console.error("Failed to log error to Firestore errorLogs:", logError);
        logWorkflowWarning(`fulfilledAction: errorLogs Firestore write failed — ${logError.message}`, {
          workflow: "sales-order",
          action: "fulfilled-action-errorlog",
          context: { orderId: this.orderID },
        });
      }
      throw error;
    }
  }

  // [DELIVERY CONFIRMATION]
  async deliveryConfirmationAction(lineItems) {
    console.log("deliveryConfirmationAction");

    await this.getOrder();
    await this.getLineItems();
    // Snapshot for the pre-commit overpay guard below.
    await this.getPayments();
    addBreadcrumb("deliveryConfirmationAction: start", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        fromStatus: this.order?.status,
        balance: this.order?.balance,
        fulfilledCount: lineItems.filter((i) => i.fulfilled).length,
        removedCount: lineItems.filter((i) => !i.fulfilled).length,
      },
    });

    if (lineItems.length === 0) {
      throw new Error(
        "No line items were provided. Please refresh the page and try again."
      );
    }

    try {
      const batch = writeBatch(firestore);
      const brandStatsDoc = doc(
        firestore,
        "brandStats",
        `${this.order.account.id}_${this.order.brand.id}`,
      );

      const salesOrderDoc = doc(firestore, "salesOrders", this.order.id);

      let status = "accepted";
      let updates = [];
      const stateDoc = doc(
        firestore,
        "states",
        this.order.location.shipping.state.stateID,
      );
      const stateResponse = await getDoc(stateDoc);
      let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

      if (this.order.fulfillmentLocation) {
        fulfillmentLocation = this.order.fulfillmentLocation.id;
      }
      console.log("545");
      const locationRef = doc(
        firestore,
        "locationManufacturing",
        `${fulfillmentLocation}_${this.order.brand.id}`,
      );
      let fulfilled = lineItems.filter((item) => item.fulfilled);
      let removed = lineItems.filter((item) => !item.fulfilled);

      if (fulfilled.length) {
        let fulfilledUpdates = [];
        status = lineItems.every((item) => item.fulfilled)
          ? "deliveryConfirmation"
          : "accepted";
        lineItems.forEach((item) => {
          console.log("lineItem", item);
          const lineItemDoc = doc(firestore, "salesOrders_lineItems", item.id);

          batch.set(
            lineItemDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              notes: item.notes,
              flags: {
                fulfilled: true,
              },
            },
            { merge: true },
          );
          fulfilledUpdates.push(
            `<span class="text-capitalize font-weight-bold">${
              item.name
            }</span> (${item.sku.toUpperCase()}) - <span class="text-uppercase">qty</span>: ${
              item.qty
            }`,
          );
        });
        updates.push(
          `<span class="text-capitalize">fulfilled items</span>: ${fulfilledUpdates.join(
            " / ",
          )}`,
        );
      }
      if (removed.length) {
        let removedUpdates = [];

        // Get current inventory for snapshots
        const inventoryCheck = await getDoc(locationRef);
        let manufacturing = inventoryCheck.exists()
          ? inventoryCheck.data().manufacturing
          : {};

        for (const item of removed) {
          const lineItemDoc = doc(firestore, "salesOrders_lineItems", item.id);
          batch.delete(lineItemDoc);

          // Reset inventory
          batch.set(
            locationRef,
            {
              manufacturing: {
                [item.categoryID]: {
                  reserved: {
                    [item.productID]: increment(-item.qty),
                  },
                },
              },
            },
            { merge: true },
          );

          // Track inventory unreservation for this SKU
          const skuID = `${fulfillmentLocation}_${this.order.brand.id}_${item.categoryID}_${item.productID}`;
          const currentAvailable =
            manufacturing?.[item.categoryID]?.available?.[item.productID] ?? 0;
          const currentReserved =
            manufacturing?.[item.categoryID]?.reserved?.[item.productID] ?? 0;
          const currentPending =
            manufacturing?.[item.categoryID]?.pending?.[item.productID] ?? 0;
          const currentTransfer =
            manufacturing?.[item.categoryID]?.transfer?.[item.productID] ?? 0;
          const currentInTesting =
            manufacturing?.[item.categoryID]?.inTesting?.[item.productID] ?? 0;

          // Use granular line-removed reasons so reporting is consistent with other flows.
          // Treat this path as an accepted/pre-fulfillment removal.
          const lineRemovedReason = "salesOrderLineRemovedAccepted";

          await createInventoryActivity({
            action: "Inventory unreserved",
            item: `${item.qty} units of ${item.name} (${item.sku}) unreserved - Order #${this.order.orderNumber.internal} line item removed`,
            skuID,
            itemData: item,
            movement: {
              type: "unreservation",
              from: "reserved",
              to: "available",
              quantity: item.qty,
              reason: lineRemovedReason,
              reasonID: this.orderID,
            },
            snapshot: {
              before: {
                available: currentAvailable,
                reserved: currentReserved,
                pending: currentPending,
                transfer: currentTransfer,
                inTesting: currentInTesting,
              },
              // "after" is calculated dynamically from "before" + movement.quantity in the display
            },
            location: {
              id: fulfillmentLocation,
              name:
                this.order.fulfillmentLocation?.name ||
                this.order.location.shipping.name,
            },
            order: this.order,
            user: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
          });

          let productLots = item.productLots ?? [];
          for (const productLot of productLots) {
            const productLotDoc = doc(firestore, "productLots", productLot.id);
            const productLotResponse = await getDoc(productLotDoc);

            if (productLotResponse.exists()) {
              const productLotData = productLotResponse.data();
              let availableUnits = productLotResponse.data().units;

              if (productLotData.hasOwnProperty("available")) {
                availableUnits =
                  Number(productLotResponse.data().available) +
                  productLot.units;
              }

              const newRelatedLineItems =
                productLotData.relatedLineItems?.filter(
                  (lineItem) => lineItem.id !== item.id,
                ) ?? [];

              await updateDoc(
                productLotDoc,
                {
                  active: availableUnits > 0,
                  available: availableUnits,
                  relatedLineItems: newRelatedLineItems,
                  updated: serverTimestamp(),
                  lastUpdated: {
                    name: store.getters.getUser.name,
                    uid: store.getters.getUser.uid,
                  },
                },
                { merge: true },
              );
            }
          }
          removedUpdates.push(
            `<span class="text-capitalize font-weight-bold">${
              item.name
            }</span> (${item.sku.toUpperCase()}) - <span class="text-uppercase">qty</span>: ${
              item.qty
            }`,
          );

          if (this.orderIsExternal) {
            switch (this.externalOrderType) {
              case "leaflink":
                if (this.externalUpdates.leaflink.includes("orderUpdated")) {
                  let externalItem = null;
                  if (item.externalIDs?.leaflinkLineItemID) {
                    externalItem = {
                      id: item.externalIDs.leaflinkLineItemID,
                    };
                  } else {
                    if (this.leaflinkClient.lineItems?.length === 0) {
                      await this.leaflinkClient.getLineItems();
                    }
                    externalItem = this.leaflinkClient.lineItems.find(
                      (external) =>
                        external.productID === item.externalIDs.leaflink,
                    );
                  }
                  if (externalItem) {
                    addBreadcrumb("leaflink: deleteLineItem (deliveryConfirmation)", {
                      workflow: "sales-order",
                      data: { orderId: this.orderID, itemId: item.id, externalItemId: externalItem.id },
                    });
                    try {
                      await this.leaflinkClient.deleteLineItem(externalItem);
                    } catch (error) {
                      logWorkflowWarning(`LeafLink: deleteLineItem (deliveryConfirmation) failed — ${error.message}`, {
                        workflow: "sales-order",
                        action: "leaflink-delivery-confirmation-delete-item",
                        context: { orderId: this.orderID, itemId: item.id, balance: this.order?.balance },
                      });
                      throw error;
                    }
                  }
                }
                break;
              default:
                break;
            }
          }
        }
        updates.push(
          `<span class="text-capitalize">removed items</span>: ${removedUpdates.join(
            " / ",
          )}`,
        );
      }

      console.log("666");

      status = "deliveryConfirmation";
      const oldStatus = store.getters.getOrderStatus.find(
          (option) => option.value === this.order.status,
        ),
        newStatus = store.getters.getOrderStatus.find(
          (option) => option.value === status,
        );

      updates.push(
        `<span class="text-capitalize">status</span> - <span class="text-uppercase">old</span>: <span class="font-weight-bold" style="color: ${oldStatus.color};">${oldStatus.text}</span> => <span class="text-uppercase">new</span>: <span class="font-weight-bold" style="color: ${newStatus.color};">${newStatus.text}</span>`,
      );

      batch.set(
        salesOrderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          records: {
            [status]: moment().startOf("day").add(12, "hours").toDate(),
          },
        },
        { merge: true },
      );

      console.log("695");

      // Recalculate order balance & QTY

      const units = fulfilled.reduce((a, b) => a + b.qty, 0);
      const total = fulfilled.reduce((a, b) => a + b.amount * b.qty, 0);

      let removedAmount = removed.reduce((a, b) => a + b.amount * b.qty, 0);
      console.log("removedAmount", removedAmount);

      const {
        total: balanceTotal,
        due: balanceDue,
        discountTotal,
      } = computeOrderBalance({
        lineSubtotal: total,
        discountType: this.order.discount?.type ?? "%",
        discountValue: this.order.discount?.value ?? 0,
        shipping: Number(this.order.balance.shipping),
        paymentsApplied: sumAppliedPayments(this.payments),
      });
      // Keep numeric locals for overpay / checkOverpaymentCredits; stringify on write.

      addBreadcrumb("deliveryConfirmationAction: balance recalculated", {
        workflow: "sales-order",
        data: {
          orderId: this.orderID,
          balanceDue,
          balanceTotal,
          fulfilledCount: fulfilled.length,
          removedCount: removed.length,
        },
      });

      // Removed lines can drop the total below what is already paid; block before commit.
      this._assertFiniteBalance("delivery-confirmation-overpay-preflight", {
        total: balanceTotal,
        due: balanceDue,
      });
      // Cross-check the payment docs against the stored ledger so a stale `due` cannot
      // understate the overpay, matching the dual basis updateLineItems commits on.
      const { overpay: deliveryOverpay } = computeOverpay({
        newTotal: balanceTotal,
        payments: this.payments,
        balanceTotal: this.order.balance.total,
        balanceDue: this.order.balance.due,
      });
      this._assertOverpaymentAbsorbable(
        "delivery-confirmation-overpay-preflight",
        deliveryOverpay,
      );
      // Gated so the payableTo lookup only runs on the rare overpaid delivery.
      if (deliveryOverpay > 0) {
        this._assertOverpaymentCreditable(
          "delivery-confirmation-overpay-preflight",
          deliveryOverpay,
          await this._isPayableToMissing(),
        );
      }

      batch.set(
        salesOrderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          balance: {
            due: parseFloat(balanceDue).toFixed(2),
            total: parseFloat(balanceTotal).toFixed(2),
          },
          lineItems: fulfilled,
          status,
          units,
          discount: {
            value: this.order.discount?.value ?? 0,
            type: this.order.discount?.type ?? "%",
            total: discountTotal,
          },
        },
        { merge: true },
      );

      // Create activity for delivery confirmation
      await createActivity({
        type: "status",
        action: "Delivery confirmation updated",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} delivery confirmation processed - ${
          fulfilled.length
        } items fulfilled, ${removed.length} items removed`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        changeData: {
          field: "deliveryConfirmation",
          oldValue: {
            text: oldStatus.text,
            value: oldStatus.value,
            color: oldStatus.color,
          },
          newValue: {
            text: newStatus.text,
            value: newStatus.value,
            color: newStatus.color,
          },
          fulfilledItems: fulfilled.map((item) => ({
            name: item.name,
            sku: item.sku,
            qty: item.qty,
            notes: item.notes,
            amount: item.amount,
            totalAmount: (item.amount * item.qty).toFixed(2),
            category: item.category,
            productID: item.productID,
            categoryID: item.categoryID,
          })),
          removedItems: removed.map((item) => ({
            name: item.name,
            sku: item.sku,
            qty: item.qty,
            amount: item.amount,
            totalAmount: (item.amount * item.qty).toFixed(2),
            category: item.category,
            productID: item.productID,
            categoryID: item.categoryID,
            reason: "Removed during delivery confirmation",
          })),
          summary: {
            totalFulfilledItems: fulfilled.length,
            totalRemovedItems: removed.length,
            totalFulfilledQty: fulfilled.reduce(
              (sum, item) => sum + item.qty,
              0,
            ),
            totalRemovedQty: removed.reduce((sum, item) => sum + item.qty, 0),
            totalFulfilledAmount: fulfilled
              .reduce((sum, item) => sum + item.amount * item.qty, 0)
              .toFixed(2),
            totalRemovedAmount: removed
              .reduce((sum, item) => sum + item.amount * item.qty, 0)
              .toFixed(2),
            balanceChange: removedAmount.toFixed(2),
          },
          orderNumber: this.order.orderNumber.internal,
          processType: "delivery_confirmation",
        },
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
      });

      // Ledger
      updates = updates.join(" | ");

      addBreadcrumb("deliveryConfirmationAction: committing batch", {
        workflow: "sales-order",
        data: { orderId: this.orderID, balanceDue, balanceTotal },
      });
      await batch.commit();

      await this._checkBalanceAnomaly("delivery-confirmation", {
        balanceDue,
        balanceTotal,
      });

      try {
        await this.checkOverpaymentCredits(balanceDue);
      } catch (creditsErr) {
        logWorkflowWarning(
          `deliveryConfirmationAction: checkOverpaymentCredits failed after commit — ${creditsErr.message}`,
          {
            workflow: "sales-order",
            action: "delivery-confirmation-overpayment-credits",
            context: { orderId: this.orderID, balanceDue, balanceTotal },
          },
        );
        await this._logPostCommitFailure(
          "deliveryConfirmationAction",
          creditsErr,
          {
            note: "Delivery confirmed; overpayment credit was not created — AR is overpaid until reconciled",
            balanceDue,
          },
        );
        throw creditsErr;
      }
    } catch (error) {
      throw error;
    }
  }

  async applyLeftoverPendingPromotionCredits(
    notes = "Credit applied on order approval",
  ) {
    const result = await applyPendingPromotionCredits({
      salesOrderClient: this,
      notes,
    });

    if (result.failedIds.length > 0 || result.remainingPendingIds.length > 0) {
      logWorkflowWarning(
        "applyLeftoverPendingPromotionCredits: some credits were not applied",
        {
          workflow: "sales-order",
          action: "pending-credits-partial",
          context: {
            orderId: this.orderID,
            appliedIds: result.appliedIds,
            failedIds: result.failedIds,
            remainingPendingIds: result.remainingPendingIds,
            balance: this.order?.balance,
          },
        },
      );
    }

    return result;
  }

  // [APPROVAL]
  async approvalAction(approved, reason) {
    addBreadcrumb("approvalAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, approved, balance: this.order?.balance },
    });
    await this.getOrder();
    await this.getLineItems();

    const targetStatus = approved ? "submitted" : "cancelled";

    // Already past draft: still recover leftover pending credits (Local Leaf class).
    if (this.order?.status === "cancelled") {
      return { pendingCredits: null };
    }
    if (
      this.order?.status === "submitted" ||
      this.order?.status === "accepted" ||
      this.order?.status === "fulfilled" ||
      this.order?.status === "shipped" ||
      this.order?.status === "complete"
    ) {
      if (approved) {
        const pendingCredits = await this.applyLeftoverPendingPromotionCredits(
          "Credit applied on order approval (recovery)",
        );
        return { pendingCredits };
      }
      return { pendingCredits: null };
    }

    // Atomic claim: only one concurrent request can transition to submitted/cancelled.
    // Capture pending credit IDs from the same snapshot so we don't lose them if
    // later steps fail after status has already moved.
    const orderDocRef = doc(firestore, "salesOrders", this.order.id);
    const claimResult = await runTransaction(firestore, async (transaction) => {
      const orderSnap = await transaction.get(orderDocRef);
      const orderData = orderSnap.exists() ? orderSnap.data() : null;
      const current = orderData?.status ?? null;
      const pendingCreditIds = collectPendingPromotionCreditIds(orderData);

      if (current === "submitted" || current === "cancelled") {
        return { claimed: false, pendingCreditIds };
      }

      transaction.update(orderDocRef, {
        status: targetStatus,
        updated: serverTimestamp(),
        lastUpdated: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
      });
      return { claimed: true, pendingCreditIds };
    });

    const pendingCreditIds = approved
      ? claimResult.pendingCreditIds || []
      : [];

    if (!claimResult.claimed) {
      // Do not apply here — the winner is still applying. Racing recovery caused
      // duplicate promo payments. Leftovers retry on Accept / Apply pending button.
      return { pendingCredits: null };
    }

    let pendingCreditsResult = null;

    try {
      const batch = writeBatch(firestore);
      const orderDoc = orderDocRef;
      const stateDoc = doc(
        firestore,
        "states",
        this.order.location.shipping.state.stateID,
      );
      const stateResponse = await getDoc(stateDoc);
      let fulfillmentLocation = stateResponse.data().fulfillmentLocation;

      if (this.order.fulfillmentLocation) {
        fulfillmentLocation = this.order.fulfillmentLocation.id;
      }

      const locationRef = doc(
        firestore,
        "locationManufacturing",
        `${fulfillmentLocation}_${this.order.brand.id}`,
      );

      let manufacturing = {};

      const oldValue = store.getters.getOrderStatus.find(
          (status) => status.value === this.order.status,
        ),
        newValue = store.getters.getOrderStatus.find(
          (status) =>
            status.value === `${approved ? "submitted" : "cancelled"}`,
        );

      // Update order
      batch.set(
        orderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          status: newValue.value,
          records: {
            [newValue.value]: moment().startOf("day").add(12, "hours").toDate(),
          },
        },
        { merge: true },
      );
      // External - Update Status
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (!approved) {
              if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                addBreadcrumb("leaflink: transitionOrder reject (approvalAction)", {
                  workflow: "sales-order",
                  data: { orderId: this.orderID, fromStatus: this.order?.status },
                });
                try {
                  await this.leaflinkClient.transitionOrder("reject");
                } catch (error) {
                  logWorkflowWarning(`LeafLink: transitionOrder("reject") (approvalAction) failed — ${error.message}`, {
                    workflow: "sales-order",
                    action: "leaflink-approval-reject-transition",
                    context: { orderId: this.orderID, balance: this.order?.balance },
                  });
                  throw error;
                }
              }
            }
            break;
          default:
            break;
        }
      }

      if (!approved) {
        // Reject: release pending credits without applying them as payments
        const rejectPendingIds = collectPendingPromotionCreditIds(this.order);
        if (rejectPendingIds.length > 0) {
          batch.set(
            orderDoc,
            {
              pendingPromotionCredit: null,
              pendingPromotionCredits: [],
            },
            { merge: true },
          );
        }

        // Read current inventory before updating (for activity snapshot)
        const locationSnap = await getDoc(locationRef);
        manufacturing =
          locationSnap.exists() && locationSnap.data().manufacturing
            ? locationSnap.data().manufacturing
            : {};

        // Aggregate by product so same product (e.g. regular + promo) gets one location update and one activity
        const rejectActivityMap = {};
        for (const item of this.lineItems) {
          const key = `${item.categoryID}_${item.productID}`;
          if (!rejectActivityMap[key]) {
            rejectActivityMap[key] = { totalQty: 0, lineItem: item };
          }
          rejectActivityMap[key].totalQty += item.qty ?? 0;
        }
        for (const key of Object.keys(rejectActivityMap)) {
          const agg = rejectActivityMap[key];
          batch.set(
            locationRef,
            {
              manufacturing: {
                [agg.lineItem.categoryID]: {
                  reserved: {
                    [agg.lineItem.productID]: increment(-agg.totalQty),
                  },
                },
              },
            },
            { merge: true },
          );
        }

        const brandStatsDoc = doc(
          firestore,
          "brandStats",
          `${this.order.account.id}_${this.order.brand.id}`,
        );
        const brandStats = await getDoc(brandStatsDoc);
        if (brandStats.data().metrics.closeDate?.order === orderDoc.id) {
          batch.set(
            brandStatsDoc,
            {
              metrics: {
                closeDate: null,
              },
            },
            { merge: true },
          );
        }
      }

      let updateString = `<span class="text-uppercase">${
        approved ? "approved" : "rejected"
      }</span>`;
      let rejectionNotes = reason.length > 0 ? ` | Notes: ${reason}` : "";

      // Create activity for order approval/rejection
      await createActivity({
        type: "status",
        action: approved ? "Order approved" : "Order rejected",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} ${
          approved ? "approved" : "rejected"
        }${reason ? ` - Reason: ${reason}` : ""}`,
        relatedTo: "salesOrders",
        relatedID: [this.order.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "approvalStatus",
          approved: approved,
          oldValue: {
            text: oldValue.text,
            value: oldValue.value,
            color: oldValue.color,
          },
          newValue: {
            text: newValue.text,
            value: newValue.value,
            color: newValue.color,
          },
          reason: reason,
          orderNumber: this.order.orderNumber.internal,
          inventoryReset: !approved,
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
        },
      });

      addBreadcrumb("approvalAction: committing batch", {
        workflow: "sales-order",
        data: { orderId: this.orderID, approved, balance: this.order?.balance },
      });
      await batch.commit();

      // Record inventory activities for rejection (reserved → available); one per product
      if (!approved && this.lineItems.length > 0) {
        const fulfillmentLocationName =
          this.order.fulfillmentLocation?.name ||
          this.order.location?.shipping?.name ||
          "";
        const rejectActivityMap = {};
        for (const item of this.lineItems) {
          const key = `${item.categoryID}_${item.productID}`;
          if (!rejectActivityMap[key]) {
            rejectActivityMap[key] = { totalQty: 0, lineItem: item };
          }
          rejectActivityMap[key].totalQty += item.qty ?? 0;
        }
        for (const key of Object.keys(rejectActivityMap)) {
          const agg = rejectActivityMap[key];
          const { categoryID, productID } = agg.lineItem;
          const mfg = manufacturing[categoryID] || {};
          const currentAvailable = mfg.available?.[productID] ?? 0;
          const currentReserved = mfg.reserved?.[productID] ?? 0;
          const currentPending = mfg.pending?.[productID] ?? 0;
          const currentTransfer = mfg.transfer?.[productID] ?? 0;
          const currentInTesting = mfg.inTesting?.[productID] ?? 0;
          const skuID = `${fulfillmentLocation}_${this.order.brand.id}_${categoryID}_${productID}`;
          try {
            await createInventoryActivity({
              action: "Inventory released",
              item: `${agg.totalQty} units of ${agg.lineItem.name} (${agg.lineItem.sku}) released from rejected Order #${this.order.orderNumber.internal}`,
              skuID,
              itemData: agg.lineItem,
              movement: {
                type: "release",
                from: "reserved",
                to: "available",
                quantity: agg.totalQty,
                reason: "salesOrderRejected",
                reasonID: this.orderID,
              },
              snapshot: {
                before: {
                  available: currentAvailable,
                  reserved: currentReserved,
                  pending: currentPending,
                  transfer: currentTransfer,
                  inTesting: currentInTesting,
                },
              },
              location: {
                id: fulfillmentLocation,
                name: fulfillmentLocationName,
              },
              order: this.order,
              user: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              extraSalesOrderData: {
                orderID: this.orderID,
                rejectionReason: reason,
              },
            });
          } catch (err) {
            console.error("createInventoryActivity (order rejected):", err);
            logWorkflowWarning(`approvalAction: inventory activity creation failed — ${err.message}`, {
              workflow: "sales-order",
              action: "approval-inventory-activity",
              context: { orderId: this.orderID, productKey: key, balance: this.order?.balance },
            });
          }
        }
      }

      // Apply pending credits AFTER status commit. Do not clear pending first —
      // only drop IDs that successfully become payments (failed ones stay for recovery).
      if (approved && pendingCreditIds.length > 0) {
        try {
          pendingCreditsResult = await applyPendingPromotionCredits({
            salesOrderClient: this,
            creditIds: pendingCreditIds,
            notes: "Credit applied on order approval",
          });

          addBreadcrumb("approvalAction: pending credits applied", {
            workflow: "sales-order",
            data: {
              orderId: this.orderID,
              appliedCount: pendingCreditsResult.appliedIds.length,
              failedCount: pendingCreditsResult.failedIds.length,
              remainingCount: pendingCreditsResult.remainingPendingIds.length,
            },
          });

          if (pendingCreditsResult.remainingPendingIds.length > 0) {
            logWorkflowWarning(
              "approvalAction: pending credits remain after approval — will retry on accept/recovery",
              {
                workflow: "sales-order",
                action: "approval-pending-credits-remaining",
                context: {
                  orderId: this.orderID,
                  remainingPendingIds: pendingCreditsResult.remainingPendingIds,
                  failedIds: pendingCreditsResult.failedIds,
                  balance: this.order?.balance,
                },
              },
            );
          }
        } catch (error) {
          console.error(
            "Error applying pending credits on order approval:",
            error,
          );
          logWorkflowWarning(
            `approvalAction: pending credits block failed — ${error.message}`,
            {
              workflow: "sales-order",
              action: "approval-pending-credits-failed",
              context: {
                orderId: this.orderID,
                pendingCreditIds,
                balance: this.order?.balance,
              },
            },
          );
          // Do not reset pending here — util/applyPayment may have already
          // cleared IDs that landed. Status is committed; recovery can retry.
          pendingCreditsResult = {
            attemptedIds: pendingCreditIds,
            appliedIds: [],
            skippedIds: [],
            failedIds: [...pendingCreditIds],
            remainingPendingIds: [...pendingCreditIds],
            zeroBalance: false,
          };
        }
      }

      const notificationsRef = collection(firestore, "notifications");
      const related = where("relatedTo.id", "==", orderDoc.id);
      const notes = where("notes", "==", "Order Approval Required");
      const viewed = where("viewed", "==", false);
      const q = query(notificationsRef, related, notes, viewed);
      const response = await getDocs(q);

      if (!response.empty) {
        await updateDoc(
          doc(firestore, "notifications", response.docs[0].id),
          {
            updated: serverTimestamp(),
            lastUpdated: {
              name: store.getters.getUser.name,
              uid: store.getters.getUser.uid,
            },
            viewed: true,
          },
          { merge: true },
        );
      }
    } catch (error) {
      throw error;
    }

    return { pendingCredits: pendingCreditsResult };
  }

  // [ACCEPTED]
  async acceptedAction() {
    addBreadcrumb("acceptedAction: start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, fromStatus: this.order?.status, balance: this.order?.balance },
    });
    console.log("acceptedAction");
    await this.getOrder();
    await this.getLineItems();

    let pendingCreditsResult = null;

    try {
      const batch = writeBatch(firestore);
      const orderDoc = doc(firestore, "salesOrders", this.order.id);
      console.log(this.order);
      const oldValue = store.getters.getOrderStatus.find(
          (status) => status.value === this.order.status,
        ),
        newValue = store.getters.getOrderStatus.find(
          (status) => status.value === "accepted",
        );

      console.log(oldValue, newValue);

      // Update order
      batch.set(
        orderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          status: newValue.value,
          records: {
            [newValue.value]: moment().startOf("day").add(12, "hours").toDate(),
          },
        },
        { merge: true },
      );

      // Create activity for order acceptance
      await createActivity({
        type: "status",
        action: "Order accepted",
        item: `Order #${this.order.orderNumber.internal.toUpperCase()} status updated to accepted`,
        relatedTo: "salesOrders",
        relatedID: [orderDoc.id],
        user: {
          name: store.getters.getUser.name,
          uid: store.getters.getUser.uid,
        },
        account: {
          id: this.order.account.id,
          name: this.order.account.name,
        },
        brand: {
          id: this.order.brand.id,
          name: this.order.brand.name,
        },
        businessImpact: "high",
        priority: "high",
        changeData: {
          field: "acceptanceStatus",
          oldValue: {
            text: oldValue.text,
            value: oldValue.value,
            color: oldValue.color,
          },
          newValue: {
            text: newValue.text,
            value: newValue.value,
            color: newValue.color,
          },
          orderNumber: this.order.orderNumber.internal,
          externalOrderUpdated:
            this.externalUpdates.leaflink.includes("statusUpdated"),
          updateType: "manual_acceptance",
        },
      });

      // Try nudool operations first
      await batch.commit();
      addBreadcrumb("acceptedAction: committed", {
        workflow: "sales-order",
        data: { orderId: this.orderID },
      });

      // Safety net: if approval left pendingPromotionCredits stuck (status moved
      // but payments never created), apply them when the order is accepted.
      try {
        pendingCreditsResult = await this.applyLeftoverPendingPromotionCredits(
          "Credit applied on order acceptance (pending recovery)",
        );
      } catch (creditError) {
        console.error(
          "acceptedAction: pending credit recovery failed:",
          creditError,
        );
        logWorkflowWarning(
          `acceptedAction: pending credit recovery failed — ${creditError.message}`,
          {
            workflow: "sales-order",
            action: "accepted-pending-credits-failed",
            context: {
              orderId: this.orderID,
              balance: this.order?.balance,
            },
          },
        );
        const leftoverIds = collectPendingPromotionCreditIds(this.order);
        pendingCreditsResult = {
          attemptedIds: leftoverIds,
          appliedIds: [],
          skippedIds: [],
          failedIds: [...leftoverIds],
          remainingPendingIds: [...leftoverIds],
          zeroBalance: false,
        };
      }

      console.log("this.orderIsExternal", this.orderIsExternal);

      // External - Update Status (only after nudool commit succeeds)
      if (this.orderIsExternal) {
        try {
          switch (this.externalOrderType) {
            case "leaflink":
              if (this.externalUpdates.leaflink.includes("statusUpdated")) {
                let orderData = await this.leaflinkClient.getOrder();
                addBreadcrumb("leaflink: acceptedAction — checking status before transition", {
                  workflow: "sales-order",
                  data: { orderId: this.orderID, leaflinkStatus: orderData?.status ?? null },
                });
                if (orderData?.status?.toLowerCase() === "submitted") {
                  await this.leaflinkClient.transitionOrder("accept");
                }
              }
              break;
            default:
              break;
          }
        } catch (e) {
          console.log("Leaflink transition failed:", e);
          // Note: nudool has already been committed successfully at this point
          // Consider adding rollback logic or notification here if needed
          logWorkflowWarning(`LeafLink: acceptedAction transitionOrder failed — ${e.message}`, {
            workflow: "sales-order",
            action: "leaflink-accepted-transition",
            context: { orderId: this.orderID, balance: this.order?.balance },
          });
          throw e;
        }
      }
    } catch (error) {
      throw error;
    }

    return { pendingCredits: pendingCreditsResult };
  }

  // Separate method for external API operations - follows Firebase best practices
  async processExternalLineItems(addedItems, allLineItems) {
    addBreadcrumb("leaflink: processExternalLineItems start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, addedItemCount: addedItems?.length },
    });
    await this.getOrder();
    await this.getLineItems();

    for (const cartItem of addedItems) {
      try {
        if (this.orderIsExternal && this.externalOrderType === "leaflink") {
          // Find the corresponding processed line item using proper identification
          // Match by productID + promo + amount to differentiate regular vs promo items
          const processedLineItem = allLineItems.find(
            (item) =>
              item.id === cartItem.id &&
              Boolean(item.promo) === Boolean(cartItem.promo) &&
              item.amount === cartItem.amount,
          );

          console.log("processedLineItem", processedLineItem);

          if (
            processedLineItem &&
            this.externalUpdates.leaflink.includes("orderUpdated")
          ) {
            const data = await this.leaflinkClient.createLineItem(
              processedLineItem,
            );

            // Update the line item with external ID
            await updateDoc(
              doc(firestore, "salesOrders_lineItems", processedLineItem.id),
              {
                "externalIDs.leaflinkLineItemID": data.id,
              },
            );
          }
        }
      } catch (error) {
        console.error("Failed to process external line item:", error);
        logWorkflowWarning(`LeafLink: processExternalLineItems failed — ${error.message}`, {
          workflow: "sales-order",
          action: "leaflink-line-items-sync",
          context: { orderId: this.orderID, balance: this.order?.balance },
        });
        // Continue with other items even if one fails
      }
    }
  }
  // Separate method for updating external line items
  async processExternalLineItemUpdates(existingItems) {
    addBreadcrumb("leaflink: processExternalLineItemUpdates start", {
      workflow: "sales-order",
      data: { orderId: this.orderID, existingItemCount: existingItems?.length },
    });
    await this.getOrder();
    await this.getLineItems();

    for (const item of existingItems) {
      try {
        if (
          this.orderIsExternal &&
          this.externalOrderType === "leaflink" &&
          item.needsQTYPatch &&
          item.externalIDs?.leaflinkLineItemID &&
          this.externalUpdates.leaflink.includes("orderUpdated")
        ) {
          const externalItem = {
            id: item.externalIDs.leaflinkLineItemID,
          };

          const payloadData = {
            ordered_unit_price: {
              amount: item.amount,
              currency: "USD",
            },
            sale_price: {
              amount: item.amount,
              currency: "USD",
            },
            notes: item.notes,
            quantity: item.qty,
            is_sample: item.sample ?? false,
          };

          await this.leaflinkClient.updateLineItem(externalItem, payloadData);
        }
      } catch (error) {
        console.error("Failed to update Leaflink line item:", error);
        logWorkflowWarning(`LeafLink: processExternalLineItemUpdates failed — ${error.message}`, {
          workflow: "sales-order",
          action: "leaflink-line-items-update-sync",
          context: { orderId: this.orderID, balance: this.order?.balance },
        });
      }
    }
  }

  async checkOverpaymentCredits(updatedBalance = null) {
    addBreadcrumb("checkOverpaymentCredits: evaluating", {
      workflow: "sales-order",
      data: {
        orderId: this.orderID,
        updatedBalance,
        currentDue: this.order?.balance?.due,
        paymentCount: this.payments?.length,
      },
    });
    const batch = writeBatch(firestore);

    await this.getOrder();
    await this.getPayments();

    const brandStatsDoc = doc(
      firestore,
      "brandStats",
      `${this.order.account.id}_${this.order.brand.id}`,
    );

    const orderDoc = doc(firestore, "salesOrders", this.order.id);

    // Use passed balance if available, otherwise fetch
    let balanceDue;
    if (updatedBalance !== null) {
      // Neccessary to fix the race condition where this.getOrder() is ran before the
      // transaction in updateLineItems is committed
      balanceDue = Number(updatedBalance);
    } else {
      balanceDue = Number(this.order.balance.due);
    }

    let filteredPayments = creditablePayments(this.payments);

    let runningBalance = Math.abs(balanceDue);

    // Lets a caller revert the aggregate promotionCredits delta on failure without reconstructing individual docs.
    let promotionCreditsDelta = 0;
    const touchedPromotionCreditIDs = new Set();
    const touchedPaymentIDs = new Set();

    if (balanceDue < 0) {
      addBreadcrumb("checkOverpaymentCredits: negative balance — creating/updating credits", {
        workflow: "sales-order",
        data: {
          orderId: this.orderID,
          balanceDue,
          overpaymentPaymentCount: filteredPayments.filter((p) => p.overpaymentCreditID).length,
        },
      });
      // Check if there is already existing overpayment credits related to a payment
      if (filteredPayments.find((item) => item.overpaymentCreditID)) {
        // Get all overpayment payments

        let overpaymentPayments = filteredPayments.filter(
          (item) => item.overpaymentCreditID,
        );

        let sortedPayments = sortNewestFirst(overpaymentPayments);

        while (runningBalance > 0 && sortedPayments.length > 0) {
          let overpaymentPayment = sortedPayments.shift();

          if (!overpaymentPayment) break;

          // Overpayment amount should never be greater than payment amount
          let amountToAdd = Math.min(
            runningBalance,
            Number(
              parseFloat(
                overpaymentPayment.amount -
                  (overpaymentPayment.creditsApplied ?? 0),
              ).toFixed(2),
            ),
          );
          // Skip payments that already have their entire amount as overpayment credit
          if (amountToAdd === 0) continue;

          // Get the overpayment credit document
          let overpaymentCreditDoc = doc(
            firestore,
            "promotionCredits",
            overpaymentPayment.overpaymentCreditID,
          );
          let paymentDoc = doc(
            firestore,
            "salesOrders_payments",
            overpaymentPayment.id,
          );
          let overpaymentCredit = await getDoc(overpaymentCreditDoc);
          // Without this, `?? 0` below would top up from zero and set+merge would mint an
          // incomplete credit doc while still charging the payment's creditsApplied.
          this._assertOverpaymentCreditExists(
            "overpayment-credit-topup",
            overpaymentCredit,
            overpaymentPayment,
          );
          let overpaymentCreditData = overpaymentCredit.data();

          let newTotal = Number(
            parseFloat(
              Number(overpaymentCreditData?.total ?? 0) + amountToAdd,
            ).toFixed(2),
          );
          let newAvailable = Number(
            parseFloat(
              Number(overpaymentCreditData?.available ?? 0) + amountToAdd,
            ).toFixed(2),
          );
          let newCreditsApplied = Number(
            parseFloat(
              Number(overpaymentPayment.creditsApplied ?? 0) + amountToAdd,
            ).toFixed(2),
          );

          batch.set(
            overpaymentCreditDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              active: true,
              total: newTotal,
              available: newAvailable,
            },
            {
              merge: true,
            },
          );

          // Brand Stats - Metrics
          batch.set(
            brandStatsDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              metrics: {
                promotionCredits: {
                  total: increment(amountToAdd),
                  available: increment(amountToAdd),
                },
              },
            },
            { merge: true },
          );
          // Payment Doc
          batch.set(
            paymentDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              creditsApplied: newCreditsApplied,
            },
            { merge: true },
          );

          promotionCreditsDelta += amountToAdd;
          touchedPromotionCreditIDs.add(overpaymentCreditDoc.id);
          touchedPaymentIDs.add(paymentDoc.id);

          runningBalance = Number(
            parseFloat(runningBalance - amountToAdd).toFixed(2),
          );
        }
      }
      if (runningBalance > 0) {
        // Create new overpayment credit(s) related to eligible payment(s) without an existing credit
        let sortedPayments = sortNewestFirst(
          filteredPayments.filter((payment) => !payment.overpaymentCreditID),
        );

        while (runningBalance > 0 && sortedPayments.length > 0) {
          let mostRecentPayment = sortedPayments.shift();

          if (!mostRecentPayment) break;

          const remainingCapacity = creditCapacity(mostRecentPayment);
          if (remainingCapacity <= 0) continue;

          let creditDoc = doc(collection(firestore, "promotionCredits"));

          const payableToRef = collection(firestore, "payableTo");
          const state = where(
              "location.state.stateID",
              "==",
              this.order.location.shipping.state.stateID,
            ),
            brand = where("brandID", "==", this.order.brand.id);
          const q = query(payableToRef, state, brand);
          const response = await getDocs(q, limit(1));

          if (response.empty) {
            logWorkflowWarning(
              "checkOverpaymentCredits: no payableTo found for overpayment credit",
              {
                workflow: "sales-order",
                action: "overpayment-payableto-missing",
                context: { orderId: this.orderID, balanceDue },
              },
            );
            throw new Error(
              "Unable to create overpayment credit: payable-to record not found for this brand/state.",
            );
          }

          let payableTo = {
            id: response.docs[0].id,
            ...response.docs[0].data(),
          };

          const brandAbbreviation = this.order.brand.name
            .split(" ")
            .map((word) => word.charAt(0))
            .join("");

          let stateAbbrevation =
            this.order.location.shipping.state.abbreviation;
          let existingCredits = payableTo.records?.creditMemos ?? 0;

          let accountDoc = doc(firestore, "accounts", this.order.account.id);
          let accountResponse = await getDoc(accountDoc);

          let countryID = accountResponse.data().location.shipping.countryID;

          let creditsApplied = Math.min(runningBalance, remainingCapacity);

          runningBalance = Number(
            parseFloat(runningBalance - creditsApplied).toFixed(2),
          );

          batch.set(
            creditDoc,
            {
              created: serverTimestamp(),
              updated: serverTimestamp(),
              createdBy: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              account: {
                name: this.order.account.name,
                id: this.order.account.id,
              },
              brand: {
                id: this.order.brand.id,
                name: this.order.brand.name,
              },
              location: {
                countryID: countryID,
                state: this.order.location.shipping.state.stateID,
              },
              active: true,
              reversed: false,
              date: moment(mostRecentPayment.paymentDate.seconds * 1000)
                .startOf("day")
                .add(9, "hours")
                .tz("America/Los_Angeles")
                .toDate(),
              expirationDate: moment()
                .add(6, "months")
                .startOf("day")
                .add(9, "hours")
                .tz("America/Los_Angeles")
                .toDate(),
              expirationReminderSent: false,
              orderNumber: {
                internal: `${brandAbbreviation}-cm-${stateAbbrevation}-${
                  existingCredits + 1
                }`,
                external: "",
              },
              total: creditsApplied,
              available: creditsApplied,
              type: "overpayment",
              relatedID: this.order.id,
              notes: "Created from order balance reduction (line items / fulfillment)",
              details: `Source payment ${mostRecentPayment.id} (${mostRecentPayment.paymentType})`,
              promotion: null,
            },
            {
              merge: true,
            },
          );

          // Payment Doc
          batch.set(
            doc(firestore, "salesOrders_payments", mostRecentPayment.id),
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              creditsApplied: Number(
                parseFloat(
                  Number(mostRecentPayment.creditsApplied ?? 0) + creditsApplied,
                ).toFixed(2),
              ),
              overpaymentCreditID: creditDoc.id,
            },
            { merge: true },
          );

          // Brand Stats - Metrics
          batch.set(
            brandStatsDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              metrics: {
                promotionCredits: {
                  total: increment(creditsApplied),
                  available: increment(creditsApplied),
                },
              },
            },
            { merge: true },
          );

          batch.set(
            doc(firestore, "payableTo", payableTo.id),
            {
              records: {
                creditMemos: increment(1),
              },
            },
            { merge: true },
          );

          promotionCreditsDelta += creditsApplied;
          touchedPromotionCreditIDs.add(creditDoc.id);
          touchedPaymentIDs.add(mostRecentPayment.id);
        }
      }

      // Fail before external side-effects / commit if overpayment was not fully absorbed.
      if (!isFullyAbsorbed(runningBalance)) {
        logWorkflowWarning(
          "Overpayment not fully absorbed — leaving negative due",
          {
            workflow: "sales-order",
            action: "check-overpayment-credits",
            context: {
              orderId: this.orderID,
              balanceDue,
              unabsorbed: runningBalance,
              eligiblePaymentCount: filteredPayments.length,
            },
          },
        );
        throw new Error(
          `Unable to absorb overpayment of ${new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
          }).format(Math.abs(balanceDue))}. Remaining unabsorbed: ${new Intl.NumberFormat(
            "en-US",
            { style: "currency", currency: "USD" },
          ).format(runningBalance)}. Reverse or adjust payments, then retry.`,
        );
      }

      let leaflinkEmail = null;
      let staff = [];
      if (this.orderIsExternal) {
        switch (this.externalOrderType) {
          case "leaflink":
            if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
              let data = await this.leaflinkClient.getUsers();
              staff = data.results;
              const integrationsRef = collection(firestore, "integrations");
              const platform = where("platform", "==", "leaflink");
              const integrationsQ = query(integrationsRef, platform);
              const integrationsResponse = await getDocs(
                integrationsQ,
                limit(1),
              );

              if (!integrationsResponse.empty) {
                leaflinkEmail = integrationsResponse.docs[0].data().email;
              }

              if (!leaflinkEmail) {
                logWorkflowWarning("checkOverpaymentCredits: aborted — LeafLink integration email not found, batch not committed", {
                  workflow: "sales-order",
                  action: "leaflink-overpayment-email-missing",
                  context: { orderId: this.orderID, balanceDue, balance: this.order?.balance },
                });
                throw new Error(
                  "Unable to absorb overpayment: LeafLink integration email not configured.",
                );
              }
            }
            break;
          default:
            break;
        }
      }

      let userLeaflinkInfo = staff.find(
        (staffMember) => staffMember.email === leaflinkEmail,
      );

      if (this.orderIsExternal) {
        try {
          switch (this.externalOrderType) {
            case "leaflink":
              if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
                let paymentDateMoment = moment();
                let actualPaymentDate = moment(
                  paymentDateMoment.clone().format("MM/DD/YYYY"),
                )
                  .add(9, "hours")
                  .tz("America/Los_Angeles");
                let paymentData = {
                  order: this.order.externalIDs.leaflink,
                  recorded_by: userLeaflinkInfo.id,
                  total: {
                    amount: balanceDue,
                    currency: "usd",
                  },
                  payment_date: moment(actualPaymentDate).toISOString(),
                  reason: "Automatic overpayment credit(s) creation",
                  payment_type: "other",
                  check_number: "",
                };
                await this.leaflinkClient.createPayment(paymentData);
              }
              break;
            default:
              break;
          }
        } catch (e) {
          logWorkflowWarning(`LeafLink: createPayment (checkOverpaymentCredits credit creation) failed — ${e.message}`, {
            workflow: "sales-order",
            action: "leaflink-overpayment-create-payment",
            context: { orderId: this.orderID, balance: this.order?.balance },
          });
          throw e;
        }
      }

      batch.set(
        orderDoc,
        {
          updated: serverTimestamp(),
          lastUpdated: {
            name: store.getters.getUser.name,
            uid: store.getters.getUser.uid,
          },
          balance: {
            due: parseFloat(0).toFixed(2),
          },
        },
        { merge: true },
      );
    } else {
      addBreadcrumb("checkOverpaymentCredits: positive balance — decrementing credits", {
        workflow: "sales-order",
        data: {
          orderId: this.orderID,
          balanceDue,
          overpaymentPaymentCount: filteredPayments.filter((p) => p.overpaymentCreditID).length,
        },
      });
      // Check if there is an existing overpayment credit related to a payment
      //      - If so, decrement the credit amount the minimum of: the running balance and the credit amount
      if (filteredPayments.find((item) => item.overpaymentCreditID)) {
        // Get the overpayment payments
        let overpaymentPayments = filteredPayments.filter(
          (item) => item.overpaymentCreditID,
        );

        let sortedPayments = overpaymentPayments.sort(
          (a, b) =>
            moment(a.created.seconds * 1000).toDate() -
            moment(b.created.seconds * 1000).toDate(), // Sort oldest to newest
        );

        let newBalance = Number(parseFloat(balanceDue).toFixed(2));

        while (runningBalance > 0 && sortedPayments.length > 0) {
          let overpaymentPayment = sortedPayments.shift();

          // Get the overpayment credit document
          let overpaymentCreditDoc = doc(
            firestore,
            "promotionCredits",
            overpaymentPayment.overpaymentCreditID,
          );
          let paymentDoc = doc(
            firestore,
            "salesOrders_payments",
            overpaymentPayment.id,
          );
          let overpaymentCredit = await getDoc(overpaymentCreditDoc);
          // A missing doc would read as total 0 and silently skip the decrement, leaving
          // the payment's creditsApplied pointing at credit that no longer exists.
          this._assertOverpaymentCreditExists(
            "overpayment-credit-decrement",
            overpaymentCredit,
            overpaymentPayment,
          );
          let overpaymentCreditData = overpaymentCredit.data();

          const creditTotal = Number(overpaymentCreditData?.total ?? 0);
          const creditAvailable = Number(overpaymentCreditData?.available ?? 0);

          let amountToSubtract = Math.min(runningBalance, creditTotal);

          if (amountToSubtract === 0) continue;

          let newTotal = Number(
            parseFloat(creditTotal - amountToSubtract).toFixed(2),
          );
          let newAvailable = Number(
            parseFloat(creditAvailable - amountToSubtract).toFixed(2),
          );
          let newCreditsApplied = Number(
            parseFloat(
              Number(overpaymentPayment.creditsApplied ?? 0) - amountToSubtract,
            ).toFixed(2),
          );

          if (newTotal <= 0) {
            batch.delete(overpaymentCreditDoc);
            batch.set(
              paymentDoc,
              {
                updated: serverTimestamp(),
                lastUpdated: {
                  name: store.getters.getUser.name,
                  uid: store.getters.getUser.uid,
                },
                overpaymentCreditID: null,
              },
              { merge: true },
            );
          } else {
            batch.set(
              overpaymentCreditDoc,
              {
                updated: serverTimestamp(),
                lastUpdated: {
                  name: store.getters.getUser.name,
                  uid: store.getters.getUser.uid,
                },
                active: true,
                total: newTotal,
                available: newAvailable,
              },
              {
                merge: true,
              },
            );
          }

          newBalance = Number(
            parseFloat(newBalance - amountToSubtract).toFixed(2),
          );

          // Update order
          batch.set(
            orderDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              balance: {
                due: parseFloat(newBalance).toFixed(2),
              },
            },
            { merge: true },
          );

          // Brand Stats - Metrics
          batch.set(
            brandStatsDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              metrics: {
                promotionCredits: {
                  total: increment(-amountToSubtract),
                  available: increment(-amountToSubtract),
                },
              },
            },
            { merge: true },
          );
          // Payment Doc
          batch.set(
            paymentDoc,
            {
              updated: serverTimestamp(),
              lastUpdated: {
                name: store.getters.getUser.name,
                uid: store.getters.getUser.uid,
              },
              creditsApplied: newCreditsApplied,
            },
            { merge: true },
          );

          promotionCreditsDelta += -amountToSubtract;
          touchedPromotionCreditIDs.add(overpaymentCreditDoc.id);
          touchedPaymentIDs.add(paymentDoc.id);

          runningBalance = Number(
            parseFloat(runningBalance - amountToSubtract).toFixed(2),
          );

          let leaflinkEmail = null;
          let staff = [];
          if (this.orderIsExternal) {
            switch (this.externalOrderType) {
              case "leaflink":
                if (this.externalUpdates.leaflink.includes("paymentsApplied")) {
                  let data = await this.leaflinkClient.getUsers();
                  staff = data.results;
                  const integrationsRef = collection(firestore, "integrations");
                  const platform = where("platform", "==", "leaflink");
                  const integrationsQ = query(integrationsRef, platform);
                  const integrationsResponse = await getDocs(
                    integrationsQ,
                    limit(1),
                  );

                  if (!integrationsResponse.empty) {
                    leaflinkEmail = integrationsResponse.docs[0].data().email;
                  }

                  if (!leaflinkEmail) {
                    logWorkflowWarning("checkOverpaymentCredits: aborted mid-loop — LeafLink integration email not found, batch not committed", {
                      workflow: "sales-order",
                      action: "leaflink-overpayment-credit-reversal-email-missing",
                      context: { orderId: this.orderID, balanceDue, balance: this.order?.balance },
                    });
                    throw new Error(
                      "Unable to reverse overpayment credit: LeafLink integration email not configured.",
                    );
                  }
                }
                break;
              default:
                break;
            }
          }

          let userLeaflinkInfo = staff.find(
            (staffMember) => staffMember.email === leaflinkEmail,
          );

          if (this.orderIsExternal) {
            try {
              switch (this.externalOrderType) {
                case "leaflink":
                  if (
                    this.externalUpdates.leaflink.includes("paymentsApplied")
                  ) {
                    let paymentDateMoment = moment();
                    let actualPaymentDate = moment(
                      paymentDateMoment.clone().format("MM/DD/YYYY"),
                    )
                      .add(9, "hours")
                      .tz("America/Los_Angeles");
                    let paymentData = {
                      order: this.order.externalIDs.leaflink,
                      recorded_by: userLeaflinkInfo.id,
                      total: {
                        amount: Number(amountToSubtract),
                        currency: "usd",
                      },
                      payment_date: moment(actualPaymentDate).toISOString(),
                      reason: "Automatic overpayment credit reversal",
                      payment_type: "other",
                      check_number: "",
                    };
                    await this.leaflinkClient.createPayment(paymentData);
                    break;
                  }
                default:
                  break;
              }
            } catch (e) {
              logWorkflowWarning(`LeafLink: createPayment (checkOverpaymentCredits credit reversal) failed — ${e.message}`, {
                workflow: "sales-order",
                action: "leaflink-overpayment-credit-reversal",
                context: { orderId: this.orderID, balance: this.order?.balance },
              });
              throw e;
            }
          }
        }
      }
    }

    await batch.commit();
    addBreadcrumb("checkOverpaymentCredits: committed", {
      workflow: "sales-order",
      data: { orderId: this.orderID, balanceDue },
    });
    return {
      // Re-rounded because summing already-rounded per-iteration amounts can still drift (IEEE-754).
      promotionCreditsDelta: Number(parseFloat(promotionCreditsDelta).toFixed(2)),
      touchedPromotionCreditIDs: [...touchedPromotionCreditIDs],
      touchedPaymentIDs: [...touchedPaymentIDs],
    };
  }

  static async fetchCampaigns(brandId, { activeOnly = true } = {}) {
    if (!brandId) return [];
    const constraints = [
      where("type", "==", "campaign"),
      where("brandID", "==", brandId),
    ];
    if (activeOnly) constraints.push(where("active", "==", true));
    const q = query(collection(firestore, "promotions"), ...constraints);
    try {
      const snap = await getDocs(q);
      return snap.docs
        .map((d) => ({ text: d.data().name, value: d.id, active: d.data().active ?? false }))
        .sort((a, b) => (a.text > b.text ? 1 : -1));
    } catch (e) {
      console.error("fetchCampaigns failed:", e);
      throw e;
    }
  }
}

export default SalesOrderClient;
